import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { getRoadDividerType, separatorCostPerMeter } from 'src/app/shared/constants/road-divider-types';
import {
  AssetMapBridgeService,
  AssetMapContext,
  AssetSavePayload,
} from 'src/app/components/admin/asset-management/asset-map-bridge.service';
import {
  AssetClassification,
  AssetFeature,
  AssetLayer,
  AssetStatus,
} from 'src/app/shared/services/asset-management.service';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import { Draw, Modify, Select, Snap, Translate } from 'ol/interaction';
import { Fill, Stroke, Style, Circle as CircleStyle, Text as TextStyle, Icon, Text, RegularShape } from 'ol/style';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import ImageStatic from 'ol/source/ImageStatic';
import Heatmap from 'ol/layer/Heatmap';
import WFS from 'ol/format/WFS';
import { fromLonLat, toLonLat } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import KML from 'ol/format/KML';
import GPX from 'ol/format/GPX';
import { FeatureLike } from 'ol/Feature';
import { defaults as defaultControls, ScaleLine } from 'ol/control';
import { LineString, Polygon, Point, Circle, Geometry } from 'ol/geom';
import { Extent, createEmpty, extend as extendExtent } from 'ol/extent';
import Overlay from 'ol/Overlay';
import { Feature } from 'ol';
import { getLength as getGeodesicLength, getArea as getGeodesicArea } from 'ol/sphere';
import { strokeOptsForGrade } from 'src/app/shared/utils/lrs-asset-symbology';
import { GisavieditorComponent } from "../gisavieditor/gisavieditor.component";
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { OsmMapApiService, friendlyOsmOverlayStatus } from 'src/app/shared/services/osm-map-api.service';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';

// Import Turf.js for geometric calculations (optional, as fallback)
declare const turf: any;

/** Vector type for theming: point, text, line, segment, sub_segment, section */
export type VectorThemeType = 'point' | 'text' | 'line' | 'segment' | 'sub_segment' | 'section';

/** Style definition for one vector type in a palette */
export interface VectorTypeStyle {
  strokeColor: string;
  fillColor: string;
  strokeWidth: number;
  pointRadius?: number;
  textColor?: string;
  fontSize?: number;
  lineDash?: number[];
}

/** Predefined palette: one style per vector type */
export interface LayerPalette {
  id: string;
  name: string;
  point: VectorTypeStyle;
  text: VectorTypeStyle;
  line: VectorTypeStyle;
  segment: VectorTypeStyle;
  sub_segment: VectorTypeStyle;
  section: VectorTypeStyle;
}

/** Predefined theming palettes for layers */
export const PREDEFINED_LAYER_PALETTES: LayerPalette[] = [
  {
    id: 'default',
    name: 'Default Blue',
    point:   { strokeColor: '#2980b9', fillColor: 'rgba(52, 152, 219, 0.8)',  strokeWidth: 2, pointRadius: 6, textColor: '#2c3e50', fontSize: 12, lineDash: [] },
    text:    { strokeColor: '#2c3e50', fillColor: '#2c3e50', strokeWidth: 1, textColor: '#2c3e50', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#3498db', fillColor: 'transparent', strokeWidth: 2, lineDash: [] },
    segment: { strokeColor: '#e74c3c', fillColor: 'transparent', strokeWidth: 3, lineDash: [5, 5], textColor: '#000', fontSize: 10 },
    sub_segment: { strokeColor: '#e67e22', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#7f8c8d', fontSize: 9 },
    section: { strokeColor: '#27ae60', fillColor: 'rgba(39, 174, 96, 0.25)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure',
    point:   { strokeColor: '#34495e', fillColor: 'rgba(52, 73, 94, 0.9)',  strokeWidth: 2, pointRadius: 7, textColor: '#ecf0f1', fontSize: 11, lineDash: [] },
    text:    { strokeColor: '#2c3e50', fillColor: '#2c3e50', strokeWidth: 1, textColor: '#2c3e50', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#5d6d7e', fillColor: 'transparent', strokeWidth: 2, lineDash: [] },
    segment: { strokeColor: '#c0392b', fillColor: 'transparent', strokeWidth: 3, lineDash: [6, 4], textColor: '#1a252f', fontSize: 10 },
    sub_segment: { strokeColor: '#7f8c8d', fillColor: 'transparent', strokeWidth: 2, lineDash: [4, 4], textColor: '#7f8c8d', fontSize: 9 },
    section: { strokeColor: '#16a085', fillColor: 'rgba(22, 160, 133, 0.2)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'road_survey',
    name: 'Road & Survey',
    point:   { strokeColor: '#d35400', fillColor: 'rgba(211, 84, 0, 0.85)',  strokeWidth: 2, pointRadius: 6, textColor: '#fff', fontSize: 10, lineDash: [] },
    text:    { strokeColor: '#fff', fillColor: '#c0392b', strokeWidth: 2, textColor: '#c0392b', fontSize: 11, lineDash: [] },
    line:    { strokeColor: '#e67e22', fillColor: 'transparent', strokeWidth: 3, lineDash: [] },
    segment: { strokeColor: '#c0392b', fillColor: 'transparent', strokeWidth: 4, lineDash: [8, 4], textColor: '#922b21', fontSize: 10 },
    sub_segment: { strokeColor: '#d35400', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#a04000', fontSize: 9 },
    section: { strokeColor: '#27ae60', fillColor: 'rgba(46, 204, 113, 0.2)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'utilities',
    name: 'Utilities',
    point:   { strokeColor: '#8e44ad', fillColor: 'rgba(142, 68, 173, 0.8)',  strokeWidth: 2, pointRadius: 6, textColor: '#fff', fontSize: 10, lineDash: [] },
    text:    { strokeColor: '#2c3e50', fillColor: '#8e44ad', strokeWidth: 1, textColor: '#6c3483', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#9b59b6', fillColor: 'transparent', strokeWidth: 3, lineDash: [5, 5] },
    segment: { strokeColor: '#6c3483', fillColor: 'transparent', strokeWidth: 2, lineDash: [4, 4], textColor: '#4a235a', fontSize: 10 },
    sub_segment: { strokeColor: '#af7ac5', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#6c3483', fontSize: 9 },
    section: { strokeColor: '#1abc9c', fillColor: 'rgba(26, 188, 156, 0.25)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'pastel',
    name: 'Pastel',
    point:   { strokeColor: '#81d4fa', fillColor: 'rgba(129, 212, 250, 0.9)',  strokeWidth: 1, pointRadius: 6, textColor: '#01579b', fontSize: 11, lineDash: [] },
    text:    { strokeColor: '#fff', fillColor: '#5c6bc0', strokeWidth: 1, textColor: '#3949ab', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#64b5f6', fillColor: 'transparent', strokeWidth: 2, lineDash: [] },
    segment: { strokeColor: '#f48fb1', fillColor: 'transparent', strokeWidth: 2, lineDash: [5, 5], textColor: '#ad1457', fontSize: 10 },
    sub_segment: { strokeColor: '#ce93d8', fillColor: 'transparent', strokeWidth: 2, lineDash: [3, 3], textColor: '#6a1b9a', fontSize: 9 },
    section: { strokeColor: '#80cbc4', fillColor: 'rgba(128, 203, 196, 0.35)', strokeWidth: 2, lineDash: [] }
  },
  {
    id: 'high_contrast',
    name: 'High Contrast',
    point:   { strokeColor: '#000', fillColor: '#ffeb3b', strokeWidth: 2, pointRadius: 7, textColor: '#000', fontSize: 11, lineDash: [] },
    text:    { strokeColor: '#000', fillColor: '#000', strokeWidth: 2, textColor: '#fff', fontSize: 12, lineDash: [] },
    line:    { strokeColor: '#000', fillColor: 'transparent', strokeWidth: 3, lineDash: [] },
    segment: { strokeColor: '#f44336', fillColor: 'transparent', strokeWidth: 4, lineDash: [6, 4], textColor: '#000', fontSize: 10 },
    sub_segment: { strokeColor: '#ff9800', fillColor: 'transparent', strokeWidth: 3, lineDash: [4, 4], textColor: '#000', fontSize: 9 },
    section: { strokeColor: '#000', fillColor: 'rgba(0,0,0,0.15)', strokeWidth: 3, lineDash: [] }
  }
];

/** Dummy GeoJSON for ROW (Right of Way) map: ~3 km highway corridor near Chennai */
const DUMMY_ROW_GEOJSON = {
  type: 'FeatureCollection',
  features: [
    { type: 'Feature', geometry: { type: 'LineString', coordinates: [[80.2150,13.0600],[80.2185,13.0615],[80.2220,13.0625],[80.2260,13.0640],[80.2300,13.0660],[80.2340,13.0672],[80.2380,13.0690],[80.2420,13.0700],[80.2460,13.0715]] }, properties: { type: 'alignment', status: 'main_road' } },
    { type: 'Feature', geometry: { type: 'LineString', coordinates: [[80.2150,13.06025],[80.2185,13.06175],[80.2220,13.06275],[80.2260,13.06425],[80.2300,13.06625],[80.2340,13.06745],[80.2380,13.06925],[80.2420,13.07025],[80.2460,13.07175]] }, properties: { type: 'alignment', status: 'boundary_right' } },
    { type: 'Feature', geometry: { type: 'LineString', coordinates: [[80.2150,13.05975],[80.2185,13.06125],[80.2220,13.06225],[80.2260,13.06375],[80.2300,13.06575],[80.2340,13.06695],[80.2380,13.06875],[80.2420,13.06975],[80.2460,13.07125]] }, properties: { type: 'alignment', status: 'boundary_left' } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [80.2185, 13.0615] }, properties: { type: 'chainage', label: 'CH122-250' } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [80.2300, 13.0660] }, properties: { type: 'chainage', label: 'CH121+500' } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [80.2420, 13.0700] }, properties: { type: 'chainage', label: 'CH122+200' } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [80.2240, 13.0632] }, properties: { type: 'warning', label: 'NBUC 36.24' } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [80.2380, 13.0690] }, properties: { type: 'structure', label: '32t Iwek', structureType: 'bridge' } },
    { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[[80.2275,13.0645],[80.2295,13.0645],[80.2295,13.0660],[80.2275,13.0660],[80.2275,13.0645]]] }, properties: { type: 'parcel', parcelId: 'LP-0452', owner: 'R. Sharma', area: '0.75 Ha', status: 'Owned' } },
    { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[[80.2330,13.0665],[80.2355,13.0665],[80.2355,13.0680],[80.2330,13.0680],[80.2330,13.0665]]] }, properties: { type: 'parcel', parcelId: 'LP-0453', owner: 'A. Verma', area: '1.20 Ha', status: 'Under Process' } },
    { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[[80.2395,13.0695],[80.2415,13.0695],[80.2415,13.0710],[80.2395,13.0710],[80.2395,13.0695]]] }, properties: { type: 'parcel', parcelId: 'LP-0456', owner: 'S. Kumar', area: '0.85 Ha', status: 'Pending' } }
  ]
};

/** Dummy GeoJSON for Machine Tracking map: path segments, chainage labels, machine points */
const DUMMY_MACHINE_TRACKING_GEOJSON = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[80.25, 13.14], [80.26, 13.145], [80.27, 13.14], [80.28, 13.155], [80.29, 13.15], [80.30, 13.16]] },
      properties: { status: 'under_construction', chainage: 'CH 119+800' }
    },
    {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[80.30, 13.16], [80.31, 13.165], [80.32, 13.17], [80.33, 13.175], [80.34, 13.18]] },
      properties: { status: 'completed', chainage: 'CH 121+250' }
    },
    {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[80.34, 13.18], [80.35, 13.19], [80.36, 13.20], [80.37, 13.21], [80.38, 13.22]] },
      properties: { status: 'under_construction', chainage: 'CH 125+400' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.265, 13.142] },
      properties: { type: 'chainage', label: 'CH 119+800' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.315, 13.168] },
      properties: { type: 'chainage', label: 'CH 121+250' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.365, 13.195] },
      properties: { type: 'chainage', label: 'CH 125+400' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.255, 13.143] },
      properties: { id: 'CHN48-EX-102', type: 'excavator', model: 'Volvo EC.210', chainage: 'CH 119+800', status: 'active', current_value: '123.030' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.285, 13.152] },
      properties: { id: 'CHN48-LD-205', type: 'loader', model: 'Caterpillar 966', chainage: 'CH 121+250', status: 'active', current_value: '98.450' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.345, 13.178] },
      properties: { id: 'CHN48-PV-301', type: 'paver', model: 'Vogele 1800', chainage: 'CH 125+400', status: 'active', current_value: '156.720' }
    },
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [80.372, 13.208] },
      properties: { id: 'CHN48-DT-108', type: 'dump_truck', model: 'Ashok Leyland 2824', chainage: 'CH 150+000', status: 'active', current_value: '87.100' }
    }
  ]
};

@Component({
  selector: 'app-gisavimvp',
  standalone: true,
  imports: [CommonModule, DatePipe, CurrencyPipe, FormsModule, GisavieditorComponent],
  templateUrl: './gisavimvp.component.html',
  styleUrl: './gisavimvp.component.css'
})
export class GisavimvpComponent implements OnInit, OnDestroy, AfterViewInit {
  // Expose Math for template use
  Math = Math;
 
  @ViewChild('mapEl', { static: true }) mapElement!: ElementRef<HTMLDivElement>;
  @ViewChild('rpMapEl') rpMapElement?: ElementRef<HTMLDivElement>;
  @ViewChild('nhaiBody') nhaiBodyRef?: ElementRef<HTMLElement>;
  @ViewChild('nhaiLayout') nhaiLayoutRef?: ElementRef<HTMLElement>;
  @ViewChild('mapContainer') mapContainerRef?: ElementRef<HTMLElement>;
  // Map and layer variables
  map!: Map;
  vectorSource!: VectorSource;
  roadSource!: VectorSource;
  projectSource!: VectorSource;
  biometricSource!: VectorSource;
  gpsSource!: VectorSource;
  waterSource!: VectorSource;
  separatorSource!: VectorSource;
  /** Machine Tracking: construction path segments, chainage labels, machine points */
  constructionPathSource!: VectorSource;
  chainageSource!: VectorSource;
  machinesSource!: VectorSource;

  /** ROW (Right of Way): road alignment, chainage, parcels, warnings */
  rowAlignmentSource!: VectorSource;
  rowChainageSource!: VectorSource;
  rowParcelSource!: VectorSource;
  rowWarningSource!: VectorSource;

  /** Asset Management bridge: dedicated source/layer for assets fetched from
   *  /api/asset-management. The bridge service owns load/save/update logic. */
  amSource!: VectorSource;
  amLayer!: VectorLayer;
  amClusterSource!: Cluster;
  amClusterLayer!: VectorLayer;
  private amStopClusterDistance: (() => void) | null = null;
  amContext: AssetMapContext | null = null;
  amLayers: AssetLayer[] = [];
  amSelectedLayerId: number | null = null;
  amStatus: 'idle' | 'loading' | 'saving' | 'error' = 'idle';
  amMessage = '';
  amFeatureCount = 0;
  amSelectedAssetId: number | null = null;
  amShowSavePanel = false;
  /** Collapse state for the floating asset-management panel. Auto-expands
   *  when the route carries an asset-management context, otherwise starts
   *  collapsed so it doesn't obscure the editor on a direct visit. */
  amPanelCollapsed = true;

  /** Active tab in the bridge panel: 'browse' (list + actions) or
   *  'create' (new-asset form). */
  amTab: 'browse' | 'create' = 'browse';

  /** In-panel search filter applied to the rendered asset list. */
  amSearch = '';

  /** Loaded asset metadata, indexed by id, in render order. Populated
   *  by the bridge's load helper as well as the local demo loader. */
  amVisibleAssets: Array<{
    id: number;
    name: string;
    code: string;
    classification: AssetClassification;
    category: string;
    status: AssetStatus;
    layerId: number | null;
    layerName: string;
    depth: number | null;
    elevation: number | null;
  }> = [];

  /**
   * Heuristic mapping from gisavimvp layer keys (in `layerRegistry`) to the
   * asset-management layer descriptor. When a key isn't listed, sensible
   * defaults are used. Keep this small + predictable so the AM panel never
   * shows a stale or fabricated entry.
   */
  private static readonly AM_LAYER_PROFILES: Record<
    string,
    {
      name: string;
      classification: AssetClassification;
      category: string;
      geometry: 'POINT' | 'LINESTRING' | 'POLYGON';
    }
  > = {
    drawing:          { name: 'Drawing layer',      classification: 'overground',  category: 'custom',       geometry: 'POINT' },
    road:             { name: 'Roads',              classification: 'overground',  category: 'road',         geometry: 'LINESTRING' },
    project:          { name: 'Project assets',     classification: 'overground',  category: 'custom',       geometry: 'POINT' },
    biometric:        { name: 'Biometric markers',  classification: 'overground',  category: 'sensor',       geometry: 'POINT' },
    gps:              { name: 'GPS tracks',         classification: 'overground',  category: 'track',        geometry: 'POINT' },
    water:            { name: 'Water utilities',    classification: 'underground', category: 'pipeline',     geometry: 'LINESTRING' },
    separator:        { name: 'Separators',         classification: 'overground',  category: 'separator',    geometry: 'LINESTRING' },
    constructionPath: { name: 'Construction paths', classification: 'overground',  category: 'construction', geometry: 'LINESTRING' },
    chainage:         { name: 'Chainage labels',    classification: 'overground',  category: 'chainage',     geometry: 'POINT' },
    machines:         { name: 'Machines',           classification: 'overground',  category: 'equipment',    geometry: 'POINT' },
    rowAlignment:     { name: 'ROW alignment',      classification: 'overground',  category: 'alignment',    geometry: 'LINESTRING' },
    rowChainage:      { name: 'ROW chainage',       classification: 'overground',  category: 'chainage',     geometry: 'POINT' },
    rowParcel:        { name: 'ROW parcels',        classification: 'overground',  category: 'parcel',       geometry: 'POLYGON' },
  };
  amSaveDraft: AssetSavePayload & { layerId?: number | null } = {
    name: '',
    asset_code: '',
    description: '',
    classification: 'overground',
    status: 'planned',
    depth_m: null,
    elevation_m: null,
  };
  /** Editor mode forwarded from asset-management ('view' | 'create' | 'edit') */
  amMode: 'view' | 'create' | 'edit' | null = null;
  rowWarningLabel: string = '';
  rowBaseMap: 'satellite' | 'dem' | '3d' = 'satellite';

  // Layer management
  layerRegistry: { [key: string]: VectorLayer } = {};
  layerGroups: { [key: string]: string[] } = {
    'Infrastructure': ['drawing', 'road', 'project'],
    'Utilities': ['water', 'separator'],
    'Tracking': ['biometric', 'gps'],
    'Overlays': ['overlay']
  };
  layerOrder: string[] = [];
  layerFilters: { [key: string]: any } = {};
  layerStatistics: { [key: string]: any } = {};
  layerMetadata: { [key: string]: any } = {}; // Extended metadata for layers
  layerLocks: { [key: string]: boolean } = {}; // Layer locking
  layerMinZoom: { [key: string]: number } = {}; // Minimum zoom level
  layerMaxZoom: { [key: string]: number } = {}; // Maximum zoom level
  layerStyles: { [key: string]: any } = {}; // Custom layer styles (may include paletteId + overrides)
  /** Palette ID per layer; used with PREDEFINED_LAYER_PALETTES */
  layerPaletteId: { [key: string]: string } = {};

  // UI State for layer management
  showLayerPanel = true;
  /** When true, sidebar is collapsed to a narrow strip to maximise map area. */
  sidebarCollapsed = true;

  /** Active panel in Layers & Tools sidebar (Layers / Draw / Road / …). */
  sidebarActiveTab = 'drawing';

  setSidebarTab(tab: string): void {
    this.sidebarActiveTab = tab;
  }

  /** NHAI layout: active module tab (Dashboard, ROW, QA, etc.) */
  private _nhaiActiveModule = 'execution';
  get nhaiActiveModule(): string { return this._nhaiActiveModule; }
  set nhaiActiveModule(val: string) {
    const prev = this._nhaiActiveModule;
    this._nhaiActiveModule = val;
    if (prev !== val) this.onModuleSwitch(prev, val);
  }
  private mapResizeObserver: ResizeObserver | null = null;
  private readonly onWindowResize = () => this.refreshMapSizeAfterLayout();

  /** Selected machine for info card (Machine Tracking view) */
  selectedMachine: {
    id: string;
    type: string;
    model: string;
    chainage: string;
    status?: string;
    telemetry?: string;
  } | null = null;

  // ======================== Road Planning State ========================
  rpSidebarCollapsed = false;
  rpRightPanelVisible = true;
  rpActiveTool = '';
  rpIsOnline = true;
  rpProjectName = 'NH-44';
  rpGpsStatus = 'Acquiring...';
  rpGpsAccuracy: number | null = null;
  rpZoomLevel = 14;
  rpScaleText = '1:25,000';
  rpLayerOpacity = 80;
  rpChainageInterval = 100;
  rpSelectedRoadIndex = -1;
  rpLayerSearchTerm = '';
  rpSelectedLayerId: string | null = 'planning';
  rpExpandedGroups: Record<string, boolean> = {
    'Map & Network': true,
    Construction: true,
    Annotations: false,
  };

  readonly rpNodesMinZoom = 16;

  private readonly rpLayerCatalog: { id: string; label: string; icon: string; group: string; overlay?: boolean }[] = [
    { id: 'cartosat', label: 'Satellite', icon: '🛰', group: 'Map & Network' },
    { id: 'osm_tiles', label: 'OSM Tiles', icon: '🗺', group: 'Map & Network' },
    { id: 'osm_roads', label: 'OSM Roads', icon: '🛣', group: 'Map & Network', overlay: true },
    { id: 'osm_nodes', label: 'OSM Nodes', icon: '⚪', group: 'Map & Network', overlay: true },
    { id: 'planning', label: 'Planning', icon: '📐', group: 'Construction' },
    { id: 'survey', label: 'Survey', icon: '📏', group: 'Construction' },
    { id: 'execution', label: 'Execution', icon: '🚧', group: 'Construction' },
    { id: 'chainage', label: 'Chainage', icon: '📍', group: 'Annotations' },
    { id: 'cross_sections', label: 'Cross-Sections', icon: '⊕', group: 'Annotations' },
    { id: 'row_boundary', label: 'ROW Boundary', icon: '▭', group: 'Annotations' },
  ];

  rpLayerVisible: Record<string, boolean> = {
    cartosat: true,
    osm_tiles: false,
    osm_roads: true,
    osm_nodes: true,
    planning: true,
    survey: true,
    execution: true,
    chainage: true,
    cross_sections: true,
    row_boundary: true,
  };

  rpRoadPresets = [
    { id: 'nh-2l', label: 'NH - 2 Lane', lanes: 2, shoulderL: 1.5, shoulderR: 1.5, separator: 0, laneWidth: 3.5, rowMargin: 3 },
    { id: 'nh-4l', label: 'NH - 4 Lane (Divided)', lanes: 4, shoulderL: 1.5, shoulderR: 1.5, separator: 2.0, laneWidth: 3.5, rowMargin: 3 },
    { id: 'nh-6l', label: 'NH - 6 Lane (Divided)', lanes: 6, shoulderL: 2.0, shoulderR: 2.0, separator: 3.0, laneWidth: 3.5, rowMargin: 4 },
    { id: 'exp-6l', label: 'Expressway - 6 Lane', lanes: 6, shoulderL: 3.0, shoulderR: 3.0, separator: 5.0, laneWidth: 3.75, rowMargin: 6 },
    { id: 'exp-8l', label: 'Expressway - 8 Lane', lanes: 8, shoulderL: 3.0, shoulderR: 3.0, separator: 5.0, laneWidth: 3.75, rowMargin: 6 },
    { id: 'sh-2l', label: 'SH - 2 Lane', lanes: 2, shoulderL: 1.0, shoulderR: 1.0, separator: 0, laneWidth: 3.5, rowMargin: 2 },
    { id: 'mdr-2l', label: 'MDR - 2 Lane', lanes: 2, shoulderL: 0.75, shoulderR: 0.75, separator: 0, laneWidth: 3.0, rowMargin: 1.5 }
  ];

  rpRoadProps: {
    name: string; type: string; lanes: number; laneWidth: number;
    shoulderL: number; shoulderR: number; separator: number;
    row: number; rowOverride: boolean;
  } = {
    name: '', type: '', lanes: 2, laneWidth: 3.5,
    shoulderL: 1.5, shoulderR: 1.5, separator: 0,
    row: 12.0, rowOverride: false
  };

  rpAccordion = {
    design: true, chainage: true, layers: true, roads: true, survey: false, rowLand: false
  };

  /** @deprecated use rpLayerVisible — kept for floating panel bindings */
  get rpRoadLayers(): { planning: boolean; survey: boolean; execution: boolean } {
    return {
      planning: this.rpLayerVisible['planning'],
      survey: this.rpLayerVisible['survey'],
      execution: this.rpLayerVisible['execution'],
    };
  }
  rpVisualizationMode: 'terrain' | 'design' = 'terrain';

  rpSelectedMachine: { id: string; model: string; type: string; chainage: string } | null = null;

  rpMachines = [
    { id: 'CHN48-EX-102', model: 'Volvo EC210', type: 'Crawler Excavator', chainage: 'CH 119+800', icon: '&#128679;', x: 12, y: 78 },
    { id: 'CHN48-DT-204', model: 'Tata Signa 2823', type: 'Tipper Truck', chainage: 'CH 119+800', icon: '&#128667;', x: 26, y: 70 },
    { id: 'CHN48-LD-205', model: 'CAT 950H', type: 'Wheel Loader', chainage: 'CH 121+250', icon: '&#128666;', x: 40, y: 56 },
    { id: 'CHN48-PV-301', model: 'Vögele Super 1800', type: 'Sensor Paver', chainage: 'CH 125+400', icon: '&#128667;', x: 56, y: 44 },
    { id: 'CHN48-DT-308', model: 'Ashok Leyland 2820', type: 'Dump Truck', chainage: 'CH 150+000', icon: '&#128667;', x: 88, y: 30 }
  ];

  rpSegments = [
    { from: 'CH 115+000', to: 'CH 119+800', status: 'completed', pct: 100 },
    { from: 'CH 119+800', to: 'CH 121+250', status: 'under_construction', pct: 65 },
    { from: 'CH 121+250', to: 'CH 125+400', status: 'under_construction', pct: 30 },
    { from: 'CH 125+400', to: 'CH 130+000', status: 'planned', pct: 0 },
    { from: 'CH 130+000', to: 'CH 150+000', status: 'planned', pct: 0 }
  ];

  rpSelectMachine(m: { id: string; model: string; type: string; chainage: string }): void {
    this.rpSelectedMachine = this.rpSelectedMachine?.id === m.id ? null : m;
  }

  rpSetVisualizationMode(mode: 'terrain' | 'design'): void {
    this.rpVisualizationMode = mode;
    this.syncRpVisualizationMode();
  }

  rpRoads: { name: string; type: string; color: string; chainageMarkers: number }[] = [];
  rpCrossSections: { chainage: string; lat: number; lon: number }[] = [];

  /** Road Planning: dedicated OpenLayers map + LRS sources */
  private rpMap?: Map;
  private rpSatelliteLayer?: TileLayer;
  private rpOsmLayer?: TileLayer;
  private rpPlanningSource = new VectorSource();
  private rpSurveySource = new VectorSource();
  private rpExecutionSource = new VectorSource();
  private rpChainageSource = new VectorSource();
  private rpPlanningLayer?: VectorLayer<VectorSource>;
  private rpSurveyLayer?: VectorLayer<VectorSource>;
  private rpExecutionLayer?: VectorLayer<VectorSource>;
  private rpChainageLayer?: VectorLayer<VectorSource>;
  private rpOsmNetworkSource = new VectorSource();
  private rpOsmNodesSource = new VectorSource();
  private rpOsmNetworkLayer?: VectorLayer<VectorSource>;
  private rpOsmNodesLayer?: VectorLayer<VectorSource>;
  private rpOsmMoveEndKey?: EventsKey;
  private rpOsmViewChangeKey?: EventsKey;
  private rpOsmCenterChangeKey?: EventsKey;
  private rpOsmLoadTimer?: ReturnType<typeof setTimeout>;
  private rpOsmRequestSeq = 0;
  private rpOsmLastBboxKey = '';
  private rpOsmLoading = false;
  private rpMapResizeObserver?: ResizeObserver;
  private rpRoutes: LrsRoute[] = [];
  private rpSelectedRouteId: number | null = null;
  rpMapStatus = '';
  rpOsmAttribution = '© Esri · © OpenStreetMap contributors';
  rpOsmRoadCount = 0;
  /** iD editor shows vertex nodes from ~zoom 16 (350 m scale). */
  private readonly RP_OSM_NODES_MIN_ZOOM = 16;
  private rpOsmNodeRebuildTimer?: ReturnType<typeof setTimeout>;
  private rpOsmNodesWereVisible = false;

  rpSetTool(tool: string): void {
    this.rpActiveTool = this.rpActiveTool === tool ? '' : tool;
  }

  rpToggleSync(): void {
    this.rpIsOnline = !this.rpIsOnline;
  }

  rpOnRoadTypeChange(typeId: string): void {
    this.rpRoadProps.type = typeId;
    const preset = this.rpRoadPresets.find(p => p.id === typeId);
    if (preset) {
      this.rpRoadProps.lanes = preset.lanes;
      this.rpRoadProps.laneWidth = preset.laneWidth;
      this.rpRoadProps.shoulderL = preset.shoulderL;
      this.rpRoadProps.shoulderR = preset.shoulderR;
      this.rpRoadProps.separator = preset.separator;
      this.rpRoadProps.rowOverride = false;
      this.rpRecalcRow();
    }
  }

  rpStepLanes(delta: number): void {
    const next = this.rpRoadProps.lanes + delta * 2;
    if (next >= 2 && next <= 12) {
      this.rpRoadProps.lanes = next;
      if (this.rpRoadProps.lanes < 4) this.rpRoadProps.separator = 0;
      this.rpRecalcRow();
    }
  }

  rpRecalcRow(): void {
    if (this.rpRoadProps.rowOverride) return;
    const preset = this.rpRoadPresets.find(p => p.id === this.rpRoadProps.type);
    const margin = preset ? preset.rowMargin : 3;
    const carriageway = this.rpRoadProps.lanes * this.rpRoadProps.laneWidth;
    this.rpRoadProps.row = carriageway + this.rpRoadProps.shoulderL + this.rpRoadProps.shoulderR
      + this.rpRoadProps.separator + margin * 2;
  }

  rpApplyProperties(): void {
    if (this.rpSelectedRoadIndex >= 0 && this.rpSelectedRoadIndex < this.rpRoads.length) {
      this.rpRoads[this.rpSelectedRoadIndex].name = this.rpRoadProps.name;
      this.rpRoads[this.rpSelectedRoadIndex].type = this.rpRoadProps.type;
    } else {
      this.rpRoads.push({
        name: this.rpRoadProps.name || 'Road ' + (this.rpRoads.length + 1),
        type: this.rpRoadProps.type,
        color: '#000000',
        chainageMarkers: 0
      });
      this.rpSelectedRoadIndex = this.rpRoads.length - 1;
    }
  }

  rpSelectRoad(index: number): void {
    this.rpSelectedRoadIndex = index;
    const road = this.rpRoads[index];
    if (road) {
      this.rpRoadProps.name = road.name;
      this.rpRoadProps.type = road.type;
      const preset = this.rpRoadPresets.find(p => p.id === road.type);
      if (preset) {
        this.rpRoadProps.lanes = preset.lanes;
        this.rpRoadProps.laneWidth = preset.laneWidth;
        this.rpRoadProps.shoulderL = preset.shoulderL;
        this.rpRoadProps.shoulderR = preset.shoulderR;
        this.rpRoadProps.separator = preset.separator;
      }
      this.rpRoadProps.rowOverride = false;
      this.rpRecalcRow();
    }
    this.rpRightPanelVisible = true;
  }

  rpGenerateChainage(): void {
    if (this.rpSelectedRoadIndex >= 0 && this.rpSelectedRoadIndex < this.rpRoads.length) {
      this.rpRoads[this.rpSelectedRoadIndex].chainageMarkers =
        Math.floor(5000 / this.rpChainageInterval);
    }
  }

  rpJumpToCrossSection(_index: number): void {
    // placeholder - would center map on cross-section
  }

  rpDeleteCrossSection(index: number): void {
    this.rpCrossSections.splice(index, 1);
  }

  rpCenterOnGps(): void {
    if (!this.rpMap) return;
    const ext = this.rpExecutionSource.getExtent();
    if (ext[0] !== Infinity) {
      this.rpMap.getView().fit(ext, { padding: [60, 60, 60, 60], maxZoom: 16, duration: 400 });
    }
  }

  rpZoomIn(): void {
    if (!this.rpMap) {
      this.ensureRpMapReady(() => this.rpZoomIn());
      return;
    }
    const view = this.rpMap.getView();
    const zoom = (view.getZoom() || 12) + 1;
    this.rpZoomLevel = Math.min(Math.round(zoom), 22);
    view.animate({ zoom, duration: 250 });
    this.scheduleRpOsmRoadNetworkLoad(300);
  }

  rpZoomOut(): void {
    if (!this.rpMap) {
      this.ensureRpMapReady(() => this.rpZoomOut());
      return;
    }
    const view = this.rpMap.getView();
    const zoom = (view.getZoom() || 12) - 1;
    this.rpZoomLevel = Math.max(Math.round(zoom), 1);
    view.animate({ zoom, duration: 250 });
    this.scheduleRpOsmRoadNetworkLoad(300);
  }

  rpToggleRoadLayer(layer: 'planning' | 'survey' | 'execution', visible: boolean): void {
    this.rpToggleLayer(layer, visible);
  }

  rpToggleLayer(layerId: string, visible: boolean): void {
    if (layerId === 'osm_nodes' && visible && !this.rpLayerVisible['osm_roads']) return;
    this.rpLayerVisible[layerId] = visible;
    if (layerId === 'osm_roads' && !visible) {
      this.rpLayerVisible['osm_nodes'] = false;
    }
    this.syncRpLayerVisibility();
    if (layerId === 'osm_roads' || layerId === 'osm_tiles' || layerId === 'cartosat') {
      this.syncRpVisualizationMode();
    }
  }

  rpSelectLayer(layerId: string): void {
    this.rpSelectedLayerId = layerId;
  }

  rpClearLayerSearch(): void {
    this.rpLayerSearchTerm = '';
  }

  getRpLayerGroups(): { name: string; layers: string[] }[] {
    const order = ['Map & Network', 'Construction', 'Annotations'];
    return order
      .map((name) => ({
        name,
        layers: this.rpLayerCatalog
          .filter((m) => m.group === name && this.rpLayerMatchesSearch(m.id))
          .map((m) => m.id),
      }))
      .filter((g) => g.layers.length > 0);
  }

  rpLayerMatchesSearch(layerId: string): boolean {
    const q = this.rpLayerSearchTerm.trim().toLowerCase();
    if (!q) return true;
    const meta = this.rpLayerCatalog.find((m) => m.id === layerId);
    if (!meta) return false;
    return meta.label.toLowerCase().includes(q) || meta.group.toLowerCase().includes(q);
  }

  getRpLayerDisplayName(layerId: string): string {
    return this.rpLayerCatalog.find((m) => m.id === layerId)?.label || layerId;
  }

  getRpLayerFullLabel(layerId: string): string {
    const hints: Record<string, string> = {
      osm_roads: 'OpenStreetMap roads & waterways',
      osm_nodes: `Vertex dots at zoom ${this.RP_OSM_NODES_MIN_ZOOM}+`,
      cartosat: 'ESRI World Imagery basemap',
      osm_tiles: 'OpenStreetMap raster tiles',
      planning: 'Dashed alignment sketch layer',
      survey: 'Field survey linework',
      execution: 'Built road centerline',
    };
    return hints[layerId] || this.getRpLayerDisplayName(layerId);
  }

  getRpLayerIcon(layerId: string): string {
    return this.rpLayerCatalog.find((m) => m.id === layerId)?.icon || '📄';
  }

  isRpOverlayLayer(layerId: string): boolean {
    return !!this.rpLayerCatalog.find((m) => m.id === layerId)?.overlay;
  }

  isRpConstructionLayer(layerId: string): boolean {
    return layerId === 'planning' || layerId === 'survey' || layerId === 'execution';
  }

  isRpGroupExpanded(groupName: string): boolean {
    return this.rpExpandedGroups[groupName] !== false;
  }

  toggleRpGroupExpanded(groupName: string): void {
    this.rpExpandedGroups[groupName] = !this.isRpGroupExpanded(groupName);
  }

  getRpLayerCount(layerId: string): number | null {
    if (layerId === 'osm_roads') return this.rpOsmRoadCount > 0 ? this.rpOsmRoadCount : null;
    if (layerId === 'osm_nodes') {
      const n = this.rpOsmNodesSource.getFeatures().length;
      return n > 0 ? n : null;
    }
    if (layerId === 'chainage') {
      const n = this.rpChainageSource.getFeatures().length;
      return n > 0 ? n : null;
    }
    return null;
  }

  getRpDrawingTargetLabel(): string {
    if (this.rpSelectedLayerId && this.isRpConstructionLayer(this.rpSelectedLayerId)) {
      return this.getRpLayerDisplayName(this.rpSelectedLayerId);
    }
    return this.getRpLayerDisplayName('planning');
  }

  rpOnSelectedLayerOpacityChange(value: number): void {
    this.rpLayerOpacity = value;
    this.syncRpLayerVisibility();
  }

  private enterRoadPlanningModule(): void {
    this.ensureRpMapReady(() => this.loadRpRoadNetwork());
  }

  /** Road Plan map lives under *ngIf — retry until #rpMapEl exists in the DOM. */
  private ensureRpMapReady(done?: () => void, attempt = 0): void {
    this.cdr.detectChanges();
    this.initRpMapIfNeeded();
    this.refreshRpMapSize();
    if (this.rpMap) {
      done?.();
      return;
    }
    if (attempt < 25) {
      setTimeout(() => this.ensureRpMapReady(done, attempt + 1), 80);
    }
  }

  private attachRpOsmReloadListeners(): void {
    if (!this.rpMap || this.rpOsmMoveEndKey) return;
    this.rpOsmMoveEndKey = this.rpMap.on('moveend', () => this.scheduleRpOsmRoadNetworkLoad());
  }

  private initRpMapIfNeeded(): void {
    const el = this.rpMapElement?.nativeElement;
    if (this.rpMap || !el) return;

    this.rpSatelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attributions: '© Esri, Maxar, Earthstar Geographics',
      }),
      zIndex: 0,
    });
    this.rpOsmLayer = new TileLayer({ source: new OSM(), zIndex: 0, visible: false });
    this.rpOsmNetworkLayer = new VectorLayer({
      source: this.rpOsmNetworkSource,
      zIndex: 8,
      style: (feature, resolution) => this.rpOsmNetworkStyle(feature, resolution),
    });
    this.rpOsmNodesLayer = new VectorLayer({
      source: this.rpOsmNodesSource,
      zIndex: 9,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      style: (_feature, resolution) => this.rpOsmNodeStyle(resolution),
    });
    this.rpPlanningLayer = new VectorLayer({
      source: this.rpPlanningSource,
      zIndex: 12,
      style: new Style({
        stroke: new Stroke({ color: '#2196F3', width: 3, lineDash: [12, 6] }),
      }),
    });
    this.rpSurveyLayer = new VectorLayer({
      source: this.rpSurveySource,
      zIndex: 14,
      style: new Style({
        stroke: new Stroke({ color: '#2ecc71', width: 2, lineDash: [3, 5] }),
      }),
    });
    this.rpExecutionLayer = new VectorLayer({
      source: this.rpExecutionSource,
      zIndex: 16,
      style: new Style({
        stroke: new Stroke({ color: '#1a1a1a', width: 10, lineCap: 'round', lineJoin: 'round' }),
      }),
    });
    this.rpChainageLayer = new VectorLayer({
      source: this.rpChainageSource,
      zIndex: 18,
      style: (feature) =>
        new Style({
          image: new CircleStyle({
            radius: 4,
            fill: new Fill({ color: '#00d2ff' }),
            stroke: new Stroke({ color: '#fff', width: 1 }),
          }),
          text: new TextStyle({
            text: String(feature.get('chainage_label') || ''),
            font: '10px Roboto Mono, monospace',
            fill: new Fill({ color: '#fff' }),
            stroke: new Stroke({ color: '#0d1520', width: 2 }),
            offsetY: -12,
          }),
        }),
    });

    this.rpMap = new Map({
      target: el,
      layers: [
        this.rpSatelliteLayer,
        this.rpOsmLayer,
        this.rpOsmNetworkLayer,
        this.rpOsmNodesLayer,
        this.rpPlanningLayer,
        this.rpSurveyLayer,
        this.rpExecutionLayer,
        this.rpChainageLayer,
      ],
      view: new View({
        center: fromLonLat(this.defaultSettings.center),
        zoom: Math.max(17, this.defaultSettings.zoom),
      }),
      controls: defaultControls({ attribution: true, zoom: false }),
    });

    this.rpMap.getView().on('change:resolution', () => {
      const z = this.rpMap?.getView().getZoom();
      if (z != null) this.rpZoomLevel = Math.round(z);
      this.scheduleRpOsmNodeRebuild();
    });

    this.rpMapResizeObserver = new ResizeObserver(() => {
      this.refreshRpMapSize();
      this.scheduleRpOsmRoadNetworkLoad(200);
    });
    this.rpMapResizeObserver.observe(el.closest('.rp-map-area') ?? el);
    this.attachRpOsmReloadListeners();
    this.syncRpLayerVisibility();
    this.syncRpVisualizationMode();
  }

  private refreshRpMapSize(): void {
    const el = this.rpMapElement?.nativeElement;
    if (!this.rpMap || !el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      this.rpMap.updateSize();
      this.rpMap.renderSync();
    }
  }

  private syncRpLayerVisibility(): void {
    const planningOn = this.rpLayerVisible['planning'];
    const surveyOn = this.rpLayerVisible['survey'];
    const executionOn = this.rpLayerVisible['execution'];
    const osmOn = this.rpLayerVisible['osm_tiles'];
    const roadsOn = this.rpLayerVisible['osm_roads'];
    const nodesOn = this.rpLayerVisible['osm_nodes'] && roadsOn;
    const chainageOn = this.rpLayerVisible['chainage'];
    const aerialOn = this.rpLayerVisible['cartosat'];
    const useAerial = this.rpVisualizationMode === 'terrain';
    this.rpSatelliteLayer?.setVisible(useAerial && aerialOn);
    this.rpOsmLayer?.setVisible(osmOn || this.rpVisualizationMode === 'design');
    this.rpOsmNetworkLayer?.setVisible(roadsOn);
    this.rpOsmNodesLayer?.setVisible(nodesOn);
    this.rpPlanningLayer?.setVisible(planningOn);
    this.rpSurveyLayer?.setVisible(surveyOn);
    this.rpExecutionLayer?.setVisible(executionOn);
    this.rpChainageLayer?.setVisible(chainageOn);
    const opacity = Math.max(0, Math.min(100, this.rpLayerOpacity)) / 100;
    this.rpPlanningLayer?.setOpacity(opacity);
    this.rpSurveyLayer?.setOpacity(opacity);
    this.rpExecutionLayer?.setOpacity(opacity);
  }

  private syncRpVisualizationMode(): void {
    if (!this.rpOsmLayer || !this.rpSatelliteLayer) return;
    if (this.rpVisualizationMode === 'design') {
      this.rpSatelliteLayer.setVisible(false);
      this.rpOsmLayer.setVisible(true);
      this.rpOsmLayer.setOpacity(0.35);
    } else {
      const aerialOn = this.rpLayerVisible['cartosat'];
      this.rpSatelliteLayer.setVisible(aerialOn);
      this.rpSatelliteLayer.setOpacity(1);
      const osmOn = this.rpLayerVisible['osm_tiles'];
      this.rpOsmLayer.setVisible(osmOn);
      this.rpOsmLayer.setOpacity(1);
    }
    this.rpOsmNodesLayer?.changed();
    this.rpOsmNetworkLayer?.changed();
  }

  private loadRpRoadNetwork(): void {
    this.loadRpOsmRoadNetwork();
    this.rpMapStatus = 'Loading road network…';
    const projectId = this.projectId ? String(this.projectId) : undefined;
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.rpRoutes = res.data || [];
        const route =
          this.rpRoutes.find((r) => r.route_code === this.rpProjectName) || this.rpRoutes[0];
        if (!route) {
          this.rpMapStatus = this.rpOsmRoadCount
            ? `OSM: ${this.rpOsmRoadCount} road segments (no LRS route)`
            : 'No LRS routes — pan/zoom to load OSM roads';
          return;
        }
        this.rpSelectedRouteId = route.id;
        this.rpProjectName = route.route_code || route.route_name || this.rpProjectName;
        this.lrsService.getRouteMap(route.id).subscribe({
          next: (mapRes) => {
            this.applyRpRouteGeometry(mapRes.data?.route);
            this.applyRpEvents(mapRes.data?.events);
            this.loadRpChainageMarkers(route.id);
            this.syncRpLayerVisibility();
            const ext = this.rpExecutionSource.getExtent();
            if (ext[0] !== Infinity && this.rpMap) {
              this.rpMap.getView().fit(ext, { padding: [60, 60, 60, 60], maxZoom: 16, duration: 500 });
            }
            this.rpMapStatus = `OSM ${this.rpOsmRoadCount} roads · LRS ${route.route_code}`;
            this.populateRpRoadsFromRoute(route);
          },
          error: () => {
            this.rpMapStatus = 'Failed to load road geometry';
          },
        });
      },
      error: () => {
        this.rpMapStatus = this.rpOsmRoadCount
          ? `OSM: ${this.rpOsmRoadCount} roads · LRS unavailable`
          : 'LRS service unavailable';
      },
    });
  }

  private applyRpRouteGeometry(routeFeature?: { type?: string; geometry?: unknown; properties?: unknown }): void {
    this.rpPlanningSource.clear();
    this.rpSurveySource.clear();
    this.rpExecutionSource.clear();
    if (!routeFeature) return;
    const feats = this.format.readFeatures(routeFeature, { featureProjection: 'EPSG:3857' });
    for (const f of feats) {
      this.rpPlanningSource.addFeature(f.clone());
      this.rpSurveySource.addFeature(f.clone());
      this.rpExecutionSource.addFeature(f.clone());
    }
  }

  private applyRpEvents(eventsFc?: { type?: string; features?: unknown[] }): void {
    if (!eventsFc?.features?.length) return;
    const evFeats = this.format.readFeatures(eventsFc, { featureProjection: 'EPSG:3857' });
    for (const f of evFeats) {
      const type = String(f.get('event_type') || '');
      if (type === 'chainage') {
        this.rpChainageSource.addFeature(f);
      } else if (type.includes('survey') || type === 'gps_track') {
        this.rpSurveySource.addFeature(f);
      } else {
        this.rpPlanningSource.addFeature(f);
      }
    }
  }

  private loadRpChainageMarkers(routeId: number): void {
    const intervalKm = Math.max(0.05, this.rpChainageInterval / 1000);
    this.lrsService.generateChainageMarkers(routeId, intervalKm, false).subscribe({
      next: (res) => {
        if (!res.data?.features?.length) return;
        const markers = this.format.readFeatures(res.data, { featureProjection: 'EPSG:3857' });
        for (const m of markers) {
          m.set('event_type', 'chainage');
          this.rpChainageSource.addFeature(m);
        }
      },
    });
  }

  private scheduleRpOsmRoadNetworkLoad(delayMs = 450): void {
    if (this.rpOsmLoadTimer) clearTimeout(this.rpOsmLoadTimer);
    this.rpOsmLoadTimer = setTimeout(() => this.loadRpOsmRoadNetwork(), delayMs);
  }

  private loadRpOsmRoadNetwork(): void {
    if (!this.rpMap) return;
    const size = this.rpMap.getSize();
    if (!size || size[0] < 10 || size[1] < 10) return;

    const bbox = this.rpMapBboxWgs84();
    if (!bbox) return;

    const bboxKey = [bbox.west, bbox.south, bbox.east, bbox.north]
      .map((v) => v.toFixed(5))
      .join(',');
    if (bboxKey === this.rpOsmLastBboxKey && this.rpOsmNetworkSource.getFeatures().length > 0) {
      this.scheduleRpOsmNodeRebuild(0);
      return;
    }

    const seq = ++this.rpOsmRequestSeq;
    this.rpOsmLoading = true;
    this.rpMapStatus = 'Loading OSM road network…';

    const zoom = this.rpMap.getView().getZoom();
    // this.osmMapApi.fetchRoadNetwork({ ...bbox, zoom: zoom ?? undefined }).subscribe({
    //   next: (res) => {
    //     if (seq !== this.rpOsmRequestSeq) return;
    //     this.rpOsmLoading = false;
    //     if (!res.success || !res.data?.features?.length) {
    //       this.rpMapStatus = 'No OSM highways in this extent';
    //       return;
    //     }
    //     const fc = {
    //       type: 'FeatureCollection' as const,
    //       features: res.data.features,
    //     };
    //     const feats = this.format.readFeatures(fc, {
    //       dataProjection: 'EPSG:4326',
    //       featureProjection: 'EPSG:3857',
    //     });
    //     this.rpOsmNetworkSource.clear();
    //     this.rpOsmNetworkSource.addFeatures(feats);
    //     this.rebuildRpOsmNodes(feats);
    //     this.rpOsmNodesWereVisible = this.rpOsmNodesShouldShow();
    //     this.rpOsmRoadCount = feats.filter((f) => f.get('feature_kind') !== 'waterway').length;
    //     const waterCount = feats.filter((f) => f.get('feature_kind') === 'waterway').length;
    //     this.rpOsmLastBboxKey = bboxKey;
    //     if (res.data.meta?.attribution) {
    //       this.rpOsmAttribution = res.data.meta.attribution.includes('openstreetmap')
    //         ? '© OpenStreetMap contributors'
    //         : String(res.data.meta.attribution);
    //     }
    //     this.rpMapStatus =
    //       waterCount > 0
    //         ? `OSM: ${this.rpOsmRoadCount} roads · ${waterCount} waterways`
    //         : `OSM: ${this.rpOsmRoadCount} road segments`;
    //     this.rpOsmNetworkLayer?.changed();
    //     this.rpOsmNodesLayer?.changed();
    //     this.rpMap?.render();
    //     this.cdr.markForCheck();
    //   },
    //   error: (err) => {
    //     if (seq !== this.rpOsmRequestSeq) return;
    //     this.rpOsmLoading = false;
    //     this.rpMapStatus = friendlyOsmOverlayStatus(err);
    //     this.cdr.markForCheck();
    //   },
    // });
  }

  private rpMapBboxWgs84(): { west: number; south: number; east: number; north: number } | null {
    if (!this.rpMap) return null;
    const extent = this.rpMap.getView().calculateExtent(this.rpMap.getSize() || [800, 600]);
    const sw = toLonLat([extent[0], extent[1]]);
    const ne = toLonLat([extent[2], extent[3]]);
    return this.clampOsmBboxArea(sw[0], sw[1], ne[0], ne[1], 0.25);
  }

  /** Shrink bbox toward center if it exceeds OSM Map API area limit. */
  private clampOsmBboxArea(
    west: number,
    south: number,
    east: number,
    north: number,
    maxArea: number
  ): { west: number; south: number; east: number; north: number } {
    let w = west;
    let s = south;
    let e = east;
    let n = north;
    let area = (e - w) * (n - s);
    if (area <= maxArea) return { west: w, south: s, east: e, north: n };
    const scale = Math.sqrt(maxArea / area) * 0.98;
    const cx = (w + e) / 2;
    const cy = (s + n) / 2;
    const hw = ((e - w) / 2) * scale;
    const hh = ((n - s) / 2) * scale;
    return { west: cx - hw, south: cy - hh, east: cx + hw, north: cy + hh };
  }

  private rpOsmNetworkStyle(feature: FeatureLike, resolution?: number): Style | Style[] | void {
    const kind = String(feature.get('feature_kind') || 'road');
    const zoomScale = resolution != null ? Math.max(0.75, Math.min(2.2, 100 / resolution)) : 1;

    if (kind === 'waterway') {
      const ww = String(feature.get('waterway') || 'stream');
      const w = (ww === 'river' ? 5 : ww === 'canal' ? 4 : 2.5) * zoomScale;
      return new Style({
        stroke: new Stroke({
          color: '#4da3ff',
          width: w,
          lineCap: 'round',
          lineJoin: 'round',
        }),
      });
    }

    const hw = String(feature.get('highway') || 'road');
    const widths: Record<string, number> = {
      motorway: 7,
      trunk: 6,
      primary: 5.5,
      secondary: 5,
      tertiary: 4.5,
      residential: 4,
      unclassified: 4,
      service: 3,
      track: 3,
      path: 2.5,
      footway: 2,
      pedestrian: 2.5,
      cycleway: 2.5,
      living_street: 3.5,
    };
    const inner = (widths[hw] || 4) * zoomScale;
    const outer = inner + Math.max(2, inner * 0.35);
    const strokeOpts = { lineCap: 'round' as const, lineJoin: 'round' as const };

    return [
      new Style({
        stroke: new Stroke({ color: '#3d3d3d', width: outer, ...strokeOpts }),
      }),
      new Style({
        stroke: new Stroke({ color: '#ffffff', width: inner, ...strokeOpts }),
      }),
    ];
  }

  /** OSM iD-style: hide nodes when zoomed out; show every way vertex when zoomed in. */
  private rpOsmNodesShouldShow(): boolean {
    const zoom = this.rpMap?.getView().getZoom();
    return zoom != null && zoom >= this.RP_OSM_NODES_MIN_ZOOM;
  }

  private scheduleRpOsmNodeRebuild(delayMs = 60): void {
    if (this.rpOsmNodeRebuildTimer) clearTimeout(this.rpOsmNodeRebuildTimer);
    const willShow = this.rpOsmNodesShouldShow();
    const immediate = willShow !== this.rpOsmNodesWereVisible;
    this.rpOsmNodeRebuildTimer = setTimeout(() => {
      this.rpOsmNodesWereVisible = willShow;
      this.rebuildRpOsmNodes(this.rpOsmNetworkSource.getFeatures() as Feature[]);
      this.rpOsmNodesLayer?.changed();
      this.rpMap?.render();
    }, immediate ? 0 : delayMs);
  }

  /** OSM iD-style vertex nodes — visible when zoomed in. */
  private rpOsmNodeStyle(_resolution?: number): Style | void {
    if (!this.rpOsmNodesShouldShow()) return undefined;
    const zoom = this.rpMap?.getView().getZoom() ?? this.RP_OSM_NODES_MIN_ZOOM;
    const radius = zoom >= 19 ? 4.5 : zoom >= 18 ? 4 : zoom >= 17 ? 3.5 : 3;
    return new Style({
      image: new CircleStyle({
        radius,
        fill: new Fill({ color: '#ffffff' }),
        stroke: new Stroke({ color: '#3a3a3a', width: 1.5 }),
      }),
    });
  }

  private rebuildRpOsmNodes(networkFeatures: Feature[]): void {
    this.rpOsmNodesSource.clear();
    if (!this.rpOsmNodesShouldShow() || !networkFeatures.length) return;

    const zoom = this.rpMap?.getView().getZoom() ?? this.RP_OSM_NODES_MIN_ZOOM;
    // Finer dedup at higher zoom so shared junction nodes stay single dots.
    const dedupDecimals = zoom >= 19 ? 2 : zoom >= 18 ? 1 : 0;
    const seen = new Set<string>();

    for (const f of networkFeatures) {
      const geom = f.getGeometry();
      if (!(geom instanceof LineString)) continue;
      for (const c of geom.getCoordinates()) {
        const key = `${c[0].toFixed(dedupDecimals)},${c[1].toFixed(dedupDecimals)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        this.rpOsmNodesSource.addFeature(new Feature(new Point(c)));
      }
    }
  }

  private populateRpRoadsFromRoute(route: LrsRoute): void {
    if (this.rpRoads.length) return;
    this.rpRoads = [
      {
        name: route.route_name || route.route_code,
        type: 'nh-4l',
        color: '#1a1a1a',
        chainageMarkers: Math.max(1, Math.floor((route.length_m || 5000) / this.rpChainageInterval)),
      },
    ];
    this.rpSelectedRoadIndex = 0;
    this.rpRoadProps.name = this.rpRoads[0].name;
  }

  private destroyRpMap(): void {
    if (this.rpOsmLoadTimer) {
      clearTimeout(this.rpOsmLoadTimer);
      this.rpOsmLoadTimer = undefined;
    }
    if (this.rpOsmNodeRebuildTimer) {
      clearTimeout(this.rpOsmNodeRebuildTimer);
      this.rpOsmNodeRebuildTimer = undefined;
    }
    this.rpOsmNodesWereVisible = false;
    if (this.rpOsmMoveEndKey) {
      unByKey(this.rpOsmMoveEndKey);
      this.rpOsmMoveEndKey = undefined;
    }
    if (this.rpOsmViewChangeKey) {
      unByKey(this.rpOsmViewChangeKey);
      this.rpOsmViewChangeKey = undefined;
    }
    if (this.rpOsmCenterChangeKey) {
      unByKey(this.rpOsmCenterChangeKey);
      this.rpOsmCenterChangeKey = undefined;
    }
    this.rpOsmLastBboxKey = '';
    this.rpOsmRequestSeq++;
    this.rpMapResizeObserver?.disconnect();
    this.rpMapResizeObserver = undefined;
    this.rpOsmNetworkSource.clear();
    this.rpOsmNodesSource.clear();
    this.rpMap?.setTarget(undefined);
    this.rpMap?.dispose();
    this.rpMap = undefined;
  }
  // ======================== End Road Planning State ========================

  /** Toggle sidebar and refresh map size so it flexes correctly (no overlap). */
  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.refreshMapSizeAfterLayout();
  }

  /** Reserved for legacy DOM hooks inside the sidebar (sub-tab buttons, etc.). */
  private ensureSidebarTabsReady(): void {
    /* Tab switching uses sidebarActiveTab + template [class.active] bindings. */
  }

  /** Switch sidebar to the Layers tab and expand sidebar if collapsed. Used by toolbar Layers button and "Manage layers…" link. */
  switchToLayersTab(): void {
    this.sidebarCollapsed = false;
    this.setSidebarTab('layers');
    setTimeout(() => this.refreshMapSizeAfterLayout(), 0);
  }

  /** Line width slider change – keep in sync with drawing style. */
  onLineThicknessChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    if (!isNaN(value)) {
      this.lineThickness = value;
      this.updateActiveDrawingStyle();
    }
  }

  /** Symbol size slider change. */
  onSymbolSizeChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value);
    if (!isNaN(value)) {
      this.symbolSize = value;
    }
  }

  /**
   * Size host → body → map row from the home-page outlet (fixes ~198px content-sized .nhai-body on Editor).
   */
  private syncGisShellLayout(): void {
    const body = this.nhaiBodyRef?.nativeElement;
    const layout = this.nhaiLayoutRef?.nativeElement;
    if (!body || !layout) return;

    const root = layout.closest('.nhai-root') as HTMLElement | null;
    const host = (root?.closest('app-gisavimvp') ?? layout.closest('app-gisavimvp')) as HTMLElement | null;
    if (!root || !host) return;

    body.style.display = 'flex';

    const outlet = host.parentElement;
    if (outlet) {
      const outletH = outlet.getBoundingClientRect().height;
      if (outletH > 80) {
        host.style.flex = '1 1 0%';
        host.style.height = `${outletH}px`;
        host.style.minHeight = `${outletH}px`;
        root.style.height = '100%';
        root.style.minHeight = '100%';
      }
    }

    const header = root.querySelector('.nhai-header') as HTMLElement | null;
    const projectHdr = root.querySelector('.project-details-header') as HTMLElement | null;
    const chrome = (header?.offsetHeight ?? 0) + (projectHdr?.offsetHeight ?? 0);
    const rootInnerH = root.clientHeight || host.getBoundingClientRect().height;
    const bodyH = Math.max(320, rootInnerH - chrome);

    body.style.flex = '1 1 0%';
    body.style.minHeight = `${bodyH}px`;
    body.style.height = `${bodyH}px`;

    const bottom = body.querySelector('.nhai-bottom-section') as HTMLElement | null;
    const footer = body.querySelector('.nhai-footer') as HTMLElement | null;
    const hideBottom =
      body.classList.contains('nhai-body--bottom-collapsed') ||
      body.classList.contains('nhai-body--no-bottom') ||
      !bottom ||
      bottom.offsetParent === null;
    let siblingH = footer?.offsetHeight ?? 32;
    if (bottom && !hideBottom) siblingH += bottom.offsetHeight;
    const mapH = Math.max(200, bodyH - siblingH);
    layout.style.flex = '1 1 0%';
    layout.style.minHeight = `${mapH}px`;
    layout.style.height = `${mapH}px`;
    layout.style.maxHeight = `${mapH}px`;
  }

  /** Right module panel (Parcel Details, Billing, etc.) — collapsed by default so map is full width */
  rightPanelCollapsed = true;

  bottomPanelCollapsed = false;

  /** Two-column layout: NHAI left workbench + map (no empty 3rd grid track). */
  isMapWideLayout(): boolean {
    return (
      this.rightPanelCollapsed ||
      this.nhaiActiveModule === 'execution' ||
      this.nhaiActiveModule === 'editor'
    );
  }

  toggleRightPanel(): void {
    this.rightPanelCollapsed = !this.rightPanelCollapsed;
    this.refreshMapSizeAfterLayout();
  }

  toggleBottomPanel(): void {
    this.bottomPanelCollapsed = !this.bottomPanelCollapsed;
    this.refreshMapSizeAfterLayout();
  }

  /** Call after tab/panel layout changes so OpenLayers repaints with the real container size. */
  private refreshMapSizeAfterLayout(): void {
    const run = () => {
      this.syncGisShellLayout();
      const el = this.mapElement?.nativeElement;
      if (!this.map || !el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width > 8 && rect.height > 8) {
        this.map.updateSize();
        this.map.renderSync();
      }
    };
    this.cdr.detectChanges();
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    [0, 50, 150, 350, 600, 1000, 1500, 2000].forEach((ms) => setTimeout(run, ms));
  }
  selectedLayerId: string | null = null;
  layerContextMenu: { visible: boolean; x: number; y: number; layerId: string | null } = {
    visible: false,
    x: 0,
    y: 0,
    layerId: null
  };
  expandedGroups: { [key: string]: boolean } = {}; // Track expanded/collapsed groups
  layerSearchTerm = '';
  draggingLayerId: string | null = null;

  // Interaction variables
  drawInteraction?: Draw;
  selectInteraction?: Select;
  modifyInteraction?: Modify;
  measureInteraction?: any;
  snapInteractions: Snap[] = [];
  translateInteraction?: Translate;
  // WMS layers registry
  private wmsLayers: { id: string; layer: ImageLayer<ImageWMS> }[] = [];
  private heatmapLayer: Heatmap | null = null;
  private clusterLayers: { [layerId: string]: VectorLayer } = {};
  private contourLayerIds: string[] = [];
  private elevationApiBase = 'https://api.open-elevation.com/api/v1/lookup';
  freehandMode = false;
  snapEnabled = true;
  /** Optional custom icon URL (SVG/PNG) for next point marker */
  defaultIconUrl = '';
  /** Offline mode: when true, edits are persisted locally and synced when back online */
  offlineMode = false;
  private offlineBannerEl: HTMLElement | null = null;
  private static readonly OFFLINE_STORAGE_KEY = 'gisEditorOfflineLayers';

  // State variables
  currentSymbol = '';
  currentLines: Feature[] = [];
  activeLineIndex = -1;
  drawingHistory: any[] = [];
  historyStep = -1;
  currentSegment = 1;
  currentSubSegment = 'A';
  lineThickness = 2;
  symbolSize = 1.0;
  isMultiLineMode = false;
  is3DMode = false;
  currentDrawingMode = ''; // Track current drawing mode for thickness updates
  gpsWatchId: any = null;
  workZone: any = null;
  autoSaveTimer: any = null;
  thematicStyles: { [key: string]: string } = {};
  layerOffsets: { [key: string]: { x: number; y: number } } = {};
  showNomenclature = true;
  showOverground = true;
  showUnderground = true;
  showCrossover = true;

  // Coordinate input variables
  currentLatitude = 28.6139;
  currentLongitude = 77.2090;
  pinpointMarker: Feature | null = null;

  // Enhanced numbering system variables
  numberingPrefix = 'S-';
  numberingSuffix = '-A';
  startNumber = 1;
  nomenclature = 'Gandhi baba'; // Base name for segments
  nomenclatureAbbreviation = 'Gb'; // Abbreviation for subsegments
  segmentSubSegmentCount: { [key: number]: number } = {}; // Track subsegment count per segment

  // Lane separator variables
  dividerType = 'painted';
  separatorWidth = 0.3;

  // Feature properties variables
  selectedFeature: Feature | null = null;
  featurePropertiesDialog: HTMLElement | null = null;

  // Elevation and 3D variables
  showElevation = true;
  elevationProfileData: any[] = [];

  // Curve drawing variables
  currentCurveType: 'bezier' | 'spline' | 'arc' | 'smooth' = 'smooth';
  curvePrecision = 30;
  curveControlPoints: number[][] = [];
  isCurveDrawing = false;
  curveDrawingListener: any = null;

  // Advanced features variables
  waterFlowDirection = 'north';
  analysisBuffer = 100;
  analysisType = 'intersection';
  exportFormat = 'geojson';
  includeMetadata = true;
  validationRules = 'basic';
  autoFixIssues = true;
  separatorDistance = 2;
  separatorCount = 0;
  placeSymbolHandler: any = null;

  // Segment drawing variables
  isSegmentDrawing = false;
  currentSegmentPoints: number[][] = [];
  segmentDrawingListener: any = null;

  // Real-time line length during drawing
  private sketchGeometryRef: any = null;
  private sketchLengthUpdateFn: (() => void) | null = null;
  nodeCounter = 1;
  segmentCounter = 1;
  numberingMode: 'auto' | 'manual' = 'auto';
  manualSegmentNumber = 1;
  manualNodeNumber = 1;

  // Junction and connection variables
  isConnectingRoads = false;
  isConnectingSegments = false;
  selectedJunction: Feature | null = null;
  firstSegment: Feature | null = null;
  highlightedFeature: Feature | null = null;

  // Popup overlay
  popup!: Overlay;

  // View history for navigation (previous/next view)
  viewHistory: Array<{ center: number[]; zoom: number; timestamp: number }> = [];
  viewHistoryIndex: number = -1;
  maxViewHistorySize: number = 50;
  isNavigatingHistory: boolean = false;

  // Project-related properties
  projectId: string | null = null;
  projectData: any = null;
  isLoadingProject: boolean = false;
  showProjectDetails: boolean = false;

  // BOQ/BOM display
  boqList: any[] = [];
  bomList: any[] = [];
  boqBomLoading: boolean = false;

  // Reporting & monitoring
  reportStatus: string = 'Last run: N/A';
  nhaiLayerSummary: string = '';
  projectSpent: number | null = null;

  private searchHighlightedFeatures: Feature[] = [];

  // Road specifications
  currentRoadSpecs = {
    type: 'highway',
    width: 12,
    depth: 0.5,
    lanes: 2,
    surface: 'bituminous',
    separatorDistance: 2
  };

  private static readonly ROAD_TYPE_DEFAULTS: Record<
    string,
    { width: number; lanes: number; depth: number }
  > = {
    highway: { width: 24, lanes: 4, depth: 0.6 },
    arterial: { width: 18, lanes: 4, depth: 0.5 },
    collector: { width: 12, lanes: 2, depth: 0.45 },
    local: { width: 8, lanes: 2, depth: 0.35 },
    service: { width: 6, lanes: 1, depth: 0.3 },
  };

  private static readonly ROAD_SURFACE_STYLES: Record<
    string,
    { fill: string; stroke: string; center: string }
  > = {
    bituminous: {
      fill: 'rgba(26, 26, 26, 0.82)',
      stroke: '#0f0f0f',
      center: '#f1f5f9',
    },
    concrete: {
      fill: 'rgba(168, 168, 168, 0.85)',
      stroke: '#6b7280',
      center: '#1e293b',
    },
    gravel: {
      fill: 'rgba(180, 140, 90, 0.8)',
      stroke: '#8b6914',
      center: '#fef3c7',
    },
    earthen: {
      fill: 'rgba(139, 115, 85, 0.78)',
      stroke: '#6b5344',
      center: '#fff7ed',
    },
  };

  // Default settings
  defaultSettings = {
    center: [77.4583, 23.1839],
    zoom: 15,
    snapTolerance: 15,
    autoSaveInterval: 5,
    showGrid: false,
    showScaleBar: true,
    showNorthArrow: true,
    measurementUnits: 'metric',
    lineColor: '#3498db',
    backgroundColor: '#f0f2f5'
  };

  // Base maps
  baseMaps: { [key: string]: TileLayer } = {
    osm: new TileLayer({
      source: new OSM()
    }),
    google: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    googleSatellite: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    googleHybrid: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    googleTerrain: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
        attributions: '© Google Maps'
      })
    }),
    satellite: new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      })
    }),
    terrain: new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'
      })
    }),
    dark: new TileLayer({
      source: new XYZ({
        url: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}.png'
      })
    })
  };

  currentBaseMap = this.baseMaps['google'];

  // GeoJSON format
  format = new GeoJSON();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private amBridge: AssetMapBridgeService,
    private lrsService: LrsService,
    private osmMapApi: OsmMapApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeVectorSources();
    this.initializeMap();
    this.setupEventListeners();
    this.setupOfflineSupport();
    this.checkForProjectId();
    this.initializeBackendIntegration();
    this.initializeAssetManagementBridge();
    if (!navigator.onLine) {
      this.offlineMode = true;
      this.showOfflineBanner();
      this.restoreLayersFromOfflineStorage();
    }
    console.log('🎯 PostGIS Backend Integration Complete!');
    console.log('📍 Available methods: save/load layers, spatial queries, geometry validation, import/export');
  }

  ngAfterViewInit(): void {
    this.ensureSidebarTabsReady();
    if (this.mapElement) {
      const el = this.mapElement.nativeElement;
      this.map.setTarget(el);
      // Keep map canvas in sync with container size (fixes tiles only in top-left)
      const doUpdateSize = () => {
        if (!this.map || !el) return;
        const rect = el.getBoundingClientRect();
        // Use geometry instead of offsetParent — nested grids / overflow can make offsetParent unreliable
        if (rect.width > 0 && rect.height > 0) {
          this.map.updateSize();
          this.map.renderSync();
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(doUpdateSize);
      });
      [100, 200, 350, 500, 700, 1000, 1500, 2500].forEach((ms) =>
        setTimeout(() => {
          this.syncGisShellLayout();
          doUpdateSize();
        }, ms)
      );
      const resizeTarget = el.closest('.map-container') ?? el;
      this.mapResizeObserver = new ResizeObserver(() => {
        this.syncGisShellLayout();
        doUpdateSize();
      });
      this.mapResizeObserver.observe(resizeTarget);
      const layoutEl = el.closest('.nhai-layout') as HTMLElement | null;
      if (layoutEl) this.mapResizeObserver.observe(layoutEl);
      const bodyEl = el.closest('.nhai-body') as HTMLElement | null;
      if (bodyEl) this.mapResizeObserver.observe(bodyEl);
      this.syncGisShellLayout();
    }
    window.addEventListener('resize', this.onWindowResize);
    // Setup scale-based visibility listeners
    this.setupScaleVisibilityListeners();
    // Initialize expanded groups
    Object.keys(this.layerGroups).forEach(group => {
      this.expandedGroups[group] = true;
    });
    // Machine Tracking: load dummy GeoJSON and setup click handler (after map is ready)
    setTimeout(() => {
      // this.loadMachineTrackingDummyData();
      // this.setupMachineTrackingClickHandler();
    }, 600);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private initializeVectorSources(): void {
    this.vectorSource = new VectorSource();
    this.roadSource = new VectorSource();
    this.projectSource = new VectorSource();
    this.biometricSource = new VectorSource();
    this.gpsSource = new VectorSource();
    this.waterSource = new VectorSource();
    this.separatorSource = new VectorSource();
    this.constructionPathSource = new VectorSource();
    this.chainageSource = new VectorSource();
    this.machinesSource = new VectorSource();
    this.rowAlignmentSource = new VectorSource();
    this.rowChainageSource = new VectorSource();
    this.rowParcelSource = new VectorSource();
    this.rowWarningSource = new VectorSource();

    // Asset-management layer is created lazily so it's added on top of all
    // baseline layers; it's still registered before the OL Map instance is
    // constructed in initializeMap().
    const built = this.amBridge.createAssetLayer();
    this.amSource = built.source;
    this.amLayer = built.layer;
    this.amClusterSource = built.clusterSource;
    this.amClusterLayer = built.clusterLayer;
  }

  private initializeMap(): void {
    // Create popup overlay
    this.popup = new Overlay({
      element: document.getElementById('popup')!,
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -10]
    });

    // Create and register vector layers with default palettes
    this.registerLayer('drawing', this.createDrawingLayer());
    this.layerPaletteId['drawing'] = 'default';
    this.registerLayer('road', this.createRoadLayer());
    this.layerPaletteId['road'] = 'road_survey';
    this.registerLayer('project', this.createProjectLayer());
    this.layerPaletteId['project'] = 'infrastructure';
    this.registerLayer('biometric', this.createBiometricLayer());
    this.layerPaletteId['biometric'] = 'utilities';
    this.registerLayer('gps', this.createGpsLayer());
    this.layerPaletteId['gps'] = 'road_survey';
    this.registerLayer('water', this.createWaterLayer());
    this.layerPaletteId['water'] = 'utilities';
    this.registerLayer('separator', this.createSeparatorLayer());
    this.layerPaletteId['separator'] = 'road_survey';
    this.registerLayer('constructionPath', this.createConstructionPathLayer());
    this.registerLayer('chainage', this.createChainageLayer());
    this.registerLayer('machines', this.createMachinesLayer());
    this.registerLayer('rowAlignment', this.createRowAlignmentLayer());
    this.registerLayer('rowChainage', this.createRowChainageLayer());
    this.registerLayer('rowParcel', this.createRowParcelLayer());
    this.setRowLayersVisible(false);

    // Initialize map with registered layers
    const allLayers = [this.currentBaseMap, ...Object.values(this.layerRegistry)];
    this.map = new Map({
      target: undefined, // Will be set in ngAfterViewInit
      layers: allLayers,
      view: new View({
        center: fromLonLat(this.defaultSettings.center),
        zoom: this.defaultSettings.zoom
      })
    });

    this.map.addOverlay(this.popup);

    // Asset Management overlay layer is added directly to the map (not via
    // registerLayer) so it doesn't appear in user-facing layer lists or get
    // included in save/export iterations over the standard registry.
    this.map.addLayer(this.amLayer);
    this.map.addLayer(this.amClusterLayer);
    this.amStopClusterDistance = this.amBridge.syncClusterDistance(this.map, this.amClusterSource);

    // Initialize view history tracking
    this.initializeViewHistory();

    // Setup map event listeners
    this.setupMapEventListeners();
    this.setupJunctionContextMenu();
  }

  private createDrawingLayer(): VectorLayer {
    return new VectorLayer({
      source: this.vectorSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('drawing', feature, resolution)
    });
  }

  private createRoadLayer(): VectorLayer {
    return new VectorLayer({
      source: this.roadSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('road', feature, resolution)
    });
  }

  private createProjectLayer(): VectorLayer {
    return new VectorLayer({
      source: this.projectSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('project', feature, resolution)
    });
  }

  private createBiometricLayer(): VectorLayer {
    return new VectorLayer({
      source: this.biometricSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('biometric', feature, resolution)
    });
  }

  private createGpsLayer(): VectorLayer {
    return new VectorLayer({
      source: this.gpsSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('gps', feature, resolution)
    });
  }

  private createWaterLayer(): VectorLayer {
    return new VectorLayer({
      source: this.waterSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('water', feature, resolution)
    });
  }

  private createSeparatorLayer(): VectorLayer {
    return new VectorLayer({
      source: this.separatorSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme('separator', feature, resolution)
    });
  }

  /** Machine Tracking: construction path with status-based coloring (completed=yellow, under_construction=red) */
  private createConstructionPathLayer(): VectorLayer {
    return new VectorLayer({
      source: this.constructionPathSource,
      style: (feature: FeatureLike) => {
        const status = feature.get('status') || 'under_construction';
        const strokeColor = status === 'completed' ? '#f1c40f' : '#e74c3c';
        const centerColor = status === 'completed' ? '#f1c40f' : '#e67e22';
        return [
          new Style({
            stroke: new Stroke({ color: '#4a4a4a', width: 12 })
          }),
          new Style({
            stroke: new Stroke({ color: centerColor, width: 4 })
          })
        ];
      }
    });
  }

  /** Machine Tracking: chainage labels along the path */
  private createChainageLayer(): VectorLayer {
    return new VectorLayer({
      source: this.chainageSource,
      style: (feature: FeatureLike) => new Style({
        text: new Text({
          text: feature.get('label') || '',
          font: 'bold 11px Arial',
          fill: new Fill({ color: '#000' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
          offsetY: -10
        })
      })
    });
  }

  /** Machine Tracking: machine point markers (active=green) */
  private createMachinesLayer(): VectorLayer {
    return new VectorLayer({
      source: this.machinesSource,
      style: (feature: FeatureLike) => new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: '#2ecc71' }),
          stroke: new Stroke({ color: '#27ae60', width: 2 })
        })
      })
    });
  }

  /** Resolution scale factor clamped to sensible range */
  private rowScale(resolution: number | undefined): number {
    const res = resolution || this.map?.getView()?.getResolution() || 10;
    return Math.min(Math.max(10 / res, 0.4), 2.5);
  }

  /** ROW: road alignment lines (main road + ROW boundaries) — width scales with zoom */
  private createRowAlignmentLayer(): VectorLayer {
    return new VectorLayer({
      source: this.rowAlignmentSource,
      style: (feature: FeatureLike, resolution?: number) => {
        const s = this.rowScale(resolution);
        const status = feature.get('status') || 'main_road';
        if (status === 'main_road') {
          return [
            new Style({ stroke: new Stroke({ color: '#6a5430', width: Math.max(s * 7, 2) }) }),
            new Style({ stroke: new Stroke({ color: '#c8903a', width: Math.max(s * 5, 1.5) }) }),
            new Style({ stroke: new Stroke({ color: '#e8b85a', width: Math.max(s * 2, 0.8) }) })
          ];
        }
        return new Style({
          stroke: new Stroke({ color: 'rgba(180,130,60,0.45)', width: Math.max(s * 0.8, 0.5), lineDash: [8, 5] })
        });
      }
    });
  }

  /** ROW: chainage/warning/structure points — marker & text scale with zoom */
  private createRowChainageLayer(): VectorLayer {
    return new VectorLayer({
      source: this.rowChainageSource,
      style: (feature: FeatureLike, resolution?: number) => {
        const s = this.rowScale(resolution);
        const lbl = feature.get('label') || '';
        const ftype = feature.get('type') || 'chainage';
        const fs = Math.round(Math.max(s * 11, 8));
        const r = Math.round(Math.max(s * 6, 3));
        const stemR = Math.round(Math.max(s * 14, 6));
        const stemD = Math.round(Math.max(s * 18, 8));
        const txtOff = -Math.round(Math.max(s * 40, 18));

        if (ftype === 'warning') {
          return [
            new Style({ image: new CircleStyle({ radius: Math.round(r * 1.4), fill: new Fill({ color: '#e8a820' }), stroke: new Stroke({ color: '#a07010', width: Math.max(s * 2, 1) }) }) }),
            new Style({ image: new RegularShape({ points: 2, radius: stemR, angle: 0, stroke: new Stroke({ color: '#a0956a', width: Math.max(s * 1.2, 0.7) }), displacement: [0, stemD] }) }),
            new Style({ text: new Text({ text: '⚠ ' + lbl, font: `bold ${fs}px "Segoe UI", Arial, sans-serif`, fill: new Fill({ color: '#5a4000' }), offsetY: txtOff, backgroundFill: new Fill({ color: 'rgba(255,245,220,0.94)' }), backgroundStroke: new Stroke({ color: '#c8a030', width: 1.2 }), padding: [3, 6, 3, 6] }) })
          ];
        }
        if (ftype === 'structure') {
          return [
            new Style({ image: new CircleStyle({ radius: Math.round(r * 1.2), fill: new Fill({ color: '#8899aa' }), stroke: new Stroke({ color: '#556677', width: Math.max(s * 1.5, 0.8) }) }) }),
            new Style({ image: new RegularShape({ points: 2, radius: Math.round(stemR * 0.85), angle: 0, stroke: new Stroke({ color: '#8a9aaa', width: Math.max(s * 1.2, 0.7) }), displacement: [0, Math.round(stemD * 0.85)] }) }),
            new Style({ text: new Text({ text: lbl, font: `600 ${Math.round(fs * 0.9)}px "Segoe UI", Arial, sans-serif`, fill: new Fill({ color: '#2a3a4a' }), offsetY: Math.round(txtOff * 0.9), backgroundFill: new Fill({ color: 'rgba(235,240,250,0.94)' }), backgroundStroke: new Stroke({ color: '#8a9aaa', width: 1 }), padding: [3, 6, 3, 6] }) })
          ];
        }
        return [
          new Style({ image: new CircleStyle({ radius: r, fill: new Fill({ color: '#7a8ea0' }), stroke: new Stroke({ color: '#4a5a6a', width: Math.max(s * 1.5, 0.8) }) }) }),
          new Style({ image: new RegularShape({ points: 2, radius: stemR, angle: 0, stroke: new Stroke({ color: '#8a9aaa', width: Math.max(s * 1.2, 0.7) }), displacement: [0, stemD] }) }),
          new Style({ text: new Text({ text: lbl, font: `bold ${fs}px "Segoe UI", Arial, sans-serif`, fill: new Fill({ color: '#1a2a3a' }), offsetY: txtOff, backgroundFill: new Fill({ color: 'rgba(240,243,248,0.94)' }), backgroundStroke: new Stroke({ color: '#8a9aaa', width: 1 }), padding: [3, 6, 3, 6] }) })
        ];
      }
    });
  }

  /** ROW: land parcel polygons — border width scales with zoom */
  private createRowParcelLayer(): VectorLayer {
    return new VectorLayer({
      source: this.rowParcelSource,
      style: (feature: FeatureLike, resolution?: number) => {
        const s = this.rowScale(resolution);
        const status = feature.get('status') || '';
        let fillColor = 'rgba(100,200,100,0.18)';
        let strokeColor = '#4a8';
        if (status === 'Under Process') { fillColor = 'rgba(230,180,50,0.18)'; strokeColor = '#c89a20'; }
        if (status === 'Pending') { fillColor = 'rgba(220,80,80,0.18)'; strokeColor = '#c44'; }
        return new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: strokeColor, width: Math.max(s * 1.5, 0.8), lineDash: [6, 4] })
        });
      }
    });
  }

  /** Load dummy ROW GeoJSON data into the ROW layers */
  private loadRowDummyData(): void {
    const fmt = new GeoJSON();
    const features = fmt.readFeatures(DUMMY_ROW_GEOJSON as any, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    this.rowAlignmentSource.clear();
    this.rowChainageSource.clear();
    this.rowParcelSource.clear();
    this.rowWarningLabel = '';

    for (const f of features) {
      const geom = f.getGeometry();
      if (!geom) continue;
      const props = f.getProperties();
      const ftype = props['type'] || '';
      if (ftype === 'alignment') {
        this.rowAlignmentSource.addFeature(f);
      } else if (ftype === 'chainage' || ftype === 'warning' || ftype === 'structure') {
        this.rowChainageSource.addFeature(f);
        if (ftype === 'warning') { this.rowWarningLabel = props['label'] || ''; }
      } else if (ftype === 'parcel') {
        this.rowParcelSource.addFeature(f);
      }
    }

    const ext = this.rowAlignmentSource.getExtent();
    if (ext[0] !== Infinity) {
      this.map.getView().fit(ext, { padding: [80, 80, 80, 80], maxZoom: 14 });
    }
  }

  /** Show/hide ROW layers based on active module */
  private setRowLayersVisible(visible: boolean): void {
    ['rowAlignment', 'rowChainage', 'rowParcel'].forEach(key => {
      const layer = this.layerRegistry[key];
      if (layer) layer.setVisible(visible);
    });
  }

  /** Show/hide Machine Tracking layers based on active module */
  private setMachineTrackingLayersVisible(visible: boolean): void {
    ['constructionPath', 'chainage', 'machines'].forEach(key => {
      const layer = this.layerRegistry[key];
      if (layer) layer.setVisible(visible);
    });
    if (!visible) this.selectedMachine = null;
  }

  rowZoomIn(): void {
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() || 10) + 1, duration: 250 });
  }

  rowZoomOut(): void {
    const view = this.map.getView();
    view.animate({ zoom: (view.getZoom() || 10) - 1, duration: 250 });
  }

  setRowBaseMap(mode: 'satellite' | 'dem' | '3d'): void {
    this.rowBaseMap = mode;
    const view = this.map.getView();
    const currentZoom = view.getZoom() || 12;

    if (mode === 'satellite') {
      this.changeBaseMap(this.baseMaps['googleSatellite'] ? 'googleSatellite' : 'osm');
      view.animate({ rotation: 0, duration: 250 });
      return;
    }

    if (mode === 'dem') {
      this.changeBaseMap('osm');
      view.animate({ rotation: 0, duration: 250 });
      return;
    }

    // 3D preview mode: satellite basemap + oblique camera rotation + slightly closer zoom.
    this.changeBaseMap(this.baseMaps['googleSatellite'] ? 'googleSatellite' : 'osm');
    view.animate(
      { zoom: Math.max(currentZoom, 14), duration: 280 },
      { rotation: Math.PI / 9, duration: 320 }
    );
  }

  private onModuleSwitch(prev: string, next: string): void {
    if (prev === 'roadplanning' && next !== 'roadplanning') {
      this.destroyRpMap();
    }
    const isRow = next === 'row';
    const isExecution = next === 'execution';
    /* Map-first modules: collapse GIS Layers sidebar so Machine Tracking / logs stay readable */
    if (isExecution || isRow) {
      this.sidebarCollapsed = true;
      this.rightPanelCollapsed = true;
    } else if (next === 'editor') {
      /* Start with layers panel visible; map uses grid stack so height stays correct */
      this.sidebarCollapsed = false;
      this.bottomPanelCollapsed = false;
    }
    this.setRowLayersVisible(isRow);
    this.setMachineTrackingLayersVisible(isExecution);

    if (isRow) {
      if (this.rowAlignmentSource.getFeatures().length === 0) {
        this.loadRowDummyData();
      } else {
        const ext = this.rowAlignmentSource.getExtent();
        if (ext[0] !== Infinity) {
          this.map.getView().fit(ext, { padding: [80, 80, 80, 80], maxZoom: 14 });
        }
      }
      this.setRowBaseMap(this.rowBaseMap);
    } else if (next === 'editor') {
      this.map.getView().animate({ rotation: 0, duration: 200 });
      if (this.baseMaps['google']) this.changeBaseMap('google');
    } else if (isExecution) {
      // Ensure Machine Tracking data exists (loaded in ngAfterViewInit, but safe here too)
      if (this.constructionPathSource.getFeatures().length === 0) {
        // this.loadMachineTrackingDummyData();
      } else {
        const extent = this.constructionPathSource.getExtent();
        if (extent[0] !== Infinity) {
          this.map.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 15 });
        }
      }
      this.map.getView().animate({ rotation: 0, duration: 200 });
      if (this.baseMaps['googleSatellite']) this.changeBaseMap('googleSatellite');
    } else if (next === 'roadplanning') {
      this.enterRoadPlanningModule();
    } else {
      // Default view for other modules
      this.map.getView().animate({ rotation: 0, duration: 200 });
      this.changeBaseMap('osm');
    }

    if (next === 'execution' && !this.selectedMachine) {
      this.bottomPanelCollapsed = false;
    }
    this.cdr.detectChanges();
    this.refreshMapSizeAfterLayout();
  }

  /** Load dummy Machine Tracking GeoJSON into construction path, chainage, and machines layers */
  private loadMachineTrackingDummyData(): void {
    const geojsonFormat = new GeoJSON();
    const features = geojsonFormat.readFeatures(DUMMY_MACHINE_TRACKING_GEOJSON as any, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    this.constructionPathSource.clear();
    this.chainageSource.clear();
    this.machinesSource.clear();
    for (const f of features) {
      const geom = f.getGeometry();
      if (!geom) continue;
      const props = f.getProperties();
      if (props['type'] === 'chainage') {
        f.set('label', props['label']);
        this.chainageSource.addFeature(f);
      } else if (
        props['id'] &&
        (props['type'] === 'excavator' ||
          props['type'] === 'loader' ||
          props['type'] === 'paver' ||
          props['type'] === 'dump_truck')
      ) {
        this.machinesSource.addFeature(f);
      } else if (
        geom.getType() === 'LineString' &&
        (props['status'] === 'completed' || props['status'] === 'under_construction')
      ) {
        this.constructionPathSource.addFeature(f);
      }
    }
    const extent = this.constructionPathSource.getExtent();
    if (extent[0] !== Infinity) {
      this.map.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 15 });
    }
    if (this.baseMaps['googleSatellite']) {
      this.changeBaseMap('googleSatellite');
    }
  }

  /** Setup click handler for machine points to show info card */
  private setupMachineTrackingClickHandler(): void {
    this.map.on('click', (evt: any) => {
      const pixel = evt.pixel || this.map.getEventPixel(evt.originalEvent);
      const feature = this.map.forEachFeatureAtPixel(pixel, (f) => f as Feature | undefined);
      if (feature && this.machinesSource.hasFeature(feature as Feature)) {
        const p = (feature as Feature).getProperties();
        const ch = String(p['chainage'] || p['chainageLabel'] || '').trim();
        this.selectedMachine = {
          id: p['id'] || '',
          type: String(p['type'] || ''),
          model: String(p['model'] || ''),
          chainage: ch || '—',
          status: String(p['status'] || ''),
          telemetry: String(p['current_value'] ?? '')
        };
      } else {
        this.selectedMachine = null;
      }
    });
  }

  /**
   * Returns the vector source to use for drawing. Uses the selected layer's source when a layer
   * is selected and it is a vector layer and not locked; otherwise uses the drawing layer.
   */
  getCurrentDrawingSource(): VectorSource {
    const layerId = this.selectedLayerId || 'drawing';
    if (this.layerLocks[layerId]) return this.vectorSource;
    const layer = this.layerRegistry[layerId];
    if (!layer) return this.vectorSource;
    const src = layer.getSource();
    if (src instanceof VectorSource) return src;
    return this.vectorSource;
  }

  /** Returns the layer id that is currently the drawing target (selected layer or 'drawing'). */
  getCurrentDrawingLayerId(): string {
    const layerId = this.selectedLayerId || 'drawing';
    if (this.layerLocks[layerId] || !this.layerRegistry[layerId]) return 'drawing';
    return layerId;
  }

  /** Returns the vector source that contains the given feature, or null if not found. */
  private getSourceForFeature(feature: Feature): VectorSource | null {
    const sources = [
      this.vectorSource,
      this.roadSource,
      this.projectSource,
      this.biometricSource,
      this.gpsSource,
      this.waterSource,
      this.separatorSource,
      this.constructionPathSource,
      this.chainageSource,
      this.machinesSource
    ];
    for (const src of sources) {
      if (src && src.getFeatures().includes(feature)) return src;
    }
    for (const layerId of this.layerOrder) {
      const layer = this.layerRegistry[layerId];
      const src = layer?.getSource();
      if (src instanceof VectorSource && src.getFeatures().includes(feature)) return src;
    }
    return null;
  }

  // Layer management methods
  private registerLayer(id: string, layer: VectorLayer): void {
    this.layerRegistry[id] = layer;
    this.layerOrder.push(id);
    // Initialize metadata
    this.layerMetadata[id] = {
      name: id,
      description: '',
      created: new Date(),
      modified: new Date()
    };
    // Initialize default zoom levels
    this.layerMinZoom[id] = 0;
    this.layerMaxZoom[id] = 24;
    this.updateLayerStatistics(id);
  }

  /** Apply layer-level color theme to a layer's style function. */
  private applyLayerTheme(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer) return;
    layer.setStyle((feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(layerId, feature, resolution));
    layer.changed();
  }

  /** Set the predefined theming palette for a layer (point, text, line, segment, sub_segment, section). */
  setLayerPalette(layerId: string, paletteId: string): void {
    this.layerPaletteId[layerId] = paletteId;
    if (this.layerStyles[layerId]) {
      this.layerStyles[layerId].paletteId = paletteId;
    }
    this.applyLayerTheme(layerId);
  }

  /** Get style for a feature with optional layer palette. resolution is used for dynamic symbol scaling by zoom. */
  private getLayerStyleWithTheme(layerId: string, feature: FeatureLike, resolution?: number): Style | Style[] {
    const base = this.getEnhancedDrawingStyle(feature, resolution);
    const theme = this.layerStyles[layerId];
    const paletteId = this.layerPaletteId[layerId] || theme?.paletteId;
    const palette = paletteId ? this.getPaletteById(paletteId) : null;
    const baseArr = Array.isArray(base) ? base : [base];

    let result: Style[];
    if (palette) {
      const vectorType = this.getVectorTypeFromFeature(feature);
      const vectorStyle = palette[vectorType];
      result = baseArr.map(s => this.applyVectorTypeStyleToStyle(s, vectorStyle, feature, resolution));
      if (theme && (theme.strokeColor || theme.fillColor || theme.strokeWidth != null)) {
        result = result.map(s => this.applyThemeToStyle(s, theme));
      }
      return result;
    }

    if (!theme || (!theme.strokeColor && theme.strokeWidth == null && !theme.fillColor)) {
      return base;
    }
    return baseArr.map(s => this.applyThemeToStyle(s, theme));
  }

  /** Scale factor for point radius by zoom (resolution). Keeps symbols readable at all zoom levels. */
  private getResolutionScale(resolution: number | undefined): number {
    if (resolution == null || !this.map) return 1;
    const view = this.map.getView();
    const refRes = view.getResolutionForZoom ? view.getResolutionForZoom(12) : 0.5;
    if (!refRes || resolution <= 0) return 1;
    const scale = refRes / resolution;
    return Math.max(0.5, Math.min(4, scale));
  }

  /** Resolve vector theme type from feature (geometry + properties). */
  private getVectorTypeFromFeature(feature: FeatureLike): VectorThemeType {
    const type = feature.get('type');
    const geom = feature.getGeometry();
    const geomType = geom ? geom.getType() : '';

    if (type === 'segment' || type === 'LineString' || type === 'MultiLineString') {
      if (feature.get('isSubSegment')) return 'sub_segment';
      if (feature.get('segmentNumber') != null || feature.get('segmentId')) return 'segment';
      return 'line';
    }
    if (type === 'segment_label' || type === 'label' || type === 'junction-label' || feature.get('text')) return 'text';
    if (geomType === 'Point' || geomType === 'MultiPoint') return 'point';
    if (geomType === 'Polygon' || geomType === 'MultiPolygon') return 'section';
    if (geomType === 'LineString' || geomType === 'MultiLineString') return 'line';
    if (geomType === 'Circle') return 'point';
    return 'line';
  }

  private getPaletteById(id: string): LayerPalette | null {
    return PREDEFINED_LAYER_PALETTES.find(p => p.id === id) || null;
  }

  /** Apply predefined vector-type style to a base OpenLayers Style. resolution used for dynamic symbol scaling. */
  private applyVectorTypeStyleToStyle(baseStyle: Style, vt: VectorTypeStyle, feature: FeatureLike, resolution?: number): Style {
    const fillColor = vt.fillColor && vt.fillColor !== 'transparent' ? vt.fillColor : (baseStyle.getFill()?.getColor() ?? 'rgba(52, 152, 219, 0.3)');
    const strokeColor = vt.strokeColor ?? baseStyle.getStroke()?.getColor() ?? '#3498db';
    const strokeWidth = vt.strokeWidth ?? baseStyle.getStroke()?.getWidth() ?? 2;
    const lineDash = vt.lineDash ?? baseStyle.getStroke()?.getLineDash() ?? [];

    let img = baseStyle.getImage();
    let radius = vt.pointRadius ?? (img && typeof (img as CircleStyle).getRadius === 'function' ? (img as CircleStyle).getRadius() : 6);
    radius = radius * this.getResolutionScale(resolution);
    if (img || vt.pointRadius != null) {
      img = new CircleStyle({
        radius,
        fill: new Fill({ color: vt.fillColor || strokeColor }),
        stroke: new Stroke({ color: strokeColor, width: 1 })
      });
    }

    let textStyle = baseStyle.getText();
    const textColor = vt.textColor ?? (typeof textStyle?.getFill === 'function' ? textStyle.getFill()?.getColor() : '#2c3e50');
    const fontSize = vt.fontSize ?? 12;
    if (textStyle) {
      const text = textStyle.getText();
      textStyle = new Text({
        text: Array.isArray(text) ? text[0] : text,
        font: `${fontSize}px Arial`,
        fill: new Fill({ color: textColor }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      });
    }

    return new Style({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth, lineDash }),
      image: img,
      text: textStyle
    });
  }

  private applyThemeToStyle(baseStyle: Style, theme: { strokeColor?: string; strokeWidth?: number; fillColor?: string }): Style {
    const fillColor = theme.fillColor ?? baseStyle.getFill()?.getColor() ?? 'rgba(52, 152, 219, 0.3)';
    const stroke = baseStyle.getStroke();
    const strokeColor = theme.strokeColor ?? stroke?.getColor() ?? '#3498db';
    const strokeWidth = theme.strokeWidth ?? stroke?.getWidth() ?? 2;
    const img = baseStyle.getImage();
    const circleImg = img && typeof (img as CircleStyle).getRadius === 'function'
      ? new CircleStyle({
          radius: (img as CircleStyle).getRadius(),
          fill: new Fill({ color: strokeColor }),
          stroke: new Stroke({ color: strokeColor, width: 1 })
        })
      : img;
    return new Style({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
      image: circleImg,
      text: baseStyle.getText()
    });
  }

  private updateLayerStatistics(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();
      
      this.layerStatistics[layerId] = {
        featureCount: features.length,
        visible: layer.getVisible(),
        opacity: layer.getOpacity(),
        zIndex: layer.getZIndex(),
        extent: source.getExtent(),
        lastUpdated: new Date()
      };
    }
  }

  // Enhanced layer management
  toggleLayerEnhanced(layerId: string, visible: boolean): void {
    const layer = this.layerRegistry[layerId];
    if (layer) {
      layer.setVisible(visible);
      this.updateLayerStatistics(layerId);
      this.updateLayerManagementUI();
    }
  }

  setLayerOpacityEnhanced(layerId: string, opacity: number): void {
    const layer = this.layerRegistry[layerId];
    if (layer) {
      layer.setOpacity(opacity);
      this.updateLayerStatistics(layerId);
    }
  }

  reorderLayer(layerId: string, newIndex: number): void {
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex !== -1) {
      // Remove from current position
      this.layerOrder.splice(currentIndex, 1);
      // Insert at new position
      this.layerOrder.splice(newIndex, 0, layerId);
      
      // Update z-index based on new order
      this.updateLayerOrder();
    }
  }

  private updateLayerOrder(): void {
    this.layerOrder.forEach((layerId, index) => {
      const layer = this.layerRegistry[layerId];
      if (layer) {
        layer.setZIndex(index + 100); // Base layers start at 100
      }
    });
  }

  groupLayers(groupName: string, layerIds: string[]): void {
    this.layerGroups[groupName] = layerIds;
    this.updateLayerManagementUI();
  }

  ungroupLayers(groupName: string): void {
    if (this.layerGroups[groupName]) {
      delete this.layerGroups[groupName];
      this.updateLayerManagementUI();
    }
  }

  filterLayer(layerId: string, filter: any): void {
    this.layerFilters[layerId] = filter;
    this.applyLayerFilter(layerId);
  }

  private applyLayerFilter(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    const filter = this.layerFilters[layerId];
    
    if (layer && layer.getSource() instanceof VectorSource && filter) {
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();
      
      features.forEach(feature => {
        const shouldShow = this.evaluateFilter(feature, filter);
        feature.set('filtered', !shouldShow);
      });
      
      // Trigger style update
      layer.changed();
    }
  }

  private evaluateFilter(feature: Feature, filter: any): boolean {
    // Simple filter evaluation - can be enhanced
    if (filter.type) {
      return feature.get('type') === filter.type;
    }
    if (filter.properties) {
      const props = feature.get('properties') || {};
      return Object.keys(filter.properties).every(key => 
        props[key] === filter.properties[key]
      );
    }
    return true;
  }

  clearLayerFilter(layerId: string): void {
    delete this.layerFilters[layerId];
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      source.getFeatures().forEach(feature => {
        feature.set('filtered', false);
      });
      layer.changed();
    }
  }

  exportLayer(layerId: string): string {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();
      
      const geoJson = {
        type: 'FeatureCollection',
        features: features.map(feature => ({
          type: 'Feature',
          geometry: (feature.getGeometry() as LineString)?.getCoordinates(),
          properties: feature.getProperties()
        }))
      };
      
      return JSON.stringify(geoJson, null, 2);
    }
    return '';
  }

  /** Export layer as KML and trigger download */
  exportLayerAsKml(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer || !(layer.getSource() instanceof VectorSource)) return;
    const source = layer.getSource() as VectorSource;
    const features = source.getFeatures();
    if (features.length === 0) {
      this.showInstructions('No features to export');
      return;
    }
    const kmlFormat = new KML();
    const kml = kmlFormat.writeFeatures(features, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${layerId}.kml`;
    a.click();
    URL.revokeObjectURL(a.href);
    this.showInstructions(`Exported ${layerId} as KML`);
  }

  /** Export layer as GPX and trigger download */
  exportLayerAsGpx(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer || !(layer.getSource() instanceof VectorSource)) return;
    const source = layer.getSource() as VectorSource;
    const features = source.getFeatures();
    if (features.length === 0) {
      this.showInstructions('No features to export');
      return;
    }
    const gpxFormat = new GPX();
    const gpx = gpxFormat.writeFeatures(features, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${layerId}.gpx`;
    a.click();
    URL.revokeObjectURL(a.href);
    this.showInstructions(`Exported ${layerId} as GPX`);
  }

  importLayer(layerId: string, geoJsonData: string): void {
    try {
      const data = JSON.parse(geoJsonData);
      const layer = this.layerRegistry[layerId];
      
      if (layer && layer.getSource() instanceof VectorSource) {
        const source = layer.getSource() as VectorSource;
        
        // Clear existing features
        source.clear();
        
        // Add imported features
        if (data.features) {
          data.features.forEach((featureData: any) => {
            const feature = new Feature({
              geometry: new LineString(featureData.geometry),
              ...featureData.properties
            });
            source.addFeature(feature);
          });
        }
        
        this.updateLayerStatistics(layerId);
      }
    } catch (error) {
      console.error('Error importing layer:', error);
    }
  }

  addLayer(layerId?: string): void {
    // Generate layer ID if not provided
    const newLayerId = layerId || `layer_${Date.now()}`;
    
    // Check if layer already exists
    if (this.layerRegistry[newLayerId]) {
      alert(`Layer "${newLayerId}" already exists. Please choose a different name.`);
      return;
    }

    // Per-layer style and theme so color changes affect only this layer
    if (!this.layerStyles[newLayerId]) {
      this.layerStyles[newLayerId] = {
        strokeColor: '#3498db',
        strokeWidth: 2,
        fillColor: 'rgba(52, 152, 219, 0.2)',
        paletteId: 'default'
      };
    }
    this.layerPaletteId[newLayerId] = this.layerPaletteId[newLayerId] || 'default';

    // Create new vector source and layer with per-layer style function
    const newSource = new VectorSource();
    const newLayer = new VectorLayer({
      source: newSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(newLayerId, feature, resolution),
      zIndex: this.layerOrder.length + 100
    });

    // Register the new layer
    this.registerLayer(newLayerId, newLayer);
    this.map.addLayer(newLayer);
    this.updateLayerManagementUI();
  }

  createLayerGroup(groupName: string, layerIds: string[]): void {
    if (this.layerGroups[groupName]) {
      // Add to existing group
      layerIds.forEach(layerId => {
        if (!this.layerGroups[groupName].includes(layerId)) {
          this.layerGroups[groupName].push(layerId);
        }
      });
    } else {
      // Create new group
      this.layerGroups[groupName] = [...layerIds];
    }
    this.updateLayerManagementUI();
  }

  removeLayerFromGroup(layerId: string, groupName: string): void {
    if (this.layerGroups[groupName]) {
      const index = this.layerGroups[groupName].indexOf(layerId);
      if (index > -1) {
        this.layerGroups[groupName].splice(index, 1);
        // Remove group if empty
        if (this.layerGroups[groupName].length === 0) {
          delete this.layerGroups[groupName];
        }
        this.updateLayerManagementUI();
      }
    }
  }
  duplicateLayer(layerId: string, newLayerId: string): void {
    const originalLayer = this.layerRegistry[layerId];
    if (originalLayer) {
      const newSource = new VectorSource();
      const newLayer = new VectorLayer({
        source: newSource,
        style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(newLayerId, feature, resolution)
      });
      this.registerLayer(newLayerId, newLayer);
      this.map.addLayer(newLayer);
      if (this.layerStyles[layerId]) {
        this.layerStyles[newLayerId] = { ...this.layerStyles[layerId] };
      }
      if (this.layerPaletteId[layerId] !== undefined) {
        this.layerPaletteId[newLayerId] = this.layerPaletteId[layerId];
      }
      this.updateLayerManagementUI();
    }
  }

  deleteLayer(layerId: string): void {
    if (!confirm(`Remove layer "${layerId}"? All features in this layer will be deleted. This cannot be undone.`)) {
      return;
    }
    const layer = this.layerRegistry[layerId];
    if (layer) {
      this.map.removeLayer(layer);
      delete this.layerRegistry[layerId];
      this.layerOrder = this.layerOrder.filter(id => id !== layerId);
      delete this.layerStatistics[layerId];
      delete this.layerFilters[layerId];
      delete this.layerStyles[layerId];
      delete this.layerPaletteId[layerId];
      delete this.layerMetadata[layerId];
      delete this.layerLocks[layerId];
      delete this.layerMinZoom[layerId];
      delete this.layerMaxZoom[layerId];
      Object.keys(this.layerGroups).forEach(groupName => {
        const arr = this.layerGroups[groupName];
        const idx = arr.indexOf(layerId);
        if (idx > -1) {
          arr.splice(idx, 1);
          if (arr.length === 0) delete this.layerGroups[groupName];
        }
      });
      this.updateLayerManagementUI();
    }
  }

  getLayerInfo(layerId: string): any {
    const layer = this.layerRegistry[layerId];
    return {
      ...this.layerStatistics[layerId],
      id: layerId,
      group: this.getLayerGroup(layerId),
      filter: this.layerFilters[layerId] || null,
      locked: this.layerLocks[layerId] || false,
      minZoom: this.layerMinZoom[layerId] || 0,
      maxZoom: this.layerMaxZoom[layerId] || 24,
      style: this.layerStyles[layerId] || null,
      metadata: this.layerMetadata[layerId] || {},
      visible: layer ? layer.getVisible() : true,
      opacity: layer ? layer.getOpacity() : 1.0
    };
  }

  // Enhanced layer management methods for QGIS-like functionality
  
  toggleLayerLock(layerId: string): void {
    this.layerLocks[layerId] = !(this.layerLocks[layerId] || false);
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      source.getFeatures().forEach(feature => {
        feature.set('locked', this.layerLocks[layerId]);
      });
    }
  }

  isLayerLocked(layerId: string): boolean {
    return this.layerLocks[layerId] || false;
  }

  setLayerMinZoom(layerId: string, zoom: number): void {
    this.layerMinZoom[layerId] = zoom;
    this.updateLayerScaleVisibility(layerId);
  }

  setLayerMaxZoom(layerId: string, zoom: number): void {
    this.layerMaxZoom[layerId] = zoom;
    this.updateLayerScaleVisibility(layerId);
  }

  private updateLayerScaleVisibility(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (!layer) return;

    const view = this.map.getView();
    const currentZoom = view.getZoom() || 0;
    const minZoom = this.layerMinZoom[layerId] || 0;
    const maxZoom = this.layerMaxZoom[layerId] || 24;

    // Only hide if outside zoom range, but preserve user's visibility setting
    if (currentZoom < minZoom || currentZoom > maxZoom) {
      layer.set('scaleHidden', true);
      if (layer.getVisible()) {
        layer.setVisible(false);
      }
    } else {
      layer.set('scaleHidden', false);
      // Restore visibility if it was hidden only due to scale
      if (!layer.getVisible() && !this.layerStatistics[layerId]?.userHidden) {
        layer.setVisible(true);
      }
    }
  }

  renameLayer(oldId: string, newId: string): void {
    if (this.layerRegistry[newId]) {
      alert(`Layer "${newId}" already exists!`);
      return;
    }

    const layer = this.layerRegistry[oldId];
    if (layer) {
      // Update registry
      this.layerRegistry[newId] = layer;
      delete this.layerRegistry[oldId];

      // Update order
      const index = this.layerOrder.indexOf(oldId);
      if (index !== -1) {
        this.layerOrder[index] = newId;
      }

      // Update groups
      Object.keys(this.layerGroups).forEach(groupName => {
        const index = this.layerGroups[groupName].indexOf(oldId);
        if (index !== -1) {
          this.layerGroups[groupName][index] = newId;
        }
      });

      // Update other references
      if (this.layerStatistics[oldId]) {
        this.layerStatistics[newId] = this.layerStatistics[oldId];
        delete this.layerStatistics[oldId];
      }
      if (this.layerFilters[oldId]) {
        this.layerFilters[newId] = this.layerFilters[oldId];
        delete this.layerFilters[oldId];
      }
      if (this.layerLocks[oldId] !== undefined) {
        this.layerLocks[newId] = this.layerLocks[oldId];
        delete this.layerLocks[oldId];
      }
      if (this.layerMinZoom[oldId] !== undefined) {
        this.layerMinZoom[newId] = this.layerMinZoom[oldId];
        delete this.layerMinZoom[oldId];
      }
      if (this.layerMaxZoom[oldId] !== undefined) {
        this.layerMaxZoom[newId] = this.layerMaxZoom[oldId];
        delete this.layerMaxZoom[oldId];
      }
      if (this.layerStyles[oldId]) {
        this.layerStyles[newId] = this.layerStyles[oldId];
        delete this.layerStyles[oldId];
      }
      if (this.layerPaletteId[oldId] !== undefined) {
        this.layerPaletteId[newId] = this.layerPaletteId[oldId];
        delete this.layerPaletteId[oldId];
      }
      if (this.layerMetadata[oldId]) {
        this.layerMetadata[newId] = this.layerMetadata[oldId];
        delete this.layerMetadata[oldId];
      }

      // Re-apply style so the layer's style function uses newId (closure was oldId)
      this.applyLayerTheme(newId);

      this.updateLayerManagementUI();
    }
  }

  getFilteredLayers(): string[] {
    if (!this.layerSearchTerm) {
      return this.layerOrder;
    }
    const searchLower = this.layerSearchTerm.toLowerCase();
    return this.layerOrder.filter(layerId => {
      const info = this.getLayerInfo(layerId);
      return layerId.toLowerCase().includes(searchLower) ||
             (info.metadata?.name || '').toLowerCase().includes(searchLower);
    });
  }

  getUngroupedLayers(): string[] {
    const groupedIds = new Set(Object.values(this.layerGroups).flat());
    return this.getFilteredLayers().filter(id => !groupedIds.has(id));
  }

  getLayerGroups(): Array<{ name: string; layers: string[] }> {
    const filteredLayers = this.getFilteredLayers();
    return Object.entries(this.layerGroups)
      .filter(([_, layerIds]) => layerIds.length > 0)
      .map(([name, layerIds]) => ({
        name,
        layers: layerIds.filter(id => filteredLayers.includes(id))
      }))
      .filter(group => group.layers.length > 0);
  }

  showCreateLayerDialog(): void {
    this.closeCreateLayerDialog();
    const dialogId = `create-layer-dialog-${Date.now()}`;
    const nameId = `createLayerName-${dialogId}`;
    const groupId = `createLayerGroup-${dialogId}`;
    const paletteId = `createLayerPalette-${dialogId}`;
    const saveId = `createLayerSave-${dialogId}`;
    const cancelId = `createLayerCancel-${dialogId}`;
    const groupOptions = Object.keys(this.layerGroups)
      .map(g => `<option value="${g}">${g}</option>`)
      .join('');
    const paletteOptions = PREDEFINED_LAYER_PALETTES
      .map(p => `<option value="${p.id}">${p.name}</option>`)
      .join('');
    const dialog = document.createElement('div');
    dialog.className = 'layer-properties-dialog create-layer-dialog';
    dialog.id = dialogId;
    dialog.innerHTML = `
      <div class="lp-card">
        <div class="lp-header">
          <div class="lp-header-text">
            <h3 class="lp-title">Create New Layer</h3>
            <span class="lp-layer-badge">Name the layer and pick a palette</span>
          </div>
          <button type="button" id="${cancelId}-x" class="lp-close" aria-label="Close">&times;</button>
        </div>
        <div class="lp-body">
          <div class="lp-form-group">
            <label for="${nameId}">Layer name</label>
            <input type="text" id="${nameId}" class="lp-input" placeholder="e.g. drawing, road, water" maxlength="64" autocomplete="off">
          </div>
          <div class="lp-form-group">
            <label for="${paletteId}">Theming palette</label>
            <select id="${paletteId}" class="lp-select" title="Predefined colors for point, text, line, segment, sub-segment, section">
              ${paletteOptions}
            </select>
          </div>
          <div class="lp-form-group">
            <label for="${groupId}">Category (optional)</label>
            <select id="${groupId}" class="lp-select">
              <option value="">No category</option>
              ${groupOptions}
              <option value="__new__">+ New category...</option>
            </select>
          </div>
          <div class="lp-form-group" id="newGroupWrap-${dialogId}" style="display:none;">
            <label for="newGroupName-${dialogId}">New category name</label>
            <input type="text" id="newGroupName-${dialogId}" class="lp-input" placeholder="e.g. Infrastructure">
          </div>
        </div>
        <div class="lp-footer">
          <div class="lp-footer-actions">
            <button type="button" id="${cancelId}" class="lp-btn lp-btn-secondary">Cancel</button>
            <button type="button" id="${saveId}" class="lp-btn lp-btn-primary">Create Layer</button>
          </div>
        </div>
      </div>
    `;
    (this as any).currentCreateLayerDialog = dialog;
    document.body.appendChild(dialog);
    setTimeout(() => (document.getElementById(nameId) as HTMLInputElement | null)?.focus(), 0);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) this.closeCreateLayerDialog(); });
    document.getElementById(groupId)?.addEventListener('change', () => {
      const wrap = document.getElementById(`newGroupWrap-${dialogId}`);
      if (wrap) wrap.style.display = (document.getElementById(groupId) as HTMLSelectElement)?.value === '__new__' ? 'block' : 'none';
    });
    document.getElementById(saveId)?.addEventListener('click', () => {
      const name = (document.getElementById(nameId) as HTMLInputElement)?.value?.trim();
      if (!name) {
        alert('Please enter a layer name.');
        return;
      }
      if (this.layerRegistry[name]) {
        alert(`Layer "${name}" already exists. Choose another name.`);
        return;
      }
      let group = (document.getElementById(groupId) as HTMLSelectElement)?.value || '';
      if (group === '__new__') {
        group = (document.getElementById(`newGroupName-${dialogId}`) as HTMLInputElement)?.value?.trim() || '';
      }
      const selectedPaletteId = (document.getElementById(paletteId) as HTMLSelectElement)?.value || 'default';
      this.createNewLayer(name, 'vector', group, selectedPaletteId);
      this.closeCreateLayerDialog();
    });
    document.getElementById(cancelId)?.addEventListener('click', () => this.closeCreateLayerDialog());
    document.getElementById(`${cancelId}-x`)?.addEventListener('click', () => this.closeCreateLayerDialog());
  }

  closeCreateLayerDialog(): void {
    const existing = (this as any).currentCreateLayerDialog || document.querySelector('.create-layer-dialog');
    if (existing?.parentNode) {
      existing.parentNode.removeChild(existing);
      (this as any).currentCreateLayerDialog = null;
    }
  }

  showImportLayerDialogWrapper(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json,.kml,.gpx';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const content = e.target.result;
          const layerName = prompt('Enter layer name:', file.name.replace(/\.[^/.]+$/, ''));
          if (layerName && layerName.trim()) {
            this.importLayer(layerName.trim(), content);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  exportLayerToFile(layerId: string | null): void {
    if (!layerId) return;
    const geoJson = this.exportLayer(layerId);
    if (geoJson) {
      const blob = new Blob([geoJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${layerId}.geojson`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  toggleGroupExpanded(groupName: string): void {
    this.expandedGroups[groupName] = !(this.expandedGroups[groupName] || false);
  }

  isGroupExpanded(groupName: string): boolean {
    return this.expandedGroups[groupName] !== false; // Default to expanded
  }

  showLayerContextMenu(event: MouseEvent, layerId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.layerContextMenu = {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      layerId: layerId
    };
    this.selectedLayerId = layerId;
  }

  hideLayerContextMenu(): void {
    this.layerContextMenu.visible = false;
  }

  moveLayerUp(layerId: string): void {
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex > 0) {
      this.reorderLayer(layerId, currentIndex - 1);
    }
  }

  moveLayerDown(layerId: string): void {
    const currentIndex = this.layerOrder.indexOf(layerId);
    if (currentIndex < this.layerOrder.length - 1) {
      this.reorderLayer(layerId, currentIndex + 1);
    }
  }

  moveLayerToTop(layerId: string): void {
    this.reorderLayer(layerId, this.layerOrder.length - 1);
  }

  moveLayerToBottom(layerId: string): void {
    this.reorderLayer(layerId, 0);
  }

  zoomToLayer(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      const extent = source.getExtent();
      if (extent && extent[0] !== Infinity) {
        this.map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 500 });
      }
    }
  }

  clearLayer(layerId: string): void {
    const layer = this.layerRegistry[layerId];
    if (layer && layer.getSource() instanceof VectorSource) {
      const source = layer.getSource() as VectorSource;
      source.clear();
      this.updateLayerStatistics(layerId);
      this.updateLayerManagementUI();
    }
  }

  // Setup zoom change listener to handle scale-based visibility
  private setupScaleVisibilityListeners(): void {
    const view = this.map.getView();
    view.on('change:resolution', () => {
      this.layerOrder.forEach(layerId => {
        if (this.layerMinZoom[layerId] !== undefined || this.layerMaxZoom[layerId] !== undefined) {
          this.updateLayerScaleVisibility(layerId);
        }
      });
    });
  }

  private getLayerGroup(layerId: string): string | null {
    for (const [groupName, layerIds] of Object.entries(this.layerGroups)) {
      if (layerIds.includes(layerId)) {
        return groupName;
      }
    }
    return null;
  }

  getAllLayersInfo(): any[] {
    return this.layerOrder.map(layerId => this.getLayerInfo(layerId));
  }

  updateLayerManagementUI(): void {
    // Update the layer management panel UI
    this.renderLayerManagementPanel();
  }

  private renderLayerManagementPanel(): void {
    const groupsContainer = document.getElementById('layerGroups');
    const ungroupedContainer = document.getElementById('layerList');
    const noLayersFound = document.getElementById('noLayersFound');
    
    if (!groupsContainer || !ungroupedContainer) return;

    // Get all layer IDs that are in groups
    const groupedLayerIds = new Set(Object.values(this.layerGroups).flat());
    
    // Get ungrouped layers
    const ungroupedLayerIds = this.layerOrder.filter(id => !groupedLayerIds.has(id));

    // Render groups
    groupsContainer.innerHTML = Object.entries(this.layerGroups)
      .filter(([_, layerIds]) => layerIds.length > 0)
      .map(([groupName, layerIds]) => `
        <div class="layer-group">
          <div class="group-header">
            <div class="group-title">
              <h4>${groupName}</h4>
              <span class="group-count">${layerIds.length}</span>
            </div>
            <button class="group-toggle" data-group="${groupName}">▼</button>
          </div>
          <div class="group-layers" data-group="${groupName}">
            ${layerIds.map(layerId => this.renderLayerItem(layerId)).join('')}
          </div>
        </div>
      `).join('');

    // Render ungrouped layers
    if (ungroupedLayerIds.length > 0) {
      ungroupedContainer.innerHTML = ungroupedLayerIds.map(layerId => this.renderLayerItem(layerId)).join('');
      if (noLayersFound) noLayersFound.style.display = 'none';
    } else {
      ungroupedContainer.innerHTML = '';
      if (noLayersFound && this.layerOrder.length === 0) {
        noLayersFound.style.display = 'block';
      } else if (noLayersFound) {
        noLayersFound.style.display = 'none';
      }
    }

    this.setupLayerManagementEvents();
  }

  private renderLayerItem(layerId: string): string {
    const info = this.getLayerInfo(layerId);
    const layer = this.layerRegistry[layerId];
    
    return `
      <div class="layer-item" data-layer="${layerId}">
        <div class="layer-header">
          <div class="layer-controls">
            <input type="checkbox" ${info.visible ? 'checked' : ''} 
                   class="layer-visibility" data-layer="${layerId}">
            <span class="layer-name">${layerId}</span>
            <span class="layer-count">(${info.featureCount})</span>
          </div>
          <div class="layer-actions">
            <button class="layer-edit" data-layer="${layerId}" title="Edit Properties">⚙️</button>
            <button class="layer-duplicate" data-layer="${layerId}" title="Duplicate">📋</button>
            <button class="layer-delete" data-layer="${layerId}" title="Delete">🗑️</button>
          </div>
        </div>
        <div class="layer-details">
          <div class="opacity-control">
            <label>Opacity:</label>
            <input type="range" min="0" max="100" value="${Math.round(info.opacity * 100)}" 
                   class="layer-opacity" data-layer="${layerId}">
            <span class="opacity-value">${Math.round(info.opacity * 100)}%</span>
          </div>
          <div class="layer-stats">
            <small>Features: ${info.featureCount} | Z-Index: ${info.zIndex}</small>
          </div>
        </div>
      </div>
    `;
  }

  private setupLayerManagementEvents(): void {
    // Layer visibility toggles
    document.querySelectorAll('.layer-visibility').forEach(toggle => {
      toggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        const layerId = target.getAttribute('data-layer')!;
        this.toggleLayerEnhanced(layerId, target.checked);
      });
    });

    // Layer opacity controls
    document.querySelectorAll('.layer-opacity').forEach(slider => {
      slider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const layerId = target.getAttribute('data-layer')!;
        const opacity = parseFloat(target.value) / 100;
        this.setLayerOpacityEnhanced(layerId, opacity);
        
        // Update display
        const valueSpan = target.parentElement?.querySelector('.opacity-value');
        if (valueSpan) {
          valueSpan.textContent = `${Math.round(opacity * 100)}%`;
        }
      });
    });

    // Layer action buttons
    document.querySelectorAll('.layer-edit').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const layerId = target.closest('[data-layer]')?.getAttribute('data-layer') || 
                       target.getAttribute('data-layer')!;
        this.editLayerProperties(layerId);
      });
    });

    document.querySelectorAll('.layer-duplicate').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const layerId = target.closest('[data-layer]')?.getAttribute('data-layer') || 
                       target.getAttribute('data-layer')!;
        const newLayerId = `${layerId}_copy_${Date.now()}`;
        this.duplicateLayer(layerId, newLayerId);
      });
    });

    document.querySelectorAll('.layer-delete').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const layerId = target.closest('[data-layer]')?.getAttribute('data-layer') || 
                       target.getAttribute('data-layer')!;
        if (confirm(`Delete layer "${layerId}"?`)) {
          this.deleteLayer(layerId);
        }
      });
    });

    // Group toggles
    document.querySelectorAll('.group-toggle').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const groupName = target.getAttribute('data-group')!;
        const groupLayers = document.querySelector(`.group-layers[data-group="${groupName}"]`);
        if (groupLayers) {
          groupLayers.classList.toggle('collapsed');
          target.textContent = groupLayers.classList.contains('collapsed') ? '▶' : '▼';
        }
      });
    });
  }

  private filterLayersBySearch(searchTerm: string): void {
    const layerItems = document.querySelectorAll('.layer-item');
    layerItems.forEach(item => {
      const layerName = item.getAttribute('data-layer') || '';
      const layerNameElement = item.querySelector('.layer-name');
      const displayName = layerNameElement?.textContent?.toLowerCase() || '';
      
      if (searchTerm === '' || layerName.toLowerCase().includes(searchTerm) || displayName.includes(searchTerm)) {
        (item as HTMLElement).style.display = '';
      } else {
        (item as HTMLElement).style.display = 'none';
      }
    });

    // Also filter groups
    const groups = document.querySelectorAll('.layer-group');
    groups.forEach(group => {
      const visibleLayers = group.querySelectorAll('.layer-item:not([style*="display: none"])');
      if (visibleLayers.length === 0 && searchTerm !== '') {
        (group as HTMLElement).style.display = 'none';
      } else {
        (group as HTMLElement).style.display = '';
      }
    });
  }

  showCreateGroupDialog(): void {
    const groupName = prompt('Enter group name:');
    if (groupName && groupName.trim()) {
      // Get selected layers (could be enhanced with checkboxes)
      const selectedLayers = this.layerOrder.filter(id => {
        const item = document.querySelector(`[data-layer="${id}"]`);
        return item && (item as HTMLElement).style.display !== 'none';
      });
      
      if (selectedLayers.length > 0) {
        this.createLayerGroup(groupName.trim(), selectedLayers);
      } else {
        this.createLayerGroup(groupName.trim(), []);
      }
    }
  }

  private showImportLayerDialog(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json,.kml,.gpx';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const content = e.target.result;
          const layerName = prompt('Enter layer name:', file.name.replace(/\.[^/.]+$/, ''));
          if (layerName && layerName.trim()) {
            this.importLayer(layerName.trim(), content);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  exportAllLayers(): void {
    const allLayersData: any = {
      layers: this.layerOrder.map(layerId => ({
        id: layerId,
        data: this.exportLayer(layerId),
        properties: this.getLayerInfo(layerId)
      })),
      groups: this.layerGroups,
      order: this.layerOrder
    };

    const blob = new Blob([JSON.stringify(allLayersData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_layers_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  editLayerProperties(layerId: string): void {
    // Close any existing dialog first
    this.closeLayerPropertiesDialog();
    
    const info = this.getLayerInfo(layerId);
    const layer = this.layerRegistry[layerId];
    
    // Create unique IDs for this dialog instance
    const dialogId = `layer-props-dialog-${Date.now()}`;
    const layerNameId = `layerName-${dialogId}`;
    const layerZIndexId = `layerZIndex-${dialogId}`;
    const layerOpacityId = `layerOpacity-${dialogId}`;
    const opacityValueId = `opacityValue-${dialogId}`;
    const layerVisibleId = `layerVisible-${dialogId}`;
    const layerGroupId = `layerGroup-${dialogId}`;
    const layerFilterId = `layerFilter-${dialogId}`;
    const saveLayerPropsId = `saveLayerProps-${dialogId}`;
    const cancelLayerPropsId = `cancelLayerProps-${dialogId}`;
    const exportLayerId = `exportLayer-${dialogId}`;
    const strokeColorId = `layerStrokeColor-${dialogId}`;
    const strokeWidthId = `layerStrokeWidth-${dialogId}`;
    const fillColorId = `layerFillColor-${dialogId}`;
    const paletteSelectId = `layerPalette-${dialogId}`;
    const theme = this.layerStyles[layerId] || {};
    const strokeColor = theme.strokeColor || '#3498db';
    const strokeWidth = theme.strokeWidth ?? 2;
    const fillColor = theme.fillColor || 'rgba(52, 152, 219, 0.2)';
    const currentPaletteId = this.layerPaletteId[layerId] || theme.paletteId || 'default';
    const paletteOptionsHtml = PREDEFINED_LAYER_PALETTES
      .map(p => `<option value="${p.id}" ${p.id === currentPaletteId ? 'selected' : ''}>${this.escapeHtml(p.name)}</option>`)
      .join('');
    
    const closeBtnId = `lp-close-${dialogId}`;
    const dialog = document.createElement('div');
    dialog.className = 'layer-properties-dialog';
    dialog.id = dialogId;
    dialog.innerHTML = `
      <div class="lp-card">
        <header class="lp-header">
          <div class="lp-header-text">
            <h2 class="lp-title">Layer properties</h2>
            <span class="lp-layer-badge">${this.escapeHtml(layerId)}</span>
          </div>
          <button type="button" id="${closeBtnId}" class="lp-close" aria-label="Close">&times;</button>
        </header>
        <div class="lp-body">
          <section class="lp-section">
            <h3 class="lp-section-title">General</h3>
            <div class="lp-form-group">
              <label for="${layerNameId}">Layer name</label>
              <input type="text" id="${layerNameId}" value="${this.escapeHtml(layerId)}" class="lp-input">
            </div>
            <div class="lp-form-group">
              <label for="${layerGroupId}">Category</label>
              <select id="${layerGroupId}" class="lp-select">
                <option value="">No category</option>
                ${Object.keys(this.layerGroups).map(group =>
                  `<option value="${group}" ${this.getLayerGroup(layerId) === group ? 'selected' : ''}>${this.escapeHtml(group)}</option>`
                ).join('')}
              </select>
            </div>
            <div class="lp-form-group">
              <label for="${layerFilterId}">Filter expression</label>
              <input type="text" id="${layerFilterId}" placeholder="e.g. type=road" class="lp-input">
            </div>
          </section>
          <section class="lp-section">
            <h3 class="lp-section-title">Display</h3>
            <div class="lp-row lp-row-2">
              <div class="lp-form-group">
                <label for="${layerOpacityId}">Opacity</label>
                <div class="lp-range-wrap">
                  <input type="range" min="0" max="100" id="${layerOpacityId}" value="${Math.round(info.opacity * 100)}" class="lp-range">
                  <span id="${opacityValueId}" class="lp-range-value">${Math.round(info.opacity * 100)}%</span>
                </div>
              </div>
              <div class="lp-form-group">
                <label for="${layerZIndexId}">Z-Index</label>
                <input type="number" id="${layerZIndexId}" value="${info.zIndex}" class="lp-input lp-input-narrow">
              </div>
            </div>
            <div class="lp-form-group lp-form-group-inline">
              <input type="checkbox" id="${layerVisibleId}" ${info.visible ? 'checked' : ''} class="lp-checkbox">
              <label for="${layerVisibleId}" class="lp-checkbox-label">Visible on map</label>
            </div>
          </section>
          <section class="lp-section lp-section-theme">
            <h3 class="lp-section-title">Style</h3>
            <div class="lp-form-group">
              <label for="${paletteSelectId}">Theming palette</label>
              <select id="${paletteSelectId}" class="lp-select" title="Predefined colors for point, text, line, segment, sub-segment, section">
                ${paletteOptionsHtml}
              </select>
            </div>
            <div class="lp-form-group newton-theme-row">
              <label>Stroke color</label>
              <div id="${dialogId}-stroke-wheel" class="newton-wheel-container"></div>
              <input type="color" id="${strokeColorId}" value="${strokeColor}" class="newton-color-input-hidden" title="Stroke color">
            </div>
            <div class="lp-form-group">
              <label for="${strokeWidthId}">Stroke width</label>
              <input type="number" id="${strokeWidthId}" min="1" max="24" value="${strokeWidth}" class="lp-input lp-input-narrow">
            </div>
            <div class="lp-form-group newton-theme-row">
              <label>Fill color</label>
              <div id="${dialogId}-fill-wheel" class="newton-wheel-container"></div>
              <input type="color" id="${fillColorId}" value="${this.hexFromRgba(fillColor)}" class="newton-color-input-hidden" title="Fill color">
            </div>
          </section>
        </div>
        <footer class="lp-footer">
          <button type="button" id="${exportLayerId}" class="lp-btn lp-btn-secondary">Export</button>
          <div class="lp-footer-actions">
            <button type="button" id="${cancelLayerPropsId}" class="lp-btn lp-btn-secondary">Cancel</button>
            <button type="button" id="${saveLayerPropsId}" class="lp-btn lp-btn-primary">Save</button>
          </div>
        </footer>
      </div>
    `;
    
    // Store dialog reference
    (this as any).currentLayerPropertiesDialog = dialog;
    
    document.body.appendChild(dialog);
    
    const strokeWheelEl = document.getElementById(`${dialogId}-stroke-wheel`);
    const fillWheelEl = document.getElementById(`${dialogId}-fill-wheel`);
    if (strokeWheelEl) this.createNewtonColorWheel(strokeWheelEl, strokeColorId, strokeColor);
    if (fillWheelEl) this.createNewtonColorWheel(fillWheelEl, fillColorId, this.hexFromRgba(fillColor));
    
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.closeLayerPropertiesDialog();
    });
    document.getElementById(closeBtnId)?.addEventListener('click', () => this.closeLayerPropertiesDialog());
    
    document.getElementById(layerOpacityId)?.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const value = document.getElementById(opacityValueId);
      if (value) value.textContent = `${target.value}%`;
    });
    
    document.getElementById(saveLayerPropsId)?.addEventListener('click', () => {
      this.saveLayerProperties(layerId, dialog, {
        layerNameId,
        layerZIndexId,
        layerOpacityId,
        layerVisibleId,
        layerGroupId,
        layerFilterId,
        strokeColorId,
        strokeWidthId,
        fillColorId,
        paletteSelectId
      });
    });
    
    document.getElementById(cancelLayerPropsId)?.addEventListener('click', () => {
      this.closeLayerPropertiesDialog();
    });
    
    document.getElementById(exportLayerId)?.addEventListener('click', () => {
      if (layerId) {
        const geoJson = this.exportLayer(layerId);
        if (geoJson) {
          const blob = new Blob([geoJson], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${layerId}.geojson`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }
    });
  }
  
  closeLayerPropertiesDialog(): void {
    const existingDialog = (this as any).currentLayerPropertiesDialog || 
                          document.querySelector('.layer-properties-dialog');
    if (existingDialog && existingDialog.parentNode) {
      existingDialog.parentNode.removeChild(existingDialog);
      (this as any).currentLayerPropertiesDialog = null;
    }
  }

  private saveLayerProperties(layerId: string, dialog: HTMLElement, ids?: {
    layerNameId: string;
    layerZIndexId: string;
    layerOpacityId: string;
    layerVisibleId: string;
    layerGroupId: string;
    layerFilterId: string;
    strokeColorId?: string;
    strokeWidthId?: string;
    fillColorId?: string;
    paletteSelectId?: string;
  }): void {
    const layerNameId = ids?.layerNameId || 'layerName';
    const layerZIndexId = ids?.layerZIndexId || 'layerZIndex';
    const layerOpacityId = ids?.layerOpacityId || 'layerOpacity';
    const layerVisibleId = ids?.layerVisibleId || 'layerVisible';
    const layerGroupId = ids?.layerGroupId || 'layerGroup';
    const layerFilterId = ids?.layerFilterId || 'layerFilter';
    
    const newName = (document.getElementById(layerNameId) as HTMLInputElement)?.value?.trim();
    const zIndex = parseInt((document.getElementById(layerZIndexId) as HTMLInputElement)?.value || '0');
    const opacity = parseFloat((document.getElementById(layerOpacityId) as HTMLInputElement)?.value || '100') / 100;
    const visible = (document.getElementById(layerVisibleId) as HTMLInputElement)?.checked || false;
    const group = (document.getElementById(layerGroupId) as HTMLSelectElement)?.value;
    const filter = (document.getElementById(layerFilterId) as HTMLInputElement)?.value;
    
    const layer = this.layerRegistry[layerId];
    if (layer) {
      layer.setZIndex(zIndex);
      layer.setOpacity(opacity);
      layer.setVisible(visible);
    }
    
    if (group) {
      Object.values(this.layerGroups).forEach(groupLayers => {
        const index = groupLayers.indexOf(layerId);
        if (index > -1) groupLayers.splice(index, 1);
      });
      if (!this.layerGroups[group]) this.layerGroups[group] = [];
      this.layerGroups[group].push(layerId);
    }
    
    if (filter) {
      const filterObj = this.parseFilterExpression(filter);
      this.filterLayer(layerId, filterObj);
    } else {
      this.clearLayerFilter(layerId);
    }
    
    if (ids?.strokeColorId && ids?.strokeWidthId && ids?.fillColorId) {
      const strokeColor = (document.getElementById(ids.strokeColorId) as HTMLInputElement)?.value || '#3498db';
      const strokeWidth = parseInt((document.getElementById(ids.strokeWidthId) as HTMLInputElement)?.value || '2', 10);
      const fillColor = (document.getElementById(ids.fillColorId) as HTMLInputElement)?.value || 'rgba(52, 152, 219, 0.2)';
      this.layerStyles[layerId] = { strokeColor, strokeWidth, fillColor: this.rgbaFromHex(fillColor) };
      this.applyLayerTheme(layerId);
    }
    
    if (ids?.paletteSelectId) {
      const paletteId = (document.getElementById(ids.paletteSelectId) as HTMLSelectElement)?.value || 'default';
      this.setLayerPalette(layerId, paletteId);
    }
    
    this.updateLayerStatistics(layerId);
    this.updateLayerManagementUI();
    this.closeLayerPropertiesDialog();
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private hexFromRgba(rgba: string): string {
    if (rgba.startsWith('#')) return rgba;
    const m = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (m) return '#' + [m[1], m[2], m[3]].map(x => parseInt(x, 10).toString(16).padStart(2, '0')).join('');
    return '#3498db';
  }

  private rgbaFromHex(hex: string): string {
    if (hex.startsWith('rgba')) return hex;
    if (hex.length === 7 && hex.startsWith('#')) {
      const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r},${g},${b},0.2)`;
    }
    return hex;
  }

  /** HSL (0-360, 0-100, 0-100) to hex. */
  private hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
      return Math.round(255 * c).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  /** Hex to HSL. Returns { h: 0-360, s: 0-100, l: 0-100 }. */
  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    if (!hex || !hex.startsWith('#')) return { h: 210, s: 100, l: 50 };
    const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  /**
   * Creates a Newton-style color wheel (hue ring + lightness slider) and appends to parent.
   * Updates the given color input (hex) on pick.
   */
  private createNewtonColorWheel(parent: HTMLElement, targetInputId: string, initialHex: string): void {
    const size = 150;
    const cx = size / 2;
    const cy = size / 2;
    const innerR = 42;
    const outerR = 68;
    const hsl = this.hexToHsl(initialHex);
    let hue = hsl.h;
    let lightness = hsl.l;

    const wrap = document.createElement('div');
    wrap.className = 'newton-wheel-wrap';
    wrap.innerHTML = `
      <canvas class="newton-wheel-canvas" width="${size}" height="${size}" style="display:block; border-radius:50%; cursor:crosshair;"></canvas>
      <div class="newton-wheel-lightness">
        <label>Lightness</label>
        <input type="range" class="newton-lightness-slider" min="0" max="100" value="${Math.round(lightness)}">
        <span class="newton-lightness-value">${Math.round(lightness)}%</span>
      </div>
    `;
    parent.appendChild(wrap);

    const canvas = wrap.querySelector('.newton-wheel-canvas') as HTMLCanvasElement;
    const slider = wrap.querySelector('.newton-lightness-slider') as HTMLInputElement;
    const valueSpan = wrap.querySelector('.newton-lightness-value') as HTMLElement;
    const targetInput = document.getElementById(targetInputId) as HTMLInputElement;

    const syncToInput = () => {
      const hex = this.hslToHex(hue, 100, lightness);
      if (targetInput) {
        targetInput.value = hex;
        targetInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };

    const drawWheel = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size, size);
      for (let i = 0; i < 360; i++) {
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, outerR, (i - 90) * Math.PI / 180, (i - 89) * Math.PI / 180);
        ctx.arc(cx, cy, innerR, (i - 89) * Math.PI / 180, (i - 90) * Math.PI / 180, true);
        ctx.closePath();
        ctx.fillStyle = `hsl(${i}, 100%, 50%)`;
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(cx, cy, innerR - 2, 0, Math.PI * 2);
      ctx.fillStyle = '#f0f0f0';
      ctx.fill();
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.stroke();
      const markerAngle = (hue - 90) * Math.PI / 180;
      const mr = (innerR + outerR) / 2;
      const mx = cx + mr * Math.cos(markerAngle);
      const my = cy + mr * Math.sin(markerAngle);
      ctx.beginPath();
      ctx.arc(mx, my, 6, 0, Math.PI * 2);
      ctx.fillStyle = this.hslToHex(hue, 100, lightness);
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    };

    const getAngle = (e: MouseEvent): number => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left - (rect.width / 2);
      const y = e.clientY - rect.top - (rect.height / 2);
      let angle = Math.atan2(y, x) * 180 / Math.PI + 90;
      if (angle < 0) angle += 360;
      return angle % 360;
    };

    const onWheelInteraction = (e: MouseEvent) => {
      e.preventDefault();
      hue = getAngle(e);
      drawWheel();
      syncToInput();
    };

    canvas.addEventListener('mousedown', (e) => {
      onWheelInteraction(e);
      const move = (e2: MouseEvent) => { onWheelInteraction(e2); };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });

    slider.addEventListener('input', () => {
      lightness = parseInt(slider.value, 10);
      if (valueSpan) valueSpan.textContent = slider.value + '%';
      drawWheel();
      syncToInput();
    });

    drawWheel();
    syncToInput();
  }

  private parseFilterExpression(filter: string): any {
    // Simple filter parser - can be enhanced for complex expressions
    const parts = filter.split('=');
    if (parts.length === 2) {
      return { [parts[0].trim()]: parts[1].trim() };
    }
    return {};
  }

  private setupMapEventListeners(): void {
    // Pointer move event for coordinates display
    this.map.on('pointermove', (event) => {
      const coordinate = toLonLat(event.coordinate);
      const zoom = this.map.getView().getZoom()!;
      const scale = Math.round(591657527.591555 / Math.pow(2, zoom));
      
      const coordinatesElement = document.getElementById('coordinates');
      if (coordinatesElement) {
        coordinatesElement.innerHTML = 
          `Lat: ${coordinate[1].toFixed(6)}, Lon: ${coordinate[0].toFixed(6)} | Scale: 1:${scale} | Zoom: ${Math.round(zoom)}`;
      }

      // Popup on hover
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => feature);
      if (feature) {
        // Bridge-managed asset-management features get a rich, structured popup.
        const amHtml = this.amBuildHoverPopup(feature as Feature<Geometry>);
        if (amHtml) {
          this.popup.getElement()!.innerHTML = amHtml;
        } else {
          const info = feature.get('info') || 'No info';
          const comment = feature.get('comment') || '';
          this.popup.getElement()!.innerHTML = `${info}<br>${comment}`;
        }
        this.popup.setPosition(event.coordinate);
        this.popup.getElement()!.style.display = 'block';
      } else {
        this.popup.getElement()!.style.display = 'none';
      }
    });

    // Click event to populate coordinate inputs
    this.map.on('click', (event) => {
      const coordinate = toLonLat(event.coordinate);
      const latitude = coordinate[1];
      const longitude = coordinate[0];
      
      // Update coordinate input fields
      const latInput = document.getElementById('latitudeInput') as HTMLInputElement;
      const lonInput = document.getElementById('longitudeInput') as HTMLInputElement;
      
      if (latInput && lonInput) {
        latInput.value = latitude.toFixed(6);
        lonInput.value = longitude.toFixed(6);
      }
    });
  }

  private setupEventListeners(): void {
    // Tab switching is attached in ensureSidebarTabsReady() (ngAfterViewInit) so DOM is ready

    // Drawing mode change
    const drawingModeSelect = document.getElementById('drawingMode');
    if (drawingModeSelect) {
      drawingModeSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.handleDrawingModeChange(target.value);
      });
    }

    // Coordinate input event listeners
    this.setupCoordinateInputListeners();

    // Enhanced feature event listeners
    this.setupEnhancedFeatureListeners();

    // Asset type and nomenclature controls
    this.setupAssetControls();

    // Symbol selection setup
    this.setupSymbolSelection();

    // Add other event listeners here...
    this.setupButtonEventListeners();
    this.setupControlEventListeners();
    
    // Setup Analysis tab listeners
    this.setupAnalysisTabListeners();
    
    // Setup Data tab listeners
    this.setupDataTabListeners();
    
    // Setup additional toolbar listeners
    this.setupAdditionalToolbarListeners();

    // NHAI header toolbar (Search, Filter, Export, Print, Reports)
    this.setupNhaiToolbarControls();
  }

  private setupControlEventListeners(): void {
    // Line thickness and symbol size are handled via Angular (input) in template (onLineThicknessChange, onSymbolSizeChange)

    // Asset type checkboxes
    const showNomenclature = document.getElementById('showNomenclature');
    if (showNomenclature) {
      showNomenclature.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showNomenclature = target.checked;
        this.updateLayerVisibility();
      });
    }

    const showOverground = document.getElementById('showOverground');
    if (showOverground) {
      showOverground.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showOverground = target.checked;
        this.updateLayerVisibility();
      });
    }

    const showUnderground = document.getElementById('showUnderground');
    if (showUnderground) {
      showUnderground.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showUnderground = target.checked;
        this.updateLayerVisibility();
      });
    }

    const showCrossover = document.getElementById('showCrossover');
    if (showCrossover) {
      showCrossover.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showCrossover = target.checked;
        this.updateLayerVisibility();
      });
    }
  }

  private updateLayerVisibility(): void {
    // Update layer visibility based on checkboxes
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source === this.vectorSource) {
          // Update drawing layer visibility
          layer.setVisible(this.showNomenclature);
        }
      }
    });
  }

  private setupCoordinateInputListeners(): void {
    // Pinpoint location button
    const pinpointBtn = document.getElementById('pinpointLocation');
    if (pinpointBtn) {
      pinpointBtn.addEventListener('click', () => {
        this.pinpointLocation();
      });
    }

    // Add marker at location button
    const addMarkerBtn = document.getElementById('addMarkerAtLocation');
    if (addMarkerBtn) {
      addMarkerBtn.addEventListener('click', () => {
        this.addMarkerAtLocation();
      });
    }

    // Update current coordinates display when map view changes
    this.map.on('moveend', () => {
      this.updateCurrentCoordinatesDisplay();
    });
  }

  private pinpointLocation(): void {
    const latInput = document.getElementById('latitudeInput') as HTMLInputElement;
    const lonInput = document.getElementById('longitudeInput') as HTMLInputElement;
    
    if (!latInput || !lonInput) {
      this.showInstructions('Coordinate input fields not found');
      return;
    }

    const latitude = parseFloat(latInput.value);
    const longitude = parseFloat(lonInput.value);

    // Validate coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      this.showInstructions('Please enter valid latitude and longitude values');
      latInput.focus();
      return;
    }

    if (latitude < -90 || latitude > 90) {
      this.showInstructions('Latitude must be between -90 and 90 degrees');
      latInput.focus();
      return;
    }

    if (longitude < -180 || longitude > 180) {
      this.showInstructions('Longitude must be between -180 and 180 degrees');
      lonInput.focus();
      return;
    }

    try {
      // Convert to map coordinates (from WGS84 to Web Mercator)
      const coordinate = fromLonLat([longitude, latitude]);
      
      // Update current coordinates
      this.currentLatitude = latitude;
      this.currentLongitude = longitude;

      // Center map on the location with smooth animation
      this.map.getView().animate({
        center: coordinate,
        zoom: 15,
        duration: 1000
      });

      // Update display
      this.updateCurrentCoordinatesDisplay();

      // Clear any existing pinpoint marker
      this.clearPinpointMarker();

      // Add a temporary pinpoint marker
      this.addPinpointMarker(coordinate);

      // Show success message
      this.showInstructions(`📍 Navigated to ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      
      // Hide message after 3 seconds
      setTimeout(() => {
        this.hideInstructions();
      }, 3000);

    } catch (error) {
      console.error('Error navigating to location:', error);
      this.showInstructions('Error navigating to location. Please check coordinates.');
    }
  }

  private addMarkerAtLocation(): void {
    const latInput = document.getElementById('latitudeInput') as HTMLInputElement;
    const lonInput = document.getElementById('longitudeInput') as HTMLInputElement;
    
    if (!latInput || !lonInput) {
      this.showInstructions('Coordinate input fields not found');
      return;
    }

    const latitude = parseFloat(latInput.value);
    const longitude = parseFloat(lonInput.value);

    // Validate coordinates
    if (isNaN(latitude) || isNaN(longitude)) {
      this.showInstructions('Please enter valid latitude and longitude values');
      latInput.focus();
      return;
    }

    if (latitude < -90 || latitude > 90) {
      this.showInstructions('Latitude must be between -90 and 90 degrees');
      latInput.focus();
      return;
    }

    if (longitude < -180 || longitude > 180) {
      this.showInstructions('Longitude must be between -180 and 180 degrees');
      lonInput.focus();
      return;
    }

    try {
      // Convert to map coordinates (from WGS84 to Web Mercator)
      const coordinate = fromLonLat([longitude, latitude]);
      
      // Create a permanent marker
      this.createPermanentMarker(coordinate, `📍 Marker at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);

      // Center map on the marker location
      this.map.getView().animate({
        center: coordinate,
        zoom: Math.max(this.map.getView().getZoom() || 10, 12),
        duration: 500
      });

      // Show success message
      this.showInstructions(`📍 Marker added at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
      
      // Hide message after 3 seconds
      setTimeout(() => {
        this.hideInstructions();
      }, 3000);

    } catch (error) {
      console.error('Error adding marker:', error);
      this.showInstructions('Error adding marker. Please check coordinates.');
    }
  }

  private addPinpointMarker(coordinate: number[]): void {
    const feature = new Feature({
      geometry: new Point(coordinate),
      type: 'pinpoint',
      timestamp: new Date().toISOString()
    });

    const style = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#e74c3c" stroke="#fff" stroke-width="2"/>
            <circle cx="12" cy="12" r="3" fill="#fff"/>
            <text x="12" y="8" text-anchor="middle" font-size="8" fill="#fff" font-weight="bold">📍</text>
          </svg>
        `),
        scale: 1.5,
        anchor: [0.5, 0.5]
      }),
      text: new Text({
        text: 'PINPOINT',
        font: '10px Arial',
        fill: new Fill({ color: '#e74c3c' }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
        offsetY: -25
      })
    });

    feature.setStyle(style);
    this.pinpointMarker = feature;
    this.getCurrentDrawingSource().addFeature(feature);

    // Auto-clear pinpoint marker after 10 seconds
    setTimeout(() => {
      this.clearPinpointMarker();
    }, 10000);
  }

  private clearPinpointMarker(): void {
    if (this.pinpointMarker) {
      const src = this.getSourceForFeature(this.pinpointMarker);
      if (src) src.removeFeature(this.pinpointMarker);
      this.pinpointMarker = null;
    }
  }

  private createPermanentMarker(coordinate: number[], label: string): void {
    const feature = new Feature({
      geometry: new Point(coordinate),
      type: 'marker',
      label: label,
      timestamp: new Date().toISOString()
    });

    const style = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#3498db"/>
          </svg>
        `),
        scale: 1.2,
        anchor: [0.5, 1]
      }),
      text: new Text({
        text: label,
        font: '12px Arial',
        fill: new Fill({ color: '#2c3e50' }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
        offsetY: -25
      })
    });

    feature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(feature);
  }

  private updateCurrentCoordinatesDisplay(): void {
    const currentCoordsElement = document.getElementById('currentCoords');
    if (currentCoordsElement) {
      const center = this.map.getView().getCenter();
      if (center) {
        // Convert from Web Mercator back to WGS84
        const lonLat = toLonLat(center);
        const lat = lonLat[1];
        const lon = lonLat[0];
        currentCoordsElement.textContent = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
      }
    }
  }

  private setupEnhancedFeatureListeners(): void {
    // Split Segment button
    const splitSegmentBtn = document.getElementById('splitSegment');
    if (splitSegmentBtn) {
      splitSegmentBtn.addEventListener('click', () => {
        this.enableSplitSegment();
      });
    }

    // Bridge Overlay button
    const bridgeOverlayBtn = document.getElementById('bridgeOverlay');
    if (bridgeOverlayBtn) {
      bridgeOverlayBtn.addEventListener('click', () => {
        this.toggleBridgeOverlay();
      });
    }

    // Enhanced numbering system
    // Nomenclature controls
    const nomenclatureInput = document.getElementById('nomenclature');
    if (nomenclatureInput) {
      nomenclatureInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.nomenclature = target.value || '';
      });
    }

    const nomenclatureAbbreviationInput = document.getElementById('nomenclatureAbbreviation');
    if (nomenclatureAbbreviationInput) {
      nomenclatureAbbreviationInput.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.nomenclatureAbbreviation = target.value || 'Gb';
      });
    }

    const numberingPrefix = document.getElementById('numberingPrefix');
    if (numberingPrefix) {
      numberingPrefix.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.numberingPrefix = target.value;
      });
    }

    const numberingSuffix = document.getElementById('numberingSuffix');
    if (numberingSuffix) {
      numberingSuffix.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.numberingSuffix = target.value;
      });
    }

    const startNumber = document.getElementById('startNumber');
    if (startNumber) {
      startNumber.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.startNumber = parseInt(target.value) || 1;
      });
    }

    // Lane separator controls
    const dividerType = document.getElementById('dividerType');
    if (dividerType) {
      dividerType.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.dividerType = target.value;
      });
    }

    const separatorWidth = document.getElementById('separatorWidth');
    if (separatorWidth) {
      separatorWidth.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.separatorWidth = parseFloat(target.value) || 0.3;
      });
    }

    // Elevation controls
    const showElevation = document.getElementById('showElevation');
    if (showElevation) {
      showElevation.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showElevation = target.checked;
        this.updateElevationDisplay();
      });
    }

    const elevationProfile = document.getElementById('elevationProfile');
    if (elevationProfile) {
      elevationProfile.addEventListener('click', () => {
        this.showElevationProfile();
      });
    }

    // Feature properties dialog
    this.setupFeaturePropertiesDialog();

    // Enhanced layer management
    this.setupEnhancedLayerManagement();

    // Enhanced measurement tools
    this.setupEnhancedMeasurementTools();
  }

  private enableSplitSegment(): void {
    this.removeInteractions();
    this.showInstructions('Click on a segment to split it at that point');
    
    this.map.on('click', (event: any) => {
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature: FeatureLike) => {
        return feature.getGeometry()?.getType() === 'LineString' ? feature as Feature : null;
      });

      if (feature) {
        this.splitSegmentAtPoint(feature, event.coordinate);
        this.hideInstructions();
      } else {
        this.showInstructions('Please click on a line segment to split it');
      }
    });
  }

  private splitSegmentAtPoint(segment: Feature, splitPoint: number[]): void {
    const geometry = segment.getGeometry() as LineString;
    const coordinates = geometry.getCoordinates();
    
    // Find the closest point on the line
    const closestPoint = this.findClosestPointOnLine(splitPoint, coordinates);
    
    // Split the line into two segments
    const splitIndex = this.findSplitIndex(coordinates, closestPoint);
    
    if (splitIndex > 0 && splitIndex < coordinates.length - 1) {
      const firstPart = coordinates.slice(0, splitIndex + 1);
      const secondPart = coordinates.slice(splitIndex);
      
      // Create new features
      const firstSegment = new Feature({
        geometry: new LineString(firstPart),
        type: 'road',
        properties: { ...segment.getProperties(), splitFrom: true }
      });
      
      const secondSegment = new Feature({
        geometry: new LineString(secondPart),
        type: 'road',
        properties: { ...segment.getProperties(), splitFrom: true }
      });
      
      const src = this.getSourceForFeature(segment) || this.getCurrentDrawingSource();
      src.removeFeature(segment);
      src.addFeature(firstSegment);
      src.addFeature(secondSegment);
      
      this.showInstructions('Segment split successfully');
      setTimeout(() => this.hideInstructions(), 2000);
    }
  }

  private findSplitIndex(coordinates: number[][], point: number[]): number {
    let minDistance = Infinity;
    let splitIndex = 0;
    
    for (let i = 0; i < coordinates.length - 1; i++) {
      const segmentStart = coordinates[i];
      const segmentEnd = coordinates[i + 1];
      const closestPoint = this.getClosestPointOnSegment(point, segmentStart, segmentEnd);
      const distance = this.calculateDistance(point, closestPoint);
      
      if (distance < minDistance) {
        minDistance = distance;
        splitIndex = i;
      }
    }
    
    return splitIndex;
  }

  private toggleBridgeOverlay(): void {
    // Toggle bridge overlay layer
    const bridgeLayer = this.layerRegistry['bridge'];
    if (bridgeLayer) {
      bridgeLayer.setVisible(!bridgeLayer.getVisible());
    } else {
      this.createBridgeOverlayLayer();
    }
  }

  private createBridgeOverlayLayer(): void {
    const bridgeSource = new VectorSource();
    const bridgeLayer = new VectorLayer({
      source: bridgeSource,
      style: new Style({
        stroke: new Stroke({
          color: '#8B4513',
          width: 4
        }),
        fill: new Fill({
          color: 'rgba(139, 69, 19, 0.3)'
        })
      })
    });
    
    this.map.addLayer(bridgeLayer);
    this.registerLayer('bridge', bridgeLayer);
  }

  private updateElevationDisplay(): void {
    // Update elevation display based on showElevation setting
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source === this.vectorSource) {
          // Update elevation styling
          layer.setStyle((feature: FeatureLike) => {
            return this.getElevationStyle(feature);
          });
        }
      }
    });
  }

  private getElevationStyle(feature: FeatureLike): Style {
    const baseStyle = this.getEnhancedDrawingStyle(feature, undefined);
    
    if (this.showElevation && feature.get('elevation')) {
      const elevation = feature.get('elevation');
      const height = Math.max(0, elevation * 0.1); // Scale elevation for visualization
      
      return new Style({
        stroke: new Stroke({
          color: '#e74c3c',
          width: 3
        }),
        fill: new Fill({
          color: `rgba(231, 76, 60, ${0.3 + height * 0.1})`
        }),
        zIndex: height
      });
    }
    
    return baseStyle as Style;
  }

  private showElevationProfile(): void {
    // Show elevation profile for selected features
    const features = this.vectorSource.getFeatures();
    const lineFeatures = features.filter(f => f.getGeometry()?.getType() === 'LineString');
    
    if (lineFeatures.length === 0) {
      this.showInstructions('No line features found for elevation profile');
      return;
    }
    
    // Build list of coords for elevation API (WGS84)
    const allCoords: number[][] = [];
    lineFeatures.forEach(feature => {
      const geometry = feature.getGeometry() as LineString;
      geometry.getCoordinates().forEach(c => allCoords.push(toLonLat(c)));
    });
    // Fetch real elevation when possible (open-elevation.com)
    this.fetchElevationsForCoordinates(allCoords).then(elevations => {
      let idx = 0;
      this.elevationProfileData = lineFeatures.map(feature => {
        const geometry = feature.getGeometry() as LineString;
        const coordinates = geometry.getCoordinates();
        const elevs = coordinates.map(() => elevations[idx++] ?? Math.random() * 100);
        return {
          name: feature.get('name') || 'Unnamed',
          coordinates,
          elevations: elevs
        };
      });
      this.displayElevationProfile();
    }).catch(() => {
      // Fallback mock
      this.elevationProfileData = lineFeatures.map(feature => {
        const geometry = feature.getGeometry() as LineString;
        const coordinates = geometry.getCoordinates();
        return {
          name: feature.get('name') || 'Unnamed',
          coordinates,
          elevations: coordinates.map(() => Math.random() * 100)
        };
      });
      this.displayElevationProfile();
    });
  }

  /** Fetch elevations for [lon, lat] pairs from open-elevation API (batch). */
  private async fetchElevationsForCoordinates(lonLatPairs: number[][]): Promise<number[]> {
    if (!lonLatPairs.length) return [];
    const locations = lonLatPairs.slice(0, 100).map(([lon, lat]) => ({ latitude: lat, longitude: lon }));
    const res = await fetch(this.elevationApiBase, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locations })
    });
    if (!res.ok) throw new Error('Elevation API error');
    const data = await res.json();
    const elevations: number[] = (data.results || []).map((r: { elevation?: number }) => r.elevation ?? 0);
    if (elevations.length < lonLatPairs.length) {
      return [...elevations, ...Array(lonLatPairs.length - elevations.length).fill(0)];
    }
    return elevations;
  }

  private displayElevationProfile(): void {
    // Create elevation profile display
    const profileDialog = document.createElement('div');
    profileDialog.className = 'elevation-profile-dialog';
    profileDialog.innerHTML = `
      <h4>📈 Elevation Profile</h4>
      <div class="profile-chart">
        ${this.elevationProfileData.map(data => `
          <div class="profile-item">
            <h5>${data.name}</h5>
            <div class="elevation-chart">
              ${data.elevations.map((elev: number, i: number) => `
                <div class="elevation-bar" style="height: ${elev}px; width: 10px; background: #3498db; margin: 1px;"></div>
              `).join('')}
            </div>
            <div class="elevation-stats">
              Min: ${Math.min(...data.elevations).toFixed(1)}m | 
              Max: ${Math.max(...data.elevations).toFixed(1)}m | 
              Avg: ${(data.elevations.reduce((a: number, b: number) => a + b, 0) / data.elevations.length).toFixed(1)}m
            </div>
          </div>
        `).join('')}
      </div>
      <button class="btn" onclick="this.parentElement.remove()">Close</button>
    `;
    
    document.body.appendChild(profileDialog);
  }

  private setupFeaturePropertiesDialog(): void {
    this.featurePropertiesDialog = document.getElementById('featurePropertiesDialog');
    
    const saveBtn = document.getElementById('saveFeatureProperties');
    const cancelBtn = document.getElementById('cancelFeatureProperties');
    
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        this.saveFeatureProperties();
      });
    }
    
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        this.hideFeaturePropertiesDialog();
      });
    }
  }

  private showFeaturePropertiesDialog(feature: Feature): void {
    this.selectedFeature = feature;
    
    if (this.featurePropertiesDialog) {
      // Populate form with existing properties
      const nameInput = document.getElementById('featureName') as HTMLInputElement;
      const widthInput = document.getElementById('featureWidth') as HTMLInputElement;
      const depthInput = document.getElementById('featureDepth') as HTMLInputElement;
      const lanesSelect = document.getElementById('featureLanes') as HTMLSelectElement;
      const materialSelect = document.getElementById('featureMaterial') as HTMLSelectElement;
      const statusSelect = document.getElementById('featureStatus') as HTMLSelectElement;
      const commentsTextarea = document.getElementById('featureComments') as HTMLTextAreaElement;
      
      if (nameInput) nameInput.value = feature.get('name') || '';
      if (widthInput) widthInput.value = feature.get('width') || '12';
      if (depthInput) depthInput.value = feature.get('depth') || '0.5';
      if (lanesSelect) lanesSelect.value = feature.get('lanes') || '2';
      if (materialSelect) materialSelect.value = feature.get('material') || 'bituminous';
      if (statusSelect) statusSelect.value = feature.get('status') || 'planned';
      if (commentsTextarea) commentsTextarea.value = feature.get('comments') || '';
      
      this.featurePropertiesDialog!.style.display = 'block';
    }
  }

  private hideFeaturePropertiesDialog(): void {
    if (this.featurePropertiesDialog) {
      this.featurePropertiesDialog.style.display = 'none';
    }
    this.selectedFeature = null;
  }

  private saveFeatureProperties(): void {
    if (!this.selectedFeature) return;
    
    const nameInput = document.getElementById('featureName') as HTMLInputElement;
    const widthInput = document.getElementById('featureWidth') as HTMLInputElement;
    const depthInput = document.getElementById('featureDepth') as HTMLInputElement;
    const lanesSelect = document.getElementById('featureLanes') as HTMLSelectElement;
    const materialSelect = document.getElementById('featureMaterial') as HTMLSelectElement;
    const statusSelect = document.getElementById('featureStatus') as HTMLSelectElement;
    const commentsTextarea = document.getElementById('featureComments') as HTMLTextAreaElement;
    
    // Update feature properties
    this.selectedFeature.set('name', nameInput?.value || '');
    this.selectedFeature.set('width', parseFloat(widthInput?.value || '12'));
    this.selectedFeature.set('depth', parseFloat(depthInput?.value || '0.5'));
    this.selectedFeature.set('lanes', parseInt(lanesSelect?.value || '2'));
    this.selectedFeature.set('material', materialSelect?.value || 'bituminous');
    this.selectedFeature.set('status', statusSelect?.value || 'planned');
    this.selectedFeature.set('comments', commentsTextarea?.value || '');
    
    // Update feature style based on new properties
    this.updateFeatureStyle(this.selectedFeature);
    
    this.hideFeaturePropertiesDialog();
    this.showInstructions('Feature properties saved successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private updateFeatureStyle(feature: Feature): void {
    const status = feature.get('status');
    const width = feature.get('width') || 2;
    
    let color = '#3498db'; // Default blue
    switch (status) {
      case 'planned': color = '#f39c12'; break; // Orange
      case 'under_construction': color = '#e74c3c'; break; // Red
      case 'completed': color = '#27ae60'; break; // Green
      case 'maintenance': color = '#9b59b6'; break; // Purple
    }
    
    const style = new Style({
      stroke: new Stroke({
        color: color,
        width: Math.max(2, width / 2)
      }),
      fill: new Fill({
        color: color + '40' // Add transparency
      })
    });
    
    feature.setStyle(style);
  }

  private setupEnhancedLayerManagement(): void {
    const addNewLayerBtn = document.getElementById('addNewLayer');
    const importLayerBtn = document.getElementById('importLayer');
    const exportAllLayersBtn = document.getElementById('exportAllLayers');

    if (addNewLayerBtn) {
      addNewLayerBtn.addEventListener('click', () => {
        this.showAddLayerDialog();
      });
    }

    if (importLayerBtn) {
      importLayerBtn.addEventListener('click', () => {
        this.showImportLayerDialog();
      });
    }

    if (exportAllLayersBtn) {
      exportAllLayersBtn.addEventListener('click', () => {
        this.exportAllLayers();
      });
    }
  }

  private setupEnhancedMeasurementTools(): void {
    const angleBtn = document.getElementById('angleBtn');
    const slopeBtn = document.getElementById('slopeBtn');
    const volumeBtn = document.getElementById('volumeBtn');

    if (angleBtn) {
      angleBtn.addEventListener('click', () => {
        this.enableAngleMeasurement();
      });
    }

    if (slopeBtn) {
      slopeBtn.addEventListener('click', () => {
        this.enableSlopeMeasurement();
      });
    }

    if (volumeBtn) {
      volumeBtn.addEventListener('click', () => {
        this.enableVolumeMeasurement();
      });
    }
  }

  private enableAngleMeasurement(): void {
    this.removeInteractions();
    this.showInstructions('Click three points to measure angle');
    
    let points: number[][] = [];
    
    this.map.on('click', (event: any) => {
      points.push(event.coordinate);
      
      if (points.length === 1) {
        this.showInstructions('Click second point');
      } else if (points.length === 2) {
        this.showInstructions('Click third point to complete angle measurement');
      } else if (points.length === 3) {
        const angle = this.calculateAngle(points[0], points[1], points[2]);
        this.showMeasurementResult(`Angle: ${angle.toFixed(2)}°`);
        points = [];
        this.hideInstructions();
      }
    });
  }

  private calculateAngle(point1: number[], vertex: number[], point2: number[]): number {
    const v1 = [point1[0] - vertex[0], point1[1] - vertex[1]];
    const v2 = [point2[0] - vertex[0], point2[1] - vertex[1]];
    
    const dot = v1[0] * v2[0] + v1[1] * v2[1];
    const mag1 = Math.sqrt(v1[0] * v1[0] + v1[1] * v1[1]);
    const mag2 = Math.sqrt(v2[0] * v2[0] + v2[1] * v2[1]);
    
    const cosAngle = dot / (mag1 * mag2);
    const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle)));
    
    return (angle * 180) / Math.PI;
  }

  private enableSlopeMeasurement(): void {
    this.removeInteractions();
    this.showInstructions('Click two points to measure slope');
    
    let points: number[][] = [];
    
    this.map.on('click', (event: any) => {
      points.push(event.coordinate);
      
      if (points.length === 1) {
        this.showInstructions('Click second point');
      } else if (points.length === 2) {
        const slope = this.calculateSlope(points[0], points[1]);
        const distance = this.calculateDistance(points[0], points[1]);
        this.showMeasurementResult(`Slope: ${slope.toFixed(2)}% | Distance: ${distance.toFixed(2)}m`);
        points = [];
        this.hideInstructions();
      }
    });
  }

  private calculateSlope(point1: number[], point2: number[]): number {
    const dx = point2[0] - point1[0];
    const dy = point2[1] - point1[1];
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Mock elevation calculation (in real implementation, you'd use elevation data)
    const elevation1 = Math.random() * 100;
    const elevation2 = Math.random() * 100;
    const elevationDiff = elevation2 - elevation1;
    
    return (elevationDiff / distance) * 100;
  }

  private enableVolumeMeasurement(): void {
    this.removeInteractions();
    this.showInstructions('Click points to define area, then enter depth for volume calculation');
    
    let points: number[][] = [];
    
    this.map.on('click', (event: any) => {
      points.push(event.coordinate);
      
      if (points.length < 3) {
        this.showInstructions(`Point ${points.length + 1} - Click to add more points or double-click to finish`);
      }
    });
    
    this.map.on('dblclick', () => {
      if (points.length >= 3) {
        this.showVolumeInputDialog(points);
        points = [];
        this.hideInstructions();
      }
    });
  }

  private showVolumeInputDialog(points: number[][]): void {
    const dialog = document.createElement('div');
    dialog.className = 'volume-input-dialog';
    dialog.innerHTML = `
      <h4>📦 Volume Calculation</h4>
      <div class="control-group">
        <label>Depth (m):</label>
        <input type="number" id="volumeDepth" min="0.1" max="100" step="0.1" value="1.0">
      </div>
      <div class="control-group">
        <label>Material Density (kg/m³):</label>
        <select id="materialDensity">
          <option value="2400">Concrete (2400)</option>
          <option value="2200">Asphalt (2200)</option>
          <option value="1800">Soil (1800)</option>
          <option value="1600">Gravel (1600)</option>
        </select>
      </div>
      <div class="btn-group">
        <button class="btn" onclick="this.calculateVolume()">Calculate</button>
        <button class="btn" onclick="this.parentElement.remove()">Cancel</button>
      </div>
    `;
    
    // Add calculate function
    (dialog as any).calculateVolume = () => {
      const depth = parseFloat((document.getElementById('volumeDepth') as HTMLInputElement)?.value || '1.0');
      const density = parseFloat((document.getElementById('materialDensity') as HTMLSelectElement)?.value || '2400');
      
      const area = this.calculatePolygonArea(points);
      const volume = area * depth;
      const weight = volume * density;
      
      this.showMeasurementResult(
        `Volume: ${volume.toFixed(2)} m³ | Weight: ${(weight/1000).toFixed(2)} tonnes | Area: ${area.toFixed(2)} m²`
      );
      
      dialog.remove();
    };
    
    document.body.appendChild(dialog);
  }

  private calculatePolygonArea(points: number[][]): number {
    let area = 0;
    const n = points.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i][0] * points[j][1];
      area -= points[j][0] * points[i][1];
    }
    
    return Math.abs(area) / 2;
  }

  private setupJunctionButtons(): void {
    // Junction buttons
    document.querySelectorAll('.junction-btn').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const junctionType = target.getAttribute('data-junction') || '';
        this.addJunction(junctionType);
        
        // Update active state
        document.querySelectorAll('.junction-btn').forEach(b => b.classList.remove('active'));
        target.classList.add('active');
      });
    });

    // Connect road to junction button
    const connectRoadBtn = document.getElementById('connectRoadToJunction');
    if (connectRoadBtn) {
      connectRoadBtn.addEventListener('click', () => this.connectRoadToJunction());
    }

    // Connect segments button
    const connectSegmentsBtn = document.getElementById('connectSegments');
    if (connectSegmentsBtn) {
      connectSegmentsBtn.addEventListener('click', () => this.connectSegments());
    }
    const highlightJunctionsBtn = document.getElementById('highlightJunctionsBtn');
    if (highlightJunctionsBtn) {
      highlightJunctionsBtn.addEventListener('click', () => this.highlightJunctionIntersections());
    }

    // Add comment button
    const addCommentBtn = document.getElementById('addComment');
    if (addCommentBtn) {
      addCommentBtn.addEventListener('click', () => this.addSegmentComment());
    }

    // Overlay controls
    const overlayRoadsBtn = document.getElementById('overlayRoads');
    if (overlayRoadsBtn) {
      overlayRoadsBtn.addEventListener('click', () => this.overlayRoads());
    }

    const clearOverlayBtn = document.getElementById('clearOverlay');
    if (clearOverlayBtn) {
      clearOverlayBtn.addEventListener('click', () => this.clearOverlay());
    }

    const applyOffsetBtn = document.getElementById('applyOffset');
    if (applyOffsetBtn) {
      applyOffsetBtn.addEventListener('click', () => this.applyLayerOffset());
    }

    // Overlay opacity control
    const overlayOpacitySlider = document.getElementById('overlayOpacity');
    if (overlayOpacitySlider) {
      overlayOpacitySlider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const opacity = parseFloat(target.value) / 100;
        this.updateOverlayOpacity(opacity);
      });
    }
  }

  private updateOverlayOpacity(opacity: number): void {
    // Update overlay layer opacity
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        if (source === this.roadSource || source === this.projectSource) {
          layer.setOpacity(opacity);
        }
      }
    });
  }

  private setupRoadInfrastructureButtons(): void {
    // Symbol popup button
    const symbolPopupBtn = document.getElementById('symbolPopupBtn');
    if (symbolPopupBtn) {
      symbolPopupBtn.addEventListener('click', () => this.toggleSymbolGrid());
    }

    // 3D view controls
    const enable3DBtn = document.getElementById('enable3D');
    if (enable3DBtn) {
      enable3DBtn.addEventListener('click', () => this.enable3DView());
    }

    const viewTopBtn = document.getElementById('viewTop');
    if (viewTopBtn) {
      viewTopBtn.addEventListener('click', () => this.setViewAngle('top'));
    }

    const viewSideBtn = document.getElementById('viewSide');
    if (viewSideBtn) {
      viewSideBtn.addEventListener('click', () => this.setViewAngle('side'));
    }

    const viewAngleBtn = document.getElementById('viewAngle');
    if (viewAngleBtn) {
      viewAngleBtn.addEventListener('click', () => this.setViewAngle('angle'));
    }

    // Measurement tools
    const pointDistanceBtn = document.getElementById('pointDistance');
    if (pointDistanceBtn) {
      pointDistanceBtn.addEventListener('click', () => this.enablePointToPointDistance());
    }

    const endToEndDistanceBtn = document.getElementById('endToEndDistance');
    if (endToEndDistanceBtn) {
      endToEndDistanceBtn.addEventListener('click', () => this.enableEndToEndDistance());
    }

    const calculateGeometryBtn = document.getElementById('calculateGeometry');
    if (calculateGeometryBtn) {
      calculateGeometryBtn.addEventListener('click', () => this.calculateGeometry());
    }

    const pushToErpBtn = document.getElementById('pushToErp');
    if (pushToErpBtn) {
      pushToErpBtn.addEventListener('click', () => this.pushGisMetricsToErp());
    }

    const takeSnapshotBtn = document.getElementById('takeSnapshot');
    if (takeSnapshotBtn) {
      takeSnapshotBtn.addEventListener('click', () => this.takeSnapshot());
    }

    const calculateGpsErrorBtn = document.getElementById('calculateGpsError');
    if (calculateGpsErrorBtn) {
      calculateGpsErrorBtn.addEventListener('click', () => this.calculateGpsError());
    }

    // Layer thematic selector
    const layerThematicSelect = document.getElementById('layerThematic');
    if (layerThematicSelect) {
      layerThematicSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.applyLayerThematic(target.value);
      });
    }
    const toggleHeatmapOverlayBtn = document.getElementById('toggleHeatmapOverlayBtn');
    if (toggleHeatmapOverlayBtn) {
      toggleHeatmapOverlayBtn.addEventListener('click', () => this.toggleHeatmapOverlay());
    }
    const loadContourBtn = document.getElementById('loadContourBtn');
    if (loadContourBtn) {
      loadContourBtn.addEventListener('click', () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.geojson,.json';
        input.onchange = (e: Event) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = () => {
              const text = reader.result as string;
              const name = (file.name || 'contours').replace(/\.[^.]+$/, '');
              this.loadContourLayer(text, name);
            };
            reader.readAsText(file);
          }
        };
        input.click();
      });
    }
    const addDemLayerBtn = document.getElementById('addDemLayerBtn');
    if (addDemLayerBtn) {
      addDemLayerBtn.addEventListener('click', () => {
        const url = prompt('DEM tile URL template (use {z}, {x}, {y} for tile coords):', 'https://example.com/terrain/{z}/{x}/{y}.png');
        if (url) this.addDemLayer(url, 'DEM');
      });
    }
  }

  private toggleSymbolGrid(): void {
    const symbolGrid = document.getElementById('symbolGrid');
    if (symbolGrid) {
      symbolGrid.style.display = symbolGrid.style.display === 'none' ? 'grid' : 'none';
    }
  }

  private setupSymbolSelection(): void {
    // Remove existing event listeners to prevent duplicates
    const existingButtons = document.querySelectorAll('.symbol-btn');
    existingButtons.forEach(button => {
      const newButton = button.cloneNode(true);
      button.parentNode?.replaceChild(newButton, button);
    });

    // Set up event listeners for all symbol buttons
    const symbolButtons = document.querySelectorAll('.symbol-btn');
    symbolButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const symbol = target.getAttribute('data-symbol') || '';
        const title = target.getAttribute('title') || '';
        
        this.currentSymbol = symbol;
        this.showInstructions(`Selected symbol: ${title} - Click on map to place`);
        
        // Remove any existing click listener to prevent multiple listeners
        this.map.un('click', this.placeSymbolHandler);
        
        // Set up new click listener for placing symbol
        this.placeSymbolHandler = (event: any) => {
          this.placeSymbol(event.coordinate, symbol, title);
        };
        this.map.on('click', this.placeSymbolHandler);
      });
    });
  }

  private placeSymbol(coordinate: number[], symbol: string, title: string): void {
    // Check if it's a multi-lane road symbol
    if (symbol.includes('🛣️') && (symbol.includes('2') || symbol.includes('4') || symbol.includes('6') || symbol.includes('8'))) {
      this.placeMultiLaneRoad(coordinate, symbol, title);
      return;
    }

    const symbolFeature = new Feature({
      geometry: new Point(coordinate),
      type: 'road_symbol',
      symbol: symbol,
      title: title,
      timestamp: new Date().toISOString()
    });
    
    const style = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <text x="12" y="16" text-anchor="middle" font-size="16" fill="#000">${symbol}</text>
          </svg>
        `),
        scale: 1.5,
        anchor: [0.5, 0.5]
      }),
      text: new Text({
        text: title,
        font: '12px Arial',
        fill: new Fill({ color: '#000' }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
        offsetY: -20
      })
    });
    
    symbolFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(symbolFeature);
    this.saveToHistory();
    
    this.hideInstructions();
    this.showInstructions(`${title} placed successfully`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private placeMultiLaneRoad(coordinate: number[], symbol: string, title: string): void {
    // Extract lane count from symbol
    const laneCount = parseInt(symbol.replace('🛣️', '')) || 2;
    
    // Start multi-lane road drawing mode
    this.startMultiLaneRoadDrawing(laneCount, title);
  }

  private startMultiLaneRoadDrawing(laneCount: number, title: string): void {
    this.removeInteractions();
    this.showInstructions(`Drawing ${laneCount}-lane road. Click to start, double-click to finish.`);
    
    let roadPoints: number[][] = [];
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: '#2c3e50',
          width: Math.max(3, laneCount),
          lineDash: laneCount > 4 ? [10, 5] : undefined
        })
      })
    });
    this.drawInteraction.on('drawstart', (e: any) => this.setupRealtimeLineLength(e.feature));
    this.drawInteraction.on('drawend', (e: any) => this.clearRealtimeLineLength(e.feature));
    this.drawInteraction.on('drawabort', () => this.clearRealtimeLineLength(null));
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'multi_lane_road');
      feature.set('lanes', laneCount);
      feature.set('width', laneCount * 3.5);
      feature.set('title', title);
      feature.set('timestamp', new Date().toISOString());
      
      const finalStyle = new Style({
        stroke: new Stroke({
          color: '#2c3e50',
          width: Math.max(3, laneCount),
          lineDash: laneCount > 4 ? [10, 5] : undefined
        }),
        text: new Text({
          text: `${laneCount} Lanes`,
          font: '12px Arial',
          fill: new Fill({ color: '#2c3e50' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
          offsetY: -15
        })
      });
      
      feature.setStyle(finalStyle);
      this.saveToHistory();
      this.hideInstructions();
      this.showInstructions(`${title} created successfully`);
      setTimeout(() => this.hideInstructions(), 2000);
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private enable3DView(): void {
    this.is3DMode = !this.is3DMode;
    
    if (this.is3DMode) {
      this.showInstructions('3D View enabled - Features will be displayed with height');
      this.updateElevationDisplay();
    } else {
      this.showInstructions('3D View disabled - Features displayed in 2D');
      this.updateElevationDisplay();
    }
    
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private applyLayerThematic(thematic: string): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(feature => {
      const currentStyle = feature.getStyle();
      let newStyle = currentStyle;
      
      switch (thematic) {
        case 'heat':
          newStyle = this.getHeatMapStyle(feature);
          break;
        case 'cluster':
          newStyle = this.getClusterStyle(feature);
          break;
        case 'default':
        default:
          newStyle = this.getDefaultStyle(feature);
          break;
      }
      
      feature.setStyle(newStyle);
    });
    
    this.showInstructions(`Applied ${thematic} thematic styling`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private getHeatMapStyle(feature: Feature): Style {
    const type = feature.get('type') || 'default';
    const colors: { [key: string]: string } = {
      'LineString': '#ff4757',
      'Polygon': '#ff6b6b',
      'Point': '#ffa502',
      'Circle': '#ff6348',
      'junction': '#ff3838',
      'default': '#ff4757'
    };
    
    return new Style({
      stroke: new Stroke({
        color: colors[type] || colors['default'],
        width: 3
      }),
      fill: new Fill({
        color: `${colors[type] || colors['default']}40`
      })
    });
  }

  private getClusterStyle(feature: Feature): Style {
    const type = feature.get('type') || 'default';
    const colors: { [key: string]: string } = {
      'LineString': '#3742fa',
      'Polygon': '#2f3542',
      'Point': '#ff9ff3',
      'Circle': '#54a0ff',
      'junction': '#5f27cd',
      'default': '#3742fa'
    };
    
    return new Style({
      stroke: new Stroke({
        color: colors[type] || colors['default'],
        width: 2,
        lineDash: [5, 5]
      }),
      fill: new Fill({
        color: `${colors[type] || colors['default']}60`
      })
    });
  }

  private getDefaultStyle(feature: Feature): Style {
    return this.getDrawingStyle(feature)[0];
  }

  private setViewAngle(angle: string): void {
    console.log('View angle set to:', angle);
  }

  private enablePointToPointDistance(): void {
    this.enableDistanceMeasurement();
  }

  private enableEndToEndDistance(): void {
    this.enableDistanceMeasurement();
  }

  /**
   * Push GIS-derived length, width, area to project/ERP record.
   * Aggregates from drawing + road layers and current road specs.
   */
  pushGisMetricsToErp(): void {
    if (!this.projectId) {
      this.showInstructions('No project loaded. Open a project first.');
      return;
    }
    let totalLength = 0;
    let totalArea = 0;
    const collectFromSource = (source: VectorSource) => {
      source.getFeatures().forEach(f => {
        const geom = f.getGeometry();
        if (geom) {
          const t = geom.getType();
          if (t === 'LineString' || t === 'MultiLineString') {
            totalLength += parseFloat(this.calculateLength(f).meters);
          } else if (t === 'Polygon' || t === 'MultiPolygon') {
            totalArea += parseFloat(this.calculateArea(f).squareMeters);
          } else if (t === 'Circle') {
            totalArea += parseFloat(this.calculateArea(f).squareMeters);
            totalLength += parseFloat(this.calculateLength(f).meters);
          }
        }
      });
    };
    collectFromSource(this.vectorSource);
    collectFromSource(this.roadSource);
    const roadWidth = parseFloat((document.getElementById('roadWidth') as HTMLInputElement)?.value || '') || this.currentRoadSpecs.width || 12;
    const roadLanes = parseInt((document.getElementById('roadLanes') as HTMLSelectElement)?.value || '2', 10) || this.currentRoadSpecs.lanes || 2;
    const metrics: any = {};
    if (totalLength > 0) {
      // Store road length in km (project_common + BOQ templates expect km).
      metrics.road_length = Math.round((totalLength / 1000) * 1000) / 1000;
    }
    if (roadWidth > 0) metrics.lane_width_adjustment = roadWidth;
    if (roadLanes > 0) metrics.lane_configuration = roadLanes;
    if (totalArea > 0) metrics.size = Math.round(totalArea * 100) / 100;
    if (Object.keys(metrics).length === 0) {
      this.showInstructions('No line or area features found. Draw roads or polygons first.');
      return;
    }
    this.apiService.pushGisMetricsToProject(this.projectId, metrics).subscribe({
      next: (res: any) => {
        const autoBoq = res?.auto_boq;
        const msg = autoBoq?.boq_code
          ? `Pushed to ERP. BOQ ${autoBoq.boq_code} generated.`
          : `Pushed to ERP: ${JSON.stringify(metrics)}`;
        this.showInstructions(msg);
        if (this.projectData) {
          Object.assign(this.projectData, metrics);
        }
        this.loadBoqAndBom();
      },
      error: (err) => {
        this.showInstructions(err?.error?.message || err?.message || 'Failed to push to ERP');
      }
    });
  }

  loadBoqAndBom(): void {
    if (!this.projectId) {
      this.showInstructions('Load a project first.');
      return;
    }
    this.boqBomLoading = true;
    this.boqList = [];
    this.bomList = [];
    const pid = this.projectId;
    this.apiService.getBoQs({ project_id: pid, limit: 20 }).subscribe({
      next: (res: any) => {
        if (res?.success && Array.isArray(res.data)) {
          this.boqList = res.data;
        }
        this.apiService.getBoms({ project_id: pid, limit: 20 }).subscribe({
          next: (bomRes: any) => {
            if (bomRes?.data && Array.isArray(bomRes.data)) {
              this.bomList = bomRes.data;
            } else if (bomRes?.success && Array.isArray(bomRes.data)) {
              this.bomList = bomRes.data;
            }
            this.boqBomLoading = false;
          },
          error: () => {
            this.boqBomLoading = false;
          }
        });
      },
      error: () => {
        this.apiService.getBoms({ project_id: pid, limit: 20 }).subscribe({
          next: (bomRes: any) => {
            if (bomRes?.data && Array.isArray(bomRes.data)) {
              this.bomList = bomRes.data;
            } else if (bomRes?.success && Array.isArray(bomRes.data)) {
              this.bomList = bomRes.data;
            }
            this.boqBomLoading = false;
          },
          error: () => { this.boqBomLoading = false; }
        });
      }
    });
  }

  openBoq(boqId: number): void {
    this.router.navigate(['/home/boq', boqId, 'view']);
  }

  openBom(bomId: number): void {
    this.router.navigate(['/home/bom'], { queryParams: { project_id: this.projectId, bom_id: bomId } });
  }

  generateBoqReport(): void {
    if (!this.projectId) {
      this.showInstructions('Load a project first.');
      return;
    }
    if (this.boqList?.length) {
      this.router.navigate(['/home/boq', this.boqList[0].boq_id, 'view'], { queryParams: { report: '1' } });
    } else {
      this.router.navigate(['/home/boq'], { queryParams: { project_id: this.projectId } });
    }
  }

  private calculateGeometry(): void {
    const features = this.vectorSource.getFeatures();
    
    if (features.length === 0) {
      this.showInstructions('No features found. Draw some shapes first.');
      return;
    }
    
    let totalArea = 0;
    let totalLength = 0;
    let polygonCount = 0;
    let lineCount = 0;
    let circleCount = 0;

    features.forEach(feature => {
      const geom = feature.getGeometry();
      if (geom) {
        const geomType = geom.getType();
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          totalArea += parseFloat(this.calculateArea(feature).squareMeters);
          polygonCount++;
        } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
          totalLength += parseFloat(this.calculateLength(feature).meters);
          lineCount++;
        } else if (geomType === 'Circle') {
          const areaResult = this.calculateArea(feature);
          const lengthResult = this.calculateLength(feature);
          totalArea += parseFloat(areaResult.squareMeters);
          totalLength += parseFloat(lengthResult.meters); // Circumference
          circleCount++;
        }
      }
    });

    const areaKm = totalArea / 1000000;
    const lengthKm = totalLength / 1000;

    let result = `
📊 Geometry Summary:
━━━━━━━━━━━━━━━━━━━━━
Total Features: ${features.length}
• Polygons: ${polygonCount}
• Lines: ${lineCount}
• Circles: ${circleCount}

📐 Total Area: ${totalArea.toFixed(2)} m² (${areaKm.toFixed(4)} km²)
📏 Total Length: ${totalLength.toFixed(2)} m (${lengthKm.toFixed(4)} km)
    `;

    this.showMeasurementResult(result);
  }

  private takeSnapshot(): void {
    this.takeMapImage();
  }

  /**
   * 3D Take Image: capture current map view to PNG file
   */
  takeMapImage(): void {
    if (!this.map || !this.mapElement) {
      this.showInstructions('Map not ready');
      return;
    }
    this.showInstructions('Capturing image...');

    const capture = () => {
      const mapElement = this.mapElement.nativeElement;
      const canvases = mapElement.querySelectorAll('canvas');
      const width = mapElement.offsetWidth;
      const height = mapElement.offsetHeight;

      const outCanvas = document.createElement('canvas');
      outCanvas.width = width;
      outCanvas.height = height;
      const ctx = outCanvas.getContext('2d');
      if (!ctx) {
        this.showInstructions('Failed to capture');
        return;
      }

      ctx.fillStyle = '#f0f2f5';
      ctx.fillRect(0, 0, width, height);

      const size = this.map.getSize();
      if (size && size[0] > 0 && size[1] > 0 && canvases.length > 0) {
        canvases.forEach((c: HTMLCanvasElement) => {
          if (c.width > 0 && c.height > 0) {
            ctx.drawImage(c, 0, 0, width, height);
          }
        });
      }

      ctx.fillStyle = 'rgba(30, 58, 95, 0.85)';
      ctx.fillRect(0, height - 28, width, 28);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '11px sans-serif';
      const scale = this.getMapScale();
      ctx.fillText(`Scale 1:${scale.toLocaleString()} • ${new Date().toLocaleString()}`, 12, height - 10);
      if (this.projectData?.project_name) {
        ctx.fillText(this.projectData.project_name, width - 180, height - 10);
      }

      outCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `map-image-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
          this.showInstructions('Image saved');
          setTimeout(() => this.hideInstructions(), 2000);
        }
      }, 'image/png');
    };

    this.map.once('rendercomplete', capture);
    this.map.renderSync();
  }

  private calculateGpsError(): void {
    // Implementation for GPS error calculation
    console.log('Calculating GPS error...');
  }

  private setupAdvancedControls(): void {
    // Enhanced curve controls
    this.setupCurveControls();

    // Lane separator controls
    const insertSeparatorBtn = document.getElementById('insertSeparator');
    if (insertSeparatorBtn) {
      insertSeparatorBtn.addEventListener('click', () => this.insertSeparator());
    }

    const calculateSeparatorBtn = document.getElementById('calculateSeparator');
    if (calculateSeparatorBtn) {
      calculateSeparatorBtn.addEventListener('click', () => this.calculateSeparator());
    }

    // Water features
    const drawNallaBtn = document.getElementById('drawNalla');
    if (drawNallaBtn) {
      drawNallaBtn.addEventListener('click', () => this.drawNalla());
    }

    const addWaterExitBtn = document.getElementById('addWaterExit');
    if (addWaterExitBtn) {
      addWaterExitBtn.addEventListener('click', () => this.addWaterExit());
    }

    // Enhanced advanced controls
    this.setupAdvancedAnalysisControls();
    this.setupExportImportControls();
    this.setupAdvancedToolsControls();
    this.setupWaterFeatureControls();
    this.setupRoadSpecificationControls();
  }

  private setupCurveControls(): void {
    // Curve type selection
    const curveType = document.getElementById('curveType');
    if (curveType) {
      curveType.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.currentCurveType = target.value as 'bezier' | 'spline' | 'arc' | 'smooth';
      });
    }

    // Start curve drawing
    const startCurveBtn = document.getElementById('startCurveDrawing');
    if (startCurveBtn) {
      startCurveBtn.addEventListener('click', () => {
        this.startCurveDrawing();
      });
    }

    // Finish curve drawing
    const finishCurveBtn = document.getElementById('finishCurveDrawing');
    if (finishCurveBtn) {
      finishCurveBtn.addEventListener('click', () => {
        this.finishCurveDrawing();
      });
    }

    // Cancel curve drawing
    const cancelCurveBtn = document.getElementById('cancelCurveDrawing');
    if (cancelCurveBtn) {
      cancelCurveBtn.addEventListener('click', () => {
        this.cancelCurveDrawing();
      });
    }

    // Curve precision control
    const curvePrecisionSlider = document.getElementById('curvePrecision');
    if (curvePrecisionSlider) {
      curvePrecisionSlider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const precision = parseInt(target.value);
        const precisionValue = document.getElementById('precisionValue');
        if (precisionValue) {
          precisionValue.textContent = precision.toString();
        }
        this.curvePrecision = precision;
        
        // Update preview if currently drawing
        if (this.isCurveDrawing && this.curveControlPoints.length >= 2) {
          this.drawCurvePreview();
        }
      });
    }

    // Curve smoothness control
    const curveSmoothnessSlider = document.getElementById('curveSmoothness');
    if (curveSmoothnessSlider) {
      curveSmoothnessSlider.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        const smoothness = parseFloat(target.value);
        const smoothnessValue = document.getElementById('smoothnessValue');
        if (smoothnessValue) {
          smoothnessValue.textContent = smoothness.toFixed(1);
        }
        
        // Update preview if currently drawing
        if (this.isCurveDrawing && this.curveControlPoints.length >= 2) {
          this.drawCurvePreview();
        }
      });
    }

    // Show control points toggle
    const showControlPoints = document.getElementById('showControlPoints');
    if (showControlPoints) {
      showControlPoints.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.updateControlPointsVisibility(target.checked);
      });
    }
  }

  private updateControlPointsVisibility(visible: boolean): void {
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      if (feature.get('type') === 'control_point') {
        feature.setStyle(visible ? this.getControlPointStyle(feature) : null);
      }
    });
  }

  private getControlPointStyle(feature: Feature): Style {
    const pointNumber = feature.get('pointNumber');
    return new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: '#e74c3c' }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      }),
      text: new Text({
        text: pointNumber.toString(),
        font: '12px Arial',
        fill: new Fill({ color: '#fff' }),
        stroke: new Stroke({ color: '#000', width: 2 }),
        offsetY: -15
      })
    });
  }

  private addMultiCurveInteraction(): void {
    this.removeInteractions();
    // Implementation for multi-curve
    console.log('Multi-curve interaction');
  }

  private addBezierCurveInteraction(): void {
    this.removeInteractions();
    // Implementation for Bézier curve
    console.log('Bézier curve interaction');
  }

  private addSplineCurveInteraction(): void {
    this.removeInteractions();
    // Implementation for spline curve
    console.log('Spline curve interaction');
  }

  private insertSeparator(): void {
    this.removeInteractions();
    this.showInstructions('Click on a road segment to insert lane separator');
    
    this.map.on('click', (event: any) => {
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature: FeatureLike) => {
        return feature.getGeometry()?.getType() === 'LineString' ? feature as Feature : null;
      });

      if (feature) {
        this.createLaneSeparator(feature, event.coordinate);
        this.hideInstructions();
      } else {
        this.showInstructions('Please click on a road segment');
      }
    });
  }

  private createLaneSeparator(roadFeature: Feature, coordinate: number[]): void {
    const geometry = roadFeature.getGeometry() as LineString;
    const roadCoords = geometry.getCoordinates();
    
    // Create perpendicular separator line
    const separatorLine = this.createPerpendicularSeparator(roadCoords, coordinate);
    
    const separatorFeature = new Feature({
      geometry: new LineString(separatorLine),
      type: 'lane_separator',
      dividerType: this.dividerType,
      width: this.separatorWidth,
      distance: this.separatorDistance,
      roadId: roadFeature.getId()
    });
    
    const style = this.getLaneSeparatorStyle(this.dividerType);
    separatorFeature.setStyle(style);
    
    this.getCurrentDrawingSource().addFeature(separatorFeature);
    this.separatorCount++;
    this.updateSeparatorCount();
    
    this.showInstructions('Lane separator inserted successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private createPerpendicularSeparator(roadCoords: number[][], point: number[]): [number, number][] {
    // Find closest point on road
    const closestPoint = this.findClosestPointOnLine(point, roadCoords);
    
    // Calculate perpendicular direction
    const segmentIndex = this.findSegmentIndex(roadCoords, closestPoint);
    const segmentStart = roadCoords[segmentIndex];
    const segmentEnd = roadCoords[segmentIndex + 1];
    
    const dx = segmentEnd[0] - segmentStart[0];
    const dy = segmentEnd[1] - segmentStart[1];
    const length = Math.sqrt(dx * dx + dy * dy);
    
    // Perpendicular vector
    const perpX = -dy / length;
    const perpY = dx / length;
    
    // Create separator line
    const halfWidth = this.separatorDistance / 2;
    const startX = closestPoint[0] + perpX * halfWidth;
    const startY = closestPoint[1] + perpY * halfWidth;
    const endX = closestPoint[0] - perpX * halfWidth;
    const endY = closestPoint[1] - perpY * halfWidth;
    
    return [[startX, startY], [endX, endY]];
  }

  private findSegmentIndex(coords: number[][], point: number[]): number {
    let minDistance = Infinity;
    let segmentIndex = 0;
    
    for (let i = 0; i < coords.length - 1; i++) {
      const closestPoint = this.getClosestPointOnSegment(point, coords[i], coords[i + 1]);
      const distance = this.calculateDistance(point, closestPoint);
      
      if (distance < minDistance) {
        minDistance = distance;
        segmentIndex = i;
      }
    }
    
    return segmentIndex;
  }

  private getLaneSeparatorStyle(dividerType: string): Style {
    const spec = getRoadDividerType(dividerType);
    return new Style({
      stroke: new Stroke({
        color: spec.strokeColor,
        width: spec.strokePx,
        lineDash: spec.lineDash
      })
    });
  }

  private calculateSeparator(): void {
    const features = this.vectorSource.getFeatures();
    const separators = features.filter(f => f.get('type') === 'lane_separator');
    
    let totalLength = 0;
    let totalCost = 0;
    
    separators.forEach(separator => {
      const geometry = separator.getGeometry() as LineString;
      const length = geometry.getLength();
      totalLength += length;
      
      const dividerType = separator.get('dividerType');
      const costPerMeter = this.getSeparatorCost(dividerType);
      totalCost += length * costPerMeter;
    });
    
    const result = `
      Total Separators: ${separators.length}
      Total Length: ${totalLength.toFixed(2)}m
      Estimated Cost: ₹${totalCost.toFixed(2)}
      Average Length: ${separators.length > 0 ? (totalLength / separators.length).toFixed(2) : 0}m
    `;
    
    this.showMeasurementResult(result);
  }

  private getSeparatorCost(dividerType: string): number {
    return separatorCostPerMeter(dividerType);
  }

  private updateSeparatorCount(): void {
    const countElement = document.getElementById('separatorCount');
    if (countElement) {
      countElement.textContent = this.separatorCount.toString();
    }
  }

  private drawNalla(): void {
    this.removeInteractions();
    this.showInstructions('Click points to draw nalla/stream. Right-click to finish.');
    
    let nallaPoints: number[][] = [];
    
    this.map.on('click', (event: any) => {
      nallaPoints.push(event.coordinate);
      
      if (nallaPoints.length === 1) {
        this.showInstructions('Click more points or right-click to finish');
      } else {
        this.drawNallaPreview(nallaPoints);
      }
    });
    
    this.map.getViewport().addEventListener('contextmenu', (event: any) => {
      event.preventDefault();
      if (nallaPoints.length >= 2) {
        this.finishNallaDrawing(nallaPoints);
        nallaPoints = [];
        this.hideInstructions();
      }
    });
  }

  private drawNallaPreview(points: number[][]): void {
    // Remove existing preview
    this.removeNallaPreview();
    
    const nallaFeature = new Feature({
      geometry: new LineString(points),
      type: 'nalla_preview'
    });
    
    const style = new Style({
      stroke: new Stroke({
        color: '#00bfff',
        width: 3,
        lineDash: [10, 5]
      })
    });
    
    nallaFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(nallaFeature);
  }

  private removeNallaPreview(): void {
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      if (feature.get('type') === 'nalla_preview') {
        this.vectorSource.removeFeature(feature);
      }
    });
  }

  private finishNallaDrawing(points: number[][]): void {
    this.removeNallaPreview();
    
    const nallaFeature = new Feature({
      geometry: new LineString(points),
      type: 'nalla',
      flowDirection: this.waterFlowDirection,
      width: 2,
      depth: 1
    });
    
    const style = new Style({
      stroke: new Stroke({
        color: '#00bfff',
        width: 3
      })
    });
    
    nallaFeature.setStyle(style);
    this.vectorSource.addFeature(nallaFeature);
    
    this.showInstructions('Nalla/stream created successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private addWaterExit(): void {
    this.removeInteractions();
    this.showInstructions('Click to place water exit point');
    
    this.map.on('click', (event: any) => {
      const waterExitFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'water_exit',
        flowDirection: this.waterFlowDirection,
        capacity: 1000 // liters per hour
      });
      
      const style = new Style({
        image: new Icon({
          src: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="#00bfff"/>
            </svg>
          `),
          scale: 1.5,
          anchor: [0.5, 0.5]
        })
      });
      
      waterExitFeature.setStyle(style);
      this.getCurrentDrawingSource().addFeature(waterExitFeature);
      
      this.hideInstructions();
      this.showInstructions('Water exit added successfully');
      setTimeout(() => this.hideInstructions(), 2000);
    });
  }

  private setupAdvancedAnalysisControls(): void {
    // Analysis buttons
    const analyzeRoadNetworkBtn = document.getElementById('analyzeRoadNetwork');
    if (analyzeRoadNetworkBtn) {
      analyzeRoadNetworkBtn.addEventListener('click', () => this.analyzeRoadNetwork());
    }

    const analyzeTrafficFlowBtn = document.getElementById('analyzeTrafficFlow');
    if (analyzeTrafficFlowBtn) {
      analyzeTrafficFlowBtn.addEventListener('click', () => this.analyzeTrafficFlow());
    }

    const analyzeDrainageBtn = document.getElementById('analyzeDrainage');
    if (analyzeDrainageBtn) {
      analyzeDrainageBtn.addEventListener('click', () => this.analyzeDrainage());
    }

    const analyzeElevationBtn = document.getElementById('analyzeElevation');
    if (analyzeElevationBtn) {
      analyzeElevationBtn.addEventListener('click', () => this.analyzeElevation());
    }

    // Analysis controls
    const analysisBuffer = document.getElementById('analysisBuffer');
    if (analysisBuffer) {
      analysisBuffer.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.analysisBuffer = parseInt(target.value);
      });
    }

    const analysisType = document.getElementById('analysisType');
    if (analysisType) {
      analysisType.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.analysisType = target.value;
      });
    }
  }

  private setupExportImportControls(): void {
    // Export buttons
    const exportRoadSpecsBtn = document.getElementById('exportRoadSpecs');
    if (exportRoadSpecsBtn) {
      exportRoadSpecsBtn.addEventListener('click', () => this.exportRoadSpecs());
    }

    const exportAnalysisBtn = document.getElementById('exportAnalysis');
    if (exportAnalysisBtn) {
      exportAnalysisBtn.addEventListener('click', () => this.exportAnalysis());
    }

    // Import buttons
    const importCADBtn = document.getElementById('importCAD');
    if (importCADBtn) {
      importCADBtn.addEventListener('click', () => this.importCAD());
    }

    const importGISBtn = document.getElementById('importGIS');
    if (importGISBtn) {
      importGISBtn.addEventListener('click', () => this.importGIS());
    }

    // Export controls
    const exportFormat = document.getElementById('exportFormat');
    if (exportFormat) {
      exportFormat.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.exportFormat = target.value;
      });
    }

    const includeMetadata = document.getElementById('includeMetadata');
    if (includeMetadata) {
      includeMetadata.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.includeMetadata = target.checked;
      });
    }
  }

  private setupRoadSpecificationControls(): void {
    const autoDrawBtn = document.getElementById('autoDrawRoadFromSpecs');
    if (autoDrawBtn) {
      autoDrawBtn.addEventListener('click', () => this.startAutoRoadFromSpecsDrawing());
    }

    const roadType = document.getElementById('roadType');
    if (roadType) {
      roadType.addEventListener('change', () => {
        this.applyRoadTypePresetToForm();
        this.updateRoadSpecs();
      });
    }

    ['roadWidth', 'roadDepth', 'roadLanes', 'surfaceType'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('change', () => this.updateRoadSpecs());
        el.addEventListener('input', () => this.updateRoadSpecs());
      }
    });
  }

  private setupAdvancedToolsControls(): void {
    // Advanced tools buttons
    const autoGenerateRoadsBtn = document.getElementById('autoGenerateRoads');
    if (autoGenerateRoadsBtn) {
      autoGenerateRoadsBtn.addEventListener('click', () => this.startAutoRoadFromSpecsDrawing());
    }

    const optimizeNetworkBtn = document.getElementById('optimizeNetwork');
    if (optimizeNetworkBtn) {
      optimizeNetworkBtn.addEventListener('click', () => this.optimizeNetwork());
    }

    const validateGeometryBtn = document.getElementById('validateGeometry');
    if (validateGeometryBtn) {
      validateGeometryBtn.addEventListener('click', () => this.validateGeometry());
    }

    const repairGeometryBtn = document.getElementById('repairGeometry');
    if (repairGeometryBtn) {
      repairGeometryBtn.addEventListener('click', () => this.repairGeometry());
    }

    // Validation controls
    const validationRules = document.getElementById('validationRules');
    if (validationRules) {
      validationRules.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.validationRules = target.value;
      });
    }

    const autoFixIssues = document.getElementById('autoFixIssues');
    if (autoFixIssues) {
      autoFixIssues.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.autoFixIssues = target.checked;
      });
    }
  }

  private setupAssetControls(): void {
    // Asset type selector
    const assetTypeSelect = document.getElementById('assetType');
    if (assetTypeSelect) {
      assetTypeSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.updateAssetVisibility();
      });
    }

    // Nomenclature controls
    const showNomenclatureCheckbox = document.getElementById('showNomenclature');
    if (showNomenclatureCheckbox) {
      showNomenclatureCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showNomenclature = target.checked;
        this.updateNomenclatureVisibility();
      });
    }

    const showOvergroundCheckbox = document.getElementById('showOverground');
    if (showOvergroundCheckbox) {
      showOvergroundCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showOverground = target.checked;
        this.updateAssetVisibility();
      });
    }

    const showUndergroundCheckbox = document.getElementById('showUnderground');
    if (showUndergroundCheckbox) {
      showUndergroundCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showUnderground = target.checked;
        this.updateAssetVisibility();
      });
    }

    const showCrossoverCheckbox = document.getElementById('showCrossover');
    if (showCrossoverCheckbox) {
      showCrossoverCheckbox.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.showCrossover = target.checked;
        this.updateAssetVisibility();
      });
    }
  }

  private updateAssetVisibility(): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(feature => {
      const assetType = feature.get('assetType') || feature.get('grade') || 'overground';
      const isVisible =
        (assetType === 'overground' && this.showOverground) ||
        (assetType === 'underground' && this.showUnderground) ||
        (assetType === 'crossover' && this.showCrossover) ||
        (assetType === 'at_grade' && this.showOverground);
      
      feature.setStyle(isVisible ? feature.getStyle() : null);
    });
  }

  private updateNomenclatureVisibility(): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(feature => {
      const currentStyle = feature.getStyle();
      if (currentStyle) {
        const styles = Array.isArray(currentStyle) ? currentStyle : [currentStyle];
        
        styles.forEach(style => {
          if (style instanceof Style) {
            const text = style.getText();
            if (text) {
              // Update text visibility by recreating the style
              const newStyle = new Style({
                text: new Text({
                  text: text.getText(),
                  font: text.getFont(),
                  fill: text.getFill(),
                  stroke: text.getStroke(),
                  offsetX: text.getOffsetX(),
                  offsetY: text.getOffsetY(),
                  rotation: text.getRotation(),
                  scale: text.getScale()
                })
              });
              feature.setStyle(newStyle);
            }
          }
        });
      }
    });
  }

  private setupWaterFeatureControls(): void {
    // Water flow direction
    const waterFlowDirection = document.getElementById('waterFlowDirection');
    if (waterFlowDirection) {
      waterFlowDirection.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.waterFlowDirection = target.value;
      });
    }

    // Add culvert button
    const addCulvertBtn = document.getElementById('addCulvert');
    if (addCulvertBtn) {
      addCulvertBtn.addEventListener('click', () => this.addCulvert());
    }
  }

  // Analysis Methods
  private analyzeRoadNetwork(): void {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    
    let totalLength = 0;
    let intersectionCount = 0;
    let averageWidth = 0;
    
    roads.forEach(road => {
      const geometry = road.getGeometry() as LineString;
      totalLength += geometry.getLength();
      averageWidth += road.get('thickness') || 2;
    });
    
    averageWidth = roads.length > 0 ? averageWidth / roads.length : 0;
    
    // Find intersections
    for (let i = 0; i < roads.length; i++) {
      for (let j = i + 1; j < roads.length; j++) {
        if (this.checkIntersection(roads[i], roads[j])) {
          intersectionCount++;
        }
      }
    }
    
    const result = `
      Road Network Analysis:
      Total Roads: ${roads.length}
      Total Length: ${totalLength.toFixed(2)}m
      Intersections: ${intersectionCount}
      Average Width: ${averageWidth.toFixed(1)}m
      Network Density: ${(roads.length / (totalLength / 1000)).toFixed(2)} roads/km
    `;
    
    this.showMeasurementResult(result);
  }

  private analyzeTrafficFlow(): void {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    
    let totalCapacity = 0;
    let bottleneckCount = 0;
    
    roads.forEach(road => {
      const lanes = road.get('lanes') || 2;
      const width = road.get('thickness') || 2;
      const capacity = lanes * 1000; // vehicles per hour per lane
      totalCapacity += capacity;
      
      if (width < 3 || lanes < 2) {
        bottleneckCount++;
      }
    });
    
    const result = `
      Traffic Flow Analysis:
      Total Capacity: ${totalCapacity.toLocaleString()} vehicles/hour
      Bottlenecks: ${bottleneckCount}
      Average Capacity: ${roads.length > 0 ? (totalCapacity / roads.length).toFixed(0) : 0} vehicles/hour
      Network Efficiency: ${((roads.length - bottleneckCount) / roads.length * 100).toFixed(1)}%
    `;
    
    this.showMeasurementResult(result);
  }

  private analyzeDrainage(): void {
    const features = this.vectorSource.getFeatures();
    const nallas = features.filter(f => f.get('type') === 'nalla');
    const waterExits = features.filter(f => f.get('type') === 'water_exit');
    
    let totalDrainageLength = 0;
    let totalCapacity = 0;
    
    nallas.forEach(nalla => {
      const geometry = nalla.getGeometry() as LineString;
      totalDrainageLength += geometry.getLength();
    });
    
    waterExits.forEach(exit => {
      totalCapacity += exit.get('capacity') || 1000;
    });
    
    const result = `
      Drainage Analysis:
      Nallas/Streams: ${nallas.length}
      Total Length: ${totalDrainageLength.toFixed(2)}m
      Water Exits: ${waterExits.length}
      Total Capacity: ${totalCapacity.toLocaleString()} L/hour
      Coverage: ${(totalDrainageLength / 1000).toFixed(2)} km
    `;
    
    this.showMeasurementResult(result);
  }

  private analyzeElevation(): void {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    
    let totalElevationChange = 0;
    let steepSegments = 0;
    
    roads.forEach(road => {
      const geometry = road.getGeometry() as LineString;
      const coords = geometry.getCoordinates();
      
      for (let i = 1; i < coords.length; i++) {
        const elevationChange = Math.abs(coords[i][2] - coords[i-1][2]) || 0;
        totalElevationChange += elevationChange;
        
        if (elevationChange > 5) { // 5m elevation change
          steepSegments++;
        }
      }
    });
    
    const result = `
      Elevation Analysis:
      Total Elevation Change: ${totalElevationChange.toFixed(2)}m
      Steep Segments (>5m): ${steepSegments}
      Average Grade: ${roads.length > 0 ? (totalElevationChange / roads.length).toFixed(2) : 0}m
      Terrain Difficulty: ${steepSegments > roads.length * 0.3 ? 'High' : 'Moderate'}
    `;
    
    this.showMeasurementResult(result);
  }

  private checkIntersection(road1: Feature, road2: Feature): boolean {
    const geom1 = road1.getGeometry() as LineString;
    const geom2 = road2.getGeometry() as LineString;
    
    const coords1 = geom1.getCoordinates();
    const coords2 = geom2.getCoordinates();
    
    // Simple intersection check
    for (let i = 0; i < coords1.length - 1; i++) {
      for (let j = 0; j < coords2.length - 1; j++) {
        if (this.linesIntersect(coords1[i], coords1[i+1], coords2[j], coords2[j+1])) {
          return true;
        }
      }
    }
    
    return false;
  }

  private linesIntersect(p1: number[], p2: number[], p3: number[], p4: number[]): boolean {
    return this.getSegmentIntersection(p1, p2, p3, p4) !== null;
  }

  /** Return intersection point [x,y] of segments p1-p2 and p3-p4, or null if none. */
  private getSegmentIntersection(p1: number[], p2: number[], p3: number[], p4: number[]): number[] | null {
    const x1 = p1[0], y1 = p1[1];
    const x2 = p2[0], y2 = p2[1];
    const x3 = p3[0], y3 = p3[1];
    const x4 = p4[0], y4 = p4[1];
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    if (Math.abs(denom) < 1e-10) return null;
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
    }
    return null;
  }

  private intersectionHighlightSource: VectorSource | null = null;
  private intersectionHighlightLayer: VectorLayer | null = null;

  /** Auto-detect line-line intersections and highlight them with point markers. */
  highlightJunctionIntersections(): void {
    if (this.intersectionHighlightLayer) {
      this.map.removeLayer(this.intersectionHighlightLayer);
      this.intersectionHighlightLayer = null;
      this.intersectionHighlightSource = null;
      this.showInstructions('Intersection highlights cleared');
      return;
    }
    const source = this.getCurrentDrawingSource();
    const features = source.getFeatures();
    const lines = features.filter(f => {
      const g = f.getGeometry();
      return g && (g.getType() === 'LineString' || g.getType() === 'MultiLineString');
    }) as Feature[];
    const points: number[][] = [];
    const tol = 0.1;
    for (let i = 0; i < lines.length; i++) {
      const coords1 = (lines[i].getGeometry() as LineString).getCoordinates();
      for (let j = i + 1; j < lines.length; j++) {
        const coords2 = (lines[j].getGeometry() as LineString).getCoordinates();
        for (let a = 0; a < coords1.length - 1; a++) {
          for (let b = 0; b < coords2.length - 1; b++) {
            const pt = this.getSegmentIntersection(coords1[a], coords1[a + 1], coords2[b], coords2[b + 1]);
            if (pt && !points.some(p => Math.abs(p[0] - pt[0]) < tol && Math.abs(p[1] - pt[1]) < tol)) {
              points.push(pt);
            }
          }
        }
      }
    }
    this.intersectionHighlightSource = new VectorSource();
    points.forEach(pt => {
      this.intersectionHighlightSource!.addFeature(new Feature({
        geometry: new Point(pt),
        type: 'junction-highlight'
      }));
    });
    this.intersectionHighlightLayer = new VectorLayer({
      source: this.intersectionHighlightSource,
      style: new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: 'rgba(231, 76, 60, 0.9)' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }),
        zIndex: 60
      }),
      zIndex: 60
    });
    this.map.addLayer(this.intersectionHighlightLayer);
    this.showInstructions(`${points.length} junction(s) detected and highlighted`);
  }

  // Export/Import Methods
  private exportRoadSpecs(): void {
    const features = this.vectorSource.getFeatures();
    const roadSpecs = features.map(feature => ({
      id: feature.getId(),
      type: feature.get('type'),
      geometry: this.format.writeGeometry(feature.getGeometry()!),
      properties: {
        thickness: feature.get('thickness'),
        lanes: feature.get('lanes'),
        material: feature.get('material'),
        status: feature.get('status'),
        width: feature.get('width'),
        depth: feature.get('depth')
      }
    }));
    
    const exportData = {
      format: this.exportFormat,
      timestamp: new Date().toISOString(),
      metadata: this.includeMetadata ? {
        project: 'NH-44 Delhi-Mumbai',
        version: '1.0',
        totalFeatures: roadSpecs.length
      } : undefined,
      features: roadSpecs
    };
    
    this.downloadFile(JSON.stringify(exportData, null, 2), 'road-specs.json');
    this.showInstructions('Road specifications exported successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private exportAnalysis(): void {
    const analysisData = {
      roadNetwork: this.getRoadNetworkStats(),
      trafficFlow: this.getTrafficFlowStats(),
      drainage: this.getDrainageStats(),
      elevation: this.getElevationStats(),
      timestamp: new Date().toISOString()
    };
    
    this.downloadFile(JSON.stringify(analysisData, null, 2), 'analysis-report.json');
    this.showInstructions('Analysis report exported successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private importCAD(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.dxf,.dwg';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.processCADFile(file);
      }
    };
    input.click();
  }

  private importGIS(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.kml,.shp';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.processGISFile(file);
      }
    };
    input.click();
  }

  private processCADFile(file: File): void {
    // Mock CAD file processing
    this.showInstructions('CAD file processing not implemented in demo');
    setTimeout(() => this.hideInstructions(), 3000);
  }

  private processGISFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        this.importGeoJSONData(data);
        this.showInstructions('GIS file imported successfully');
        setTimeout(() => this.hideInstructions(), 2000);
      } catch (error) {
        this.showInstructions('Error importing GIS file');
        setTimeout(() => this.hideInstructions(), 3000);
      }
    };
    reader.readAsText(file);
  }

  private importGeoJSONData(data: any): void {
    if (data.features) {
      data.features.forEach((featureData: any) => {
        const features = this.format.readFeatures(featureData);
        features.forEach(feature => {
          this.getCurrentDrawingSource().addFeature(feature);
        });
      });
    }
  }

  // Advanced Tools Methods — draw a single road from Advanced tab specifications
  private autoGenerateRoads(): void {
    this.startAutoRoadFromSpecsDrawing();
  }

  /** Read road specification fields from the Advanced tab into currentRoadSpecs. */
  private readRoadSpecsFromForm(): typeof this.currentRoadSpecs {
    this.updateRoadSpecs();
    const roadTypeEl = document.getElementById('roadType') as HTMLSelectElement | null;
    if (roadTypeEl?.value) {
      this.currentRoadSpecs.type = roadTypeEl.value;
    }
    return { ...this.currentRoadSpecs };
  }

  /** Apply default width/lanes/depth when road type changes. */
  private applyRoadTypePresetToForm(): void {
    const type =
      (document.getElementById('roadType') as HTMLSelectElement)?.value || 'highway';
    const preset =
      GisavimvpComponent.ROAD_TYPE_DEFAULTS[type] ||
      GisavimvpComponent.ROAD_TYPE_DEFAULTS['highway'];
    const widthEl = document.getElementById('roadWidth') as HTMLInputElement | null;
    const depthEl = document.getElementById('roadDepth') as HTMLInputElement | null;
    const lanesEl = document.getElementById('roadLanes') as HTMLSelectElement | null;
    if (widthEl) widthEl.value = String(preset.width);
    if (depthEl) depthEl.value = String(preset.depth);
    if (lanesEl) lanesEl.value = String(preset.lanes);
    this.updateRoadSpecs();
  }

  /** Effective carriageway width (m): max of form width and lanes × 3.5 m standard lane. */
  private effectiveRoadWidthM(specs: typeof this.currentRoadSpecs): number {
    const laneStandardM = 3.5;
    const fromLanes = Math.max(1, specs.lanes) * laneStandardM;
    return Math.max(specs.width || fromLanes, fromLanes);
  }

  private getRoadSurfacePalette(surface: string): { fill: string; stroke: string; center: string } {
    return (
      GisavimvpComponent.ROAD_SURFACE_STYLES[surface] ||
      GisavimvpComponent.ROAD_SURFACE_STYLES['bituminous']
    );
  }

  private createRoadPreviewStyles(
    specs: typeof this.currentRoadSpecs,
    widthM: number
  ): Style[] {
    const palette = this.getRoadSurfacePalette(specs.surface);
    return [
      new Style({
        stroke: new Stroke({
          color: palette.center,
          width: 3,
          lineDash: [12, 8],
        }),
      }),
      new Style({
        text: new TextStyle({
          text: `${specs.type} · ${widthM}m · ${specs.lanes}L`,
          font: '11px Arial',
          fill: new Fill({ color: '#fff' }),
          stroke: new Stroke({ color: '#000', width: 2 }),
          offsetY: -14,
        }),
      }),
    ];
  }

  private createRoadSurfaceStyle(
    specs: typeof this.currentRoadSpecs
  ): Style {
    const palette = this.getRoadSurfacePalette(specs.surface);
    return new Style({
      fill: new Fill({ color: palette.fill }),
      stroke: new Stroke({ color: palette.stroke, width: 2 }),
    });
  }

  private createRoadCenterlineStyle(
    specs: typeof this.currentRoadSpecs,
    label: string
  ): Style[] {
    const palette = this.getRoadSurfacePalette(specs.surface);
    return [
      new Style({
        stroke: new Stroke({
          color: palette.center,
          width: 2,
          lineDash: specs.lanes > 1 ? [10, 8] : undefined,
        }),
      }),
      new Style({
        text: new TextStyle({
          text: label,
          font: '11px Arial',
          fill: new Fill({ color: '#fff' }),
          stroke: new Stroke({ color: '#111', width: 2 }),
          placement: 'line',
          overflow: true,
        }),
      }),
    ];
  }

  /** Buffer centerline to road surface polygon using Turf (WGS84 geodesic). */
  private buildRoadCorridorPolygon(
    centerline: LineString,
    widthM: number
  ): Polygon | null {
    if (typeof turf === 'undefined') {
      return null;
    }
    try {
      const fmt = new GeoJSON();
      const feature = new Feature({ geometry: centerline.clone() });
      const geoJson = fmt.writeFeatureObject(feature, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      const halfWidthKm = widthM / 2 / 1000;
      const buffered = turf.buffer(geoJson, halfWidthKm, {
        units: 'kilometers',
        steps: 24,
      });
      if (!buffered?.geometry) {
        return null;
      }
      const geom = fmt.readGeometry(buffered.geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });
      return geom instanceof Polygon ? geom : null;
    } catch (e) {
      console.warn('Road corridor buffer failed', e);
      return null;
    }
  }

  /** Lane divider lines offset inside the carriageway (requires Turf lineOffset). */
  private addLaneMarkingsFromSpecs(
    centerline: LineString,
    specs: typeof this.currentRoadSpecs,
    totalWidthM: number
  ): void {
    const lanes = Math.max(1, specs.lanes);
    if (lanes < 2 || typeof turf === 'undefined' || !turf.lineOffset) {
      return;
    }
    const fmt = new GeoJSON();
    const base = fmt.writeFeatureObject(new Feature({ geometry: centerline.clone() }), {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    const laneWidthM = totalWidthM / lanes;
    for (let i = 1; i < lanes; i++) {
      const offsetM = -totalWidthM / 2 + i * laneWidthM;
      try {
        const offsetKm = offsetM / 1000;
        const offsetGeo = turf.lineOffset(base, offsetKm, { units: 'kilometers' });
        if (!offsetGeo?.geometry) {
          continue;
        }
        const line = fmt.readGeometry(offsetGeo.geometry, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        });
        if (!(line instanceof LineString)) {
          continue;
        }
        const marking = new Feature({ geometry: line });
        marking.set('type', 'road_lane_marking');
        marking.set('roadSpecs', { ...specs });
        marking.setStyle(
          new Style({
            stroke: new Stroke({
              color: 'rgba(255, 255, 255, 0.9)',
              width: 1.5,
              lineDash: [6, 10],
            }),
          })
        );
        this.roadSource.addFeature(marking);
      } catch (err) {
        console.warn('Lane marking offset failed', err);
      }
    }
  }

  /** Build road surface polygon + centerline + lane markings from a drawn centerline. */
  private finalizeAutoRoadFromCenterline(centerline: LineString): void {
    const specs = this.readRoadSpecsFromForm();
    const widthM = this.effectiveRoadWidthM(specs);
    const lengthM = getGeodesicLength(centerline, { projection: 'EPSG:3857' });
    const label = `${specs.type} · ${widthM.toFixed(1)}m · ${specs.lanes}L · ${lengthM.toFixed(0)}m`;

    const corridor = this.buildRoadCorridorPolygon(centerline, widthM);
    if (corridor) {
      const surface = new Feature({ geometry: corridor });
      surface.set('type', 'road_surface');
      surface.set('roadSpecs', { ...specs, widthM, lengthM, depthM: specs.depth });
      surface.setStyle(this.createRoadSurfaceStyle(specs));
      this.roadSource.addFeature(surface);
    }

    this.addLaneMarkingsFromSpecs(centerline, specs, widthM);

    const centerFeature = new Feature({ geometry: centerline.clone() });
    centerFeature.set('type', 'road_centerline');
    centerFeature.set('roadSpecs', { ...specs, widthM, lengthM, depthM: specs.depth });
    centerFeature.setStyle(this.createRoadCenterlineStyle(specs, label));
    this.roadSource.addFeature(centerFeature);

    this.saveToHistory();
    const ext = corridor?.getExtent() || centerline.getExtent();
    if (ext && ext[0] !== Infinity) {
      this.map.getView().fit(ext, { padding: [48, 48, 48, 48], maxZoom: 18, duration: 350 });
    }

    this.showInstructions(
      `Road drawn: ${specs.type}, ${widthM.toFixed(1)} m wide, ${specs.lanes} lane(s), ${specs.surface}, ${lengthM.toFixed(1)} m long`
    );
    setTimeout(() => this.hideInstructions(), 4000);
  }

  /**
   * Interactive draw: click centerline on map; road width/lanes/surface from specification form.
   */
  startAutoRoadFromSpecsDrawing(): void {
    this.removeInteractions();
    this.readRoadSpecsFromForm();
    const specs = { ...this.currentRoadSpecs };
    const widthM = this.effectiveRoadWidthM(specs);

    this.showInstructions(
      `Auto road (${specs.type}): ${widthM.toFixed(1)} m × ${specs.lanes} lane(s), ${specs.surface}. Click centerline — double-click to finish.`
    );

    this.drawInteraction = new Draw({
      source: this.roadSource,
      type: 'LineString',
      style: () => this.createRoadPreviewStyles(specs, widthM),
    });

    this.drawInteraction.on('drawstart', (e: { feature: Feature }) =>
      this.setupRealtimeLineLength(e.feature)
    );
    this.drawInteraction.on('drawend', (e: { feature: Feature }) => {
      this.clearRealtimeLineLength(e.feature);
      const geom = e.feature.getGeometry();
      if (geom instanceof LineString && geom.getCoordinates().length >= 2) {
        this.roadSource.removeFeature(e.feature);
        this.finalizeAutoRoadFromCenterline(geom);
      }
      this.removeInteractions();
    });
    this.drawInteraction.on('drawabort', () => this.clearRealtimeLineLength(null));

    this.map.addInteraction(this.drawInteraction);
    this.applySnapState();
  }

  private generateMockRoadNetwork(): Feature[] {
    const roads: Feature[] = [];
    const center = this.map.getView().getCenter()!;
    
    // Generate a simple grid network
    for (let i = 0; i < 5; i++) {
      const startX = center[0] + (i - 2) * 1000;
      const endX = center[0] + (i - 2) * 1000;
      const startY = center[1] - 2000;
      const endY = center[1] + 2000;
      
      const road = new Feature({
        geometry: new LineString([[startX, startY], [endX, endY]]),
        type: 'auto_generated_road',
        thickness: 3,
        lanes: 2
      });
      
      road.setStyle(new Style({
        stroke: new Stroke({
          color: '#ff6b6b',
          width: 3
        })
      }));
      
      roads.push(road);
    }
    
    return roads;
  }

  private optimizeNetwork(): void {
    this.showInstructions('Optimizing road network...');
    
    setTimeout(() => {
      const features = this.vectorSource.getFeatures();
      const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
      
      let optimized = 0;
      roads.forEach(road => {
        // Mock optimization - adjust thickness based on lanes
        const lanes = road.get('lanes') || 2;
        const optimalThickness = lanes * 1.5;
        road.set('thickness', optimalThickness);
        optimized++;
      });
      
      this.showInstructions(`Optimized ${optimized} road segments`);
      setTimeout(() => this.hideInstructions(), 3000);
    }, 2000);
  }

  private validateGeometry(): void {
    const features = this.vectorSource.getFeatures();
    const issues: string[] = [];
    
    features.forEach(feature => {
      const geometry = feature.getGeometry();
      if (geometry) {
        // Check for self-intersections
        if (geometry.getType() === 'LineString') {
          const coords = (geometry as LineString).getCoordinates();
          if (this.hasSelfIntersection(coords)) {
            issues.push(`Self-intersection in feature ${feature.getId()}`);
          }
        }
        
        // Check for duplicate coordinates
        if (this.hasDuplicateCoordinates(geometry)) {
          issues.push(`Duplicate coordinates in feature ${feature.getId()}`);
        }
      }
    });
    
    if (issues.length === 0) {
      this.showInstructions('No geometry issues found');
    } else {
      this.showInstructions(`Found ${issues.length} geometry issues`);
      console.log('Geometry Issues:', issues);
    }
    
    setTimeout(() => this.hideInstructions(), 3000);
  }

  private repairGeometry(): void {
    this.showInstructions('Repairing geometry issues...');
    
    setTimeout(() => {
      const features = this.vectorSource.getFeatures();
      let repaired = 0;
      
      features.forEach(feature => {
        const geometry = feature.getGeometry();
        if (geometry && this.hasDuplicateCoordinates(geometry)) {
          // Remove duplicate coordinates
          if (geometry.getType() === 'LineString') {
            const coords = (geometry as LineString).getCoordinates();
            const uniqueCoords = this.removeDuplicateCoordinates(coords);
            feature.setGeometry(new LineString(uniqueCoords));
            repaired++;
          }
        }
      });
      
      this.showInstructions(`Repaired ${repaired} geometry issues`);
      setTimeout(() => this.hideInstructions(), 3000);
    }, 2000);
  }

  private addCulvert(): void {
    this.removeInteractions();
    this.showInstructions('Click to place culvert');
    
    this.map.on('click', (event: any) => {
      const culvertFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'culvert',
        diameter: 1.5, // meters
        material: 'concrete',
        capacity: 5000 // liters per hour
      });
      
      const style = new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#8b4513' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }),
        text: new Text({
          text: 'C',
          font: '12px Arial',
          fill: new Fill({ color: '#fff' }),
          offsetY: -15
        })
      });
      
      culvertFeature.setStyle(style);
      this.getCurrentDrawingSource().addFeature(culvertFeature);
      
      this.hideInstructions();
      this.showInstructions('Culvert added successfully');
      setTimeout(() => this.hideInstructions(), 2000);
    });
  }

  // Helper Methods
  private downloadFile(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private getRoadNetworkStats(): any {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    return {
      totalRoads: roads.length,
      totalLength: roads.reduce((sum, road) => sum + (road.getGeometry() as LineString).getLength(), 0)
    };
  }

  private getTrafficFlowStats(): any {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    return {
      totalCapacity: roads.reduce((sum, road) => sum + ((road.get('lanes') || 2) * 1000), 0),
      bottlenecks: roads.filter(road => (road.get('thickness') || 2) < 3).length
    };
  }

  private getDrainageStats(): any {
    const features = this.vectorSource.getFeatures();
    const nallas = features.filter(f => f.get('type') === 'nalla');
    const exits = features.filter(f => f.get('type') === 'water_exit');
    return {
      nallas: nallas.length,
      waterExits: exits.length,
      totalCapacity: exits.reduce((sum, exit) => sum + (exit.get('capacity') || 1000), 0)
    };
  }

  private getElevationStats(): any {
    const features = this.vectorSource.getFeatures();
    const roads = features.filter(f => f.get('type') === 'LineString' || f.get('type') === 'curve');
    return {
      totalElevationChange: roads.reduce((sum, road) => {
        const coords = (road.getGeometry() as LineString).getCoordinates();
        return sum + coords.reduce((elevSum, coord, i) => {
          return i > 0 ? elevSum + Math.abs(coord[2] - coords[i-1][2]) : elevSum;
        }, 0);
      }, 0)
    };
  }

  private hasSelfIntersection(coords: number[][]): boolean {
    for (let i = 0; i < coords.length - 3; i++) {
      for (let j = i + 2; j < coords.length - 1; j++) {
        if (this.linesIntersect(coords[i], coords[i+1], coords[j], coords[j+1])) {
          return true;
        }
      }
    }
    return false;
  }

  private hasDuplicateCoordinates(geometry: any): boolean {
    if (geometry.getType() === 'LineString') {
      const coords = geometry.getCoordinates();
      for (let i = 1; i < coords.length; i++) {
        if (coords[i][0] === coords[i-1][0] && coords[i][1] === coords[i-1][1]) {
          return true;
        }
      }
    }
    return false;
  }

  private removeDuplicateCoordinates(coords: number[][]): number[][] {
    return coords.filter((coord, index) => {
      if (index === 0) return true;
      const prev = coords[index - 1];
      return coord[0] !== prev[0] || coord[1] !== prev[1];
    });
  }

  private setupSurveyControls(): void {
    // GPS controls
    const enableGPSBtn = document.getElementById('enableGPS');
    if (enableGPSBtn) {
      enableGPSBtn.addEventListener('click', () => this.enableGPS());
    }

    const addSurveyPointBtn = document.getElementById('addSurveyPoint');
    if (addSurveyPointBtn) {
      addSurveyPointBtn.addEventListener('click', () => this.addSurveyPoint());
    }

    const addSurveyAreaBtn = document.getElementById('addSurveyArea');
    if (addSurveyAreaBtn) {
      addSurveyAreaBtn.addEventListener('click', () => this.addSurveyArea());
    }

    // Testing controls
    const passTestBtn = document.getElementById('passTest');
    if (passTestBtn) {
      passTestBtn.addEventListener('click', () => this.passTest());
    }

    const failTestBtn = document.getElementById('failTest');
    if (failTestBtn) {
      failTestBtn.addEventListener('click', () => this.failTest());
    }
  }

  private setupManagementControls(): void {
    const showProjectParamsBtn = document.getElementById('showProjectParams');
    if (showProjectParamsBtn) {
      showProjectParamsBtn.addEventListener('click', () => this.showProjectParams());
    }
    const generateProjectReportBtn = document.getElementById('generateProjectReport');
    if (generateProjectReportBtn) {
      generateProjectReportBtn.addEventListener('click', () => this.generateProjectReport());
    }
    const refreshReportStatusBtn = document.getElementById('refreshReportStatus');
    if (refreshReportStatusBtn) {
      refreshReportStatusBtn.addEventListener('click', () => this.refreshReportStatus());
    }
  }

  private setupBiometricControls(): void {
    const markAttendanceBtn = document.getElementById('markAttendance');
    if (markAttendanceBtn) {
      markAttendanceBtn.addEventListener('click', () => this.markAttendance());
    }

    const viewAttendanceBtn = document.getElementById('viewAttendance');
    if (viewAttendanceBtn) {
      viewAttendanceBtn.addEventListener('click', () => this.viewAttendance());
    }

    const exportAttendanceBtn = document.getElementById('exportAttendance');
    if (exportAttendanceBtn) {
      exportAttendanceBtn.addEventListener('click', () => this.exportAttendance());
    }

    const attendanceMapBtn = document.getElementById('attendanceMap');
    if (attendanceMapBtn) {
      attendanceMapBtn.addEventListener('click', () => this.showAttendanceMap());
    }

    const setWorkZoneBtn = document.getElementById('setWorkZone');
    if (setWorkZoneBtn) {
      setWorkZoneBtn.addEventListener('click', () => this.setWorkZone());
    }

    const trackWorkersBtn = document.getElementById('trackWorkers');
    if (trackWorkersBtn) {
      trackWorkersBtn.addEventListener('click', () => this.trackWorkers());
    }
  }

  private setupSettingsControls(): void {
    const saveDefaultsBtn = document.getElementById('saveDefaults');
    if (saveDefaultsBtn) {
      saveDefaultsBtn.addEventListener('click', () => this.saveDefaults());
    }

    const loadDefaultsBtn = document.getElementById('loadDefaults');
    if (loadDefaultsBtn) {
      loadDefaultsBtn.addEventListener('click', () => this.loadDefaults());
    }

    const resetDefaultsBtn = document.getElementById('resetDefaults');
    if (resetDefaultsBtn) {
      resetDefaultsBtn.addEventListener('click', () => this.resetDefaults());
    }

    // Settings panel controls
    const settingsBtn = document.getElementById('settingsBtn');
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => this.toggleSettingsPanel());
    }

    const applySettingsBtn = document.getElementById('applySettings');
    if (applySettingsBtn) {
      applySettingsBtn.addEventListener('click', () => this.applySettings());
    }

    const closeSettingsBtn = document.getElementById('closeSettings');
    if (closeSettingsBtn) {
      closeSettingsBtn.addEventListener('click', () => this.closeSettingsPanel());
    }
  }

  // Survey methods
  private enableGPS(): void {
    if (navigator.geolocation) {
      this.gpsWatchId = navigator.geolocation.watchPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          
          // Update GPS accuracy display
          const gpsAccuracyElement = document.getElementById('gpsAccuracy');
          if (gpsAccuracyElement) {
            gpsAccuracyElement.textContent = accuracy.toFixed(1);
          }
          
          // Add GPS point to map
          const gpsFeature = new Feature({
            geometry: new Point(fromLonLat([lon, lat])),
            type: 'gps',
            accuracy: accuracy,
            timestamp: new Date()
          });
          
          this.gpsSource.addFeature(gpsFeature);
        },
        (error) => {
          console.error('GPS Error:', error);
        },
        { enableHighAccuracy: true }
      );
    }
  }

  private addSurveyPoint(): void {
    this.removeInteractions();
    this.map.on('click', (event: any) => {
      const surveyFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'survey',
        timestamp: new Date(),
        info: 'Survey Point'
      });
      
      this.getCurrentDrawingSource().addFeature(surveyFeature);
      this.updateSurveyPointCount();
      this.saveToHistory();
    });
  }

  private addSurveyArea(): void {
    this.removeInteractions();
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(52, 152, 219, 0.2)'
        }),
        stroke: new Stroke({
          color: '#3498db',
          width: 2
        })
      })
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'survey-area');
      feature.set('timestamp', new Date());
      feature.set('info', 'Survey Area');
      this.updateSurveyPointCount();
      this.saveToHistory();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private updateSurveyPointCount(): void {
    const surveyPoints = this.vectorSource.getFeatures().filter(f => f.get('type') === 'survey');
    const surveyPointCountElement = document.getElementById('surveyPointCount');
    if (surveyPointCountElement) {
      surveyPointCountElement.textContent = surveyPoints.length.toString();
    }
  }

  private passTest(): void {
    const testsPassedElement = document.getElementById('testsPassed');
    const testsPendingElement = document.getElementById('testsPending');
    
    if (testsPassedElement && testsPendingElement) {
      const passed = parseInt(testsPassedElement.textContent || '0') + 1;
      const pending = parseInt(testsPendingElement.textContent || '0') - 1;
      
      testsPassedElement.textContent = passed.toString();
      testsPendingElement.textContent = pending.toString();
    }
  }

  private failTest(): void {
    const testsFailedElement = document.getElementById('testsFailed');
    const testsPendingElement = document.getElementById('testsPending');
    
    if (testsFailedElement && testsPendingElement) {
      const failed = parseInt(testsFailedElement.textContent || '0') + 1;
      const pending = parseInt(testsPendingElement.textContent || '0') - 1;
      
      testsFailedElement.textContent = failed.toString();
      testsPendingElement.textContent = pending.toString();
    }
  }

  // Management methods
  private showProjectParams(): void {
    const parameterDisplay = document.getElementById('parameterDisplay');
    if (parameterDisplay) {
      parameterDisplay.style.display = parameterDisplay.style.display === 'none' ? 'block' : 'none';
    }
  }

  getBudgetOverrun(): string | null {
    if (!this.projectData?.budget_amount || this.projectSpent == null) return null;
    const budget = parseFloat(String(this.projectData.budget_amount));
    if (isNaN(budget) || budget <= 0) return null;
    if (this.projectSpent > budget) {
      const over = this.projectSpent - budget;
      return `₹${over.toLocaleString('en-IN', { maximumFractionDigits: 0 })} over budget`;
    }
    return null;
  }

  getTimeOverrun(): string | null {
    if (!this.projectData?.end_date) return null;
    const endDate = new Date(this.projectData.end_date);
    if (isNaN(endDate.getTime())) return null;
    const now = new Date();
    if (endDate >= now) return null;
    const completion = parseFloat(String(this.projectData.unit_completion_rate ?? this.projectData.foundation_progress ?? 0)) || 0;
    if (completion >= 100) return null;
    const daysPast = Math.floor((now.getTime() - endDate.getTime()) / (24 * 60 * 60 * 1000));
    return `${daysPast} days past due`;
  }

  generateProjectReport(): void {
    if (!this.projectId) {
      this.showInstructions('Load a project first.');
      return;
    }
    this.router.navigate(['/home/boq'], { queryParams: { project_id: this.projectId, report: '1' } });
  }

  refreshReportStatus(): void {
    this.reportStatus = `Last checked: ${new Date().toLocaleTimeString()}`;
  }

  private fetchProjectBillsForOverrun(): void {
    if (!this.projectId) return;
    this.projectSpent = null;
    this.apiService.getProjectBills({ project_id: this.projectId, limit: 100 }).subscribe({
      next: (res: any) => {
        if (res?.success && Array.isArray(res.data)) {
          const total = res.data.reduce((sum: number, b: any) => sum + (parseFloat(b.bill_amount) || 0), 0);
          this.projectSpent = total;
        }
      },
      error: () => {}
    });
  }

  // Biometric methods
  private markAttendance(): void {
    const presentCountElement = document.getElementById('presentCount');
    if (presentCountElement) {
      const present = parseInt(presentCountElement.textContent || '0') + 1;
      presentCountElement.textContent = present.toString();
    }
  }

  private viewAttendance(): void {
    console.log('Viewing attendance details...');
  }

  private exportAttendance(): void {
    console.log('Exporting attendance data...');
  }

  private showAttendanceMap(): void {
    console.log('Showing attendance map...');
  }

  private setWorkZone(): void {
    this.removeInteractions();
    this.map.on('click', (event: any) => {
      if (!this.workZone) {
        // Start creating work zone
        this.workZone = {
          center: event.coordinate,
          radius: 100 // meters
        };
      } else {
        // Complete work zone
        this.workZone.radius = this.calculateDistance(this.workZone.center, event.coordinate);
        this.createWorkZoneFeature();
        this.workZone = null;
      }
    });
  }

  private createWorkZoneFeature(): void {
    if (this.workZone) {
      const workZoneFeature = new Feature({
        geometry: new Circle(this.workZone.center, this.workZone.radius),
        type: 'work-zone',
        info: 'Work Zone'
      });
      
      this.getCurrentDrawingSource().addFeature(workZoneFeature);
      this.saveToHistory();
    }
  }

  private calculateDistance(coord1: number[], coord2: number[]): number {
    // Simple distance calculation
    const dx = coord1[0] - coord2[0];
    const dy = coord1[1] - coord2[1];
    return Math.sqrt(dx * dx + dy * dy);
  }

  private trackWorkers(): void {
    console.log('Tracking workers...');
  }

  // Settings methods
  private toggleSettingsPanel(): void {
    const settingsPanel = document.getElementById('settingsPanel');
    if (settingsPanel) {
      settingsPanel.style.display = settingsPanel.style.display === 'none' ? 'block' : 'none';
    }
  }

  private applySettings(): void {
    // Apply settings from the settings panel
    const snapToGrid = (document.getElementById('snapToGrid') as HTMLInputElement)?.checked;
    const showCoords = (document.getElementById('showCoords') as HTMLInputElement)?.checked;
    const autoSave = (document.getElementById('autoSave') as HTMLInputElement)?.checked;
    const defaultLineColor = (document.getElementById('defaultLineColor') as HTMLInputElement)?.value;
    const backgroundColor = (document.getElementById('backgroundColor') as HTMLInputElement)?.value;
    
    this.snapEnabled = !!snapToGrid;
    this.defaultSettings.lineColor = defaultLineColor || this.defaultSettings.lineColor;
    this.defaultSettings.backgroundColor = backgroundColor || this.defaultSettings.backgroundColor;
    this.applySnapState();
    console.log('Settings applied:', { snapToGrid, showCoords, autoSave, defaultLineColor, backgroundColor });
  }

  /** Collect all vector sources for Snap interaction. */
  private getSnapSources(): VectorSource[] {
    const sources: VectorSource[] = [
      this.vectorSource,
      this.roadSource,
      this.projectSource,
      this.biometricSource,
      this.gpsSource,
      this.waterSource,
      this.separatorSource
    ];
    this.layerOrder.forEach(layerId => {
      const layer = this.layerRegistry[layerId];
      if (layer?.getSource() instanceof VectorSource) {
        sources.push(layer.getSource() as VectorSource);
      }
    });
    return sources;
  }

  /** Add or remove Snap interaction based on snapEnabled. One Snap per vector source. */
  private applySnapState(): void {
    this.snapInteractions.forEach(s => this.map.removeInteraction(s));
    this.snapInteractions = [];
    if (!this.snapEnabled || !this.map) return;
    const tolerance = this.defaultSettings.snapTolerance ?? 15;
    this.getSnapSources().forEach(source => {
      const snap = new Snap({
        source,
        pixelTolerance: tolerance
      });
      this.map.addInteraction(snap);
      this.snapInteractions.push(snap);
    });
  }

  private closeSettingsPanel(): void {
    const settingsPanel = document.getElementById('settingsPanel');
    if (settingsPanel) {
      settingsPanel.style.display = 'none';
    }
  }

  private saveDefaults(): void {
    localStorage.setItem('gisEditorDefaults', JSON.stringify(this.defaultSettings));
    console.log('Defaults saved');
  }

  private loadDefaults(): void {
    const saved = localStorage.getItem('gisEditorDefaults');
    if (saved) {
      this.defaultSettings = { ...this.defaultSettings, ...JSON.parse(saved) };
      console.log('Defaults loaded');
    }
  }

  private resetDefaults(): void {
    localStorage.removeItem('gisEditorDefaults');
    this.defaultSettings = {
      center: [77.2090, 28.6139],
      zoom: 10,
      snapTolerance: 15,
      autoSaveInterval: 5,
      showGrid: false,
      showScaleBar: true,
      showNorthArrow: true,
      measurementUnits: 'metric',
      lineColor: '#3498db',
      backgroundColor: '#f0f2f5'
    };
    console.log('Defaults reset');
  }

  private setupLayerManagement(): void {
    // Layer toggle controls
    const drawingLayerToggle = document.getElementById('drawingLayerToggle');
    if (drawingLayerToggle) {
      drawingLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('drawing', target.checked);
      });
    }

    const roadLayerToggle = document.getElementById('roadLayerToggle');
    if (roadLayerToggle) {
      roadLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('road', target.checked);
      });
    }

    const projectLayerToggle = document.getElementById('projectLayerToggle');
    if (projectLayerToggle) {
      projectLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('project', target.checked);
      });
    }

    const biometricLayerToggle = document.getElementById('biometricLayerToggle');
    if (biometricLayerToggle) {
      biometricLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('biometric', target.checked);
      });
    }

    const gpsLayerToggle = document.getElementById('gpsLayerToggle');
    if (gpsLayerToggle) {
      gpsLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('gps', target.checked);
      });
    }

    const waterLayerToggle = document.getElementById('waterLayerToggle');
    if (waterLayerToggle) {
      waterLayerToggle.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.toggleLayerEnhanced('water', target.checked);
      });
    }

    // Layer opacity controls
    const drawingOpacity = document.getElementById('drawingOpacity');
    if (drawingOpacity) {
      drawingOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('drawing', parseFloat(target.value) / 100);
      });
    }

    const roadOpacity = document.getElementById('roadOpacity');
    if (roadOpacity) {
      roadOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('road', parseFloat(target.value) / 100);
      });
    }

    const projectOpacity = document.getElementById('projectOpacity');
    if (projectOpacity) {
      projectOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('project', parseFloat(target.value) / 100);
      });
    }

    const biometricOpacity = document.getElementById('biometricOpacity');
    if (biometricOpacity) {
      biometricOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('biometric', parseFloat(target.value) / 100);
      });
    }

    const gpsLayerOpacity = document.getElementById('gpsLayerOpacity');
    if (gpsLayerOpacity) {
      gpsLayerOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('gps', parseFloat(target.value) / 100);
      });
    }

    const waterOpacity = document.getElementById('waterOpacity');
    if (waterOpacity) {
      waterOpacity.addEventListener('input', (event) => {
        const target = event.target as HTMLInputElement;
        this.setLayerOpacityEnhanced('water', parseFloat(target.value) / 100);
      });
    }

    // Initialize enhanced layer management UI
    this.updateLayerManagementUI();
    this.setupEnhancedLayerManagementEvents();
  }

  private setupEnhancedLayerManagementEvents(): void {
    // Layer Management Controls
    const addNewLayerBtn = document.getElementById('addNewLayer');
    if (addNewLayerBtn) {
      addNewLayerBtn.addEventListener('click', () => {
        const layerName = prompt('Enter layer name:');
        if (layerName && layerName.trim()) {
          this.addLayer(layerName.trim());
        }
      });
    }

    const importLayerBtn = document.getElementById('importLayer');
    if (importLayerBtn) {
      importLayerBtn.addEventListener('click', () => {
        this.showImportLayerDialog();
      });
    }

    const exportAllLayersBtn = document.getElementById('exportAllLayers');
    if (exportAllLayersBtn) {
      exportAllLayersBtn.addEventListener('click', () => {
        this.exportAllLayers();
      });
    }

    const createGroupBtn = document.getElementById('createGroup');
    if (createGroupBtn) {
      createGroupBtn.addEventListener('click', () => {
        this.showCreateGroupDialog();
      });
    }

    // Layer search
    const layerSearchInput = document.getElementById('layerSearchInput');
    if (layerSearchInput) {
      layerSearchInput.addEventListener('input', (event) => {
        const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
        this.filterLayersBySearch(searchTerm);
      });
    }
  }

  private showAddLayerDialog(): void {
    const dialog = document.createElement('div');
    dialog.className = 'add-layer-dialog';
    dialog.innerHTML = `
      <div class="dialog-content">
        <h3>Add New Layer</h3>
        <div class="form-group">
          <label>Layer Name:</label>
          <input type="text" id="newLayerName" placeholder="Enter layer name">
        </div>
        <div class="form-group">
          <label>Layer Type:</label>
          <select id="newLayerType">
            <option value="vector">Vector Layer</option>
            <option value="tile">Tile Layer</option>
            <option value="image">Image Layer</option>
          </select>
        </div>
        <div class="form-group">
          <label>Group:</label>
          <select id="newLayerGroup">
            <option value="">No Group</option>
            ${Object.keys(this.layerGroups).map(group => 
              `<option value="${group}">${group}</option>`
            ).join('')}
          </select>
        </div>
        <div class="button-group">
          <button id="createLayer" class="btn">Create</button>
          <button id="cancelAddLayer" class="btn">Cancel</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    document.getElementById('createLayer')?.addEventListener('click', () => {
      const name = (document.getElementById('newLayerName') as HTMLInputElement)?.value;
      const type = (document.getElementById('newLayerType') as HTMLSelectElement)?.value;
      const group = (document.getElementById('newLayerGroup') as HTMLSelectElement)?.value;
      
      if (name) {
        this.createNewLayer(name, type, group);
        document.body.removeChild(dialog);
      }
    });
    
    document.getElementById('cancelAddLayer')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });
  }

  createNewLayer(name: string, type: string, group: string, paletteId?: string): void {
    const newSource = new VectorSource();
    const layerId = name;
    const pid = paletteId || 'default';
    if (!this.layerStyles[layerId]) {
      this.layerStyles[layerId] = {
        strokeColor: '#3498db',
        strokeWidth: 2,
        fillColor: 'rgba(52, 152, 219, 0.2)',
        paletteId: pid
      };
    } else if (this.layerStyles[layerId]) {
      this.layerStyles[layerId].paletteId = pid;
    }
    this.layerPaletteId[layerId] = pid;
    const newLayer = new VectorLayer({
      source: newSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(layerId, feature, resolution)
    });
    
    this.registerLayer(name, newLayer);
    this.map.addLayer(newLayer);
    
    if (group) {
      if (!this.layerGroups[group]) this.layerGroups[group] = [];
      this.layerGroups[group].push(name);
    }
    
    this.updateLayerManagementUI();
  }

  private showImportLayerOptions(geoJsonContent: string): void {
    const dialog = document.createElement('div');
    dialog.className = 'import-layer-dialog';
    dialog.innerHTML = `
      <div class="dialog-content">
        <h3>Import Layer</h3>
        <div class="form-group">
          <label>Layer Name:</label>
          <input type="text" id="importLayerName" placeholder="Enter layer name">
        </div>
        <div class="form-group">
          <label>Group:</label>
          <select id="importLayerGroup">
            <option value="">No Group</option>
            ${Object.keys(this.layerGroups).map(group => 
              `<option value="${group}">${group}</option>`
            ).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>Import Options:</label>
          <label><input type="checkbox" id="clearExisting" checked> Clear existing features</label>
        </div>
        <div class="button-group">
          <button id="importLayer" class="btn">Import</button>
          <button id="cancelImport" class="btn">Cancel</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    document.getElementById('importLayer')?.addEventListener('click', () => {
      const name = (document.getElementById('importLayerName') as HTMLInputElement)?.value;
      const group = (document.getElementById('importLayerGroup') as HTMLSelectElement)?.value;
      const clearExisting = (document.getElementById('clearExisting') as HTMLInputElement)?.checked;
      
      if (name) {
        this.importLayerToNewLayer(name, geoJsonContent, group, clearExisting);
        document.body.removeChild(dialog);
      }
    });
    
    document.getElementById('cancelImport')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });
  }

  private importLayerToNewLayer(name: string, geoJsonContent: string, group: string, clearExisting: boolean): void {
    const newSource = new VectorSource();
    this.layerPaletteId[name] = 'default';
    if (!this.layerStyles[name]) {
      this.layerStyles[name] = { paletteId: 'default' };
    } else {
      this.layerStyles[name].paletteId = 'default';
    }
    const newLayer = new VectorLayer({
      source: newSource,
      style: (feature: FeatureLike, resolution?: number) => this.getLayerStyleWithTheme(name, feature, resolution)
    });
    
    this.registerLayer(name, newLayer);
    this.map.addLayer(newLayer);
    
    if (group) {
      if (!this.layerGroups[group]) this.layerGroups[group] = [];
      this.layerGroups[group].push(name);
    }
    
    this.importLayer(name, geoJsonContent);
    this.updateLayerManagementUI();
  }

  private setupThemeSelection(): void {
    // Theme selection buttons
    document.querySelectorAll('.theme-btn').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const theme = target.getAttribute('data-theme') || 'osm';
        this.changeBaseMap(theme);
        
        // Update active state
        document.querySelectorAll('.theme-btn').forEach(b => b.classList.remove('active'));
        target.classList.add('active');
      });
    });
  }

  private toggleLayer(layerName: string, visible: boolean): void {
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        let shouldToggle = false;
        
        switch (layerName) {
          case 'drawing':
            shouldToggle = source === this.vectorSource;
            break;
          case 'road':
            shouldToggle = source === this.roadSource;
            break;
          case 'project':
            shouldToggle = source === this.projectSource;
            break;
          case 'biometric':
            shouldToggle = source === this.biometricSource;
            break;
          case 'gps':
            shouldToggle = source === this.gpsSource;
            break;
          case 'water':
            shouldToggle = source === this.waterSource;
            break;
        }
        
        if (shouldToggle) {
          layer.setVisible(visible);
        }
      }
    });
  }

  private setLayerOpacity(layerName: string, opacity: number): void {
    const layers = this.map.getLayers().getArray();
    layers.forEach(layer => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource();
        let shouldSetOpacity = false;
        
        switch (layerName) {
          case 'drawing':
            shouldSetOpacity = source === this.vectorSource;
            break;
          case 'road':
            shouldSetOpacity = source === this.roadSource;
            break;
          case 'project':
            shouldSetOpacity = source === this.projectSource;
            break;
          case 'biometric':
            shouldSetOpacity = source === this.biometricSource;
            break;
          case 'gps':
            shouldSetOpacity = source === this.gpsSource;
            break;
          case 'water':
            shouldSetOpacity = source === this.waterSource;
            break;
        }
        
        if (shouldSetOpacity) {
          layer.setOpacity(opacity);
        }
      }
    });
  }

  private changeBaseMap(theme: string): void {
    const layers = this.map.getLayers().getArray();
    const currentBaseLayer = layers.find(layer => layer instanceof TileLayer);
    if (currentBaseLayer) {
      this.map.removeLayer(currentBaseLayer);
    }
    if (theme === 'custom') {
      const url = prompt(
        'Enter tile URL template (use {z}, {x}, {y} for tile coordinates):',
        'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
      );
      if (url) {
        this.baseMaps['custom'] = new TileLayer({
          source: new XYZ({ url, attributions: 'Custom' })
        });
        this.map.getLayers().insertAt(0, this.baseMaps['custom']);
        this.currentBaseMap = this.baseMaps['custom'];
      }
      return;
    }
    const newBaseMap = this.baseMaps[theme];
    if (newBaseMap) {
      this.map.getLayers().insertAt(0, newBaseMap);
      this.currentBaseMap = newBaseMap;
    }
  }

  private setupAdditionalButtonListeners(): void {
    // Pan button
    const panBtn = document.getElementById('panBtn');
    if (panBtn) {
      panBtn.addEventListener('click', () => this.removeInteractions());
    }

    // Modify button
    const modifyBtn = document.getElementById('modifyBtn');
    if (modifyBtn) {
      modifyBtn.addEventListener('click', () => this.enableModify());
    }
    const moveBtn = document.getElementById('moveBtn');
    if (moveBtn) {
      moveBtn.addEventListener('click', () => this.enableTranslate());
    }

    // Delete button
    const deleteBtn = document.getElementById('deleteBtn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => this.enableDelete());
    }

    // Zoom buttons
    const zoomInBtn = document.getElementById('zoomIn');
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }

    const zoomOutBtn = document.getElementById('zoomOut');
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }

    // Clear button
    const clearBtn = document.getElementById('clearBtn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearAll());
    }

    // Export button
    const exportBtn = document.getElementById('exportBtn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.exportData());
    }

    // Import button
    const importBtn = document.getElementById('importBtn');
    if (importBtn) {
      importBtn.addEventListener('click', () => this.importData());
    }

    // Segment drawing buttons
    const startSegmentBtn = document.getElementById('startSegmentDrawing');
    if (startSegmentBtn) {
      startSegmentBtn.addEventListener('click', () => this.startSegmentDrawing());
    }

    const stopSegmentBtn = document.getElementById('stopSegmentDrawing');
    if (stopSegmentBtn) {
      stopSegmentBtn.addEventListener('click', () => this.stopSegmentDrawing());
    }

    // Numbering mode selector
    const numberingTypeSelect = document.getElementById('numberingType');
    if (numberingTypeSelect) {
      numberingTypeSelect.addEventListener('change', (event) => {
        const target = event.target as HTMLSelectElement;
        this.setNumberingMode(target.value as 'auto' | 'manual');
      });
    }

    // Reset numbering button
    const resetNumbersBtn = document.getElementById('resetNumbers');
    if (resetNumbersBtn) {
      resetNumbersBtn.addEventListener('click', () => this.resetNumbering());
    }

    // Manual numbering inputs
    const manualNodeNumberInput = document.getElementById('manualNodeNumber');
    if (manualNodeNumberInput) {
      manualNodeNumberInput.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.setManualNodeNumber(parseInt(target.value));
      });
    }

    const manualSegmentNumberInput = document.getElementById('manualSegmentNumber');
    if (manualSegmentNumberInput) {
      manualSegmentNumberInput.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement;
        this.setManualSegmentNumber(parseInt(target.value));
      });
    }
  }

  private setupButtonEventListeners(): void {
    // Toolbar buttons
    this.setupToolbarButtons();
    
    // Select button
    const selectBtn = document.getElementById('selectBtn');
    if (selectBtn) {
      selectBtn.addEventListener('click', () => this.enableSelect());
    }

    // Symbol buttons
    document.querySelectorAll('.symbol-btn').forEach(btn => {
      btn.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        this.currentSymbol = target.getAttribute('data-symbol') || '';
        document.querySelectorAll('.symbol-btn').forEach(b => b.classList.remove('active'));
        target.classList.add('active');
        if ((document.getElementById('drawingMode') as HTMLSelectElement)?.value === '') {
          this.addInteraction('Point');
        }
      });
    });

    // Add more button event listeners
    this.setupAdditionalButtonListeners();
    this.setupJunctionButtons();
    this.setupRoadInfrastructureButtons();
    this.setupAdvancedControls();
    this.setupSurveyControls();
    this.setupManagementControls();
    this.setupBiometricControls();
    this.setupSettingsControls();
    this.setupLayerManagement();
    this.setupThemeSelection();
  }

  private setupToolbarButtons(): void {
    // Zoom controls
    const zoomInBtn = document.getElementById('zoomIn');
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }

    const zoomOutBtn = document.getElementById('zoomOut');
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }

    const zoomFitBtn = document.getElementById('zoomFit');
    if (zoomFitBtn) {
      zoomFitBtn.addEventListener('click', () => this.zoomFit());
    }

    const zoomExtentBtn = document.getElementById('zoomExtent');
    if (zoomExtentBtn) {
      zoomExtentBtn.addEventListener('click', () => this.zoomExtent());
    }

    // View history navigation
    const zoomPreviousBtn = document.getElementById('zoomPrevious');
    if (zoomPreviousBtn) {
      zoomPreviousBtn.addEventListener('click', () => this.previousView());
    }

    const zoomNextBtn = document.getElementById('zoomNext');
    if (zoomNextBtn) {
      zoomNextBtn.addEventListener('click', () => this.nextView());
    }

    // Pan button
    const panBtn = document.getElementById('panBtn');
    if (panBtn) {
      panBtn.addEventListener('click', () => this.enablePan());
    }

    // Edit button
    const modifyBtn = document.getElementById('modifyBtn');
    if (modifyBtn) {
      modifyBtn.addEventListener('click', () => this.enableModify());
    }
    const moveBtn = document.getElementById('moveBtn');
    if (moveBtn) {
      moveBtn.addEventListener('click', () => this.enableTranslate());
    }

    // Delete button
    const deleteBtn = document.getElementById('deleteBtn');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', () => this.enableDelete());
    }

    // Measurement tools
    const measureBtn = document.getElementById('measureBtn');
    if (measureBtn) {
      measureBtn.addEventListener('click', () => this.enableDistanceMeasurement());
    }

    const areaBtn = document.getElementById('areaBtn');
    if (areaBtn) {
      areaBtn.addEventListener('click', () => this.enableAreaMeasurement());
    }

    const bearingBtn = document.getElementById('bearingBtn');
    if (bearingBtn) {
      bearingBtn.addEventListener('click', () => this.enableBearingMeasurement());
    }

    // Undo/Redo
    const undoBtn = document.getElementById('undoBtn');
    if (undoBtn) {
      undoBtn.addEventListener('click', () => this.undo());
    }

    const redoBtn = document.getElementById('redoBtn');
    if (redoBtn) {
      redoBtn.addEventListener('click', () => this.redo());
    }

    // Clear button
    const clearBtn = document.getElementById('clearBtn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearAll());
    }

    // Export/Import
    const exportBtn = document.getElementById('exportBtn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.exportData());
    }

    const importBtn = document.getElementById('importBtn');
    if (importBtn) {
      importBtn.addEventListener('click', () => this.importData());
    }

    // Print button
    const printBtn = document.getElementById('printBtn');
    if (printBtn) {
      printBtn.addEventListener('click', () => this.printMap());
    }

    // 3D Take Image button
    const takeMapImageBtn = document.getElementById('takeMapImageBtn');
    if (takeMapImageBtn) {
      takeMapImageBtn.addEventListener('click', () => this.takeMapImage());
    }
    const nhaiTakeImageBtn = document.getElementById('nhaiTakeImageBtn');
    if (nhaiTakeImageBtn) {
      nhaiTakeImageBtn.addEventListener('click', () => this.takeMapImage());
    }

    // Fullscreen/Mini-map
    const fullscreenBtn = document.getElementById('fullscreenBtn');
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
    }

    const miniMapBtn = document.getElementById('miniMapBtn');
    if (miniMapBtn) {
      miniMapBtn.addEventListener('click', () => this.toggleMiniMap());
    }

    // Help button
    const helpBtn = document.getElementById('helpBtn');
    if (helpBtn) {
      helpBtn.addEventListener('click', () => this.showHelp());
    }
  }

  handleDrawingModeChange(mode: string): void {
    this.removeInteractions();
    this.currentDrawingMode = mode; // Store current drawing mode
    
    if (mode) {
      if (mode === 'Point') {
        this.addPointInteraction();
      } else if (mode === 'Text') {
        this.addTextInteraction();
      } else if (mode === 'Circle') {
        this.addCircleInteraction();
      } else if (mode === 'Polygon') {
        this.addPolygonInteraction();
      } else if (mode === 'CurveLine') {
        this.addCurveInteraction();
      } else if (mode.endsWith('Duct')) {
        this.addDuctInteraction(mode);
      } else if (mode === 'MultiLineString') {
        this.addMultiLineInteraction();
      } else {
        this.isMultiLineMode = false;
        const miniToolbar = document.getElementById('miniToolbar');
        if (miniToolbar) {
          miniToolbar.style.display = 'none';
        }
        this.addInteraction(mode);
      }
    } else {
      this.currentDrawingMode = ''; // Clear when no mode selected
    }
  }

  // Complete drawing tools implementation
  private addPointInteraction(): void {
    this.removeInteractions();
    this.showInstructions('Click to place point markers');
    
    this.map.on('click', (event: any) => {
      const pointFeature = new Feature({
        geometry: new Point(event.coordinate),
        type: 'point_marker',
        symbol: this.defaultIconUrl ? undefined : '📍',
        iconUrl: this.defaultIconUrl || undefined,
        size: this.symbolSize,
        timestamp: new Date().toISOString()
      });
      if (!this.defaultIconUrl) {
        const style = new Style({
          image: new CircleStyle({
            radius: 8 * this.symbolSize,
            fill: new Fill({ color: '#e74c3c' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          }),
          text: new Text({
            text: '📍',
            font: `${16 * this.symbolSize}px Arial`,
            fill: new Fill({ color: '#fff' }),
            offsetY: -20
          })
        });
        pointFeature.setStyle(style);
      }
      this.getCurrentDrawingSource().addFeature(pointFeature);
      this.saveToHistory();
    });
  }

  private addTextInteraction(): void {
    this.removeInteractions();
    
    this.map.on('click', (event: any) => {
      const textContent = (document.getElementById('textContent') as HTMLInputElement)?.value || '';
      const textColor = (document.getElementById('textColor') as HTMLInputElement)?.value || '#000000';
      const textSize = parseInt((document.getElementById('textSize') as HTMLSelectElement)?.value || '14');
      
      const textFeature = new Feature({
        geometry: new Point(event.coordinate),
        text: textContent,
        textColor: textColor,
        textSize: textSize,
        type: 'text'
      });
      
      this.getCurrentDrawingSource().addFeature(textFeature);
      this.saveToHistory();
    });
  }

  private addCircleInteraction(): void {
    this.removeInteractions();
    
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'Circle',
      style: this.createDrawingStyle(thickness)
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'circle');
      feature.set('thickness', thickness);
      feature.set('symbolSize', this.symbolSize);
      this.saveToHistory();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private addPolygonInteraction(): void {
    this.removeInteractions();
    this.showInstructions('Click to start polygon, double-click to finish');
    
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(52, 152, 219, 0.3)'
        }),
        stroke: new Stroke({
          color: this.defaultSettings.lineColor,
          width: thickness
        })
      })
    });
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', 'polygon');
      feature.set('thickness', thickness);
      feature.set('area', this.calculateArea(feature).squareMeters);
      this.saveToHistory();
      this.hideInstructions();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private addMultiLineInteraction(): void {
    this.removeInteractions();
    this.isMultiLineMode = true;
    this.showInstructions('Click to start multi-line drawing, right-click to finish');
    
    const miniToolbar = document.getElementById('miniToolbar');
    if (miniToolbar) {
      miniToolbar.style.display = 'block';
    }
    
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    let currentLinePoints: number[][] = [];
    let lineCount = 0;
    
    this.map.on('click', (event: any) => {
      currentLinePoints.push(event.coordinate);
      
      if (currentLinePoints.length === 1) {
        this.showInstructions('Click to add more points, right-click to finish line');
      } else {
        const tempLine = new LineString(currentLinePoints);
        const len = getGeodesicLength(tempLine, { projection: 'EPSG:3857' });
        this.showInstructions(`Length: ${len.toFixed(2)} m (${(len / 1000).toFixed(4)} km) — Click to add points, right-click to finish`);
        // Create line segment
        const lineFeature = new Feature({
          geometry: new LineString(currentLinePoints),
          type: 'multi_line',
          lineNumber: lineCount + 1,
          thickness: thickness
        });
        
        const style = new Style({
          stroke: new Stroke({
            color: this.defaultSettings.lineColor,
            width: thickness,
            lineCap: 'round'
          })
        });
        
        lineFeature.setStyle(style);
        this.getCurrentDrawingSource().addFeature(lineFeature);
        
        // Update mini toolbar
        const activeLineCount = document.getElementById('activeLineCount');
        const currentLineId = document.getElementById('currentLineId');
        if (activeLineCount) activeLineCount.textContent = (lineCount + 1).toString();
        if (currentLineId) currentLineId.textContent = (lineCount + 1).toString();
        
        lineCount++;
        this.saveToHistory();
      }
    });
    
    this.map.getViewport().addEventListener('contextmenu', (event: any) => {
      event.preventDefault();
      if (currentLinePoints.length >= 2) {
        // Finish current line
        const lineFeature = new Feature({
          geometry: new LineString(currentLinePoints),
          type: 'multi_line',
          lineNumber: lineCount + 1,
          thickness: this.lineThickness
        });
        
        const style = new Style({
          stroke: new Stroke({
            color: this.defaultSettings.lineColor,
            width: this.lineThickness,
            lineCap: 'round'
          })
        });
        
        lineFeature.setStyle(style);
        this.getCurrentDrawingSource().addFeature(lineFeature);
        
        lineCount++;
        currentLinePoints = [];
        this.saveToHistory();
        this.showInstructions('Line finished. Click to start new line, ESC to exit');
      }
    });
    
    // ESC to exit multi-line mode
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isMultiLineMode) {
        this.exitMultiLineMode();
      }
    });
  }

  private exitMultiLineMode(): void {
    this.isMultiLineMode = false;
    const miniToolbar = document.getElementById('miniToolbar');
    if (miniToolbar) {
      miniToolbar.style.display = 'none';
    }
    this.hideInstructions();
    this.removeInteractions();
  }

  private addCurveInteraction(): void {
    this.removeInteractions();
    this.startCurveDrawing();
  }

  private startCurveDrawing(): void {
    this.isCurveDrawing = true;
    this.curveControlPoints = [];
    
    // Update UI
    const startBtn = document.getElementById('startCurveDrawing');
    const finishBtn = document.getElementById('finishCurveDrawing');
    const cancelBtn = document.getElementById('cancelCurveDrawing');
    
    if (startBtn) startBtn.style.display = 'none';
    if (finishBtn) finishBtn.style.display = 'inline-block';
    if (cancelBtn) cancelBtn.style.display = 'inline-block';
    
    this.showInstructions('Click points to define curve. Right-click to finish, ESC to cancel.');
    
    // Set up click listener for curve points
    this.curveDrawingListener = this.map.on('click', (event: any) => {
      this.addCurvePoint(event.coordinate);
    });
    
    // Set up right-click to finish
    this.map.getViewport().addEventListener('contextmenu', (event: any) => {
      event.preventDefault();
      if (this.curveControlPoints.length >= 2) {
        this.finishCurveDrawing();
      }
    });
    
    // Set up ESC key to cancel
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isCurveDrawing) {
        this.cancelCurveDrawing();
      }
    });
  }

  private addCurvePoint(coordinate: number[]): void {
    this.curveControlPoints.push(coordinate);
    
    // Add visual control point
    this.addControlPointMarker(coordinate, this.curveControlPoints.length);
    
    // Update instructions
    if (this.curveControlPoints.length === 1) {
      this.showInstructions('Click second point to define curve direction');
    } else if (this.curveControlPoints.length === 2) {
      this.showInstructions('Click more points or right-click to finish curve');
    } else {
      this.showInstructions(`Point ${this.curveControlPoints.length} - Click more or right-click to finish`);
    }
    
    // Draw preview curve if we have enough points
    if (this.curveControlPoints.length >= 2) {
      this.drawCurvePreview();
    }
  }

  private addControlPointMarker(coordinate: number[], pointNumber: number): void {
    const marker = new Feature({
      geometry: new Point(coordinate),
      type: 'control_point',
      pointNumber: pointNumber
    });
    
    const style = new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: '#e74c3c' }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      }),
      text: new Text({
        text: pointNumber.toString(),
        font: '12px Arial',
        fill: new Fill({ color: '#fff' }),
        stroke: new Stroke({ color: '#000', width: 2 }),
        offsetY: -15
      })
    });
    
    marker.setStyle(style);
    this.getCurrentDrawingSource().addFeature(marker);
  }

  private drawCurvePreview(): void {
    // Remove existing preview
    this.removeCurvePreview();
    
    const curveCoordinates = this.generateCurveCoordinates(this.curveControlPoints);
    
    const previewFeature = new Feature({
      geometry: new LineString(curveCoordinates),
      type: 'curve_preview'
    });
    
    const style = new Style({
      stroke: new Stroke({
        color: '#3498db',
        width: this.lineThickness + 1,
        lineDash: [5, 5]
      })
    });
    
    previewFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(previewFeature);
  }

  private removeCurvePreview(): void {
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      if (feature.get('type') === 'curve_preview') {
        this.vectorSource.removeFeature(feature);
      }
    });
  }

  private generateCurveCoordinates(points: number[][]): [number, number][] {
    if (points.length < 2) return [];
    
    const precision = this.curvePrecision;
    const coordinates: [number, number][] = [];
    
    switch (this.currentCurveType) {
      case 'bezier':
        return this.generateBezierCurve(points, precision);
      case 'spline':
        return this.generateSplineCurve(points, precision);
      case 'arc':
        return this.generateArcCurve(points, precision);
      case 'smooth':
      default:
        return this.generateSmoothCurve(points, precision);
    }
  }

  private generateBezierCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 2) return [];
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const p2 = points[i + 2] || p1;
      const p3 = points[i + 3] || p2;
      
        for (let t = 0; t <= 1; t += 1 / steps) {
          const x = this.cubicBezier(p0[0], p1[0], p2[0], p3[0], t);
          const y = this.cubicBezier(p0[1], p1[1], p2[1], p3[1], t);
          coordinates.push([x, y] as [number, number]);
        }
    }
    
    return coordinates;
  }

  private cubicBezier(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;
    
    return uuu * p0 + 3 * uu * t * p1 + 3 * u * tt * p2 + ttt * p3;
  }

  private generateSplineCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 3) return this.generateSmoothCurve(points, precision);
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    // Catmull-Rom spline
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = i > 0 ? points[i - 1] : points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1];
      
      for (let t = 0; t <= 1; t += 1 / steps) {
        const x = this.catmullRom(p0[0], p1[0], p2[0], p3[0], t);
        const y = this.catmullRom(p0[1], p1[1], p2[1], p3[1], t);
        coordinates.push([x, y] as [number, number]);
      }
    }
    
    return coordinates;
  }

  private catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const t2 = t * t;
    const t3 = t2 * t;
    
    return 0.5 * (
      (2 * p1) +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3
    );
  }

  private generateArcCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 3) return this.generateSmoothCurve(points, precision);
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    // Create circular arc through three points
    for (let i = 0; i < points.length - 2; i += 2) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2];
      
      const center = this.calculateArcCenter(p1, p2, p3);
      if (center) {
        const radius = this.calculateDistance(center, p1);
        const startAngle = Math.atan2(p1[1] - center[1], p1[0] - center[0]);
        const endAngle = Math.atan2(p3[1] - center[1], p3[0] - center[0]);
        
        for (let j = 0; j <= steps; j++) {
          const t = j / steps;
          const angle = startAngle + (endAngle - startAngle) * t;
          const x = center[0] + radius * Math.cos(angle);
          const y = center[1] + radius * Math.sin(angle);
          coordinates.push([x, y] as [number, number]);
        }
      }
    }
    
    return coordinates;
  }

  private calculateArcCenter(p1: number[], p2: number[], p3: number[]): number[] | null {
    const ax = p1[0];
    const ay = p1[1];
    const bx = p2[0];
    const by = p2[1];
    const cx = p3[0];
    const cy = p3[1];
    
    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 1e-10) return null; // Points are collinear
    
    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / d;
    
    return [ux, uy];
  }

  private generateSmoothCurve(points: number[][], precision: number): [number, number][] {
    if (points.length < 2) return [];
    
    const coordinates: [number, number][] = [];
    const steps = Math.max(precision, 10);
    
    // Simple smooth curve using quadratic interpolation
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      
      for (let t = 0; t <= 1; t += 1 / steps) {
        const x = this.quadraticInterpolation(p1[0], p2[0], p3[0], t);
        const y = this.quadraticInterpolation(p1[1], p2[1], p3[1], t);
        coordinates.push([x, y] as [number, number]);
      }
    }
    
    return coordinates;
  }

  private quadraticInterpolation(p1: number, p2: number, p3: number, t: number): number {
    const u = 1 - t;
    return u * u * p1 + 2 * u * t * p2 + t * t * p3;
  }

  private finishCurveDrawing(): void {
    if (this.curveControlPoints.length < 2) {
      this.showInstructions('Need at least 2 points to create a curve');
      return;
    }
    
    const curveCoordinates = this.generateCurveCoordinates(this.curveControlPoints);
    
    const curveFeature = new Feature({
      geometry: new LineString(curveCoordinates),
      type: 'curve',
      curveType: this.currentCurveType,
      controlPoints: this.curveControlPoints,
      thickness: this.lineThickness,
      precision: this.curvePrecision
    });
    
    const style = new Style({
        stroke: new Stroke({
          color: this.defaultSettings.lineColor,
          width: this.lineThickness,
          lineCap: 'round'
      })
    });
    
    curveFeature.setStyle(style);
    this.getCurrentDrawingSource().addFeature(curveFeature);
    
    this.cleanupCurveDrawing();
      this.saveToHistory();
    this.showInstructions('Curve created successfully');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private cancelCurveDrawing(): void {
    this.cleanupCurveDrawing();
    this.hideInstructions();
  }

  private cleanupCurveDrawing(): void {
    this.isCurveDrawing = false;
    this.curveControlPoints = [];
    
    // Remove preview and control points
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      const type = feature.get('type');
      if (type === 'curve_preview' || type === 'control_point') {
        this.vectorSource.removeFeature(feature);
      }
    });
    
    // Update UI
    const startBtn = document.getElementById('startCurveDrawing');
    const finishBtn = document.getElementById('finishCurveDrawing');
    const cancelBtn = document.getElementById('cancelCurveDrawing');
    
    if (startBtn) startBtn.style.display = 'inline-block';
    if (finishBtn) finishBtn.style.display = 'none';
    if (cancelBtn) cancelBtn.style.display = 'none';
    
    // Remove event listeners
    if (this.curveDrawingListener) {
      this.map.un('click', this.curveDrawingListener);
      this.curveDrawingListener = null;
    }
  }

  private addInteraction(type: string): void {
    // Use this.lineThickness to ensure it's synced with the slider
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    this.updateRoadSpecs();
    
    const useFreehand = (type === 'LineString' || type === 'Polygon') && this.freehandMode;
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: type as any,
      style: this.createDrawingStyle(thickness, this.currentRoadSpecs),
      freehand: useFreehand
    });

    if (type === 'LineString') {
      this.drawInteraction.on('drawstart', (e: any) => this.setupRealtimeLineLength(e.feature));
      this.drawInteraction.on('drawend', (e: any) => this.clearRealtimeLineLength(e.feature));
      this.drawInteraction.on('drawabort', () => this.clearRealtimeLineLength(null));
    }
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('thickness', thickness);
      feature.set('symbolSize', this.symbolSize);
      feature.set('roadSpecs', {...this.currentRoadSpecs});
      feature.set('underground', (document.getElementById('assetType') as HTMLSelectElement)?.value === 'underground');
      const grade = (document.getElementById('assetType') as HTMLSelectElement)?.value || 'overground';
      feature.set('assetType', grade);
      feature.set('grade', grade);
      feature.set('info', 'Feature Info');
      
      if (this.currentSymbol && type === 'Point') {
        feature.set('symbol', this.currentSymbol);
      }
      
      // Update nomenclature from UI if available
      const nomenclatureInput = document.getElementById('nomenclature') as HTMLInputElement;
      const abbreviationInput = document.getElementById('nomenclatureAbbreviation') as HTMLInputElement;
      if (nomenclatureInput) {
        this.nomenclature = nomenclatureInput.value || 'Gandhi baba';
      }
      if (abbreviationInput) {
        this.nomenclatureAbbreviation = abbreviationInput.value || 'Gb';
      }
      
      // Apply auto-numbering for segments if enabled
      if (this.isMultiLineMode) {
        this.currentLines.push(feature);
        this.updateLineCount();
        const numberingType = (document.getElementById('numberingType') as HTMLSelectElement)?.value || 'auto';
        if (numberingType === 'auto') {
          this.autoNumberSegment(feature);
        } else {
          // Manual numbering - still generate ID and label
          const segmentNumber = this.manualSegmentNumber;
          const currentSource = this.getCurrentDrawingSource();
          const existingMainSegment = currentSource.getFeatures().find(f => {
            const fSegmentNumber = f.get('segmentNumber');
            const fIsSubSegment = f.get('isSubSegment');
            return fSegmentNumber === segmentNumber && !fIsSubSegment;
          });
          
          let segmentId: string;
          let segmentLabel: string;
          let isSubSegment = false;
          
          if (existingMainSegment) {
            // Subsegment
            isSubSegment = true;
            if (!this.segmentSubSegmentCount[segmentNumber]) {
              this.segmentSubSegmentCount[segmentNumber] = 1;
            } else {
              this.segmentSubSegmentCount[segmentNumber]++;
            }
            const subSegmentNum = this.segmentSubSegmentCount[segmentNumber];
            segmentId = `s${segmentNumber}_${subSegmentNum}`;
            segmentLabel = `${this.nomenclatureAbbreviation} ${segmentId}`;
          } else {
            // Main segment
            segmentId = `s${segmentNumber}`;
            segmentLabel = `${this.nomenclature} segment ${segmentNumber}`;
            this.segmentSubSegmentCount[segmentNumber] = 0;
          }
          
          feature.set('segmentNumber', segmentNumber);
          feature.set('segmentId', segmentId);
          feature.set('label', segmentLabel);
          feature.set('isSubSegment', isSubSegment);
          feature.set('nomenclature', this.nomenclature);
          feature.set('nomenclatureAbbreviation', this.nomenclatureAbbreviation);
        }
      }
      
      this.saveToHistory();
      this.updateMeasurementDisplay(feature);
    });
    
    this.map.addInteraction(this.drawInteraction);
    this.applySnapState();
  }

  private addDuctInteraction(type: string): void {
    this.removeInteractions();
    
    const grade = (document.getElementById('assetType') as HTMLSelectElement)?.value || 'overground';
    const isUnderground = grade === 'underground';
    const ductOpts = strokeOptsForGrade(grade);
    // Ensure we use the current lineThickness value
    const thickness = this.lineThickness || parseInt((document.getElementById('lineThickness') as HTMLInputElement)?.value || '2');
    
    this.drawInteraction = new Draw({
      source: this.getCurrentDrawingSource(),
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: ductOpts.color,
          width: thickness + 1,
          lineDash: ductOpts.lineDash
        })
      })
    });
    this.drawInteraction.on('drawstart', (e: any) => this.setupRealtimeLineLength(e.feature));
    this.drawInteraction.on('drawend', (e: any) => this.clearRealtimeLineLength(e.feature));
    this.drawInteraction.on('drawabort', () => this.clearRealtimeLineLength(null));
    
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.set('type', type.toLowerCase());
      feature.set('thickness', thickness);
      feature.set('underground', isUnderground);
      feature.set('assetType', grade);
      feature.set('grade', grade);
      feature.set('info', `${type} Duct`);
      this.saveToHistory();
    });
    
    this.map.addInteraction(this.drawInteraction);
  }

  private enableSelect(): void {
    this.removeInteractions();
    this.selectInteraction = new Select({ multi: true });
    this.map.addInteraction(this.selectInteraction);
    this.applySnapState();
    this.selectInteraction.on('select', (e) => {
      if (e.selected.length > 0) {
        const feature = e.selected[0];
        this.showFeatureInfo(feature);
      }
    });
  }

  private removeInteractions(): void {
    this.clearRealtimeLineLength(null);
    if (this.drawInteraction) this.map.removeInteraction(this.drawInteraction);
    if (this.selectInteraction) this.map.removeInteraction(this.selectInteraction);
    if (this.modifyInteraction) this.map.removeInteraction(this.modifyInteraction);
    if (this.translateInteraction) this.map.removeInteraction(this.translateInteraction);
    if (this.measureInteraction) this.map.removeInteraction(this.measureInteraction);
    if (this.placeSymbolHandler) {
      this.map.un('click', this.placeSymbolHandler);
      this.placeSymbolHandler = null;
    }
    this.snapInteractions.forEach(s => this.map.removeInteraction(s));
    this.snapInteractions = [];
    this.applySnapState();
  }

  // Style creation methods
  private getDrawingStyle(feature: FeatureLike): Style[] {
    return [new Style({
      fill: new Fill({
        color: 'rgba(52, 152, 219, 0.3)'
      }),
      stroke: new Stroke({
        color: this.defaultSettings.lineColor,
        width: 2
      })
    })];
  }

  private getRoadStyle(feature: FeatureLike): Style[] {
    return [new Style({
      stroke: new Stroke({
        color: '#e67e22',
        width: 4
      })
    })];
  }

  private getProjectStyle(feature: FeatureLike): Style[] {
    return [new Style({
      fill: new Fill({
        color: 'rgba(46, 125, 50, 0.2)'
      }),
      stroke: new Stroke({
        color: '#2e7d32',
        width: 2
      })
    })];
  }

  private getBiometricStyle(feature: FeatureLike): Style[] {
    return [new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          color: '#27ae60'
        })
      })
    })];
  }

  private getGpsStyle(feature: FeatureLike): Style[] {
    return [new Style({
      image: new CircleStyle({
        radius: 8,
        fill: new Fill({
          color: 'rgba(76, 175, 80, 0.8)'
        })
      })
    })];
  }

  private getWaterStyle(feature: FeatureLike): Style[] {
    return [new Style({
      stroke: new Stroke({
        color: '#3498db',
        width: 4,
        lineDash: [10, 5]
      })
    })];
  }

  private getSeparatorStyle(feature: FeatureLike): Style[] {
    return [new Style({
      stroke: new Stroke({
        color: '#f39c12',
        width: 1,
        lineDash: [3, 3]
      })
    })];
  }

  private updateRoadSpecs(): void {
    const roadType = document.getElementById('roadType') as HTMLSelectElement;
    const roadWidth = document.getElementById('roadWidth') as HTMLInputElement;
    const roadDepth = document.getElementById('roadDepth') as HTMLInputElement;
    const roadLanes = document.getElementById('roadLanes') as HTMLSelectElement;
    const surfaceType = document.getElementById('surfaceType') as HTMLSelectElement;
    const separatorDistance = document.getElementById('separatorDistance') as HTMLInputElement;

    if (roadType?.value) this.currentRoadSpecs.type = roadType.value;
    if (roadWidth) this.currentRoadSpecs.width = parseFloat(roadWidth.value);
    if (roadDepth) this.currentRoadSpecs.depth = parseFloat(roadDepth.value);
    if (roadLanes) this.currentRoadSpecs.lanes = parseInt(roadLanes.value, 10);
    if (surfaceType) this.currentRoadSpecs.surface = surfaceType.value;
    if (separatorDistance) this.currentRoadSpecs.separatorDistance = parseFloat(separatorDistance.value);
  }

  private autoNumberSegment(feature: Feature): void {
    const center = feature.getGeometry()!.getExtent();
    const centerPoint = [(center[0] + center[2]) / 2, (center[1] + center[3]) / 2];
    
    let segmentLabel: string;
    let segmentId: string;
    let segmentNumber: number;
    let isSubSegment = false;
    
    // Determine segment number
    if (this.numberingMode === 'auto') {
      segmentNumber = this.currentSegment;
    } else {
      segmentNumber = this.manualSegmentNumber;
    }
    
    // Check if a main segment with this number already exists (making this a subsegment)
    const currentSource = this.getCurrentDrawingSource();
    const existingMainSegment = currentSource.getFeatures().find(f => {
      const fSegmentNumber = f.get('segmentNumber');
      const fIsSubSegment = f.get('isSubSegment');
      const fType = f.get('type');
      return fSegmentNumber === segmentNumber && 
             !fIsSubSegment && 
             (fType === 'segment' || fType === 'LineString' || fType === 'MultiLineString');
    });
    
    if (existingMainSegment) {
      // This is a subsegment - increment subsegment count for this segment number
      isSubSegment = true;
      if (!this.segmentSubSegmentCount[segmentNumber]) {
        this.segmentSubSegmentCount[segmentNumber] = 1;
      } else {
        this.segmentSubSegmentCount[segmentNumber]++;
      }
      const subSegmentNum = this.segmentSubSegmentCount[segmentNumber];
      segmentId = `s${segmentNumber}_${subSegmentNum}`;
      // Subsegment label: "Gb s1_1", "Gb s1_2", etc.
      segmentLabel = `${this.nomenclatureAbbreviation} ${segmentId}`;
    } else {
      // This is a new main segment
      isSubSegment = false;
      segmentId = `s${segmentNumber}`;
      // Main segment label: "Gandhi baba segment 1", "Gandhi baba segment 2", etc.
      segmentLabel = `${this.nomenclature} segment ${segmentNumber}`;
      // Initialize subsegment count for this segment
      this.segmentSubSegmentCount[segmentNumber] = 0;
      
      // Increment current segment counter only for new main segments in auto mode
      if (this.numberingMode === 'auto') {
        this.currentSegment++;
      }
    }
    
    // Create label feature
    const textFeature = new Feature({
      geometry: new Point(centerPoint),
      text: segmentLabel,
      textSize: 14,
      textColor: '#000',
      type: 'segment_label'
    });
    
    currentSource.addFeature(textFeature);
    
    // Update feature properties
    feature.set('segmentNumber', segmentNumber);
    feature.set('segmentId', segmentId);
    feature.set('label', segmentLabel);
    feature.set('nomenclature', this.nomenclature);
    feature.set('nomenclatureAbbreviation', this.nomenclatureAbbreviation);
    feature.set('isSubSegment', isSubSegment);
    
    // Update UI
    const currentSegmentElement = document.getElementById('currentSegment');
    if (currentSegmentElement) {
      currentSegmentElement.innerText = this.currentSegment.toString();
    }
  }

  private generateSegmentId(): string {
    const segmentNumber = this.numberingMode === 'auto' ? this.currentSegment : this.manualSegmentNumber;
    
    // Check if a main segment with this number already exists (making this a subsegment)
    const currentSource = this.getCurrentDrawingSource();
    const existingMainSegment = currentSource.getFeatures().find(f => {
      const fSegmentNumber = f.get('segmentNumber');
      const fIsSubSegment = f.get('isSubSegment');
      const fType = f.get('type');
      return fSegmentNumber === segmentNumber && 
             !fIsSubSegment && 
             (fType === 'segment' || fType === 'LineString' || fType === 'MultiLineString');
    });
    
    if (existingMainSegment) {
      // This is a subsegment - increment subsegment count
      if (!this.segmentSubSegmentCount[segmentNumber]) {
        this.segmentSubSegmentCount[segmentNumber] = 1;
      } else {
        this.segmentSubSegmentCount[segmentNumber]++;
      }
      const subSegmentNum = this.segmentSubSegmentCount[segmentNumber];
      return `s${segmentNumber}_${subSegmentNum}`;
    } else {
      // This is a new main segment
      this.segmentSubSegmentCount[segmentNumber] = 0;
      if (this.numberingMode === 'auto') {
        this.currentSegment++;
      }
      return `s${segmentNumber}`;
    }
  }

  private generateSegmentLabel(segmentNumber: number, segmentId: string): string {
    // Check if this is a subsegment
    const isSubSegment = segmentId.includes('_');
    
    if (isSubSegment) {
      // Subsegment: "Gb s1_1", "Gb s1_2", etc.
      return `${this.nomenclatureAbbreviation} ${segmentId}`;
    } else {
      // Main segment: "Gandhi baba segment 1", "Gandhi baba segment 2", etc.
      return `${this.nomenclature} segment ${segmentNumber}`;
    }
  }

  private updateLineCount(): void {
    const activeLineCountElement = document.getElementById('activeLineCount');
    if (activeLineCountElement) {
      activeLineCountElement.innerText = this.currentLines.length.toString();
    }
  }

  private saveToHistory(): void {
    // Implementation for saving to history
    this.drawingHistory = this.drawingHistory.slice(0, this.historyStep + 1);
    this.drawingHistory.push({
      vectorSource: this.vectorSource.getFeatures(),
      roadSource: this.roadSource.getFeatures(),
    });
    this.historyStep++;
  }

  private updateMeasurementDisplay(feature: Feature): void {
    const measurementResults = document.getElementById('measurementResults');
    if (measurementResults) {
      const length = this.calculateLength(feature);
      measurementResults.innerHTML = `Length: ${length.meters} m / ${length.km} km`;
    }
  }

  private calculateLength(feature: Feature): { meters: string; km: string } {
    const geom = feature.getGeometry();
    if (!geom) {
      return { meters: '0.00', km: '0.00' };
    }
    
    let length = 0;
    
    try {
      if (geom.getType() === 'LineString' || geom.getType() === 'MultiLineString') {
        // Use OpenLayers geodesic calculation (most accurate)
        length = getGeodesicLength(geom, { projection: 'EPSG:3857' });
        
        // Fallback to turf.js if available and OL calculation fails
        if (isNaN(length) || length === 0) {
          const geojsonString = this.format.writeGeometry(geom, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          if (typeof turf !== 'undefined' && geojsonString) {
            try {
              const geojsonObj = JSON.parse(geojsonString);
              length = turf.length(geojsonObj, { units: 'meters' });
            } catch (e) {
              console.warn('Turf.js calculation failed:', e);
            }
          }
        }
      } else if (geom.getType() === 'Circle') {
        // For circles, calculate circumference
        const radius = (geom as Circle).getRadius();
        // Convert from Web Mercator units to meters (approximate at center latitude)
        const center = (geom as Circle).getCenter();
        const centerLonLat = toLonLat(center);
        const latFactor = Math.cos(centerLonLat[1] * Math.PI / 180);
        const radiusMeters = radius * latFactor;
        length = 2 * Math.PI * radiusMeters;
      }
    } catch (error) {
      console.error('Error calculating length:', error);
    }
    
    const km = length / 1000;
    return {
      meters: length.toFixed(2),
      km: km.toFixed(4)
    };
  }

  private showFeatureInfo(feature: Feature): void {
    const details = document.getElementById('featureDetails');
    const featureInfo = document.getElementById('featureInfo');
    
    if (details && featureInfo) {
      details.innerHTML = '';
      
      if (feature.get('roadSpecs')) {
        const specs = feature.get('roadSpecs');
        details.innerHTML += `Width (m): <input id="editWidth" type="number" value="${specs.width}"><br>`;
        details.innerHTML += `Depth (m): <input id="editDepth" type="number" value="${specs.depth}"><br>`;
      } else if (feature.get('junction')) {
        const specs = feature.get('specs') || {width: 10, depth: 0.5};
        details.innerHTML += `Junction Width: <input id="editWidth" type="number" value="${specs.width}"><br>`;
        details.innerHTML += `Junction Depth: <input id="editDepth" type="number" value="${specs.depth}"><br>`;
      }
      
      details.innerHTML += `Comment: <input id="editComment" type="text" value="${feature.get('comment') || ''}"><br>`;
      featureInfo.style.display = 'block';
      
      const saveBtn = document.getElementById('saveFeatureProps');
      if (saveBtn) {
        saveBtn.onclick = () => {
          const newWidth = parseFloat((document.getElementById('editWidth') as HTMLInputElement)?.value || '0');
          const newDepth = parseFloat((document.getElementById('editDepth') as HTMLInputElement)?.value || '0');
          const newComment = (document.getElementById('editComment') as HTMLInputElement)?.value || '';
          
          if (feature.get('roadSpecs')) {
            let specs = feature.get('roadSpecs');
            specs.width = newWidth;
            specs.depth = newDepth;
            feature.set('roadSpecs', specs);
          } else if (feature.get('junction')) {
            feature.set('specs', {width: newWidth, depth: newDepth});
          }
          
          feature.set('comment', newComment);
          this.map.getView().changed();
          featureInfo.style.display = 'none';
        };
      }
    }
  }

  private createDrawingStyle(thickness: number = 2, roadSpecs?: any): Style[] {
    if (roadSpecs) {
      const widthM = this.effectiveRoadWidthM(roadSpecs);
      const palette = this.getRoadSurfacePalette(roadSpecs.surface || 'bituminous');
      const strokeW = Math.max(3, Math.min(16, widthM * 0.35));
      return [
        new Style({
          stroke: new Stroke({
            color: palette.stroke,
            width: strokeW,
          }),
          fill: new Fill({ color: palette.fill }),
        }),
      ];
    }

    return [
      new Style({
        fill: new Fill({
          color: 'rgba(52, 152, 219, 0.3)',
        }),
        stroke: new Stroke({
          color: this.defaultSettings.lineColor,
          width: thickness,
        }),
        image: new CircleStyle({
          radius: 5 + thickness,
          fill: new Fill({
            color: this.defaultSettings.lineColor,
          }),
        }),
      }),
    ];
  }

  private updateActiveDrawingStyle(): void {
    // Update the active drawing interaction's style if it exists
    if (this.drawInteraction && this.currentDrawingMode) {
      // Recreate the drawing interaction with the updated thickness
      this.handleDrawingModeChange(this.currentDrawingMode);
    }
  }

  private enableModify(): void {
    this.removeInteractions();
    this.selectInteraction = new Select({ multi: true });
    this.map.addInteraction(this.selectInteraction);
    const selected = this.selectInteraction.getFeatures();
    this.modifyInteraction = new Modify({ features: selected });
    this.map.addInteraction(this.modifyInteraction);
    this.applySnapState();
  }

  private enableTranslate(): void {
    this.removeInteractions();
    this.selectInteraction = new Select({ multi: true });
    this.map.addInteraction(this.selectInteraction);
    const selected = this.selectInteraction.getFeatures();
    this.translateInteraction = new Translate({ features: selected });
    this.translateInteraction.on('translateend', () => this.saveToHistory());
    this.map.addInteraction(this.translateInteraction);
    this.applySnapState();
  }

  private enableDelete(): void {
    if (this.selectInteraction && this.selectInteraction.getFeatures().getLength() > 0) {
      const features = this.selectInteraction.getFeatures();
      features.forEach((feature) => {
        const src = this.getSourceForFeature(feature as Feature);
        if (src) src.removeFeature(feature as Feature);
      });
      features.clear();
      this.saveToHistory();
    }
  }

  private zoomIn(): void {
    const view = this.map.getView();
    const zoom = view.getZoom()!;
    view.setZoom(zoom + 1);
  }

  private zoomOut(): void {
    const view = this.map.getView();
    const zoom = view.getZoom()!;
    view.setZoom(zoom - 1);
  }

  // ==================== VIEW HISTORY (PREVIOUS/NEXT VIEW) ====================

  /**
   * Initialize view history tracking - call this after map is created
   */
  private initializeViewHistory(): void {
    const view = this.map.getView();
    
    // Save initial view state
    this.saveViewState();
    
    // Listen for view changes
    view.on('change:center', () => {
      if (!this.isNavigatingHistory) {
        this.saveViewState();
      }
    });
    
    view.on('change:resolution', () => {
      if (!this.isNavigatingHistory) {
        this.saveViewState();
      }
    });
  }

  /**
   * Save current view state to history
   */
  private saveViewState(): void {
    const view = this.map.getView();
    const center = view.getCenter();
    const zoom = view.getZoom();
    
    if (!center || zoom === undefined) return;
    
    const newState = {
      center: center,
      zoom: zoom,
      timestamp: Date.now()
    };
    
    // If we're not at the end of history, truncate forward history
    if (this.viewHistoryIndex < this.viewHistory.length - 1) {
      this.viewHistory = this.viewHistory.slice(0, this.viewHistoryIndex + 1);
    }
    
    // Don't save if same as current position (debounce)
    const lastState = this.viewHistory[this.viewHistory.length - 1];
    if (lastState) {
      const centerSame = Math.abs(lastState.center[0] - newState.center[0]) < 1 &&
                         Math.abs(lastState.center[1] - newState.center[1]) < 1;
      const zoomSame = Math.abs(lastState.zoom - newState.zoom) < 0.1;
      if (centerSame && zoomSame) return;
    }
    
    // Add new state
    this.viewHistory.push(newState);
    
    // Limit history size
    if (this.viewHistory.length > this.maxViewHistorySize) {
      this.viewHistory.shift();
    }
    
    this.viewHistoryIndex = this.viewHistory.length - 1;
  }

  /**
   * Navigate to previous view in history
   */
  previousView(): void {
    if (this.viewHistoryIndex <= 0) {
      this.showInstructions('No previous view available');
      setTimeout(() => this.hideInstructions(), 1500);
      return;
    }
    
    this.isNavigatingHistory = true;
    this.viewHistoryIndex--;
    
    const state = this.viewHistory[this.viewHistoryIndex];
    const view = this.map.getView();
    
    view.animate({
      center: state.center,
      zoom: state.zoom,
      duration: 300
    }, () => {
      this.isNavigatingHistory = false;
    });
    
    this.showInstructions(`View ${this.viewHistoryIndex + 1} of ${this.viewHistory.length}`);
    setTimeout(() => this.hideInstructions(), 1500);
  }

  /**
   * Navigate to next view in history
   */
  nextView(): void {
    if (this.viewHistoryIndex >= this.viewHistory.length - 1) {
      this.showInstructions('No next view available');
      setTimeout(() => this.hideInstructions(), 1500);
      return;
    }
    
    this.isNavigatingHistory = true;
    this.viewHistoryIndex++;
    
    const state = this.viewHistory[this.viewHistoryIndex];
    const view = this.map.getView();
    
    view.animate({
      center: state.center,
      zoom: state.zoom,
      duration: 300
    }, () => {
      this.isNavigatingHistory = false;
    });
    
    this.showInstructions(`View ${this.viewHistoryIndex + 1} of ${this.viewHistory.length}`);
    setTimeout(() => this.hideInstructions(), 1500);
  }

  /**
   * Check if previous view is available
   */
  canGoPreviousView(): boolean {
    return this.viewHistoryIndex > 0;
  }

  /**
   * Check if next view is available
   */
  canGoNextView(): boolean {
    return this.viewHistoryIndex < this.viewHistory.length - 1;
  }

  /**
   * Clear view history
   */
  clearViewHistory(): void {
    this.viewHistory = [];
    this.viewHistoryIndex = -1;
    this.saveViewState();
  }

  private clearAll(): void {
    this.vectorSource.clear();
    this.roadSource.clear();
    this.projectSource.clear();
    this.biometricSource.clear();
    this.gpsSource.clear();
    this.waterSource.clear();
    this.separatorSource.clear();
  }

  private exportData(): void {
    const features = this.vectorSource.getFeatures();
    const geojson = this.format.writeFeatures(features, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326'
    });
    
    const blob = new Blob([geojson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `map-data-${new Date().toISOString().slice(0, 19)}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private importData(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const features = this.format.readFeatures(text, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857'
            });
            this.vectorSource.addFeatures(features);
          } catch (e) {
            alert('Invalid GeoJSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }

  // Segment drawing methods
  startSegmentDrawing(): void {
    this.isSegmentDrawing = true;
    this.currentSegmentPoints = [];
    this.removeInteractions();
    
    // Update UI to show drawing mode is active
    const startBtn = document.getElementById('startSegmentDrawing');
    const stopBtn = document.getElementById('stopSegmentDrawing');
    const instructions = document.getElementById('drawingInstructions');
    
    if (startBtn) startBtn.classList.add('segment-drawing-active');
    if (stopBtn) stopBtn.style.display = 'inline-block';
    if (instructions) instructions.classList.add('active');
    
    // Add click listener for drawing segments
    this.segmentDrawingListener = this.map.on('click', (event: any) => {
      this.addSegmentPoint(event.coordinate);
    });
    
    // Add double-click listener to finish segment
    this.map.on('dblclick', (event: any) => {
      this.finishSegmentDrawing();
    });
    
    // Add right-click listener to cancel segment
    this.map.on('pointerdown' as any, (event: any) => {
      if (event.originalEvent && event.originalEvent.button === 2) { // Right click
        event.preventDefault();
        this.cancelSegmentDrawing();
      }
    });
  }

  stopSegmentDrawing(): void {
    this.isSegmentDrawing = false;
    this.currentSegmentPoints = [];
    if (this.segmentDrawingListener) {
      this.map.un('click', this.segmentDrawingListener);
      this.segmentDrawingListener = null;
    }
    
    // Update UI to show drawing mode is inactive
    const startBtn = document.getElementById('startSegmentDrawing');
    const stopBtn = document.getElementById('stopSegmentDrawing');
    const instructions = document.getElementById('drawingInstructions');
    
    if (startBtn) startBtn.classList.remove('segment-drawing-active');
    if (stopBtn) stopBtn.style.display = 'none';
    if (instructions) instructions.classList.remove('active');
  }

  private addSegmentPoint(coordinate: number[]): void {
    this.currentSegmentPoints.push(coordinate);
    
    // Create a node at this point
    this.createNode(coordinate);
    
    // If we have at least 2 points, draw a line segment
    if (this.currentSegmentPoints.length >= 2) {
      this.drawSegmentLine();
    }
  }

  private createNode(coordinate: number[]): void {
    const nodeNumber = this.numberingMode === 'auto' ? this.nodeCounter : this.manualNodeNumber;
    const nodeId = `N${nodeNumber}`;
    
    const nodeFeature = new Feature({
      geometry: new Point(coordinate),
      type: 'node',
      nodeId: nodeId,
      nodeNumber: nodeNumber,
      info: `Node ${nodeId}`,
      style: 'node'
    });
    
    this.getCurrentDrawingSource().addFeature(nodeFeature);
    
    // Add node label
    const labelFeature = new Feature({
      geometry: new Point(coordinate),
      text: nodeId,
      textSize: 12,
      textColor: '#000000',
      type: 'label'
    });
    
    this.getCurrentDrawingSource().addFeature(labelFeature);
    
    // Update counters
    if (this.numberingMode === 'auto') {
      this.nodeCounter++;
    } else {
      this.manualNodeNumber++;
    }
    
    this.updateNodeCounter();
  }

  private drawSegmentLine(): void {
    if (this.currentSegmentPoints.length < 2) return;
    
    // Get the last two points
    const startPoint = this.currentSegmentPoints[this.currentSegmentPoints.length - 2];
    const endPoint = this.currentSegmentPoints[this.currentSegmentPoints.length - 1];
    
    // Create the line feature first
    const segmentFeature = new Feature({
      geometry: new LineString([startPoint, endPoint]),
      type: 'segment'
    });
    
    // Use autoNumberSegment to generate proper ID and label
    this.autoNumberSegment(segmentFeature);
    
    // Add the feature to the source
    this.getCurrentDrawingSource().addFeature(segmentFeature);
    
    // Update counters (only for main segments, autoNumberSegment handles subsegments)
    if (!segmentFeature.get('isSubSegment')) {
      if (this.numberingMode === 'auto') {
        this.segmentCounter++;
      } else {
        this.manualSegmentNumber++;
      }
    }
    
    this.updateSegmentCounter();
  }

  private finishSegmentDrawing(): void {
    this.stopSegmentDrawing();
    this.saveToHistory();
  }

  private cancelSegmentDrawing(): void {
    // Remove any temporary features
    this.currentSegmentPoints = [];
    this.stopSegmentDrawing();
  }

  private updateNodeCounter(): void {
    const nodeCounterElement = document.getElementById('nodeCounter');
    if (nodeCounterElement) {
      nodeCounterElement.textContent = this.nodeCounter.toString();
    }
  }

  private updateSegmentCounter(): void {
    const segmentCounterElement = document.getElementById('segmentCounter');
    if (segmentCounterElement) {
      segmentCounterElement.textContent = this.segmentCounter.toString();
    }
  }

  setNumberingMode(mode: 'auto' | 'manual'): void {
    this.numberingMode = mode;
    const numberingTypeSelect = document.getElementById('numberingType') as HTMLSelectElement;
    if (numberingTypeSelect) {
      numberingTypeSelect.value = mode;
    }
  }

  resetNumbering(): void {
    this.nodeCounter = 1;
    this.segmentCounter = 1;
    this.manualNodeNumber = 1;
    this.manualSegmentNumber = 1;
    this.updateNodeCounter();
    this.updateSegmentCounter();
  }

  setManualNodeNumber(number: number): void {
    this.manualNodeNumber = number;
  }

  setManualSegmentNumber(number: number): void {
    this.manualSegmentNumber = number;
  }

  // Enhanced segment management methods
  addSegmentComment(): void {
    const commentInput = document.getElementById('segmentComment') as HTMLInputElement;
    const comment = commentInput?.value || '';
    
    if (comment) {
      // Store comment for current segment
      console.log('Adding comment to segment:', comment);
      this.updateSegmentDisplay();
      commentInput.value = '';
    }
  }

  private updateSegmentDisplay(): void {
    const currentSegmentElement = document.getElementById('currentSegment');
    if (currentSegmentElement) {
      currentSegmentElement.textContent = `Current: Node ${this.nodeCounter}, Segment ${this.segmentCounter}`;
    }
  }

  // Junction and circle tools
  addJunction(type: string): void {
    this.removeInteractions();
    
    this.map.on('click', (event: any) => {
      const junctionFeature = this.createJunctionFeature(event.coordinate, type);
      this.getCurrentDrawingSource().addFeature(junctionFeature);
      this.saveToHistory();
    });
  }

  private createJunctionFeature(coordinate: number[], type: string): Feature {
    const junctionFeature = new Feature({
      geometry: new Point(coordinate),
      type: 'junction',
      junctionType: type,
      info: `${type} Junction`,
      timestamp: new Date(),
      properties: {
        width: 10,
        lanes: 2,
        surface: 'bituminous',
        status: 'active'
      }
    });

    // Add junction label
    const labelFeature = new Feature({
      geometry: new Point(coordinate),
      text: this.getJunctionLabel(type),
      textSize: 12,
      textColor: '#000000',
      type: 'junction-label',
      junctionType: type
    });

    this.getCurrentDrawingSource().addFeature(labelFeature);
    
    // If it's a roundabout, create the circular geometry
    if (type === 'roundabout') {
      this.createRoundaboutGeometry(coordinate, junctionFeature);
    }
    
    return junctionFeature;
  }

  private createRoundaboutGeometry(center: number[], junctionFeature: Feature): void {
    const radius = 20; // meters
    const points = 32; // number of points for smooth circle
    
    const circleCoords: number[][] = [];
    for (let i = 0; i < points; i++) {
      const angle = (i * 2 * Math.PI) / points;
      const x = center[0] + radius * Math.cos(angle);
      const y = center[1] + radius * Math.sin(angle);
      circleCoords.push([x, y]);
    }
    circleCoords.push(circleCoords[0]); // Close the circle
    
    const roundaboutFeature = new Feature({
      geometry: new LineString(circleCoords),
      type: 'roundabout',
      junctionId: junctionFeature.get('id'),
      radius: radius,
      info: 'Roundabout Circle'
    });
    
    this.getCurrentDrawingSource().addFeature(roundaboutFeature);
  }

  private getJunctionLabel(type: string): string {
    const labels: { [key: string]: string } = {
      '1': '→',
      '2': '↔',
      '3': '3',
      '4': '4',
      'roundabout': '⭕'
    };
    return labels[type] || 'J';
  }

  connectRoadToJunction(): void {
    this.removeInteractions();
    this.isConnectingRoads = true;
    
    // Show instructions
    this.showInstructions('Click on a junction first, then click on a road to connect them');
    
    this.map.on('click', (event: any) => {
      if (!this.selectedJunction) {
        // Select junction
        const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
          return feature.get('type') === 'junction' ? feature : null;
        });
        
        if (feature) {
          this.selectedJunction = feature as Feature;
          this.highlightJunction(feature as Feature);
          this.showInstructions('Now click on a road to connect to this junction');
        }
      } else {
        // Connect to road
        const roadFeature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
          return feature.get('type') === 'segment' || feature.get('type') === 'LineString' ? feature : null;
        });
        
        if (roadFeature) {
          this.createRoadJunctionConnection(this.selectedJunction!, roadFeature as Feature);
          this.clearJunctionSelection();
        }
      }
    });
  }

  connectSegments(): void {
    this.removeInteractions();
    this.isConnectingSegments = true;
    
    this.showInstructions('Click on two segments to connect them');
    
    this.map.on('click', (event: any) => {
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature.get('type') === 'segment' ? feature : null;
      });
      
      if (feature) {
        if (!this.firstSegment) {
          this.firstSegment = feature as Feature;
          this.highlightSegment(feature as Feature);
          this.showInstructions('Now click on the second segment to connect');
        } else if (feature !== this.firstSegment) {
          this.createSegmentConnection(this.firstSegment, feature as Feature);
          this.clearSegmentSelection();
        }
      }
    });
  }

  private createRoadJunctionConnection(junction: Feature, road: Feature): void {
    const junctionCoord = (junction.getGeometry() as Point).getCoordinates();
    const roadGeom = road.getGeometry() as LineString;
    const roadCoords = roadGeom.getCoordinates();
    
    // Find closest point on road to junction
    const closestPoint = this.findClosestPointOnLine(junctionCoord, roadCoords);
    
    // Create connection line
    const connectionFeature = new Feature({
      geometry: new LineString([junctionCoord, closestPoint]),
      type: 'connection',
      junctionId: junction.get('id'),
      roadId: road.get('id'),
      info: 'Road-Junction Connection'
    });
    
    this.getCurrentDrawingSource().addFeature(connectionFeature);
    this.saveToHistory();
  }

  private createSegmentConnection(segment1: Feature, segment2: Feature): void {
    const geom1 = segment1.getGeometry() as LineString;
    const geom2 = segment2.getGeometry() as LineString;
    
    const coords1 = geom1.getCoordinates();
    const coords2 = geom2.getCoordinates();
    
    // Find closest endpoints
    const connectionPoints = this.findClosestEndpoints(coords1, coords2);
    
    // Create connection line
    const connectionFeature = new Feature({
      geometry: new LineString(connectionPoints),
      type: 'segment-connection',
      segment1Id: segment1.get('id'),
      segment2Id: segment2.get('id'),
      info: 'Segment Connection'
    });
    
    this.getCurrentDrawingSource().addFeature(connectionFeature);
    this.saveToHistory();
  }

  private findClosestPointOnLine(point: number[], lineCoords: number[][]): number[] {
    let minDistance = Infinity;
    let closestPoint = lineCoords[0];
    
    for (let i = 0; i < lineCoords.length - 1; i++) {
      const segmentStart = lineCoords[i];
      const segmentEnd = lineCoords[i + 1];
      const closest = this.getClosestPointOnSegment(point, segmentStart, segmentEnd);
      const distance = this.calculateDistance(point, closest);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = closest;
      }
    }
    
    return closestPoint;
  }

  private getClosestPointOnSegment(point: number[], segStart: number[], segEnd: number[]): number[] {
    const A = point[0] - segStart[0];
    const B = point[1] - segStart[1];
    const C = segEnd[0] - segStart[0];
    const D = segEnd[1] - segStart[1];
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return segStart;
    
    const param = dot / lenSq;
    
    if (param < 0) return segStart;
    if (param > 1) return segEnd;
    
    return [
      segStart[0] + param * C,
      segStart[1] + param * D
    ];
  }

  private findClosestEndpoints(coords1: number[][], coords2: number[][]): number[][] {
    const endpoints1 = [coords1[0], coords1[coords1.length - 1]];
    const endpoints2 = [coords2[0], coords2[coords2.length - 1]];
    
    let minDistance = Infinity;
    let closestPair = [endpoints1[0], endpoints2[0]];
    
    for (const ep1 of endpoints1) {
      for (const ep2 of endpoints2) {
        const distance = this.calculateDistance(ep1, ep2);
        if (distance < minDistance) {
          minDistance = distance;
          closestPair = [ep1, ep2];
        }
      }
    }
    
    return closestPair;
  }

  private highlightJunction(feature: Feature): void {
    // Remove previous highlights
    this.clearHighlights();
    
    // Add highlight style
    feature.set('highlighted', true);
    this.highlightedFeature = feature;
    this.map.getView().changed();
  }

  private highlightSegment(feature: Feature): void {
    // Remove previous highlights
    this.clearHighlights();
    
    // Add highlight style
    feature.set('highlighted', true);
    this.highlightedFeature = feature;
    this.map.getView().changed();
  }

  private clearHighlights(): void {
    if (this.highlightedFeature) {
      this.highlightedFeature.set('highlighted', false);
      this.highlightedFeature = null;
      this.map.getView().changed();
    }
  }

  private clearJunctionSelection(): void {
    this.selectedJunction = null;
    this.isConnectingRoads = false;
    this.clearHighlights();
    this.hideInstructions();
  }

  private clearSegmentSelection(): void {
    this.firstSegment = null;
    this.isConnectingSegments = false;
    this.clearHighlights();
    this.hideInstructions();
  }

  /** Compute cumulative length from first to last point along a LineString (all segments). */
  private getLineGeometryLengthMeters(geom: any): number {
    if (!geom || geom.getType() !== 'LineString') return 0;
    const coords = (geom as LineString).getCoordinates();
    if (coords.length < 2) return 0;
    let length = 0;
    for (let i = 1; i < coords.length; i++) {
      length += getGeodesicLength(new LineString([coords[i - 1], coords[i]]), { projection: 'EPSG:3857' });
    }
    return length;
  }

  private setupRealtimeLineLength(sketch: Feature): void {
    this.clearRealtimeLineLength(null);
    const geom = sketch?.getGeometry();
    if (!geom) return;
    this.sketchGeometryRef = geom;
    this.sketchLengthUpdateFn = () => {
      const len = this.getLineGeometryLengthMeters(geom);
      const msg = len >= 0.01
        ? `Length: ${len.toFixed(2)} m (${(len / 1000).toFixed(4)} km) — Click to add points, double-click to finish`
        : 'Click to start line, double-click to finish';
      this.showInstructions(msg);
    };
    geom.on('change', this.sketchLengthUpdateFn);
    this.sketchLengthUpdateFn();
  }

  private clearRealtimeLineLength(_sketch: Feature | null): void {
    if (this.sketchGeometryRef && this.sketchLengthUpdateFn) {
      try {
        this.sketchGeometryRef.un('change', this.sketchLengthUpdateFn);
      } catch (_) {}
    }
    this.sketchGeometryRef = null;
    this.sketchLengthUpdateFn = null;
  }

  private showInstructions(message: string): void {
    const instructions = document.getElementById('drawingInstructions');
    if (instructions) {
      instructions.textContent = message;
      instructions.classList.add('active');
    }
  }

  private hideInstructions(): void {
    const instructions = document.getElementById('drawingInstructions');
    if (instructions) {
      instructions.classList.remove('active');
    }
  }

  // Junction management methods
  editJunctionProperties(junction: Feature): void {
    const properties = junction.get('properties') || {};
    const junctionType = junction.get('junctionType');
    
    // Create properties dialog
    const dialog = document.createElement('div');
    dialog.className = 'junction-properties-dialog';
    dialog.innerHTML = `
      <div class="dialog-content">
        <h3>Edit Junction Properties</h3>
        <div class="form-group">
          <label>Junction Type:</label>
          <select id="junctionTypeSelect">
            <option value="1" ${junctionType === '1' ? 'selected' : ''}>One Way</option>
            <option value="2" ${junctionType === '2' ? 'selected' : ''}>Two Way</option>
            <option value="3" ${junctionType === '3' ? 'selected' : ''}>3-Way</option>
            <option value="4" ${junctionType === '4' ? 'selected' : ''}>4-Way</option>
            <option value="roundabout" ${junctionType === 'roundabout' ? 'selected' : ''}>Roundabout</option>
          </select>
        </div>
        <div class="form-group">
          <label>Width (m):</label>
          <input type="number" id="junctionWidth" value="${properties.width || 10}" min="3" max="50">
        </div>
        <div class="form-group">
          <label>Lanes:</label>
          <input type="number" id="junctionLanes" value="${properties.lanes || 2}" min="1" max="8">
        </div>
        <div class="form-group">
          <label>Surface:</label>
          <select id="junctionSurface">
            <option value="bituminous" ${properties.surface === 'bituminous' ? 'selected' : ''}>Bituminous</option>
            <option value="concrete" ${properties.surface === 'concrete' ? 'selected' : ''}>Concrete</option>
            <option value="gravel" ${properties.surface === 'gravel' ? 'selected' : ''}>Gravel</option>
          </select>
        </div>
        <div class="form-group">
          <label>Status:</label>
          <select id="junctionStatus">
            <option value="active" ${properties.status === 'active' ? 'selected' : ''}>Active</option>
            <option value="under-construction" ${properties.status === 'under-construction' ? 'selected' : ''}>Under Construction</option>
            <option value="planned" ${properties.status === 'planned' ? 'selected' : ''}>Planned</option>
            <option value="closed" ${properties.status === 'closed' ? 'selected' : ''}>Closed</option>
          </select>
        </div>
        <div class="button-group">
          <button id="saveJunctionProps" class="btn">Save</button>
          <button id="cancelJunctionProps" class="btn">Cancel</button>
          <button id="deleteJunction" class="btn delete">Delete</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(dialog);
    
    // Event listeners
    document.getElementById('saveJunctionProps')?.addEventListener('click', () => {
      this.saveJunctionProperties(junction, dialog);
    });
    
    document.getElementById('cancelJunctionProps')?.addEventListener('click', () => {
      document.body.removeChild(dialog);
    });
    
    document.getElementById('deleteJunction')?.addEventListener('click', () => {
      this.deleteJunction(junction);
      document.body.removeChild(dialog);
    });
  }

  private saveJunctionProperties(junction: Feature, dialog: HTMLElement): void {
    const type = (document.getElementById('junctionTypeSelect') as HTMLSelectElement)?.value;
    const width = parseFloat((document.getElementById('junctionWidth') as HTMLInputElement)?.value || '10');
    const lanes = parseInt((document.getElementById('junctionLanes') as HTMLInputElement)?.value || '2');
    const surface = (document.getElementById('junctionSurface') as HTMLSelectElement)?.value;
    const status = (document.getElementById('junctionStatus') as HTMLSelectElement)?.value;
    
    // Update junction properties
    junction.set('junctionType', type);
    junction.set('properties', {
      width: width,
      lanes: lanes,
      surface: surface,
      status: status
    });
    
    // Update label
    const labelFeature = this.vectorSource.getFeatures().find(f => 
      f.get('type') === 'junction-label' && 
      f.get('junctionType') === junction.get('junctionType')
    );
    
    if (labelFeature) {
      labelFeature.set('text', this.getJunctionLabel(type));
      labelFeature.set('junctionType', type);
    }
    
    // If type changed to/from roundabout, update geometry
    if (type === 'roundabout' && junction.get('junctionType') !== 'roundabout') {
      this.createRoundaboutGeometry(
        (junction.getGeometry() as Point).getCoordinates(), 
        junction
      );
    } else if (type !== 'roundabout' && junction.get('junctionType') === 'roundabout') {
      // Remove roundabout geometry
      const roundaboutFeatures = this.vectorSource.getFeatures().filter(f => 
        f.get('type') === 'roundabout' && f.get('junctionId') === junction.get('id')
      );
      roundaboutFeatures.forEach(f => this.vectorSource.removeFeature(f));
    }
    
    this.map.getView().changed();
    document.body.removeChild(dialog);
  }

  private deleteJunction(junction: Feature): void {
    // Remove junction
    this.vectorSource.removeFeature(junction);
    
    // Remove associated features
    const junctionId = junction.get('id');
    const associatedFeatures = this.vectorSource.getFeatures().filter(f => 
      f.get('junctionId') === junctionId || 
      f.get('type') === 'junction-label' ||
      f.get('type') === 'roundabout'
    );
    
    associatedFeatures.forEach(f => this.vectorSource.removeFeature(f));
    
    // Remove connections
    const connections = this.vectorSource.getFeatures().filter(f => 
      f.get('junctionId') === junctionId
    );
    
    connections.forEach(f => this.vectorSource.removeFeature(f));
    
    this.saveToHistory();
  }

  // Add right-click context menu for junctions
  private setupJunctionContextMenu(): void {
    this.map.on('contextmenu' as any, (event: any) => {
      event.preventDefault();
      
      const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => {
        return feature.get('type') === 'junction' ? feature : null;
      });
      
      if (feature) {
        this.showJunctionContextMenu(event.originalEvent, feature as Feature);
      }
    });
  }

  private showJunctionContextMenu(event: MouseEvent, junction: Feature): void {
    const contextMenu = document.createElement('div');
    contextMenu.className = 'context-menu';
    contextMenu.style.position = 'fixed';
    contextMenu.style.left = event.clientX + 'px';
    contextMenu.style.top = event.clientY + 'px';
    contextMenu.style.zIndex = '10000';
    contextMenu.innerHTML = `
      <div class="context-menu-item" data-action="edit">Edit Properties</div>
      <div class="context-menu-item" data-action="connect">Connect Road</div>
      <div class="context-menu-item" data-action="delete">Delete Junction</div>
    `;
    
    document.body.appendChild(contextMenu);
    
    // Event listeners
    contextMenu.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.getAttribute('data-action');
      
      switch (action) {
        case 'edit':
          this.editJunctionProperties(junction);
          break;
        case 'connect':
          this.selectedJunction = junction;
          this.connectRoadToJunction();
          break;
        case 'delete':
          this.deleteJunction(junction);
          break;
      }
      
      document.body.removeChild(contextMenu);
    });
    
    // Remove context menu when clicking elsewhere
    document.addEventListener('click', () => {
      if (document.body.contains(contextMenu)) {
        document.body.removeChild(contextMenu);
      }
    }, { once: true });
  }

  // Overlay controls
  overlayRoads(): void {
    this.showInstructions('Loading road overlay data...');
    
    // Create mock road overlay data
    setTimeout(() => {
      const center = this.map.getView().getCenter()!;
      const mockRoads = this.generateMockRoadOverlay(center);
      
      mockRoads.forEach(road => {
        this.roadSource.addFeature(road);
      });
      
      this.showInstructions(`Loaded ${mockRoads.length} road segments`);
      setTimeout(() => this.hideInstructions(), 2000);
    }, 1000);
  }

  private generateMockRoadOverlay(center: number[]): Feature[] {
    const roads: Feature[] = [];
    
    // Generate a grid of roads around the center
    for (let i = -3; i <= 3; i++) {
      for (let j = -3; j <= 3; j++) {
        const startX = center[0] + i * 500;
        const startY = center[1] + j * 500;
        const endX = center[0] + (i + 1) * 500;
        const endY = center[1] + (j + 1) * 500;
        
        // Horizontal road
        const hRoad = new Feature({
          geometry: new LineString([[startX, startY], [endX, startY]]),
          type: 'overlay_road',
          roadType: 'highway',
          lanes: 4
        });
        
        hRoad.setStyle(new Style({
          stroke: new Stroke({
            color: '#ff6b6b',
            width: 4,
            lineDash: [10, 5]
          })
        }));
        
        roads.push(hRoad);
        
        // Vertical road
        const vRoad = new Feature({
          geometry: new LineString([[startX, startY], [startX, endY]]),
          type: 'overlay_road',
          roadType: 'highway',
          lanes: 4
        });
        
        vRoad.setStyle(new Style({
          stroke: new Stroke({
            color: '#ff6b6b',
            width: 4,
            lineDash: [10, 5]
          })
        }));
        
        roads.push(vRoad);
      }
    }
    
    return roads;
  }

  clearOverlay(): void {
    // Clear overlay layers
    this.roadSource.clear();
    this.projectSource.clear();
  }

  applyLayerOffset(): void {
    const offsetX = parseFloat((document.getElementById('layerOffsetX') as HTMLInputElement)?.value || '0');
    const offsetY = parseFloat((document.getElementById('layerOffsetY') as HTMLInputElement)?.value || '0');
    
    this.layerOffsets['drawing'] = { x: offsetX, y: offsetY };
    this.updateLayerOffsets();
  }

  private updateLayerOffsets(): void {
    // Apply offsets to layers
    Object.keys(this.layerOffsets).forEach(layerName => {
      const offset = this.layerOffsets[layerName];
      // Implementation for applying offsets
    });
  }

  // Enhanced style methods for segments and nodes (resolution for dynamic symbol scaling)
  private getEnhancedDrawingStyle(feature: FeatureLike, resolution?: number): Style | Style[] {
    const featureType = feature.get('type');
    const symbol = feature.get('symbol');
    const text = feature.get('text');
    const thickness = feature.get('thickness') || 2;
    const scale = this.getResolutionScale(resolution);
    const size = (feature.get('symbolSize') || 1.0) * scale;
    const roadSpecs = feature.get('roadSpecs');
    const type = feature.get('type');
    const isUnderground = feature.get('underground') || false;
    
    let style: Style[] = [];
    
    // Handle different feature types
    if (featureType === 'node') {
      style.push(this.createNodeStyle(feature));
    } else if (featureType === 'segment') {
      style.push(this.createSegmentStyle(feature));
    } else if (featureType === 'junction') {
      style.push(this.createJunctionStyle(feature));
    } else if (featureType === 'connection' || featureType === 'segment-connection') {
      style.push(this.createConnectionStyle(feature));
    } else if (featureType === 'roundabout') {
      style.push(this.createRoundaboutStyle(feature));
    } else if (featureType === 'label' || featureType === 'segment-label' || featureType === 'junction-label') {
      const color = feature.get('textColor') || '#000000';
      const fontSize = feature.get('textSize') || 12;
      const textStyle = this.createTextStyle(text, color, fontSize);
      if (textStyle) style.push(textStyle);
    } else if (feature.get('iconUrl')) {
      style.push(this.createIconUrlStyle(feature.get('iconUrl'), size));
    } else if (symbol) {
      style.push(this.createSymbolStyle(symbol, size));
    } else if (text) {
      const color = feature.get('textColor') || '#000000';
      const fontSize = feature.get('textSize') || 14;
      const textStyle = this.createTextStyle(text, color, fontSize);
      if (textStyle) style.push(textStyle);
    } else if (roadSpecs) {
      style = this.createDrawingStyle(thickness, roadSpecs);
    } else if (type && type.endsWith('Duct')) {
      const ductStyle = this.createDuctStyle(type, feature.get('grade') || (isUnderground ? 'underground' : 'overground'));
      if (ductStyle) style.push(ductStyle);
    } else {
      const singleStyle = this.createDrawingStyle(thickness);
      if (Array.isArray(singleStyle)) {
        style.push(...singleStyle);
      } else {
        style.push(singleStyle);
      }
    }
    
    return style;
  }

  private createTextStyle(text: string, color: string, fontSize: number): Style | null {
    if (!text) return null;
    
    return new Style({
      text: new TextStyle({
        text: text,
        font: `${fontSize}px Arial`,
        fill: new Fill({
          color: color
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  private createSymbolStyle(symbol: string, size: number): Style {
    return new Style({
      image: new CircleStyle({
        radius: size * 5,
        fill: new Fill({ color: '#3498db' }),
        stroke: new Stroke({ color: '#2980b9', width: 2 })
      })
    });
  }

  /** Style for custom icon (SVG or PNG URL). */
  private createIconUrlStyle(url: string, size: number): Style {
    return new Style({
      image: new Icon({
        src: url,
        scale: size * 0.15,
        anchor: [0.5, 0.5],
        crossOrigin: 'anonymous'
      })
    });
  }

  private createDuctStyle(type: string, gradeOrUnderground: string | boolean): Style {
    const grade = typeof gradeOrUnderground === 'boolean'
      ? (gradeOrUnderground ? 'underground' : 'overground')
      : gradeOrUnderground;
    const opts = strokeOptsForGrade(grade);
    return new Style({
      stroke: new Stroke({
        color: opts.color,
        width: opts.width,
        lineDash: opts.lineDash,
      }),
    });
  }

  private createNodeStyle(feature: FeatureLike): Style {
    const nodeId = feature.get('nodeId') || 'N1';
    const isSelected = feature.get('selected') || false;
    
    return new Style({
      image: new CircleStyle({
        radius: isSelected ? 12 : 8,
        fill: new Fill({
          color: isSelected ? '#ff6b6b' : '#4ecdc4'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      }),
      text: new TextStyle({
        text: nodeId,
        font: 'bold 10px Arial',
        fill: new Fill({
          color: '#ffffff'
        }),
        stroke: new Stroke({
          color: '#000000',
          width: 1
        }),
        offsetY: -20
      })
    });
  }

  private createJunctionStyle(feature: FeatureLike): Style {
    const junctionType = feature.get('junctionType') || '1';
    const isHighlighted = feature.get('highlighted') || false;
    const isSelected = feature.get('selected') || false;
    
    let color = '#e74c3c'; // Default red
    let radius = 10;
    
    // Different colors for different junction types
    switch (junctionType) {
      case '1':
        color = '#3498db'; // Blue for one-way
        break;
      case '2':
        color = '#2ecc71'; // Green for two-way
        break;
      case '3':
        color = '#f39c12'; // Orange for 3-way
        break;
      case '4':
        color = '#9b59b6'; // Purple for 4-way
        break;
      case 'roundabout':
        color = '#e67e22'; // Dark orange for roundabout
        radius = 15;
        break;
    }
    
    if (isHighlighted) {
      color = '#ff6b6b';
      radius = 15;
    } else if (isSelected) {
      color = '#ffa500';
      radius = 12;
    }
    
    return new Style({
      image: new CircleStyle({
        radius: radius,
        fill: new Fill({
          color: color
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  private createConnectionStyle(feature: FeatureLike): Style {
    const connectionType = feature.get('type');
    let color = '#34495e';
    let width = 2;
    let lineDash: number[] = [];
    
    if (connectionType === 'connection') {
      color = '#e74c3c';
      width = 3;
      lineDash = [5, 5];
    } else if (connectionType === 'segment-connection') {
      color = '#3498db';
      width = 2;
      lineDash = [3, 3];
    }
    
    return new Style({
      stroke: new Stroke({
        color: color,
        width: width,
        lineDash: lineDash
      })
    });
  }

  private createRoundaboutStyle(feature: FeatureLike): Style {
    const radius = feature.get('radius') || 20;
    const isHighlighted = feature.get('highlighted') || false;
    
    let color = '#e67e22';
    let width = 4;
    
    if (isHighlighted) {
      color = '#ff6b6b';
      width = 6;
    }
    
    return new Style({
      stroke: new Stroke({
        color: color,
        width: width,
        lineCap: 'round'
      }),
      fill: new Fill({
        color: 'rgba(230, 126, 34, 0.1)'
      })
    });
  }

  private createSegmentStyle(feature: FeatureLike): Style {
    const segmentId = feature.get('segmentId') || 'S1';
    const isSelected = feature.get('selected') || false;
    
    return new Style({
      stroke: new Stroke({
        color: isSelected ? '#ff6b6b' : '#e74c3c',
        width: isSelected ? 4 : 3,
        lineDash: isSelected ? [] : [5, 5]
      }),
      text: new TextStyle({
        text: segmentId,
        font: 'bold 10px Arial',
        fill: new Fill({
          color: '#000000'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  // Toolbar Methods
  private zoomFit(): void {
    const view = this.map.getView();
    const features = this.vectorSource.getFeatures();
    if (features.length > 0) {
      const extent = this.vectorSource.getExtent();
      view.fit(extent, { padding: [20, 20, 20, 20] });
    }
  }

  private zoomExtent(): void {
    const view = this.map.getView();
    const extent = this.map.getView().getProjection().getExtent();
    view.fit(extent, { padding: [20, 20, 20, 20] });
  }

  private enablePan(): void {
    this.removeInteractions();
    // Pan is enabled by default in OpenLayers
  }

  private enableDistanceMeasurement(): void {
    this.removeInteractions();
    this.measureInteraction = new Draw({
      source: this.vectorSource,
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    });

    this.measureInteraction.on('drawend', (event: any) => {
      const feature = event.feature;
      const length = this.calculateLength(feature);
      this.showMeasurementResult(`Distance: ${length.meters} m (${length.km} km)`);
    });

    this.map.addInteraction(this.measureInteraction);
  }

  private enableAreaMeasurement(): void {
    this.removeInteractions();
    this.measureInteraction = new Draw({
      source: this.vectorSource,
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 0, 0, 0.2)'
        }),
        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    });

    this.measureInteraction.on('drawend', (event: any) => {
      const feature = event.feature;
      const area = this.calculateArea(feature);
      this.showMeasurementResult(`Area: ${area.squareMeters} m² (${area.squareKm} km²)`);
    });

    this.map.addInteraction(this.measureInteraction);
  }

  private enableBearingMeasurement(): void {
    this.removeInteractions();
    this.measureInteraction = new Draw({
      source: this.vectorSource,
      type: 'LineString',
      style: new Style({
        stroke: new Stroke({
          color: '#ff0000',
          width: 2
        })
      })
    });

    this.measureInteraction.on('drawend', (event: any) => {
      const feature = event.feature;
      const bearing = this.calculateBearing(feature);
      this.showMeasurementResult(`Bearing: ${bearing}°`);
    });

    this.map.addInteraction(this.measureInteraction);
  }

  private calculateArea(feature: Feature): { squareMeters: string; squareKm: string } {
    const geom = feature.getGeometry();
    if (!geom) {
      return { squareMeters: '0.00', squareKm: '0.00' };
    }
    
    let area = 0;
    
    try {
      if (geom.getType() === 'Polygon' || geom.getType() === 'MultiPolygon') {
        // Use OpenLayers geodesic calculation (most accurate)
        area = getGeodesicArea(geom, { projection: 'EPSG:3857' });
        
        // Fallback to turf.js if available and OL calculation fails
        if (isNaN(area) || area === 0) {
          const geojsonString = this.format.writeGeometry(geom, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          if (typeof turf !== 'undefined' && geojsonString) {
            try {
              const geojsonObj = JSON.parse(geojsonString);
              area = turf.area(geojsonObj);
            } catch (e) {
              console.warn('Turf.js area calculation failed:', e);
            }
          }
        }
      } else if (geom.getType() === 'Circle') {
        // For circles, calculate area
        const radius = (geom as Circle).getRadius();
        // Convert from Web Mercator units to meters (approximate at center latitude)
        const center = (geom as Circle).getCenter();
        const centerLonLat = toLonLat(center);
        const latFactor = Math.cos(centerLonLat[1] * Math.PI / 180);
        const radiusMeters = radius * latFactor;
        area = Math.PI * radiusMeters * radiusMeters;
      }
    } catch (error) {
      console.error('Error calculating area:', error);
    }
    
    // Ensure positive area (some calculations might return negative for counter-clockwise polygons)
    area = Math.abs(area);
    
    const squareKm = area / 1000000;
    return {
      squareMeters: area.toFixed(2),
      squareKm: squareKm.toFixed(6)
    };
  }

  private calculateBearing(feature: Feature): string {
    const geom = feature.getGeometry() as LineString;
    const coordinates = geom.getCoordinates();
    if (coordinates.length >= 2) {
      const start = coordinates[0];
      const end = coordinates[coordinates.length - 1];
      const bearing = Math.atan2(end[0] - start[0], end[1] - start[1]) * 180 / Math.PI;
      return ((bearing + 360) % 360).toFixed(1);
    }
    return '0';
  }

  private showMeasurementResult(result: string): void {
    const measurementDisplay = document.getElementById('measurementDisplay');
    if (measurementDisplay) {
      measurementDisplay.style.display = 'block';
      const measurementResults = document.getElementById('measurementResults');
      if (measurementResults) {
        measurementResults.innerHTML = result;
      }
    }
  }

  private undo(): void {
    if (this.historyStep > 0) {
      this.historyStep--;
      this.restoreFromHistory();
    }
  }

  private redo(): void {
    if (this.historyStep < this.drawingHistory.length - 1) {
      this.historyStep++;
      this.restoreFromHistory();
    }
  }

  private restoreFromHistory(): void {
    if (this.drawingHistory[this.historyStep]) {
      const state = this.drawingHistory[this.historyStep];
      this.vectorSource.clear();
      this.vectorSource.addFeatures(state.vectorSource);
      this.roadSource.clear();
      this.roadSource.addFeatures(state.roadSource);
    }
  }

  private printMap(): void {
    this.openDialog('printDialog');
  }

  /**
   * Execute print with configured options
   */
  executePrint(): void {
    this.showInstructions('Preparing map for print...');
    
    // Get print options from dialog
    const paperSize = (document.getElementById('paperSize') as HTMLSelectElement)?.value || 'a4';
    const orientation = (document.getElementById('orientation') as HTMLSelectElement)?.value || 'landscape';
    const printTitle = (document.getElementById('printTitle') as HTMLInputElement)?.value || 'GIS Map';
    const printScale = (document.getElementById('printScale') as HTMLInputElement)?.value || '';
    
    // Get include options
    const includeLegend = (document.getElementById('includeLegend') as HTMLInputElement)?.checked ?? true;
    const includeScale = (document.getElementById('includeScale') as HTMLInputElement)?.checked ?? true;
    const includeNorth = (document.getElementById('includeNorth') as HTMLInputElement)?.checked ?? true;
    const includeDate = (document.getElementById('includeDate') as HTMLInputElement)?.checked ?? true;
    
    // Paper dimensions (in mm)
    const paperDimensions: { [key: string]: { width: number; height: number } } = {
      'a4': { width: 297, height: 210 },
      'a3': { width: 420, height: 297 },
      'letter': { width: 279, height: 216 },
      'legal': { width: 356, height: 216 }
    };
    
    const paper = paperDimensions[paperSize] || paperDimensions['a4'];
    const isLandscape = orientation === 'landscape';
    const pageWidth = isLandscape ? paper.width : paper.height;
    const pageHeight = isLandscape ? paper.height : paper.width;
    
    // Convert mm to pixels (assuming 96 DPI for screen)
    const dpi = 150; // Higher DPI for better print quality
    const mmToPixels = (mm: number) => Math.round(mm * dpi / 25.4);
    const mapWidth = mmToPixels(pageWidth - 20); // 10mm margin on each side
    const mapHeight = mmToPixels(pageHeight - 50); // Space for header/footer
    
    // Render map to canvas
    this.map.once('rendercomplete', () => {
      const mapCanvas = document.createElement('canvas');
      const mapContext = mapCanvas.getContext('2d')!;
      
      mapCanvas.width = mapWidth;
      mapCanvas.height = mapHeight;
      
      // Draw white background
      mapContext.fillStyle = 'white';
      mapContext.fillRect(0, 0, mapWidth, mapHeight);
      
      // Get all canvas elements from the map
      const mapElement = this.mapElement.nativeElement;
      const canvases = mapElement.querySelectorAll('canvas');
      
      // Calculate scaling to fit map in print area
      const sourceWidth = this.map.getSize()![0];
      const sourceHeight = this.map.getSize()![1];
      const scale = Math.min(mapWidth / sourceWidth, mapHeight / sourceHeight);
      
      // Center the map
      const offsetX = (mapWidth - sourceWidth * scale) / 2;
      const offsetY = (mapHeight - sourceHeight * scale) / 2;
      
      canvases.forEach((canvas: HTMLCanvasElement) => {
        if (canvas.width > 0) {
          mapContext.drawImage(canvas, offsetX, offsetY, sourceWidth * scale, sourceHeight * scale);
        }
      });
      
      // Create print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        this.showInstructions('Failed to open print window. Check popup blocker.');
        return;
      }
      
      const currentDate = new Date().toLocaleDateString();
      const currentView = this.map.getView();
      const center = toLonLat(currentView.getCenter()!);
      const zoom = currentView.getZoom();
      
      // Generate legend HTML if needed
      let legendHtml = '';
      if (includeLegend) {
        legendHtml = `
          <div class="legend">
            <h4>Legend</h4>
            <div class="legend-item"><span class="legend-color" style="background: #3498db;"></span> Drawing Features</div>
            <div class="legend-item"><span class="legend-color" style="background: #e74c3c;"></span> Road Network</div>
            <div class="legend-item"><span class="legend-color" style="background: #27ae60;"></span> Project Areas</div>
            <div class="legend-item"><span class="legend-color" style="background: #9b59b6;"></span> Imported Points</div>
          </div>
        `;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${printTitle} - Print</title>
            <style>
              @page {
                size: ${pageWidth}mm ${pageHeight}mm;
                margin: 10mm;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: Arial, sans-serif;
                color: #333;
              }
              .print-container {
                width: 100%;
                max-width: ${pageWidth - 20}mm;
                margin: 0 auto;
              }
              .header {
                text-align: center;
                padding: 10px 0;
                border-bottom: 2px solid #2c3e50;
                margin-bottom: 10px;
              }
              .header h1 {
                margin: 0;
                font-size: 24px;
                color: #2c3e50;
              }
              .header .subtitle {
                font-size: 12px;
                color: #7f8c8d;
                margin-top: 5px;
              }
              .map-container {
                text-align: center;
                position: relative;
              }
              .map-container img {
                max-width: 100%;
                height: auto;
                border: 1px solid #ccc;
              }
              .footer {
                display: flex;
                justify-content: space-between;
                padding: 10px 0;
                border-top: 1px solid #ccc;
                margin-top: 10px;
                font-size: 10px;
                color: #7f8c8d;
              }
              .north-arrow {
                position: absolute;
                top: 10px;
                right: 10px;
                font-size: 24px;
                background: rgba(255,255,255,0.9);
                padding: 5px 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
              }
              .scale-bar {
                position: absolute;
                bottom: 10px;
                left: 10px;
                background: rgba(255,255,255,0.9);
                padding: 5px 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
                font-size: 10px;
              }
              .legend {
                position: absolute;
                bottom: 10px;
                right: 10px;
                background: rgba(255,255,255,0.95);
                padding: 10px;
                border: 1px solid #ccc;
                border-radius: 4px;
                font-size: 10px;
              }
              .legend h4 {
                margin: 0 0 8px 0;
                font-size: 12px;
              }
              .legend-item {
                display: flex;
                align-items: center;
                margin: 4px 0;
              }
              .legend-color {
                width: 20px;
                height: 10px;
                margin-right: 8px;
                border: 1px solid #999;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              <div class="header">
                <h1>${printTitle}</h1>
                <div class="subtitle">
                  ${printScale ? `Scale: ${printScale} | ` : ''}
                  Center: ${center[1].toFixed(4)}°N, ${center[0].toFixed(4)}°E | Zoom: ${zoom?.toFixed(1)}
                </div>
              </div>
              <div class="map-container">
                <img src="${mapCanvas.toDataURL('image/png')}" alt="Map">
                ${includeNorth ? '<div class="north-arrow">⬆️ N</div>' : ''}
                ${includeScale ? '<div class="scale-bar">Scale: 1:' + Math.round(this.getMapScale()) + '</div>' : ''}
                ${legendHtml}
              </div>
              <div class="footer">
                ${includeDate ? `<span>Printed: ${currentDate}</span>` : '<span></span>'}
                <span>Generated by GIS Map Editor</span>
                <span>Project: ${this.projectData?.project_name || 'N/A'}</span>
              </div>
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      this.closeDialog('printDialog');
      this.showInstructions('Print dialog opened');
    });
    
    // Force a render to capture the current state
    this.map.renderSync();
  }

  /**
   * Preview print layout
   */
  previewPrint(): void {
    this.executePrint();
  }

  /**
   * Calculate current map scale
   */
  private getMapScale(): number {
    const view = this.map.getView();
    const resolution = view.getResolution();
    if (!resolution) return 10000;
    
    // Convert resolution (meters per pixel) to scale
    // Assuming 0.28mm pixel size (standard screen)
    const metersPerPixel = resolution;
    const pixelsPerMeter = 1 / 0.00028; // 0.28mm = 0.00028m
    
    return Math.round(metersPerPixel * pixelsPerMeter);
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      this.mapElement.nativeElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  private toggleMiniMap(): void {
    // Implementation for mini-map toggle
    console.log('Mini-map toggle functionality');
  }

  private showHelp(): void {
    alert(`GIS Map Editor Help:

Drawing Tools:
• Select a drawing mode from the dropdown
• Use line width and symbol size sliders to adjust appearance
• Click on the map to draw features

Segment Management:
• Use "Start Drawing" to begin segment creation
• Click on map to place nodes
• Segments are automatically created between consecutive nodes
• Double-click to finish drawing

Measurement Tools:
• Distance: Click two points to measure distance
• Area: Draw a polygon to measure area
• Bearing: Draw a line to measure bearing

Navigation:
• Zoom: Use +/- buttons or mouse wheel
• Pan: Click and drag to move around
• Fit: Automatically fit all features in view

For more help, contact the system administrator.`);
  }

  private checkForProjectId(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.projectId = params['id'];
        this.fetchProjectDetails();
      } else {
        // Check if there's a projectId in query params as fallback
        this.route.queryParams.subscribe(queryParams => {
          if (queryParams['id']) {
            this.projectId = queryParams['id'];
            this.fetchProjectDetails();
          }
        });
      }
    });
  }


  centerMapOnProjectLocation(location: any): void {
    if (!this.map || !location.lat || !location.long) return;
    
    try {
      const coordinate = fromLonLat([location.long, location.lat]);
      this.map.getView().setCenter(coordinate);
      this.map.getView().setZoom(15); // Set appropriate zoom level
      
      // Add a marker for the project location
      this.addProjectLocationMarker(location);
    } catch (error) {
      console.error('Error centering map on project location:', error);
    }
  }

  private addProjectLocationMarker(location: any): void {
    if (!this.map) return;
    
    try {
      const coordinate = fromLonLat([location.long, location.lat]);
      
      // Create a feature for the project location
      const marker = new Feature({
        geometry: new Point(coordinate),
        name: this.projectData?.project_name || 'Project Location',
        type: 'project_location'
      });

      // Style the marker
      const markerStyle = new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#ffd700' }),
          stroke: new Stroke({ color: '#ffffff', width: 3 })
        }),
        text: new TextStyle({
          text: '📍',
          font: '16px Arial',
          offsetY: -20,
          fill: new Fill({ color: '#ffffff' }),
          stroke: new Stroke({ color: '#000000', width: 2 })
        })
      });

      marker.setStyle(markerStyle);

      // Add to project layer
      if (this.projectSource) {
        this.projectSource.addFeature(marker);
      }
    } catch (error) {
      console.error('Error adding project location marker:', error);
    }
  }

  toggleProjectDetails(): void {
    this.showProjectDetails = !this.showProjectDetails;
  }

  closeProjectDetails(): void {
    this.showProjectDetails = false;
  }

  loadProjectDetails(projectId: string): void {
    this.projectId = projectId;
    this.boqList = [];
    this.bomList = [];
    this.projectSpent = null;
    this.fetchProjectDetails();
  }

  /**
   * Initialize backend integration
   */
  private initializeBackendIntegration(): void {
    // Initialize auto-save if enabled
    if (this.defaultSettings.autoSaveInterval > 0) {
      this.initializeAutoSave();
    }

    // Load available GIS layers from backend
    this.getAvailableLayers();

    console.log('Backend integration initialized');
  }

  /**
   * Enhanced fetch project details with GIS data loading
   */
  private fetchProjectDetails(): void {
    if (!this.projectId) return;

    this.isLoadingProject = true;
    this.showProjectDetails = !true;

    this.apiService.getProjectId(this.projectId).subscribe({
      next: (res: any) => {
        console.log('Project data:', res);
        this.projectData = res;
        this.isLoadingProject = false;
        res.location = res.location[0];
        // Center map on project location if available
        if (res.location && res.location.lat && res.location.long) {
          this.centerMapOnProjectLocation(res.location);
        }

        // Load GIS layers from backend after project is loaded
        this.loadAllLayersFromBackend();

        // Fetch project bills for overrun monitoring
        this.fetchProjectBillsForOverrun();
      },
      error: (err) => {
        console.error('Error fetching project details:', err);
        this.isLoadingProject = false;
        this.showProjectDetails = false;
      },
      complete: () => console.log('Project fetch complete'),
    });
  }

  // ==================== BACKEND INTEGRATION METHODS ====================

  /**
   * Save current layer data to PostGIS backend
   */
  saveLayerToBackend(layerId: string): void {
    try {
      const layer = this.layerRegistry[layerId];
      if (!layer) {
        this.showInstructions(`Layer '${layerId}' not found`);
        return;
      }

      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();

      // Convert features to GeoJSON
      const geoJson = {
        type: 'FeatureCollection',
        features: features.map(feature => ({
          type: 'Feature',
          id: feature.getId() || undefined,
          geometry: this.format.writeGeometry(feature.getGeometry()!),
          properties: feature.getProperties()
        }))
      };

      // Save to backend
      this.apiService.saveProjectGisData(this.projectId, layerId, {
        type: geoJson.type,
        features: geoJson.features,
        properties: {}
      } as any).subscribe({
        next: (response) => {
          console.log(`Layer ${layerId} saved successfully:`, response);
          this.showInstructions(`Layer '${layerId}' saved to database`);
          this.updateLayerStatistics(layerId);
        },
        error: (error) => {
          console.error(`Error saving layer ${layerId}:`, error);
          this.showInstructions(`Error saving layer '${layerId}' to database`);
        }
      });
    } catch (error) {
      console.error('Error in saveLayerToBackend:', error);
      this.showInstructions('Error saving layer data');
    }
  }

  /**
   * Load layer data from PostGIS backend
   */
  loadLayerFromBackend(layerId: string): void {
    try {
      this.apiService.loadProjectGisData(this.projectId, layerId).subscribe({
        next: (response) => {
          if (response && response.success && response.data) {
            console.log(`Layer ${layerId} loaded successfully:`, response.data);
            this.importLayer(layerId, typeof response.data === 'string' ? response.data : JSON.stringify(response.data));
            this.showInstructions(`Layer '${layerId}' loaded from database`);
            this.updateLayerStatistics(layerId);
          } else {
            console.log(`No data found for layer ${layerId}`);
          }
        },
        error: (error) => {
          console.error(`Error loading layer ${layerId}:`, error);
          this.showInstructions(`Error loading layer '${layerId}' from database`);
        }
      });
    } catch (error) {
      console.error('Error in loadLayerFromBackend:', error);
      this.showInstructions('Error loading layer data');
    }
  }

  /**
   * Auto-save layer data (debounced)
   */
  private autoSaveLayer(layerId: string): void {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }

    this.autoSaveTimer = setTimeout(() => {
      this.saveLayerToBackend(layerId);
    }, this.defaultSettings.autoSaveInterval * 1000);
  }

  /**
   * Create a new GIS feature in the backend
   */
  createGisFeature(layerId: string, feature: Feature): void {
    try {
      const geometry = this.format.writeGeometry(feature.getGeometry()!);
      const properties = feature.getProperties();

      const featureData = {
        name: properties['name'] || `Feature ${Date.now()}`,
        description: properties['description'] || '',
        geometry: geometry,
        properties: properties
      };

      this.apiService.createGisFeature(layerId, featureData).subscribe({
        next: (response) => {
          if (response.success) {
            feature.setId(response.data.id);
            console.log(`Feature created in ${layerId}:`, response.data);
            this.showInstructions('Feature saved to database');
          }
        },
        error: (error) => {
          console.error(`Error creating feature in ${layerId}:`, error);
          this.showInstructions('Error saving feature to database');
        }
      });
    } catch (error) {
      console.error('Error in createGisFeature:', error);
    }
  }

  /**
   * Update an existing GIS feature in the backend
   */
  updateGisFeature(layerId: string, feature: Feature): void {
    try {
      const featureId = feature.getId();
      if (!featureId) {
        console.warn('Feature has no ID, creating new feature instead');
        this.createGisFeature(layerId, feature);
        return;
      }

      const geometry = this.format.writeGeometry(feature.getGeometry()!);
      const properties = feature.getProperties();

      const featureData = {
        name: properties['name'],
        description: properties['description'],
        geometry: geometry,
        properties: properties
      };

      this.apiService.updateGisFeature(layerId, featureId as number, featureData).subscribe({
        next: (response) => {
          if (response.success) {
            console.log(`Feature updated in ${layerId}:`, response.data);
          }
        },
        error: (error) => {
          console.error(`Error updating feature in ${layerId}:`, error);
        }
      });
    } catch (error) {
      console.error('Error in updateGisFeature:', error);
    }
  }

  /**
   * Delete a GIS feature from the backend
   */
  deleteGisFeature(layerId: string, featureId: string | number): void {
    this.apiService.deleteGisFeature(layerId, featureId as number).subscribe({
      next: (response) => {
        if (response.success) {
          console.log(`Feature deleted from ${layerId}:`, response);
          this.showInstructions('Feature deleted from database');
        }
      },
      error: (error) => {
        console.error(`Error deleting feature from ${layerId}:`, error);
        this.showInstructions('Error deleting feature from database');
      }
    });
  }

  /**
   * Import GeoJSON data from backend
   */
  importGeoJSONFromBackend(layerId: string): void {
    console.log('Importing GeoJSON from backend:', layerId);
    this.apiService.loadProjectGisData(this.projectId, layerId).subscribe({
      next: (response) => {
        if (response && response.success && response.data) {
          this.importLayer(layerId, typeof response.data === 'string' ? response.data : JSON.stringify(response.data));
          this.showInstructions(`Layer '${layerId}' imported from database`);
          this.updateLayerStatistics(layerId);
        }
      },
      error: (error) => {
        console.error('Error importing from backend:', error);
        this.showInstructions('Error importing data from database');
      }
    });
  }

  /**
   * Export current layer as GeoJSON to backend
   */
  exportLayerToBackend(layerId: string): void {
    const geoJson = this.exportLayer(layerId);
    if (geoJson) {
      const geoJsonData = JSON.parse(geoJson);
      this.apiService.saveProjectGisData(this.projectId, layerId, {
        type: geoJsonData.type,
        features: geoJsonData.features,
        properties: {}
      } as any).subscribe({
        next: (response) => {
          console.log(`Layer ${layerId} exported to backend:`, response);
          this.showInstructions(`Layer '${layerId}' exported to database`);
        },
        error: (error) => {
          console.error(`Error exporting layer ${layerId} to backend:`, error);
          this.showInstructions('Error exporting layer to database');
        }
      });
    }
  }

  /**
   * Perform spatial query on backend
   */
  performSpatialQuery(queryData: any): void {
    this.apiService.performSpatialQuery(queryData).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Spatial query results:', response.data);
          this.showInstructions(`Spatial query completed: ${response.data.features.length} results`);

          // Display results in a new layer
          const resultsLayerId = `query_results_${Date.now()}`;
          this.importLayer(resultsLayerId, typeof response.data === 'string' ? response.data : JSON.stringify(response.data));
          this.showInstructions(`Query results shown in layer '${resultsLayerId}'`);
        }
      },
      error: (error) => {
        console.error('Error performing spatial query:', error);
        this.showInstructions('Error performing spatial query');
      }
    });
  }

  /**
   * Get layer statistics from backend
   */
  getLayerStatsFromBackend(layerId: string): void {
    this.apiService.getLayerStats(layerId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log(`Layer stats for ${layerId}:`, response.data);
          this.showInstructions(`Layer statistics: ${response.data.feature_count} features`);
        }
      },
      error: (error) => {
        console.error(`Error getting layer stats for ${layerId}:`, error);
        this.showInstructions('Error retrieving layer statistics');
      }
    });
  }

  /**
   * Validate geometry using backend
   */
  validateLayerGeometry(layerId: string): void {
    this.apiService.validateGeometry(layerId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const { invalid_features, invalid_count } = response.data;

          if (invalid_count === 0) {
            this.showInstructions('All geometries are valid');
          } else {
            this.showInstructions(`Found ${invalid_count} invalid geometries`);
            console.log('Invalid geometries:', invalid_features);

            // Optionally repair geometries
            if (confirm(`Found ${invalid_count} invalid geometries. Repair them?`)) {
              this.repairLayerGeometry(layerId);
            }
          }
        }
      },
      error: (error) => {
        console.error(`Error validating geometry for ${layerId}:`, error);
        this.showInstructions('Error validating geometries');
      }
    });
  }

  /**
   * Repair geometry using backend
   */
  repairLayerGeometry(layerId: string): void {
    this.apiService.repairGeometry(layerId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const { repaired_count } = response.data;

          if (repaired_count > 0) {
            this.showInstructions(`${repaired_count} geometries repaired`);
            this.updateLayerStatistics(layerId);
          } else {
            this.showInstructions('No geometries needed repair');
          }
        }
      },
      error: (error) => {
        console.error(`Error repairing geometry for ${layerId}:`, error);
        this.showInstructions('Error repairing geometries');
      }
    });
  }

  /**
   * Create buffer analysis using backend
   */
  createBufferAnalysis(layerId: string, distance: number, unit: string = 'meters'): void {
    const source = this.layerRegistry[layerId]?.getSource() as VectorSource;
    if (!source) {
      this.showInstructions(`Layer '${layerId}' not found`);
      return;
    }

    const features = source.getFeatures();
    if (features.length === 0) {
      this.showInstructions(`No features found in layer '${layerId}'`);
      return;
    }

    // Get the first feature for buffer analysis
    const feature = features[0];
    const geometry = this.format.writeGeometry(feature.getGeometry()!);

    const analysisData = {
      layerName: layerId,
      geometry: geometry,
      distance: distance,
      unit: unit
    };

    this.apiService.bufferAnalysis(analysisData).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Buffer analysis results:', response.data);
          this.showInstructions(`Buffer analysis completed`);

          // Import buffer results as new layer
          const bufferLayerId = `${layerId}_buffer_${distance}${unit}`;
          this.importLayer(bufferLayerId, typeof response.data === 'string' ? response.data : JSON.stringify(response.data));
          this.showInstructions(`Buffer results shown in layer '${bufferLayerId}'`);
        }
      },
      error: (error) => {
        console.error('Error performing buffer analysis:', error);
        this.showInstructions('Error performing buffer analysis');
      }
    });
  }

  /**
   * Perform intersection analysis using backend
   */
  performIntersectionAnalysisBackend(layerId1: string, layerId2: string): void {
    const analysisData = {
      layerName1: layerId1,
      layerName2: layerId2,
      operation: 'intersects'
    };

    this.apiService.intersectionAnalysis(analysisData).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Intersection analysis results:', response.data);
          this.showInstructions(`Intersection analysis completed: ${response.data.length} relationships found`);

          // Import intersection results as new layer
          const intersectionLayerId = `${layerId1}_${layerId2}_intersection`;
          const intersectionGeoJson = {
            type: 'FeatureCollection',
            features: response.data.map((result: any) => ({
              type: 'Feature',
              geometry: result.intersection_geometry,
              properties: {
                layer1_feature: result.layer1_feature,
                layer2_feature: result.layer2_feature,
                intersection_area: result.intersection_area
              }
            }))
          };

          this.importLayer(intersectionLayerId, JSON.stringify(intersectionGeoJson));
          this.showInstructions(`Intersection results shown in layer '${intersectionLayerId}'`);
        }
      },
      error: (error) => {
        console.error('Error performing intersection analysis:', error);
        this.showInstructions('Error performing intersection analysis');
      }
    });
  }

  /**
   * Batch save all layers to backend
   */
  saveAllLayersToBackend(): void {
    const layerIds = Object.keys(this.layerRegistry);
    let savedCount = 0;
    let errorCount = 0;

    this.showInstructions('Saving all layers to database...');

    layerIds.forEach(layerId => {
      this.saveLayerToBackend(layerId);
      // Note: In a real implementation, you might want to use Promise.all or similar
      // to handle all saves concurrently
    });

    setTimeout(() => {
      this.showInstructions(`Save operation initiated for ${layerIds.length} layers`);
    }, 1000);
  }

  /**
   * Load all layers from backend
   */
  loadAllLayersFromBackend(): void {
    const layerIds = Object.keys(this.layerRegistry);
    let loadedCount = 0;
    let errorCount = 0;

    this.showInstructions('Loading all layers from database...');

    layerIds.forEach(layerId => {
      this.loadLayerFromBackend(layerId);
    });

    setTimeout(() => {
      this.showInstructions(`Load operation initiated for ${layerIds.length} layers`);
    }, 1000);
  }

  /**
   * Initialize auto-save for all layers
   */
  initializeAutoSave(): void {
    if (this.defaultSettings.autoSaveInterval > 0) {
      // Set up auto-save for all layers when they change
      Object.keys(this.layerRegistry).forEach(layerId => {
        const layer = this.layerRegistry[layerId];
        const source = layer.getSource() as VectorSource;

        // Listen for feature changes
        source.on('addfeature', () => this.autoSaveLayer(layerId));
        source.on('removefeature', () => this.autoSaveLayer(layerId));

        // Listen for geometry changes
        layer.getSource()?.on('featuresloadend', () => this.autoSaveLayer(layerId));
      });

      this.showInstructions(`Auto-save enabled (${this.defaultSettings.autoSaveInterval}s interval)`);
    }
  }

  /**
   * Get all available GIS layers from backend
   */
  getAvailableLayers(): void {
    this.apiService.getGisLayers().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          console.log('Available GIS layers:', response.data);
          this.showInstructions(`Found ${response.data.length} GIS layers in database`);
        }
      },
      error: (error) => {
        console.error('Error getting available layers:', error);
        this.showInstructions('Error retrieving available layers');
      }
    });
  }

  // ==================== PUBLIC UI INTEGRATION METHODS ====================

  /**
   * Save current layer to backend (public method for UI binding)
   */
  saveCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.saveLayerToBackend(targetLayerId);
    } else {
      this.showInstructions('No active layer to save');
    }
  }

  /**
   * Load current layer from backend (public method for UI binding)
   */
  loadCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.loadLayerFromBackend(targetLayerId);
    } else {
      this.showInstructions('No active layer to load');
    }
  }

  /**
   * Save all layers (public method for UI binding)
   */
  saveAllLayers(): void {
    if (this.offlineMode || !navigator.onLine) {
      this.persistLayersToOfflineStorage();
      this.showInstructions('Saved locally (offline). Will sync when back online.');
      return;
    }
    this.saveAllLayersToBackend();
  }

  /**
   * Load all layers (public method for UI binding)
   */
  loadAllLayers(): void {
    if (this.offlineMode || !navigator.onLine) {
      this.restoreLayersFromOfflineStorage();
      return;
    }
    this.loadAllLayersFromBackend();
  }

  /**
   * Validate current layer geometry (public method for UI binding)
   */
  validateCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.validateLayerGeometry(targetLayerId);
    } else {
      this.showInstructions('No active layer to validate');
    }
  }

  /**
   * Repair current layer geometry (public method for UI binding)
   */
  repairCurrentLayer(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.repairLayerGeometry(targetLayerId);
    } else {
      this.showInstructions('No active layer to repair');
    }
  }

  /**
   * Export current layer (public method for UI binding)
   */
  exportCurrentLayer(layerId?: string, format: string = 'geojson'): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (!targetLayerId) {
      this.showInstructions('No active layer to export');
      return;
    }
    const fmt = (format || 'geojson').toLowerCase();
    if (fmt === 'geojson') {
      this.exportLayerToBackend(targetLayerId);
      return;
    }
    if (fmt === 'kml') {
      this.exportLayerAsKml(targetLayerId);
      return;
    }
    if (fmt === 'gpx') {
      this.exportLayerAsGpx(targetLayerId);
      return;
    }
    if (fmt === 'shp' || fmt === 'shapefile') {
      this.showInstructions('Shapefile export: use backend or export as GeoJSON and convert offline');
      return;
    }
    this.apiService.exportGisToFormat(targetLayerId, format).subscribe({
      next: (response) => {
        if (response.success) {
          this.showInstructions(`Layer exported as ${format.toUpperCase()}`);
        }
      },
      error: () => {
        this.showInstructions(`Error exporting layer as ${format.toUpperCase()}`);
      }
    });
  }

  /**
   * Perform buffer analysis on current layer (public method for UI binding)
   */
  performBufferAnalysis(distance: number = 100, unit: string = 'meters'): void {
    const activeLayerId = this.getActiveLayerId();
    if (activeLayerId) {
      this.createBufferAnalysis(activeLayerId, distance, unit);
    } else {
      this.showInstructions('No active layer for buffer analysis');
    }
  }

  /**
   * Perform intersection analysis (public method for UI binding)
   */
  performIntersectionAnalysisUI(layerId2: string): void {
    const layerId1 = this.getActiveLayerId();
    if (layerId1 && layerId2) {
      this.performIntersectionAnalysisBackend(layerId1, layerId2);
    } else {
      this.showInstructions('Select two layers for intersection analysis');
    }
  }

  /**
   * Get layer statistics (public method for UI binding)
   */
  getLayerStatistics(layerId?: string): void {
    const targetLayerId = layerId || this.getActiveLayerId();
    if (targetLayerId) {
      this.getLayerStatsFromBackend(targetLayerId);
    } else {
      this.showInstructions('No active layer for statistics');
    }
  }

  /**
   * Toggle auto-save (public method for UI binding)
   */
  toggleAutoSave(): void {
    if (this.defaultSettings.autoSaveInterval > 0) {
      this.defaultSettings.autoSaveInterval = 0;
      this.showInstructions('Auto-save disabled');
    } else {
      this.defaultSettings.autoSaveInterval = 5; // Default 5 seconds
      this.initializeAutoSave();
      this.showInstructions('Auto-save enabled (5s interval)');
    }
    localStorage.setItem('gisEditorDefaults', JSON.stringify(this.defaultSettings));
  }

  /**
   * Get active layer ID (helper method)
   */
  private getActiveLayerId(): string | null {
    // Get the currently selected layer from layer management UI
    const activeTab = document.querySelector('.layer-tab.active');
    if (activeTab) {
      return activeTab.getAttribute('data-layer');
    }

    // Fallback to first available layer
    const layerIds = Object.keys(this.layerRegistry);
    return layerIds.length > 0 ? layerIds[0] : null;
  }

  /**
   * Enhanced save layer method with UI feedback
   */
  saveLayerWithFeedback(layerId: string): void {
    this.showInstructions(`Saving layer '${layerId}'...`);
    this.saveLayerToBackend(layerId);
  }

  /**
   * Enhanced load layer method with UI feedback
   */
  loadLayerWithFeedback(layerId: string): void {
    this.showInstructions(`Loading layer '${layerId}'...`);
    this.loadLayerFromBackend(layerId);
  }

  // ==================== MISSING FEATURES IMPLEMENTATION ====================

  /**
   * Initialize all sidebar tab switching
   */
  private setupTabSwitching(): void {
    const tabs = document.querySelectorAll('.layer-tab');
    const tabContents = document.querySelectorAll('.tab-content');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('data-tab');
        
        // Remove active from all tabs and contents
        tabs.forEach(t => t.classList.remove('active'));
        tabContents.forEach(tc => tc.classList.remove('active'));
        
        // Add active to clicked tab
        tab.classList.add('active');
        
        // Show corresponding content
        const content = document.getElementById(`${tabId}-tab`);
        if (content) {
          content.classList.add('active');
        }
      });
    });
  }

  // ==================== ANALYSIS TAB FEATURES ====================

  /**
   * Buffer analysis - creates buffer zones around features
   */
  private bufferAnalysis(): void {
    const bufferSize = parseInt((document.getElementById('analysisBufferSize') as HTMLInputElement)?.value || '100');
    const features = this.vectorSource.getFeatures();
    
    if (features.length === 0) {
      this.showInstructions('No features to analyze');
      return;
    }

    this.showInstructions(`Creating ${bufferSize}m buffer zones...`);

    features.forEach(feature => {
      const geometry = feature.getGeometry();
      if (geometry && typeof turf !== 'undefined') {
        try {
          const geoJson = this.format.writeGeometryObject(geometry);
          const buffered = turf.buffer(geoJson, bufferSize / 1000, { units: 'kilometers' });
          
          if (buffered) {
            const bufferFeature = new Feature({
              geometry: this.format.readGeometry(buffered.geometry),
              type: 'buffer',
              bufferSize: bufferSize,
              sourceFeatureId: feature.getId()
            });

            bufferFeature.setStyle(new Style({
              fill: new Fill({ color: 'rgba(52, 152, 219, 0.2)' }),
              stroke: new Stroke({ color: '#3498db', width: 2, lineDash: [5, 5] })
            }));

            this.vectorSource.addFeature(bufferFeature);
          }
        } catch (error) {
          console.error('Buffer analysis error:', error);
        }
      }
    });

    this.showInstructions(`Buffer analysis complete: ${features.length} buffers created`);
    setTimeout(() => this.hideInstructions(), 3000);
  }

  /**
   * Intersection analysis - finds intersecting features between layers
   */
  private intersectionAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    const roadFeatures = this.roadSource.getFeatures();
    let intersections = 0;

    this.showInstructions('Analyzing intersections...');

    features.forEach(f1 => {
      const geom1 = f1.getGeometry();
      if (!geom1) return;

      roadFeatures.forEach(f2 => {
        const geom2 = f2.getGeometry();
        if (!geom2) return;

        if (typeof turf !== 'undefined') {
          try {
            const gj1 = this.format.writeGeometryObject(geom1);
            const gj2 = this.format.writeGeometryObject(geom2);
            const intersection = turf.intersect(turf.feature(gj1), turf.feature(gj2));

            if (intersection) {
              intersections++;
              const intersectFeature = new Feature({
                geometry: this.format.readGeometry(intersection.geometry),
                type: 'intersection'
              });

              intersectFeature.setStyle(new Style({
                fill: new Fill({ color: 'rgba(231, 76, 60, 0.5)' }),
                stroke: new Stroke({ color: '#e74c3c', width: 3 })
              }));

              this.vectorSource.addFeature(intersectFeature);
            }
          } catch (error) {
            // Features don't intersect
          }
        }
      });
    });

    this.showMeasurementResult(`Intersection Analysis:\nFound ${intersections} intersections`);
  }

  /**
   * Overlay analysis - overlays multiple layers
   */
  private overlayAnalysisFunc(): void {
    this.showInstructions('Performing overlay analysis...');

    const layers = Object.keys(this.layerRegistry);
    let overlayResults: any[] = [];

    layers.forEach((layerId, index) => {
      const layer = this.layerRegistry[layerId];
      const source = layer.getSource() as VectorSource;
      const features = source.getFeatures();

      overlayResults.push({
        layer: layerId,
        featureCount: features.length,
        visible: layer.getVisible()
      });
    });

    const result = overlayResults.map(r => `${r.layer}: ${r.featureCount} features`).join('\n');
    this.showMeasurementResult(`Overlay Analysis:\n${result}`);
  }

  /**
   * Proximity analysis - finds features within a distance
   */
  private proximityAnalysis(): void {
    const bufferSize = parseInt((document.getElementById('analysisBufferSize') as HTMLInputElement)?.value || '100');
    
    this.showInstructions('Click on a feature to analyze proximity...');

    const clickHandler = (event: any) => {
      const features = this.map.getFeaturesAtPixel(event.pixel);
      if (features.length > 0) {
        const centerFeature = features[0] as Feature;
        const centerGeom = centerFeature.getGeometry();
        
        if (centerGeom && typeof turf !== 'undefined') {
          let nearbyCount = 0;
          const allFeatures = this.vectorSource.getFeatures();

          allFeatures.forEach(f => {
            if (f !== centerFeature) {
              const geom = f.getGeometry();
              if (geom) {
                const center1 = turf.centroid(this.format.writeGeometryObject(centerGeom));
                const center2 = turf.centroid(this.format.writeGeometryObject(geom));
                const distance = turf.distance(center1, center2, { units: 'meters' });

                if (distance <= bufferSize) {
                  nearbyCount++;
                  f.setStyle(new Style({
                    stroke: new Stroke({ color: '#f39c12', width: 3 }),
                    fill: new Fill({ color: 'rgba(243, 156, 18, 0.3)' })
                  }));
                }
              }
            }
          });

          this.showMeasurementResult(`Proximity Analysis:\n${nearbyCount} features within ${bufferSize}m`);
        }
      }

      this.map.un('click', clickHandler);
    };

    this.map.on('click', clickHandler);
  }

  /**
   * Network analysis - analyzes connectivity
   */
  private networkAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    const lineFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof LineString;
    });

    let totalLength = 0;
    let nodeCount = 0;
    let endpoints: number[][] = [];

    lineFeatures.forEach(f => {
      const geom = f.getGeometry() as LineString;
      const coords = geom.getCoordinates();
      totalLength += geom.getLength();
      
      endpoints.push(coords[0]);
      endpoints.push(coords[coords.length - 1]);
    });

    // Count unique nodes (simplified)
    const uniqueNodes = new Set(endpoints.map(e => `${Math.round(e[0])},${Math.round(e[1])}`));
    nodeCount = uniqueNodes.size;

    const result = `
Network Analysis:
Total Links: ${lineFeatures.length}
Total Nodes: ${nodeCount}
Total Length: ${(totalLength / 1000).toFixed(2)} km
Avg Link Length: ${lineFeatures.length > 0 ? (totalLength / lineFeatures.length).toFixed(2) : 0} m
Network Density: ${(lineFeatures.length / nodeCount).toFixed(2)} links/node
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Terrain analysis - analyzes elevation data
   */
  private terrainAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    let minElev = Infinity;
    let maxElev = -Infinity;
    let totalElev = 0;
    let elevCount = 0;

    features.forEach(f => {
      const elev = f.get('elevation') || f.get('z') || 0;
      if (typeof elev === 'number') {
        minElev = Math.min(minElev, elev);
        maxElev = Math.max(maxElev, elev);
        totalElev += elev;
        elevCount++;
      }
    });

    const avgElev = elevCount > 0 ? totalElev / elevCount : 0;
    const elevRange = maxElev - minElev;

    const result = `
Terrain Analysis:
Features Analyzed: ${elevCount}
Min Elevation: ${minElev !== Infinity ? minElev.toFixed(2) : 'N/A'} m
Max Elevation: ${maxElev !== -Infinity ? maxElev.toFixed(2) : 'N/A'} m
Elevation Range: ${elevRange !== Infinity ? elevRange.toFixed(2) : 'N/A'} m
Average Elevation: ${avgElev.toFixed(2)} m
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Earthwork analysis - cut/fill volumes along road alignment
   */
  private async earthworkAnalysis(): Promise<void> {
    const features = this.vectorSource.getFeatures().filter(f => {
      const t = f.getGeometry()?.getType();
      return t === 'LineString' || t === 'MultiLineString';
    });
    if (features.length === 0) {
      this.showInstructions('Draw a line (road alignment) first.');
      return;
    }
    const roadWidth = parseFloat((document.getElementById('earthworkRoadWidth') as HTMLInputElement)?.value || '12');
    const designGrade = parseFloat((document.getElementById('earthworkDesignGrade') as HTMLInputElement)?.value || '2') / 100;

    const allCoords: number[][] = [];
    const lineCoords: number[][][] = [];
    features.forEach(f => {
      const geom = f.getGeometry();
      let coords: number[][];
      if (geom instanceof LineString) {
        coords = geom.getCoordinates();
      } else if (geom && (geom as any).getLineStrings) {
        const lines = (geom as any).getLineStrings();
        coords = lines.flatMap((ls: LineString) => ls.getCoordinates());
      } else {
        coords = [];
      }
      const lonLats = coords.map((c: number[]) => toLonLat(c));
      allCoords.push(...lonLats);
      lineCoords.push(lonLats);
    });

    let elevations: number[] = [];
    try {
      elevations = await this.fetchElevationsForCoordinates(allCoords);
    } catch {
      elevations = allCoords.map(() => 50 + Math.random() * 30);
    }

    let idx = 0;
    let totalCut = 0;
    let totalFill = 0;
    let totalLength = 0;

    lineCoords.forEach(lonLats => {
      const elevs = lonLats.map(() => elevations[idx++] ?? 50);
      if (lonLats.length < 2) return;
      const startElev = elevs[0];
      let cumDist = 0;
      const distances: number[] = [0];
      for (let i = 1; i < lonLats.length; i++) {
        cumDist += getGeodesicLength(new LineString([fromLonLat(lonLats[i - 1]), fromLonLat(lonLats[i])]));
        distances.push(cumDist);
      }
      totalLength += cumDist;
      for (let i = 1; i < lonLats.length; i++) {
        const segLen = distances[i] - distances[i - 1];
        const designElevStart = startElev + distances[i - 1] * designGrade;
        const designElevEnd = startElev + distances[i] * designGrade;
        const groundStart = elevs[i - 1];
        const groundEnd = elevs[i];
        const diffStart = designElevStart - groundStart;
        const diffEnd = designElevEnd - groundEnd;
        const avgDiff = (diffStart + diffEnd) / 2;
        const vol = avgDiff * segLen * roadWidth;
        if (vol > 0) totalCut += vol;
        else totalFill += Math.abs(vol);
      }
    });

    const result = `
📐 Earthwork Analysis:
━━━━━━━━━━━━━━━━━━━━━
Alignment Length: ${(totalLength / 1000).toFixed(3)} km
Road Width: ${roadWidth} m
Design Grade: ${(designGrade * 100).toFixed(2)}%
Total Cut: ${(totalCut / 1000).toFixed(2)} m³
Total Fill: ${(totalFill / 1000).toFixed(2)} m³
Net: ${((totalCut - totalFill) / 1000).toFixed(2)} m³
    `;
    this.showMeasurementResult(result);
  }

  /**
   * Highway geometry - curvature, gradient, superelevation
   */
  private async highwayGeometryAnalysis(): Promise<void> {
    const features = this.vectorSource.getFeatures().filter(f => {
      const t = f.getGeometry()?.getType();
      return t === 'LineString' || t === 'MultiLineString';
    });
    if (features.length === 0) {
      this.showInstructions('Draw a line (road alignment) first.');
      return;
    }
    const superelevation = parseFloat((document.getElementById('earthworkSuperelevation') as HTMLInputElement)?.value || '4');

    const allCoords: number[][] = [];
    features.forEach(f => {
      const geom = f.getGeometry();
      let coords: number[][];
      if (geom instanceof LineString) {
        coords = geom.getCoordinates();
      } else if (geom && (geom as any).getLineStrings) {
        const lines = (geom as any).getLineStrings();
        coords = lines.flatMap((ls: LineString) => ls.getCoordinates());
      } else {
        coords = [];
      }
      coords.forEach((c: number[]) => allCoords.push(toLonLat(c)));
    });

    let elevations: number[] = [];
    try {
      elevations = await this.fetchElevationsForCoordinates(allCoords);
    } catch {
      elevations = allCoords.map(() => 50 + Math.random() * 30);
    }

    let totalLength = 0;
    const gradients: number[] = [];
    const curvatures: number[] = [];

    for (let i = 1; i < allCoords.length; i++) {
      const from = fromLonLat(allCoords[i - 1]);
      const to = fromLonLat(allCoords[i]);
      const segLen = getGeodesicLength(new LineString([from, to]));
      totalLength += segLen;
      const elevDiff = (elevations[i] ?? 50) - (elevations[i - 1] ?? 50);
      const gradient = segLen > 0 ? (elevDiff / segLen) * 100 : 0;
      gradients.push(gradient);
    }

    if (typeof turf !== 'undefined') {
      for (let i = 2; i < allCoords.length; i++) {
        const p1 = turf.point(allCoords[i - 2]);
        const p2 = turf.point(allCoords[i - 1]);
        const p3 = turf.point(allCoords[i]);
        const bearing1 = turf.bearing(p1, p2);
        const bearing2 = turf.bearing(p2, p3);
        let delta = bearing2 - bearing1;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        const segLen = getGeodesicLength(new LineString([fromLonLat(allCoords[i - 1]), fromLonLat(allCoords[i])]));
        const radius = segLen > 0 ? (180 / Math.PI) * (segLen / Math.abs(delta * Math.PI / 180)) : 0;
        if (isFinite(radius) && radius > 0 && radius < 1e6) curvatures.push(radius);
      }
    }

    const maxGrad = gradients.length ? Math.max(...gradients.map(Math.abs)) : 0;
    const avgGrad = gradients.length ? gradients.reduce((a, b) => a + b, 0) / gradients.length : 0;
    const minRadius = curvatures.length ? Math.min(...curvatures) : 0;

    const result = `
🛣️ Highway Geometry:
━━━━━━━━━━━━━━━━━━━━━
Total Length: ${(totalLength / 1000).toFixed(3)} km
Max Gradient: ${maxGrad.toFixed(2)}%
Avg Gradient: ${avgGrad.toFixed(2)}%
Min Curve Radius: ${minRadius > 0 ? minRadius.toFixed(0) + ' m' : 'N/A (straight)'}
Superelevation: ${superelevation}%
    `;
    this.showMeasurementResult(result);
  }

  /**
   * Calculate area statistics
   */
  private calculateAreaStats(): void {
    const features = this.vectorSource.getFeatures();
    const polygonFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof Polygon || (geom && geom.getType() === 'Polygon');
    });

    if (polygonFeatures.length === 0) {
      this.showInstructions('No polygon features found. Draw some polygons first.');
      return;
    }

    let totalArea = 0;
    let minArea = Infinity;
    let maxArea = 0;

    polygonFeatures.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        // Use geodesic area calculation for accuracy
        const area = Math.abs(getGeodesicArea(geom, { projection: 'EPSG:3857' }));
        totalArea += area;
        minArea = Math.min(minArea, area);
        maxArea = Math.max(maxArea, area);
      }
    });

    const avgArea = totalArea / polygonFeatures.length;

    const result = `
📐 Area Statistics:
━━━━━━━━━━━━━━━━━━━━━
Polygon Count: ${polygonFeatures.length}
Total Area: ${(totalArea / 1000000).toFixed(4)} km² (${totalArea.toFixed(2)} m²)
Min Area: ${minArea.toFixed(2)} m²
Max Area: ${maxArea.toFixed(2)} m²
Average Area: ${avgArea.toFixed(2)} m²
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Calculate length statistics
   */
  private calculateLengthStats(): void {
    const features = this.vectorSource.getFeatures();
    const lineFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof LineString || (geom && geom.getType() === 'LineString');
    });

    if (lineFeatures.length === 0) {
      this.showInstructions('No line features found. Draw some lines first.');
      return;
    }

    let totalLength = 0;
    let minLength = Infinity;
    let maxLength = 0;

    lineFeatures.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        // Use geodesic length calculation for accuracy
        const length = getGeodesicLength(geom, { projection: 'EPSG:3857' });
        totalLength += length;
        minLength = Math.min(minLength, length);
        maxLength = Math.max(maxLength, length);
      }
    });

    const avgLength = totalLength / lineFeatures.length;

    const result = `
📏 Length Statistics:
━━━━━━━━━━━━━━━━━━━━━
Line Count: ${lineFeatures.length}
Total Length: ${(totalLength / 1000).toFixed(4)} km (${totalLength.toFixed(2)} m)
Min Length: ${minLength.toFixed(2)} m
Max Length: ${maxLength.toFixed(2)} m
Average Length: ${avgLength.toFixed(2)} m
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Density analysis
   */
  private densityAnalysisFunc(): void {
    const features = this.vectorSource.getFeatures();
    const extent = this.vectorSource.getExtent();
    
    if (!extent || extent[0] === Infinity) {
      this.showInstructions('No features to analyze');
      return;
    }

    const width = extent[2] - extent[0];
    const height = extent[3] - extent[1];
    const areaKm2 = (width * height) / 1000000;

    const featureDensity = features.length / areaKm2;

    const result = `
Density Analysis:
Total Features: ${features.length}
Extent Area: ${areaKm2.toFixed(4)} km²
Feature Density: ${featureDensity.toFixed(2)} features/km²
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Clustering analysis
   */
  private clusteringAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    const pointFeatures = features.filter(f => {
      const geom = f.getGeometry();
      return geom instanceof Point;
    });

    if (pointFeatures.length === 0) {
      this.showInstructions('No point features for clustering');
      return;
    }

    // Simple clustering based on proximity
    const clusterDistance = 100; // meters
    let clusters: Feature[][] = [];
    let assigned = new Set<Feature>();

    pointFeatures.forEach(f => {
      if (assigned.has(f)) return;

      const cluster: Feature[] = [f];
      assigned.add(f);

      const fCoords = (f.getGeometry() as Point).getCoordinates();

      pointFeatures.forEach(other => {
        if (assigned.has(other) || other === f) return;

        const otherCoords = (other.getGeometry() as Point).getCoordinates();
        const dx = fCoords[0] - otherCoords[0];
        const dy = fCoords[1] - otherCoords[1];
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist <= clusterDistance) {
          cluster.push(other);
          assigned.add(other);
        }
      });

      clusters.push(cluster);
    });

    const result = `
Clustering Analysis:
Total Points: ${pointFeatures.length}
Clusters Found: ${clusters.length}
Cluster Distance: ${clusterDistance}m
Avg Cluster Size: ${(pointFeatures.length / clusters.length).toFixed(1)} points
Largest Cluster: ${Math.max(...clusters.map(c => c.length))} points
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Hotspot analysis
   */
  private hotspotAnalysis(): void {
    const features = this.vectorSource.getFeatures();
    
    // Simple grid-based hotspot analysis
    const gridSize = 500; // meters
    const extent = this.vectorSource.getExtent();
    
    if (!extent || extent[0] === Infinity) {
      this.showInstructions('No features for hotspot analysis');
      return;
    }

    const gridCounts: { [key: string]: number } = {};

    features.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        let coord: number[];
        if (geom instanceof Point) {
          coord = geom.getCoordinates();
        } else {
          // Use centroid for other geometries
          const ext = geom.getExtent();
          coord = [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2];
        }

        const gridX = Math.floor(coord[0] / gridSize);
        const gridY = Math.floor(coord[1] / gridSize);
        const key = `${gridX},${gridY}`;
        gridCounts[key] = (gridCounts[key] || 0) + 1;
      }
    });

    const counts = Object.values(gridCounts);
    const maxCount = Math.max(...counts);
    const avgCount = counts.reduce((a, b) => a + b, 0) / counts.length;

    const hotspots = Object.entries(gridCounts).filter(([_, count]) => count > avgCount * 1.5).length;

    const result = `
Hotspot Analysis:
Grid Size: ${gridSize}m
Total Cells: ${counts.length}
Max Density: ${maxCount} features/cell
Avg Density: ${avgCount.toFixed(1)} features/cell
Hotspots (>1.5x avg): ${hotspots}
    `;

    this.showMeasurementResult(result);
  }

  /**
   * Pattern analysis
   */
  private patternAnalysis(): void {
    const features = this.vectorSource.getFeatures();

    // Count feature types
    const typeCounts: { [key: string]: number } = {};
    features.forEach(f => {
      const type = f.get('type') || 'unknown';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    const typeList = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => `${type}: ${count}`)
      .join('\n');

    const result = `
Pattern Analysis:
Total Features: ${features.length}
Unique Types: ${Object.keys(typeCounts).length}

Feature Types:
${typeList}
    `;

    this.showMeasurementResult(result);
  }

  // ==================== CARTOGRAPHIC TOOLS ====================

  /**
   * Generate map layout
   */
  private generateMap(): void {
    this.showInstructions('Generating map layout...');
    
    const title = (document.getElementById('mapTitle') as HTMLInputElement)?.value || 'Map';
    const scale = (document.getElementById('mapScale') as HTMLSelectElement)?.value || '1:10000';

    // Create map title overlay
    const titleDiv = document.createElement('div');
    titleDiv.className = 'map-title-overlay';
    titleDiv.innerHTML = `<h2>${title}</h2><p>Scale: ${scale}</p>`;
    titleDiv.style.cssText = 'position: absolute; top: 10px; left: 50%; transform: translateX(-50%); background: white; padding: 10px 20px; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); z-index: 1000;';

    const mapContainer = document.querySelector('.map-container');
    if (mapContainer) {
      // Remove existing title
      const existingTitle = mapContainer.querySelector('.map-title-overlay');
      if (existingTitle) existingTitle.remove();
      mapContainer.appendChild(titleDiv);
    }

    this.showInstructions('Map layout generated');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Create layout
   */
  private createLayout(): void {
    this.openDialog('printDialog');
  }

  /**
   * Add legend to map
   */
  private addLegend(): void {
    const legendDiv = document.createElement('div');
    legendDiv.className = 'map-legend';
    legendDiv.style.cssText = 'position: absolute; bottom: 50px; right: 10px; background: white; padding: 15px; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); z-index: 1000; max-width: 200px;';

    // Build legend content from layer styles
    let legendContent = '<h4 style="margin: 0 0 10px 0;">Legend</h4>';
    
    Object.keys(this.layerRegistry).forEach(layerId => {
      const layer = this.layerRegistry[layerId];
      if (layer.getVisible()) {
        legendContent += `
          <div style="display: flex; align-items: center; margin-bottom: 5px;">
            <span style="width: 20px; height: 3px; background: #3498db; margin-right: 8px;"></span>
            <span style="font-size: 12px;">${layerId}</span>
          </div>
        `;
      }
    });

    legendDiv.innerHTML = legendContent;

    const mapContainer = document.querySelector('.map-container');
    if (mapContainer) {
      const existingLegend = mapContainer.querySelector('.map-legend');
      if (existingLegend) existingLegend.remove();
      mapContainer.appendChild(legendDiv);
    }

    this.showInstructions('Legend added');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Add scale bar
   */
  private addScaleBar(): void {
    // ScaleLine control is already added in map initialization
    // This toggles its visibility
    const scaleControls = this.map.getControls().getArray().filter(c => c instanceof ScaleLine);
    
    if (scaleControls.length === 0) {
      this.map.addControl(new ScaleLine({ units: 'metric' }));
      this.showInstructions('Scale bar added');
    } else {
      this.showInstructions('Scale bar already exists');
    }
    setTimeout(() => this.hideInstructions(), 2000);
  }

  // ==================== DATA TAB FEATURES ====================

  /**
   * Load contour layer from GeoJSON (e.g. contour lines). Adds as vector layer.
   */
  loadContourLayer(geoJsonContent: string, layerName: string = 'contours'): void {
    try {
      const features = this.format.readFeatures(geoJsonContent, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
      });
      const id = layerName || `contour_${Date.now()}`;
      const source = new VectorSource({ features });
      const layer = new VectorLayer({
        source,
        style: new Style({
          stroke: new Stroke({ color: '#2c3e50', width: 1, lineDash: [4, 4] }),
          fill: new Fill({ color: 'transparent' })
        }),
        zIndex: 40
      });
      this.map.addLayer(layer);
      this.contourLayerIds.push(id);
      this.showInstructions(`Contour layer "${id}" loaded (${features.length} features)`);
    } catch (e) {
      this.showInstructions('Invalid contour GeoJSON');
    }
  }

  /**
   * Add DEM (elevation) tile layer from XYZ URL template. Use {z},{x},{y} for tile coords.
   */
  addDemLayer(urlTemplate: string, layerName: string = 'DEM'): void {
    const id = `dem_${Date.now()}`;
    const layer = new TileLayer({
      source: new XYZ({ url: urlTemplate }),
      opacity: 0.6,
      zIndex: 35
    });
    this.map.addLayer(layer);
    this.showInstructions(`DEM layer added. Remove via layer list if needed.`);
  }

  /**
   * Toggle heatmap overlay for current layer (density visualization)
   */
  toggleHeatmapOverlay(layerId?: string): void {
    const targetId = layerId || this.getActiveLayerId();
    if (this.heatmapLayer) {
      this.map.removeLayer(this.heatmapLayer);
      this.heatmapLayer = null;
      this.showInstructions('Heatmap overlay removed');
      return;
    }
    const layer = targetId ? this.layerRegistry[targetId] : null;
    const source = layer?.getSource();
    if (!source || !(source instanceof VectorSource)) {
      this.showInstructions('Select a vector layer for heatmap');
      return;
    }
    const features = (source as VectorSource).getFeatures().filter(f => f.getGeometry()?.getType() === 'Point');
    if (features.length === 0) {
      this.showInstructions('Layer has no point features for heatmap');
      return;
    }
    features.forEach(f => f.set('weight', 1));
    this.heatmapLayer = new Heatmap({
      source: source as VectorSource,
      blur: 15,
      radius: 8,
      weight: (f) => (f.get('weight') as number) ?? 1,
      zIndex: 45
    });
    this.map.addLayer(this.heatmapLayer);
    this.showInstructions('Heatmap overlay added');
  }

  /**
   * Add WMS layer (opens dialog)
   */
  private addWmsLayer(): void {
    this.openDialog('wmsDialog');
  }

  /**
   * Submit WMS dialog: create ImageWMS layer from form and add to map
   */
  private submitWmsLayer(): void {
    const url = (document.getElementById('wmsUrl') as HTMLInputElement)?.value?.trim();
    const layerName = (document.getElementById('wmsLayerName') as HTMLInputElement)?.value?.trim();
    const format = (document.getElementById('wmsFormat') as HTMLSelectElement)?.value || 'image/png';
    const transparency = parseInt((document.getElementById('wmsTransparency') as HTMLInputElement)?.value || '0', 10) / 100;
    if (!url || !layerName) {
      this.showInstructions('Please enter WMS URL and layer name');
      return;
    }
    try {
      const wmsSource = new ImageWMS({
        url,
        params: { LAYERS: layerName, FORMAT: format },
        ratio: 1,
        serverType: 'geoserver'
      });
      const wmsId = `wms_${layerName}_${Date.now()}`;
      const imageLayer = new ImageLayer({
        source: wmsSource,
        opacity: 1 - transparency,
        zIndex: 50 + this.wmsLayers.length
      });
      this.map.addLayer(imageLayer);
      this.wmsLayers.push({ id: wmsId, layer: imageLayer });
      this.closeDialog('wmsDialog');
      this.showInstructions(`WMS layer "${layerName}" added`);
    } catch (err) {
      console.error('WMS layer error:', err);
      this.showInstructions('Failed to add WMS layer. Check URL and layer name.');
    }
  }

  /**
   * Add WFS layer: load features from WFS GetFeature (1.1.0) and add as vector layer.
   */
  private addWfsLayer(): void {
    const baseUrl = prompt('WFS base URL (e.g. https://geo.example.com/geoserver/wfs):', '');
    if (!baseUrl) return;
    const typeName = prompt('Feature type name (e.g. namespace:layername or layername):', '');
    if (!typeName) return;
    this.showInstructions(`Loading WFS: ${typeName}...`);
    const view = this.map.getView();
    const extent = view.calculateExtent(this.map.getSize() || [800, 600]);
    const [minX, minY, maxX, maxY] = extent;
    const sep = baseUrl.includes('?') ? '&' : '?';
    const bbox = `${minX},${minY},${maxX},${maxY}`;
    const url = `${baseUrl}${sep}SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&TYPENAME=${encodeURIComponent(typeName)}&BBOX=${bbox}&SRSNAME=EPSG:3857`;
    fetch(url)
      .then(res => res.text())
      .then(text => {
        const wfsFormat = new WFS({ version: '1.1.0' });
        const features = wfsFormat.readFeatures(text, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857'
        });
        if (features.length === 0) {
          this.showInstructions('No features returned. Check URL and type name.');
          return;
        }
        const layerId = `wfs_${typeName.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}`;
        const source = new VectorSource({ features });
        const layer = new VectorLayer({
          source,
          style: (f, res) => this.getLayerStyleWithTheme(layerId, f, res),
          zIndex: 42
        });
        this.map.addLayer(layer);
        this.registerLayer(layerId, layer);
        this.showInstructions(`WFS loaded: ${features.length} features`);
      })
      .catch(err => {
        console.error('WFS load error:', err);
        this.showInstructions('Failed to load WFS. Check URL and CORS.');
      });
  }

  /**
   * Add vector layer
   */
  private addVectorLayerDialog(): void {
    const name = (document.getElementById('newLayerName') as HTMLInputElement)?.value || `Layer_${Date.now()}`;
    const type = (document.getElementById('newLayerType') as HTMLSelectElement)?.value || 'point';

    this.addLayer(name);
    this.showInstructions(`Vector layer '${name}' created`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /** Raster (image overlay) layers for removal */
  private rasterImageLayers: Array<ImageLayer<ImageStatic>> = [];

  /**
   * Add raster layer: georeferenced image overlay using current view extent (or user extent).
   */
  private addRasterLayer(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.tif,.tiff,.png,.jpg,.jpeg';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      this.showInstructions(`Loading raster: ${file.name}...`);
      const url = URL.createObjectURL(file);
      const view = this.map.getView();
      const extent = view.calculateExtent(this.map.getSize() || [800, 600]);
      try {
        const source = new ImageStatic({
          url,
          imageExtent: extent,
          projection: view.getProjection()
        });
        const layer = new ImageLayer({ source, opacity: 0.8, zIndex: 30 });
        this.map.addLayer(layer);
        this.rasterImageLayers.push(layer);
        this.showInstructions('Raster layer added. Use layer opacity to blend.');
      } catch (err) {
        this.showInstructions('Failed to add raster layer.');
      }
      setTimeout(() => this.hideInstructions(), 2500);
    };
    input.click();
  }

  /**
   * Add database layer
   */
  private addDatabaseLayer(): void {
    this.showInstructions('Database Layer: Configure connection');
    
    const connectionString = prompt('Enter database connection (host:port/database):');
    if (connectionString) {
      this.showInstructions(`Connecting to: ${connectionString}...`);
      // In production, would connect to PostGIS database
      setTimeout(() => {
        this.showInstructions('Database connected (simulated)');
        setTimeout(() => this.hideInstructions(), 2000);
      }, 1500);
    }
  }

  /**
   * Add API layer
   */
  private addApiLayer(): void {
    this.showInstructions('API Layer: Enter endpoint URL');
    
    const apiUrl = prompt('Enter API Endpoint URL:');
    if (apiUrl) {
      this.showInstructions(`Fetching from: ${apiUrl}...`);
      
      fetch(apiUrl)
        .then(res => res.json())
        .then(data => {
          if (data.type === 'FeatureCollection' || data.features) {
            this.importGeoJSONData(data);
            this.showInstructions('API data loaded successfully');
          } else {
            this.showInstructions('Invalid GeoJSON response');
          }
        })
        .catch(err => {
          console.error('API fetch error:', err);
          this.showInstructions('Error fetching API data');
        });
    }
  }

  /**
   * Import Shapefile
   */
  private importShapefile(): void {
    this.showInstructions('Shapefile import requires shpjs library');
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,.shp';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        this.showInstructions(`Processing shapefile: ${file.name}...`);
        // Would use shpjs library to parse
        setTimeout(() => {
          this.showInstructions('Shapefile imported (requires shpjs library)');
          setTimeout(() => this.hideInstructions(), 2000);
        }, 1000);
      }
    };
    
    input.click();
  }

  /**
   * Import GeoJSON file
   */
  private importGeoJSONFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.json';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target?.result as string);
            this.importGeoJSONData(data);
            this.showInstructions(`GeoJSON '${file.name}' imported successfully`);
          } catch (error) {
            this.showInstructions('Error parsing GeoJSON file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Import KML file
   */
  private importKMLFile(): void {
    this.showInstructions('KML import: Select KML/KMZ file');
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.kml,.kmz';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const kmlContent = event.target?.result as string;
            const kmlFormat = new KML({
              extractStyles: true,
              showPointNames: true
            });
            
            const features = kmlFormat.readFeatures(kmlContent, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857'
            });
            
            if (features.length > 0) {
              features.forEach(feature => {
                feature.set('source', 'kml');
                feature.set('importedFrom', file.name);
                feature.set('importDate', new Date().toISOString());
                
                // Apply default style if no style extracted
                if (!feature.getStyle()) {
                  const geomType = feature.getGeometry()?.getType();
                  feature.setStyle(this.getDefaultImportStyle(geomType || 'Point'));
                }
                
                this.vectorSource.addFeature(feature);
              });
              
              // Zoom to imported features
              const extent = this.vectorSource.getExtent();
              this.map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 16 });
              
              this.showInstructions(`KML '${file.name}' imported: ${features.length} features`);
              this.saveToHistory();
            } else {
              this.showInstructions('No features found in KML file');
            }
          } catch (error) {
            console.error('KML import error:', error);
            this.showInstructions('Error parsing KML file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Import GPX file
   */
  private importGPXFile(): void {
    this.showInstructions('GPX import: Select GPX file');
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.gpx';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const gpxContent = event.target?.result as string;
            const gpxFormat = new GPX();
            
            const features = gpxFormat.readFeatures(gpxContent, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857'
            });
            
            if (features.length > 0) {
              let trackCount = 0;
              let waypointCount = 0;
              let routeCount = 0;
              
              features.forEach(feature => {
                feature.set('source', 'gpx');
                feature.set('importedFrom', file.name);
                feature.set('importDate', new Date().toISOString());
                
                const geomType = feature.getGeometry()?.getType();
                
                // Categorize feature type
                if (geomType === 'Point') {
                  waypointCount++;
                  feature.setStyle(new Style({
                    image: new CircleStyle({
                      radius: 8,
                      fill: new Fill({ color: '#e74c3c' }),
                      stroke: new Stroke({ color: '#fff', width: 2 })
                    }),
                    text: new Text({
                      text: feature.get('name') || '',
                      font: '11px Arial',
                      fill: new Fill({ color: '#2c3e50' }),
                      stroke: new Stroke({ color: '#fff', width: 2 }),
                      offsetY: -15
                    })
                  }));
                } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
                  trackCount++;
                  feature.setStyle(new Style({
                    stroke: new Stroke({
                      color: '#3498db',
                      width: 3
                    })
                  }));
                }
                
                this.vectorSource.addFeature(feature);
              });
              
              // Zoom to imported features
              const extent = this.vectorSource.getExtent();
              this.map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 16 });
              
              this.showInstructions(`GPX '${file.name}' imported: ${waypointCount} waypoints, ${trackCount} tracks`);
              this.saveToHistory();
            } else {
              this.showInstructions('No features found in GPX file');
            }
          } catch (error) {
            console.error('GPX import error:', error);
            this.showInstructions('Error parsing GPX file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Get default style for imported features based on geometry type
   */
  private getDefaultImportStyle(geomType: string): Style {
    switch (geomType) {
      case 'Point':
      case 'MultiPoint':
        return new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: '#27ae60' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          })
        });
      case 'LineString':
      case 'MultiLineString':
        return new Style({
          stroke: new Stroke({
            color: '#2980b9',
            width: 3
          })
        });
      case 'Polygon':
      case 'MultiPolygon':
        return new Style({
          stroke: new Stroke({
            color: '#8e44ad',
            width: 2
          }),
          fill: new Fill({
            color: 'rgba(142, 68, 173, 0.3)'
          })
        });
      default:
        return new Style({
          stroke: new Stroke({ color: '#333', width: 2 }),
          fill: new Fill({ color: 'rgba(51, 51, 51, 0.3)' }),
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: '#333' }),
            stroke: new Stroke({ color: '#fff', width: 2 })
          })
        });
    }
  }

  /**
   * Import CSV file with coordinates
   */
  private importCSVFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const csv = event.target?.result as string;
            const lines = csv.split('\n');
            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            
            const latIdx = headers.findIndex(h => h.includes('lat'));
            const lonIdx = headers.findIndex(h => h.includes('lon') || h.includes('lng'));
            
            if (latIdx === -1 || lonIdx === -1) {
              this.showInstructions('CSV must contain lat/lon columns');
              return;
            }

            let imported = 0;
            for (let i = 1; i < lines.length; i++) {
              const values = lines[i].split(',');
              const lat = parseFloat(values[latIdx]);
              const lon = parseFloat(values[lonIdx]);
              
              if (!isNaN(lat) && !isNaN(lon)) {
                const coord = fromLonLat([lon, lat]);
                const feature = new Feature({
                  geometry: new Point(coord),
                  type: 'csv_point',
                  ...headers.reduce((obj, h, idx) => ({ ...obj, [h]: values[idx] }), {})
                });
                
                feature.setStyle(new Style({
                  image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({ color: '#9b59b6' }),
                    stroke: new Stroke({ color: '#fff', width: 2 })
                  })
                }));
                
                this.vectorSource.addFeature(feature);
                imported++;
              }
            }
            
            this.showInstructions(`CSV imported: ${imported} points created`);
          } catch (error) {
            console.error('CSV import error:', error);
            this.showInstructions('Error parsing CSV file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  }

  /**
   * Check for duplicate features
   */
  private checkDuplicates(): void {
    const features = this.vectorSource.getFeatures();
    const geometryHashes: { [key: string]: Feature[] } = {};

    features.forEach(f => {
      const geom = f.getGeometry();
      if (geom) {
        const coords = JSON.stringify(this.format.writeGeometryObject(geom));
        const hash = coords.substring(0, 100); // Simple hash
        
        if (!geometryHashes[hash]) {
          geometryHashes[hash] = [];
        }
        geometryHashes[hash].push(f);
      }
    });

    let duplicateCount = 0;
    Object.values(geometryHashes).forEach(group => {
      if (group.length > 1) {
        duplicateCount += group.length - 1;
        // Highlight duplicates
        group.slice(1).forEach(f => {
          f.setStyle(new Style({
            stroke: new Stroke({ color: '#e74c3c', width: 3 }),
            fill: new Fill({ color: 'rgba(231, 76, 60, 0.3)' })
          }));
        });
      }
    });

    this.showMeasurementResult(`Duplicate Check:\nFound ${duplicateCount} duplicate features\n(Highlighted in red)`);
  }

  /**
   * Check for gaps in polygon coverage
   */
  private checkGaps(): void {
    const features = this.vectorSource.getFeatures();
    const polygons = features.filter(f => f.getGeometry() instanceof Polygon);

    if (polygons.length < 2) {
      this.showInstructions('Need at least 2 polygons to check gaps');
      return;
    }

    // Simplified gap detection
    this.showMeasurementResult(`Gap Analysis:\nAnalyzed ${polygons.length} polygons\n(Full gap detection requires Turf.js union operations)`);
  }

  /**
   * Clean data - remove invalid features
   */
  private cleanData(): void {
    const features = this.vectorSource.getFeatures();
    let removedCount = 0;

    features.forEach(f => {
      const geom = f.getGeometry();
      if (!geom) {
        this.vectorSource.removeFeature(f);
        removedCount++;
        return;
      }

      // Check for empty geometries
      if (geom instanceof LineString && geom.getCoordinates().length < 2) {
        this.vectorSource.removeFeature(f);
        removedCount++;
      } else if (geom instanceof Polygon && geom.getCoordinates()[0].length < 4) {
        this.vectorSource.removeFeature(f);
        removedCount++;
      }
    });

    this.showInstructions(`Data cleaned: ${removedCount} invalid features removed`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  // ==================== DIALOG MANAGEMENT ====================

  /**
   * Open a dialog
   */
  openDialog(dialogId: string): void {
    const dialog = document.getElementById(dialogId);
    if (dialog) {
      dialog.style.display = 'flex';
    }
  }

  /**
   * Close a dialog
   */
  closeDialog(dialogId: string): void {
    const dialog = document.getElementById(dialogId);
    if (dialog) {
      dialog.style.display = 'none';
    }
  }

  /**
   * Show analysis tab in analysis dialog
   */
  showAnalysisTab(tabName: string): void {
    const tabs = document.querySelectorAll('#analysisDialog .tab-btn');
    const contents = document.querySelectorAll('#analysisDialog .analysis-tab-content');

    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));

    const activeTab = document.querySelector(`#analysisDialog .tab-btn[onclick*="${tabName}"]`);
    const activeContent = document.getElementById(`analysis${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);

    if (activeTab) activeTab.classList.add('active');
    if (activeContent) activeContent.classList.add('active');
  }

  /**
   * Show properties tab in layer properties dialog
   */
  showPropertiesTab(tabName: string): void {
    const tabs = document.querySelectorAll('#layerPropertiesDialog .tab-btn');
    const contents = document.querySelectorAll('#layerPropertiesDialog .properties-tab-content');

    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));

    const activeTab = document.querySelector(`#layerPropertiesDialog .tab-btn[onclick*="${tabName}"]`);
    const activeContent = document.getElementById(`properties${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);

    if (activeTab) activeTab.classList.add('active');
    if (activeContent) activeContent.classList.add('active');
  }

  /**
   * Show search tab in search dialog
   */
  showSearchTab(tabName: string): void {
    const tabs = document.querySelectorAll('#searchDialog .tab-btn');
    const contents = document.querySelectorAll('#searchDialog .search-tab-content');

    tabs.forEach(t => t.classList.remove('active'));
    contents.forEach(c => c.classList.remove('active'));

    const activeTab = document.querySelector(`#searchDialog .tab-btn[onclick*="${tabName}"]`);
    const activeContent = document.getElementById(`search${tabName.charAt(0).toUpperCase() + tabName.slice(1)}`);

    if (activeTab) activeTab.classList.add('active');
    if (activeContent) activeContent.classList.add('active');
  }

  /**
   * Select import type in import dialog
   */
  selectImportType(importType: string): void {
    const importContent = document.getElementById('importContent');
    if (!importContent) return;

    if (importType === 'file') {
      importContent.innerHTML = `
        <div class="form-group">
          <label>Select File:</label>
          <input type="file" id="importFile" accept=".shp,.geojson,.kml,.gpx,.csv,.dxf">
        </div>
        <div class="form-group">
          <label>Layer Name:</label>
          <input type="text" id="importLayerName" placeholder="Enter layer name">
        </div>
      `;
    } else if (importType === 'url') {
      importContent.innerHTML = `
        <div class="form-group">
          <label>Service URL:</label>
          <input type="url" id="importUrl" placeholder="https://...">
        </div>
        <div class="form-group">
          <label>Service Type:</label>
          <select id="serviceType">
            <option value="wms">WMS</option>
            <option value="wfs">WFS</option>
            <option value="geojson">GeoJSON API</option>
          </select>
        </div>
      `;
    } else if (importType === 'database') {
      importContent.innerHTML = `
        <div class="form-group">
          <label>Host:</label>
          <input type="text" id="dbHost" placeholder="localhost">
        </div>
        <div class="form-group">
          <label>Port:</label>
          <input type="number" id="dbPort" value="5432">
        </div>
        <div class="form-group">
          <label>Database:</label>
          <input type="text" id="dbName" placeholder="gis_database">
        </div>
        <div class="form-group">
          <label>Table/View:</label>
          <input type="text" id="dbTable" placeholder="features">
        </div>
      `;
    }
  }

  // ==================== NHAI HEADER TOOLBAR ====================

  private setupNhaiToolbarControls(): void {
    const searchInput = document.getElementById('nhaiSearchInput');
    if (searchInput) {
      searchInput.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') {
          e.preventDefault();
          this.runNhaiQuickSearch();
        }
      });
    }

    const filterBtn = document.getElementById('nhaiFilterBtn');
    if (filterBtn) {
      filterBtn.addEventListener('click', () => {
        this.populateSearchLayerSelect();
        this.openDialog('searchDialog');
      });
    }

    const exportBtn = document.getElementById('nhaiExportBtn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.openDialog('nhaiExportDialog'));
    }

    const printBtn = document.getElementById('nhaiPrintBtn');
    if (printBtn) {
      printBtn.addEventListener('click', () => this.printMap());
    }

    const reportsBtn = document.getElementById('nhaiReportsBtn');
    if (reportsBtn) {
      reportsBtn.addEventListener('click', () => {
        this.refreshReportStatus();
        this.openDialog('nhaiReportsDialog');
      });
    }

    const executeSearchBtn = document.getElementById('executeSearchBtn');
    if (executeSearchBtn) {
      executeSearchBtn.addEventListener('click', () => this.executeAdvancedSearch());
    }

    const clearSearchBtn = document.getElementById('clearSearchBtn');
    if (clearSearchBtn) {
      clearSearchBtn.addEventListener('click', () => this.clearAdvancedSearch());
    }

    const exportDrawingBtn = document.getElementById('nhaiExportDrawingBtn');
    if (exportDrawingBtn) {
      exportDrawingBtn.addEventListener('click', () => {
        this.exportData();
        this.closeDialog('nhaiExportDialog');
      });
    }

    const exportRoadsBtn = document.getElementById('nhaiExportRoadsBtn');
    if (exportRoadsBtn) {
      exportRoadsBtn.addEventListener('click', () => {
        this.exportRoadLayerGeoJSON();
        this.closeDialog('nhaiExportDialog');
      });
    }

    const exportRoadSpecsBtn = document.getElementById('nhaiExportRoadSpecsBtn');
    if (exportRoadSpecsBtn) {
      exportRoadSpecsBtn.addEventListener('click', () => {
        this.exportRoadSpecs();
        this.closeDialog('nhaiExportDialog');
      });
    }

    const exportPngBtn = document.getElementById('nhaiExportPngBtn');
    if (exportPngBtn) {
      exportPngBtn.addEventListener('click', () => {
        this.takeMapImage();
        this.closeDialog('nhaiExportDialog');
      });
    }

    const exportActiveLayerBtn = document.getElementById('nhaiExportActiveLayerBtn');
    if (exportActiveLayerBtn) {
      exportActiveLayerBtn.addEventListener('click', () => {
        this.exportCurrentLayer();
        this.closeDialog('nhaiExportDialog');
      });
    }

    const refreshReportBtn = document.getElementById('nhaiRefreshReportBtn');
    if (refreshReportBtn) {
      refreshReportBtn.addEventListener('click', () => this.refreshReportStatus());
    }

    const layerSummaryBtn = document.getElementById('nhaiLayerSummaryBtn');
    if (layerSummaryBtn) {
      layerSummaryBtn.addEventListener('click', () => this.buildNhaiLayerSummary());
    }

    const generateReportBtn = document.getElementById('nhaiGenerateReportBtn');
    if (generateReportBtn) {
      generateReportBtn.addEventListener('click', () => {
        this.generateProjectReport();
        this.closeDialog('nhaiReportsDialog');
      });
    }
  }

  runNhaiQuickSearch(): void {
    const input = document.getElementById('nhaiSearchInput') as HTMLInputElement | null;
    const query = (input?.value || '').trim().toLowerCase();
    if (!query) {
      this.showInstructions('Enter a search term');
      return;
    }
    const matches = this.findFeaturesMatchingQuery(query);
    if (matches.length === 0) {
      this.showInstructions('No features found');
      return;
    }
    this.highlightSearchResults(matches);
    this.zoomToFeaturesExtent(matches);
    this.showInstructions(`Found ${matches.length} feature(s)`);
    setTimeout(() => this.hideInstructions(), 2500);
  }

  executeAdvancedSearch(): void {
    const activeTab = document.querySelector('#searchDialog .search-tab-content.active');
    const tabId = activeTab?.id || 'searchAttribute';

    if (tabId === 'searchSpatial') {
      this.showInstructions('Spatial filter: draw or select features, then use attribute search');
      return;
    }

    if (tabId === 'searchTemporal') {
      this.executeTemporalSearch();
      return;
    }

    const layerScope = (document.getElementById('searchLayer') as HTMLSelectElement)?.value || 'all';
    const attr = (document.getElementById('searchAttrName') as HTMLSelectElement)?.value || 'name';
    const operator = (document.getElementById('searchOperator') as HTMLSelectElement)?.value || 'contains';
    const value = ((document.getElementById('searchValue') as HTMLInputElement)?.value || '').trim();
    if (!value) {
      this.showInstructions('Enter a search value');
      return;
    }

    const pool = this.getFeaturesForSearchScope(layerScope);
    const matches = pool.filter((f) => this.featureMatchesAttributeFilter(f, attr, operator, value));
    if (matches.length === 0) {
      this.showInstructions('No matching features');
      return;
    }
    this.highlightSearchResults(matches);
    this.zoomToFeaturesExtent(matches);
    this.closeDialog('searchDialog');
    this.showInstructions(`Filter matched ${matches.length} feature(s)`);
    setTimeout(() => this.hideInstructions(), 2500);
  }

  clearAdvancedSearch(): void {
    this.clearSearchHighlights();
    const searchValue = document.getElementById('searchValue') as HTMLInputElement | null;
    if (searchValue) searchValue.value = '';
    const nhaiSearch = document.getElementById('nhaiSearchInput') as HTMLInputElement | null;
    if (nhaiSearch) nhaiSearch.value = '';
    this.showInstructions('Search cleared');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  private populateSearchLayerSelect(): void {
    const select = document.getElementById('searchLayer') as HTMLSelectElement | null;
    if (!select) return;
    const prev = select.value;
    select.innerHTML = `
      <option value="all">All Layers</option>
      <option value="drawing">Drawing</option>
      <option value="roads">Roads</option>
      <option value="project">Project</option>
    `;
    this.layerOrder.forEach((id) => {
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = this.layerMetadata[id]?.name || id;
      select.appendChild(opt);
    });
    if (Array.from(select.options).some((o) => o.value === prev)) {
      select.value = prev;
    }
  }

  private getAllSearchableSources(): VectorSource[] {
    const seen = new Set<VectorSource>();
    const list: VectorSource[] = [];
    const add = (src: VectorSource | undefined) => {
      if (src && !seen.has(src)) {
        seen.add(src);
        list.push(src);
      }
    };
    add(this.vectorSource);
    add(this.roadSource);
    add(this.projectSource);
    add(this.biometricSource);
    add(this.gpsSource);
    add(this.waterSource);
    add(this.separatorSource);
    add(this.constructionPathSource);
    add(this.chainageSource);
    add(this.machinesSource);
    add(this.rowAlignmentSource);
    this.layerOrder.forEach((id) => {
      const layer = this.layerRegistry[id];
      const src = layer?.getSource();
      if (src instanceof VectorSource) add(src);
    });
    return list;
  }

  private getFeaturesForSearchScope(scope: string): Feature[] {
    if (scope === 'drawing') return [...this.vectorSource.getFeatures()];
    if (scope === 'roads') return [...this.roadSource.getFeatures()];
    if (scope === 'project') return [...this.projectSource.getFeatures()];
    if (scope !== 'all' && this.layerRegistry[scope]) {
      const src = this.layerRegistry[scope].getSource();
      if (src instanceof VectorSource) return [...src.getFeatures()];
    }
    const out: Feature[] = [];
    this.getAllSearchableSources().forEach((src) => out.push(...src.getFeatures()));
    return out;
  }

  private findFeaturesMatchingQuery(query: string): Feature[] {
    const q = query.toLowerCase();
    return this.getFeaturesForSearchScope('all').filter((f) => {
      const id = String(f.getId() ?? '');
      if (id.toLowerCase().includes(q)) return true;
      const keys = f.getKeys();
      for (const key of keys) {
        if (key.startsWith('_')) continue;
        const val = f.get(key);
        if (val != null && String(val).toLowerCase().includes(q)) return true;
      }
      const geom = f.getGeometry();
      if (geom) {
        const type = geom.getType().toLowerCase();
        if (type.includes(q)) return true;
      }
      return false;
    });
  }

  private getFeatureAttributeValue(feature: Feature, attr: string): string {
    if (attr === 'id') {
      return String(feature.getId() ?? feature.get('id') ?? '');
    }
    const direct = feature.get(attr);
    if (direct != null) return String(direct);
    const props = feature.get('properties');
    if (props && typeof props === 'object' && attr in props) {
      return String((props as Record<string, unknown>)[attr]);
    }
    return '';
  }

  private featureMatchesAttributeFilter(
    feature: Feature,
    attr: string,
    operator: string,
    rawValue: string
  ): boolean {
    const fieldVal = this.getFeatureAttributeValue(feature, attr).toLowerCase();
    const compare = rawValue.toLowerCase();
    const numField = parseFloat(fieldVal);
    const numCompare = parseFloat(compare);

    switch (operator) {
      case 'equals':
        return fieldVal === compare;
      case 'contains':
        return fieldVal.includes(compare);
      case 'starts_with':
        return fieldVal.startsWith(compare);
      case 'ends_with':
        return fieldVal.endsWith(compare);
      case 'greater_than':
        return !Number.isNaN(numField) && !Number.isNaN(numCompare) && numField > numCompare;
      case 'less_than':
        return !Number.isNaN(numField) && !Number.isNaN(numCompare) && numField < numCompare;
      default:
        return fieldVal.includes(compare);
    }
  }

  private executeTemporalSearch(): void {
    const field = (document.getElementById('dateField') as HTMLSelectElement)?.value || 'created_date';
    const start = (document.getElementById('startDate') as HTMLInputElement)?.value;
    const end = (document.getElementById('endDate') as HTMLInputElement)?.value;
    if (!start && !end) {
      this.showInstructions('Select a date range');
      return;
    }
    const startMs = start ? new Date(start).getTime() : 0;
    const endMs = end ? new Date(end + 'T23:59:59').getTime() : Number.MAX_SAFE_INTEGER;
    const pool = this.getFeaturesForSearchScope('all');
    const matches = pool.filter((f) => {
      const raw = f.get(field) ?? f.get('created_at') ?? f.get('modified_at');
      if (!raw) return false;
      const t = new Date(String(raw)).getTime();
      if (Number.isNaN(t)) return false;
      return t >= startMs && t <= endMs;
    });
    if (matches.length === 0) {
      this.showInstructions('No features in date range');
      return;
    }
    this.highlightSearchResults(matches);
    this.zoomToFeaturesExtent(matches);
    this.closeDialog('searchDialog');
    this.showInstructions(`Temporal filter: ${matches.length} feature(s)`);
  }

  private highlightSearchResults(features: Feature[]): void {
    this.clearSearchHighlights();
    const highlightStyle = new Style({
      stroke: new Stroke({ color: '#00bcd4', width: 4 }),
      fill: new Fill({ color: 'rgba(0, 188, 212, 0.25)' }),
      image: new CircleStyle({
        radius: 9,
        fill: new Fill({ color: '#00bcd4' }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
      }),
    });
    features.forEach((f) => {
      if (!f.get('_originalStyle')) {
        f.set('_originalStyle', f.getStyle());
      }
      f.setStyle(highlightStyle);
    });
    this.searchHighlightedFeatures = features;
    if (this.selectInteraction) {
      const coll = this.selectInteraction.getFeatures();
      coll.clear();
      features.forEach((f) => coll.push(f));
    }
  }

  private clearSearchHighlights(): void {
    this.searchHighlightedFeatures.forEach((f) => {
      const original = f.get('_originalStyle');
      if (original !== undefined) {
        f.setStyle(original);
        f.unset('_originalStyle');
      } else {
        f.setStyle(undefined);
      }
    });
    this.searchHighlightedFeatures = [];
    if (this.selectInteraction) {
      this.selectInteraction.getFeatures().clear();
    }
  }

  private zoomToFeaturesExtent(features: Feature[]): void {
    if (!this.map || features.length === 0) return;
    const extent = createEmpty();
    let hasExtent = false;
    features.forEach((f) => {
      const geom = f.getGeometry();
      if (!geom) return;
      extendExtent(extent, geom.getExtent());
      hasExtent = true;
    });
    if (hasExtent) {
      this.map.getView().fit(extent, { padding: [80, 80, 80, 80], maxZoom: 17, duration: 400 });
    }
  }

  private exportRoadLayerGeoJSON(): void {
    const features = this.roadSource.getFeatures();
    if (features.length === 0) {
      this.showInstructions('No road features to export');
      return;
    }
    const geojson = this.format.writeFeatures(features, {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    });
    const blob = new Blob([geojson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `road-layer-${new Date().toISOString().slice(0, 19)}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
    this.showInstructions('Road layer exported');
  }

  buildNhaiLayerSummary(): void {
    const drawing = this.vectorSource.getFeatures().length;
    const roads = this.roadSource.getFeatures().length;
    const project = this.projectSource.getFeatures().length;
    const custom = this.layerOrder.length;
    const lines: string[] = [
      `Drawing features: ${drawing}`,
      `Road features: ${roads}`,
      `Project features: ${project}`,
      `Custom layers: ${custom}`,
    ];
    this.layerOrder.forEach((id) => {
      const src = this.layerRegistry[id]?.getSource();
      if (src instanceof VectorSource) {
        lines.push(`${this.layerMetadata[id]?.name || id}: ${src.getFeatures().length}`);
      }
    });
    this.nhaiLayerSummary = lines.join('\n');
    this.refreshReportStatus();
    this.cdr.detectChanges();
    this.showInstructions('Layer summary updated');
  }

  // ==================== ADDITIONAL TOOLBAR HANDLERS ====================

  /**
   * Select all features
   */
  private selectAll(): void {
    const features = this.vectorSource.getFeatures();
    
    if (!this.selectInteraction) {
      this.enableSelect();
    }
    
    // Highlight all features
    features.forEach(f => {
      const originalStyle = f.getStyle();
      f.set('_originalStyle', originalStyle);
      
      f.setStyle(new Style({
        stroke: new Stroke({ color: '#f39c12', width: 3 }),
        fill: new Fill({ color: 'rgba(243, 156, 18, 0.3)' }),
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: '#f39c12' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        })
      }));
    });

    this.showInstructions(`Selected ${features.length} features`);
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Clear selection
   */
  private clearSelection(): void {
    const features = this.vectorSource.getFeatures();
    
    features.forEach(f => {
      const originalStyle = f.get('_originalStyle');
      if (originalStyle) {
        f.setStyle(originalStyle);
        f.unset('_originalStyle');
      }
    });

    if (this.selectInteraction) {
      this.selectInteraction.getFeatures().clear();
    }

    this.showInstructions('Selection cleared');
    setTimeout(() => this.hideInstructions(), 2000);
  }

  /**
   * Setup additional event listeners for Analysis tab
   */
  private setupAnalysisTabListeners(): void {
    // Spatial Analysis
    const bufferAnalysisBtn = document.getElementById('bufferAnalysis');
    if (bufferAnalysisBtn) {
      bufferAnalysisBtn.addEventListener('click', () => this.bufferAnalysis());
    }

    const intersectionAnalysisBtn = document.getElementById('intersectionAnalysis');
    if (intersectionAnalysisBtn) {
      intersectionAnalysisBtn.addEventListener('click', () => this.intersectionAnalysis());
    }

    const overlayAnalysisBtn = document.getElementById('overlayAnalysis');
    if (overlayAnalysisBtn) {
      overlayAnalysisBtn.addEventListener('click', () => this.overlayAnalysisFunc());
    }

    const proximityAnalysisBtn = document.getElementById('proximityAnalysis');
    if (proximityAnalysisBtn) {
      proximityAnalysisBtn.addEventListener('click', () => this.proximityAnalysis());
    }

    const networkAnalysisBtn = document.getElementById('networkAnalysis');
    if (networkAnalysisBtn) {
      networkAnalysisBtn.addEventListener('click', () => this.networkAnalysis());
    }

    const terrainAnalysisBtn = document.getElementById('terrainAnalysis');
    if (terrainAnalysisBtn) {
      terrainAnalysisBtn.addEventListener('click', () => this.terrainAnalysis());
    }

    const earthworkAnalysisBtn = document.getElementById('earthworkAnalysis');
    if (earthworkAnalysisBtn) {
      earthworkAnalysisBtn.addEventListener('click', () => this.earthworkAnalysis());
    }

    const highwayGeometryBtn = document.getElementById('highwayGeometry');
    if (highwayGeometryBtn) {
      highwayGeometryBtn.addEventListener('click', () => this.highwayGeometryAnalysis());
    }

    // Statistical Analysis
    const calculateAreaBtn = document.getElementById('calculateArea');
    if (calculateAreaBtn) {
      calculateAreaBtn.addEventListener('click', () => this.calculateAreaStats());
    }

    const calculateLengthBtn = document.getElementById('calculateLength');
    if (calculateLengthBtn) {
      calculateLengthBtn.addEventListener('click', () => this.calculateLengthStats());
    }

    const densityAnalysisBtn = document.getElementById('densityAnalysis');
    if (densityAnalysisBtn) {
      densityAnalysisBtn.addEventListener('click', () => this.densityAnalysisFunc());
    }

    const clusteringAnalysisBtn = document.getElementById('clusteringAnalysis');
    if (clusteringAnalysisBtn) {
      clusteringAnalysisBtn.addEventListener('click', () => this.clusteringAnalysis());
    }

    const hotspotAnalysisBtn = document.getElementById('hotspotAnalysis');
    if (hotspotAnalysisBtn) {
      hotspotAnalysisBtn.addEventListener('click', () => this.hotspotAnalysis());
    }

    const patternAnalysisBtn = document.getElementById('patternAnalysis');
    if (patternAnalysisBtn) {
      patternAnalysisBtn.addEventListener('click', () => this.patternAnalysis());
    }

    // Cartographic Tools
    const generateMapBtn = document.getElementById('generateMap');
    if (generateMapBtn) {
      generateMapBtn.addEventListener('click', () => this.generateMap());
    }

    const createLayoutBtn = document.getElementById('createLayout');
    if (createLayoutBtn) {
      createLayoutBtn.addEventListener('click', () => this.createLayout());
    }

    const addLegendBtn = document.getElementById('addLegend');
    if (addLegendBtn) {
      addLegendBtn.addEventListener('click', () => this.addLegend());
    }

    const addScaleBtn = document.getElementById('addScale');
    if (addScaleBtn) {
      addScaleBtn.addEventListener('click', () => this.addScaleBar());
    }
  }

  /**
   * Setup Data tab listeners
   */
  private setupDataTabListeners(): void {
    // Data Sources
    const addWmsLayerBtn = document.getElementById('addWmsLayer');
    if (addWmsLayerBtn) {
      addWmsLayerBtn.addEventListener('click', () => this.addWmsLayer());
    }
    const submitWmsLayerBtn = document.getElementById('submitWmsLayerBtn');
    if (submitWmsLayerBtn) {
      submitWmsLayerBtn.addEventListener('click', () => this.submitWmsLayer());
    }

    const addWfsLayerBtn = document.getElementById('addWfsLayer');
    if (addWfsLayerBtn) {
      addWfsLayerBtn.addEventListener('click', () => this.addWfsLayer());
    }

    const addVectorLayerBtn = document.getElementById('addVectorLayer');
    if (addVectorLayerBtn) {
      addVectorLayerBtn.addEventListener('click', () => this.addVectorLayerDialog());
    }

    const addRasterLayerBtn = document.getElementById('addRasterLayer');
    if (addRasterLayerBtn) {
      addRasterLayerBtn.addEventListener('click', () => this.addRasterLayer());
    }

    const addDatabaseLayerBtn = document.getElementById('addDatabaseLayer');
    if (addDatabaseLayerBtn) {
      addDatabaseLayerBtn.addEventListener('click', () => this.addDatabaseLayer());
    }

    const addApiLayerBtn = document.getElementById('addApiLayer');
    if (addApiLayerBtn) {
      addApiLayerBtn.addEventListener('click', () => this.addApiLayer());
    }

    // Import buttons
    const importShapefileBtn = document.getElementById('importShapefile');
    if (importShapefileBtn) {
      importShapefileBtn.addEventListener('click', () => this.importShapefile());
    }

    const importGeoJSONBtn = document.getElementById('importGeoJSON');
    if (importGeoJSONBtn) {
      importGeoJSONBtn.addEventListener('click', () => this.importGeoJSONFile());
    }

    const importKMLBtn = document.getElementById('importKML');
    if (importKMLBtn) {
      importKMLBtn.addEventListener('click', () => this.importKMLFile());
    }

    const importGPXBtn = document.getElementById('importGPX');
    if (importGPXBtn) {
      importGPXBtn.addEventListener('click', () => this.importGPXFile());
    }

    const importCSVBtn = document.getElementById('importCSV');
    if (importCSVBtn) {
      importCSVBtn.addEventListener('click', () => this.importCSVFile());
    }

    // Validation buttons
    const checkDuplicatesBtn = document.getElementById('checkDuplicates');
    if (checkDuplicatesBtn) {
      checkDuplicatesBtn.addEventListener('click', () => this.checkDuplicates());
    }

    const checkGapsBtn = document.getElementById('checkGaps');
    if (checkGapsBtn) {
      checkGapsBtn.addEventListener('click', () => this.checkGaps());
    }

    const cleanDataBtn = document.getElementById('cleanData');
    if (cleanDataBtn) {
      cleanDataBtn.addEventListener('click', () => this.cleanData());
    }

    const loadBoqBomBtn = document.getElementById('loadBoqBomBtn');
    if (loadBoqBomBtn) {
      loadBoqBomBtn.addEventListener('click', () => this.loadBoqAndBom());
    }

    const generateBoqReportBtn = document.getElementById('generateBoqReportBtn');
    if (generateBoqReportBtn) {
      generateBoqReportBtn.addEventListener('click', () => this.generateBoqReport());
    }
  }

  /**
   * Setup additional toolbar listeners
   */
  private setupAdditionalToolbarListeners(): void {
    // Select All button
    const selectAllBtn = document.getElementById('selectAllBtn');
    if (selectAllBtn) {
      selectAllBtn.addEventListener('click', () => this.selectAll());
    }

    // Clear Selection button
    const clearSelectionBtn = document.getElementById('clearSelectionBtn');
    if (clearSelectionBtn) {
      clearSelectionBtn.addEventListener('click', () => this.clearSelection());
    }

    // Save button (to backend)
    const saveBtn = document.getElementById('saveBtn');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => this.saveAllLayers());
    }

    // Load button (from backend)
    const loadBtn = document.getElementById('loadBtn');
    if (loadBtn) {
      loadBtn.addEventListener('click', () => this.loadAllLayers());
    }

    // Angle measurement button
    const angleBtn = document.getElementById('angleBtn');
    if (angleBtn) {
      angleBtn.addEventListener('click', () => this.enableAngleMeasurement());
    }

    // Layers button – switch sidebar to Layers tab (and expand if collapsed)
    const layersBtn = document.getElementById('layersBtn');
    if (layersBtn) {
      layersBtn.addEventListener('click', () => this.switchToLayersTab());
    }

    // Print dialog buttons
    const printMapBtn = document.getElementById('printMapBtn');
    if (printMapBtn) {
      printMapBtn.addEventListener('click', () => this.executePrint());
    }

    const previewPrintBtn = document.getElementById('previewPrintBtn');
    if (previewPrintBtn) {
      previewPrintBtn.addEventListener('click', () => this.previewPrint());
    }

    // Help button
    const helpBtn = document.getElementById('helpBtn');
    if (helpBtn) {
      helpBtn.addEventListener('click', () => this.showHelp());
    }
  }

  private setupOfflineSupport(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('offline', () => {
      this.offlineMode = true;
      this.showOfflineBanner();
      this.persistLayersToOfflineStorage();
    });
    window.addEventListener('online', () => {
      this.offlineMode = false;
      this.hideOfflineBanner();
      this.showInstructions('Back online. Edits can be synced to server.');
    });
  }

  private showOfflineBanner(): void {
    if (this.offlineBannerEl) return;
    const el = document.createElement('div');
    el.id = 'gis-offline-banner';
    el.style.cssText = 'position:absolute;top:8px;left:50%;transform:translateX(-50%);background:#e74c3c;color:#fff;padding:8px 16px;border-radius:6px;z-index:1000;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,0.2);';
    el.textContent = 'Offline — edits saved locally and will sync when back online';
    const container = document.querySelector('.map-container');
    if (container) container.appendChild(el);
    this.offlineBannerEl = el;
  }

  private hideOfflineBanner(): void {
    if (this.offlineBannerEl?.parentNode) {
      this.offlineBannerEl.parentNode.removeChild(this.offlineBannerEl);
      this.offlineBannerEl = null;
    }
  }

  private persistLayersToOfflineStorage(): void {
    try {
      const data: { drawing: string; road: string } = {
        drawing: this.format.writeFeatures(this.vectorSource.getFeatures(), { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' }),
        road: this.format.writeFeatures(this.roadSource.getFeatures(), { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' })
      };
      localStorage.setItem(GisavimvpComponent.OFFLINE_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('Offline persist failed', e);
    }
  }

  private restoreLayersFromOfflineStorage(): void {
    try {
      const raw = localStorage.getItem(GisavimvpComponent.OFFLINE_STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.drawing) {
        const features = this.format.readFeatures(data.drawing, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
        this.vectorSource.addFeatures(features);
      }
      if (data.road) {
        const features = this.format.readFeatures(data.road, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });
        this.roadSource.addFeatures(features);
      }
      this.showInstructions('Restored locally saved layers (offline mode)');
    } catch (e) {
      console.warn('Offline restore failed', e);
    }
  }

  private cleanup(): void {
    this.hideOfflineBanner();
    window.removeEventListener('resize', this.onWindowResize);
    this.amStopClusterDistance?.();
    this.amStopClusterDistance = null;
    if (this.mapResizeObserver && this.mapElement?.nativeElement) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
    this.removeInteractions();
    this.stopSegmentDrawing();
    if (this.gpsWatchId) {
      navigator.geolocation.clearWatch(this.gpsWatchId);
    }
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    this.destroyRpMap();
  }

  // ===========================================================================
  // GIS Asset Management bridge
  // ===========================================================================

  /** Wire route → bridge → map. Loads layer metadata, fetches assets that
   *  match the active context, focuses the requested asset, and seeds the
   *  save panel for create flow. */
  private initializeAssetManagementBridge(): void {
    this.amBridge.activateFromRoute(this.route).subscribe({
      next: (ctx) => {
        this.amContext = ctx;
        this.amMode = ctx?.mode || null;
        this.amSelectedAssetId = ctx?.assetId || null;
        this.amSelectedLayerId = ctx?.layerId || null;
        // Auto-expand the floating panel when launched from the asset console.
        this.amPanelCollapsed = !ctx;
        if (ctx?.classification) {
          this.amSaveDraft.classification = ctx.classification;
        }
        if (ctx?.category) {
          this.amSaveDraft.asset_category = ctx.category;
        }

        // Reuse the editor's own layers — gisavimvp's layerRegistry is the
        // single source of truth for what "layers" exist on this map.
        this.amSyncLayersFromRegistry();
        if (!this.amSelectedLayerId && this.amLayers.length) {
          this.amSelectedLayerId = this.amLayers[0].id;
        }
        if (ctx) {
          this.amReloadAssets(/* fitView */ true);
        }

        // For "create" mode pop the save panel up front.
        if (ctx?.mode === 'create') {
          this.amShowSavePanel = true;
        }
      },
      error: (err) =>
        console.warn('[asset-mgmt] activateFromRoute failed', err),
    });
  }

  /** Reload assets for the current layer/classification context. */
  amReloadAssets(fitView = false): void {
    if (!this.amSource) return;
    this.amStatus = 'loading';
    this.amMessage = '';
    this.amBridge
      .loadAssetsInto(this.amSource, {
        layerId: this.amSelectedLayerId,
        classification: this.amContext?.classification || null,
        mapProjection: this.map?.getView().getProjection().getCode() || 'EPSG:3857',
      })
      .subscribe({
        next: (res) => {
          this.amFeatureCount = res.count;
          this.amStatus = 'idle';
          this.amSyncVisibleAssets();
          if (fitView && res.count > 0) {
            if (this.amSelectedAssetId) {
              this.amBridge.focusAsset(this.map, this.amSource, this.amSelectedAssetId);
            } else {
              this.amBridge.fitAllAssets(this.map, this.amSource);
            }
          }
        },
        error: (err) => {
          this.amStatus = 'error';
          this.amMessage =
            err?.error?.message || err?.message || 'Failed to load assets';
        },
      });
  }

  /** Switch the active asset-management layer filter. */
  amOnLayerChange(layerId: number | null): void {
    this.amSelectedLayerId = layerId;
    this.amReloadAssets(true);
  }

  /** Pick the first feature in the drawing source as the candidate to save. */
  private amPickCandidateFeature(): Feature<Geometry> | null {
    const drawingFeatures = this.vectorSource?.getFeatures() || [];
    if (drawingFeatures.length) {
      // prefer the most recently added feature
      return drawingFeatures[drawingFeatures.length - 1];
    }
    // fall back to the most recent feature in any non-asset-mgmt source
    const candidates: VectorSource[] = [
      this.vectorSource,
      this.roadSource,
      this.waterSource,
      this.separatorSource,
    ].filter(Boolean);
    for (let i = candidates.length - 1; i >= 0; i -= 1) {
      const feats = candidates[i].getFeatures();
      if (feats.length) return feats[feats.length - 1];
    }
    return null;
  }

  amOpenSavePanel(): void {
    this.amShowSavePanel = true;
    this.amMessage = '';
    if (!this.amSaveDraft.classification) {
      this.amSaveDraft.classification = 'overground';
    }
    if (!this.amSaveDraft.status) {
      this.amSaveDraft.status = 'planned';
    }
  }

  amCloseSavePanel(): void {
    this.amShowSavePanel = false;
  }

  /** Save the latest drawn feature as a brand-new asset. */
  amSaveDrawingAsAsset(): void {
    const layerId =
      this.amSaveDraft.layerId !== undefined && this.amSaveDraft.layerId !== null
        ? Number(this.amSaveDraft.layerId)
        : this.amSelectedLayerId;
    if (!layerId) {
      this.amMessage = 'Choose a layer before saving.';
      return;
    }
    const feat = this.amPickCandidateFeature();
    if (!feat) {
      this.amMessage = 'Draw a feature on the map first.';
      return;
    }
    this.amStatus = 'saving';
    this.amMessage = '';

    this.amBridge
      .saveOlFeatureAsAsset(
        feat,
        layerId,
        {
          name: this.amSaveDraft.name,
          asset_code: this.amSaveDraft.asset_code,
          description: this.amSaveDraft.description,
          classification: this.amSaveDraft.classification,
          asset_category: this.amSaveDraft.asset_category,
          status: this.amSaveDraft.status,
          depth_m: this.amSaveDraft.depth_m ?? null,
          elevation_m: this.amSaveDraft.elevation_m ?? null,
          attributes: this.amSaveDraft.attributes,
        },
        this.map.getView().getProjection().getCode() || 'EPSG:3857'
      )
      .subscribe({
        next: (asset) => {
          this.amStatus = 'idle';
          this.amMessage = `Saved as asset #${asset.id}`;
          this.amSelectedAssetId = asset.id;
          this.amSelectedLayerId = asset.properties.layer_id;
          this.amShowSavePanel = false;
          // remove the draft from the drawing layer; it's now owned by AM.
          if (this.vectorSource && this.vectorSource.hasFeature(feat)) {
            this.vectorSource.removeFeature(feat);
          }
          this.amReloadAssets(true);
        },
        error: (err) => {
          this.amStatus = 'error';
          this.amMessage =
            err?.error?.message || err?.message || 'Save failed';
        },
      });
  }

  /** Replace the geometry of the focused asset with the latest drawn feature. */
  amUpdateFocusedGeometry(): void {
    if (!this.amSelectedAssetId) {
      this.amMessage = 'No asset is currently focused.';
      return;
    }
    const feat = this.amPickCandidateFeature();
    if (!feat) {
      this.amMessage = 'Draw the new geometry on the map first.';
      return;
    }
    this.amStatus = 'saving';
    this.amMessage = '';
    this.amBridge
      .updateAssetGeometryFromOl(
        this.amSelectedAssetId,
        feat,
        { change_summary: 'Geometry updated from map editor' },
        this.map.getView().getProjection().getCode() || 'EPSG:3857'
      )
      .subscribe({
        next: (asset) => {
          this.amStatus = 'idle';
          this.amMessage = `Updated asset #${asset.id} → v${asset.properties.current_version}`;
          if (this.vectorSource && this.vectorSource.hasFeature(feat)) {
            this.vectorSource.removeFeature(feat);
          }
          this.amReloadAssets(true);
        },
        error: (err) => {
          this.amStatus = 'error';
          this.amMessage =
            err?.error?.message || err?.message || 'Update failed';
        },
      });
  }

  /** Soft-delete the currently focused asset. */
  amDeleteFocusedAsset(): void {
    if (!this.amSelectedAssetId) {
      this.amMessage = 'No asset is currently focused.';
      return;
    }
    if (!confirm('Delete this asset? It will be soft-deleted and can be restored later.')) {
      return;
    }
    this.amStatus = 'saving';
    this.amBridge.deleteAsset(this.amSelectedAssetId).subscribe({
      next: () => {
        this.amStatus = 'idle';
        this.amMessage = 'Asset deleted';
        this.amSelectedAssetId = null;
        this.amReloadAssets(false);
      },
      error: (err) => {
        this.amStatus = 'error';
        this.amMessage =
          err?.error?.message || err?.message || 'Delete failed';
      },
    });
  }

  /** Pan/zoom to the focused asset. */
  amFocusActiveAsset(): void {
    if (!this.amSelectedAssetId) return;
    this.amBridge.focusAsset(this.map, this.amSource, this.amSelectedAssetId);
  }

  amBackToConsole(): void {
    this.router.navigate(['/home/asset-management']);
  }

  /** True when the gis-mvp editor is being used inside an asset-management
   *  workflow (i.e. user opened it from the asset console). */
  amIsActive(): boolean {
    return !!this.amContext;
  }

  /** Refresh the in-panel asset list from the current source contents. */
  private amSyncVisibleAssets(): void {
    if (!this.amSource) {
      this.amVisibleAssets = [];
      return;
    }
    const next: typeof this.amVisibleAssets = [];
    for (const f of this.amSource.getFeatures()) {
      if (!this.amBridge.isBridgeFeature(f as Feature<Geometry>)) continue;
      const id = this.amBridge.getAssetIdFromFeature(f as Feature<Geometry>) ?? 0;
      const props = (f.get('amProperties') || {}) as any;
      const layerId = props.layer_id ?? null;
      const layer = this.amBridge.getLayer(layerId);
      next.push({
        id,
        name: props.name || props.asset_code || `Asset #${id}`,
        code: props.asset_code || '',
        classification: (props.classification || 'overground') as AssetClassification,
        category: props.asset_category || '—',
        status: (props.status || 'planned') as AssetStatus,
        layerId,
        layerName: layer?.name || '—',
        depth: props.depth_m ?? null,
        elevation: props.elevation_m ?? null,
      });
    }
    next.sort((a, b) => a.name.localeCompare(b.name));
    this.amVisibleAssets = next;
  }

  /** Filtered list driven by the in-panel search box. */
  get amFilteredAssets(): typeof this.amVisibleAssets {
    const q = (this.amSearch || '').trim().toLowerCase();
    if (!q) return this.amVisibleAssets;
    return this.amVisibleAssets.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.code.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q) ||
        a.status.toLowerCase().includes(q)
    );
  }

  /** Aggregate counts for the panel stats header. */
  get amStats(): {
    total: number;
    underground: number;
    overground: number;
    active: number;
    planned: number;
    inactive: number;
  } {
    let underground = 0;
    let overground = 0;
    let active = 0;
    let planned = 0;
    let inactive = 0;
    for (const a of this.amVisibleAssets) {
      if (a.classification === 'underground') underground += 1;
      else overground += 1;
      if (a.status === 'active') active += 1;
      else if (a.status === 'planned') planned += 1;
      else if (
        a.status === 'inactive' ||
        a.status === 'decommissioned' ||
        a.status === 'abandoned'
      ) {
        inactive += 1;
      }
    }
    return {
      total: this.amVisibleAssets.length,
      underground,
      overground,
      active,
      planned,
      inactive,
    };
  }

  /** Pan/zoom to a specific asset (used by the in-panel asset list). */
  amFocusAssetById(id: number): void {
    this.amSelectedAssetId = id;
    this.amBridge.focusAsset(this.map, this.amSource, id);
  }

  amSetTab(tab: 'browse' | 'create'): void {
    this.amTab = tab;
    if (tab === 'create') {
      this.amShowSavePanel = true;
    }
  }

  /** Build a rich hover popup for a bridge-managed asset feature. Returns
   *  null when the feature isn't an asset-management feature so the caller
   *  can fall back to the legacy popup behaviour. */
  private amBuildHoverPopup(feature: Feature<Geometry> | FeatureLike | null): string | null {
    const members = this.amBridge.featuresFromHit(feature as Feature<Geometry>);
    if (!members.length) return null;
    if (members.length > 1) {
      const escape = (v: any): string =>
        v === null || v === undefined
          ? ''
          : String(v)
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;');
      return `<strong>${escape(members.length)} assets</strong><br>Zoom in to inspect`;
    }
    feature = members[0];
    if (!feature || !this.amBridge.isBridgeFeature(feature as Feature<Geometry>)) {
      return null;
    }
    const props = feature.get('amProperties') || {};
    const layer = this.amBridge.getLayer(props.layer_id);

    const escape = (v: any): string =>
      v === null || v === undefined
        ? ''
        : String(v)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

    const name =
      props.name || props.asset_code || `Asset #${(feature as any).getId?.() || ''}`;
    const code = props.asset_code || '';
    const cls = props.classification || '—';
    const cat = props.asset_category || '—';
    const status = props.status || '—';
    const depth = props.depth_m != null ? `${props.depth_m} m` : null;
    const elevation = props.elevation_m != null ? `${props.elevation_m} m` : null;
    const version = props.current_version || 1;
    const layerName = layer ? layer.name : props.layer_id ? `Layer #${props.layer_id}` : '—';
    const desc = props.description || '';

    const clsColor = cls === 'underground' ? '#8e44ad' : '#1e88e5';
    const statusColor =
      status === 'active'
        ? '#2e7d32'
        : status === 'planned'
        ? '#1565c0'
        : status === 'under_construction'
        ? '#ef6c00'
        : status === 'inactive' || status === 'decommissioned' || status === 'abandoned'
        ? '#9e9e9e'
        : '#455a64';

    const row = (label: string, value: string) =>
      value
        ? `<div class="am-pop-row"><span class="am-pop-label">${label}</span><span class="am-pop-value">${value}</span></div>`
        : '';

    return `
      <div class="am-popup">
        <div class="am-popup-head" style="border-color:${clsColor}">
          <div class="am-popup-title">${escape(name)}</div>
          ${code ? `<div class="am-popup-code">${escape(code)}</div>` : ''}
        </div>
        <div class="am-popup-body">
          <div class="am-popup-chips">
            <span class="am-pop-chip" style="background:${clsColor}1a;color:${clsColor};border-color:${clsColor}66">${escape(cls)}</span>
            <span class="am-pop-chip">${escape(cat)}</span>
            <span class="am-pop-chip" style="background:${statusColor}1a;color:${statusColor};border-color:${statusColor}66">${escape(status)}</span>
            <span class="am-pop-chip">v${escape(version)}</span>
          </div>
          ${row('Layer', escape(layerName))}
          ${depth ? row('Depth', escape(depth)) : ''}
          ${elevation ? row('Elevation', escape(elevation)) : ''}
          ${desc ? `<div class="am-pop-desc">${escape(desc)}</div>` : ''}
        </div>
      </div>
    `;
  }

  /** Render a representative, locally-built set of GIS assets directly onto
   *  the bridge layer. Useful for demos and offline testing — no API hits.
   *  Generates a mix of underground (pipelines, cables, sewers) and
   *  overground (poles, transformers, buildings) assets centred around the
   *  current view center. */
  amLoadDemoData(): void {
    if (!this.amSource || !this.map) return;
    this.amStatus = 'loading';
    this.amMessage = '';

    const projection =
      this.map.getView().getProjection().getCode() || 'EPSG:3857';

    // Use the current map center (in lon/lat) as the anchor so demo data is
    // visible regardless of where the user has navigated.
    const view = this.map.getView();
    const center3857 = view.getCenter() || fromLonLat(this.defaultSettings.center);
    const [lng, lat] = toLonLat(center3857);

    // Always rebuild the layer list from the editor's registry first so demo
    // data is rooted in the same layer set as the rest of the UI.
    this.amSyncLayersFromRegistry();

    const demoAssets = this.buildDemoAssetFeatures(lng, lat);

    // Wipe any previously rendered bridge features so demo runs are idempotent.
    this.amSource
      .getFeatures()
      .filter((f) => f.get('__amAssetFeature__'))
      .forEach((f) => this.amSource.removeFeature(f));

    const olFeatures: Feature<Geometry>[] = [];
    for (const af of demoAssets) {
      const ol = this.amBridge.assetFeatureToOl(af, projection);
      if (ol) olFeatures.push(ol);
    }
    if (olFeatures.length) {
      this.amSource.addFeatures(olFeatures);
    }
    this.amFeatureCount = olFeatures.length;
    this.amStatus = 'idle';
    this.amMessage = `Loaded ${olFeatures.length} demo assets across ${this.amLayers.length} editor layers (local — no API call)`;
    this.amSyncVisibleAssets();

    // Fit view to the demo extents so the user instantly sees them.
    this.amBridge.fitAllAssets(this.map, this.amSource);
  }

  /** Build a deterministic-but-varied set of AssetFeature records around
   *  the supplied lon/lat anchor. Layers come from the gisavimvp registry
   *  so the demo data is bucketed under the same layers users already see
   *  in the editor's layer panel. */
  private buildDemoAssetFeatures(
    lng: number,
    lat: number
  ): AssetFeature[] {
    const now = new Date().toISOString();
    // ~0.001 deg ≈ ~110 m. Spread features across roughly a 1 km box.
    const δ = (n: number) => n * 0.001;

    const features: AssetFeature[] = [];

    // Layer ids derived from gisavimvp's actual layer keys, so the AM panel
    // and the editor's layer panel agree on what "layer X" means. We fall
    // back to the first available registry layer if a preferred key isn't
    // registered (e.g. running in a slimmed-down editor).
    const layerKeyOf = (preferred: string, fallback: string): string => {
      if (this.layerRegistry?.[preferred]) return preferred;
      if (this.layerRegistry?.[fallback]) return fallback;
      return this.layerOrder.find((k) => k && k !== 'assetManagement') || preferred;
    };
    const lid = (preferred: string, fallback = 'drawing'): number =>
      this.amLayerKeyToId(layerKeyOf(preferred, fallback));
    const lcat = (preferred: string, fallback = 'drawing'): string => {
      const key = layerKeyOf(preferred, fallback);
      return GisavimvpComponent.AM_LAYER_PROFILES[key]?.category || 'custom';
    };

    const push = (
      id: number,
      props: Partial<AssetFeature['properties']> & {
        layer_id: number;
        asset_category: string;
        classification: AssetClassification;
        status: AssetStatus;
        name: string;
      },
      geometry: AssetFeature['geometry']
    ) => {
      features.push({
        type: 'Feature',
        id,
        geometry,
        properties: {
          uuid: `demo-${id}`,
          organization_id: null,
          branch_id: null,
          asset_code: `DEMO-${id}`,
          description: `Locally-generated demo asset #${id}`,
          attributes: { source: 'demo', generated_at: now },
          current_version: 1,
          created_at: now,
          updated_at: now,
          ...props,
        },
      });
    };

    // ---- Underground: water pipelines on the editor's `water` layer ----
    push(
      8001,
      {
        layer_id: lid('water'),
        name: 'Trunk water main',
        classification: 'underground',
        asset_category: lcat('water'),
        status: 'active',
        depth_m: 1.8,
      },
      {
        type: 'LineString',
        coordinates: [
          [lng - δ(2.5), lat - δ(2)],
          [lng - δ(1.0), lat - δ(1.5)],
          [lng + δ(0.5), lat - δ(0.8)],
          [lng + δ(2.0), lat],
        ],
      }
    );
    push(
      8002,
      {
        layer_id: lid('water'),
        name: 'Distribution line A',
        classification: 'underground',
        asset_category: lcat('water'),
        status: 'active',
        depth_m: 1.2,
      },
      {
        type: 'LineString',
        coordinates: [
          [lng - δ(0.8), lat - δ(1.5)],
          [lng - δ(0.6), lat],
          [lng - δ(0.4), lat + δ(1.2)],
        ],
      }
    );

    // ---- Underground: feeders / cables on `separator` ----
    push(
      8101,
      {
        layer_id: lid('separator', 'water'),
        name: 'HV feeder',
        classification: 'underground',
        asset_category: lcat('separator', 'water'),
        status: 'active',
        depth_m: 1.0,
      },
      {
        type: 'LineString',
        coordinates: [
          [lng + δ(2.5), lat + δ(2.0)],
          [lng + δ(1.0), lat + δ(1.0)],
          [lng - δ(0.5), lat + δ(0.5)],
          [lng - δ(2.0), lat],
        ],
      }
    );
    push(
      8102,
      {
        layer_id: lid('separator', 'water'),
        name: 'Spur cable B-12',
        classification: 'underground',
        asset_category: lcat('separator', 'water'),
        status: 'planned',
        depth_m: 0.9,
      },
      {
        type: 'LineString',
        coordinates: [
          [lng - δ(0.5), lat + δ(0.5)],
          [lng - δ(0.5), lat - δ(0.6)],
        ],
      }
    );

    // ---- Underground: sewer on `water` ----
    push(
      8201,
      {
        layer_id: lid('water'),
        name: 'Sewer collector',
        classification: 'underground',
        asset_category: 'sewer',
        status: 'under_construction',
        depth_m: 2.5,
      },
      {
        type: 'LineString',
        coordinates: [
          [lng - δ(2.0), lat + δ(2.0)],
          [lng - δ(1.5), lat + δ(1.0)],
          [lng - δ(1.0), lat - δ(0.2)],
          [lng - δ(0.6), lat - δ(1.5)],
        ],
      }
    );

    // ---- Overground: poles on `project` ----
    const poleAnchors = [
      [lng - δ(1.5), lat - δ(1.0)],
      [lng - δ(0.5), lat - δ(0.5)],
      [lng + δ(0.7), lat - δ(0.2)],
      [lng + δ(1.8), lat + δ(0.5)],
    ];
    poleAnchors.forEach(([x, y], i) => {
      push(
        8300 + i,
        {
          layer_id: lid('project'),
          name: `Pole P-${100 + i}`,
          classification: 'overground',
          asset_category: 'pole',
          status: i === 2 ? 'inactive' : 'active',
          elevation_m: 8.5,
        },
        { type: 'Point', coordinates: [x, y] }
      );
    });

    // ---- Overground: transformers on `project` ----
    push(
      8401,
      {
        layer_id: lid('project'),
        name: 'TX-22 (500 kVA)',
        classification: 'overground',
        asset_category: 'transformer',
        status: 'active',
      },
      { type: 'Point', coordinates: [lng + δ(1.0), lat + δ(1.0)] }
    );
    push(
      8402,
      {
        layer_id: lid('project'),
        name: 'TX-23 (250 kVA)',
        classification: 'overground',
        asset_category: 'transformer',
        status: 'active',
      },
      { type: 'Point', coordinates: [lng - δ(1.2), lat + δ(1.4)] }
    );

    // ---- Overground: building footprints on `rowParcel` ----
    push(
      8501,
      {
        layer_id: lid('rowParcel', 'project'),
        name: 'Substation building',
        classification: 'overground',
        asset_category: 'building',
        status: 'active',
      },
      {
        type: 'Polygon',
        coordinates: [
          [
            [lng + δ(0.6), lat + δ(0.6)],
            [lng + δ(1.4), lat + δ(0.6)],
            [lng + δ(1.4), lat + δ(1.3)],
            [lng + δ(0.6), lat + δ(1.3)],
            [lng + δ(0.6), lat + δ(0.6)],
          ],
        ],
      }
    );
    push(
      8502,
      {
        layer_id: lid('rowParcel', 'project'),
        name: 'Pump house',
        classification: 'overground',
        asset_category: 'building',
        status: 'active',
      },
      {
        type: 'Polygon',
        coordinates: [
          [
            [lng - δ(2.4), lat - δ(1.6)],
            [lng - δ(1.7), lat - δ(1.6)],
            [lng - δ(1.7), lat - δ(1.0)],
            [lng - δ(2.4), lat - δ(1.0)],
            [lng - δ(2.4), lat - δ(1.6)],
          ],
        ],
      }
    );

    // ---- Overground: roads on `road` ----
    push(
      8601,
      {
        layer_id: lid('road'),
        name: 'Service road segment',
        classification: 'overground',
        asset_category: lcat('road'),
        status: 'active',
      },
      {
        type: 'LineString',
        coordinates: [
          [lng + δ(2.0), lat - δ(2.0)],
          [lng + δ(2.4), lat - δ(0.8)],
          [lng + δ(2.6), lat + δ(0.5)],
          [lng + δ(2.5), lat + δ(2.0)],
        ],
      }
    );

    return features;
  }

  /** Show / hide the floating asset-management panel. Refreshes the list of
   *  available layers (sourced from the gisavimvp registry) on every expand
   *  so newly-added editor layers appear immediately. */
  amTogglePanel(): void {
    this.amPanelCollapsed = !this.amPanelCollapsed;
    if (!this.amPanelCollapsed) {
      this.amSyncLayersFromRegistry();
      if (!this.amSelectedLayerId && this.amLayers.length) {
        this.amSelectedLayerId = this.amLayers[0].id;
      }
    }
  }

  /**
   * Build the asset-management layer list from gisavimvp's `layerRegistry`
   * and push it into the bridge cache. The synthetic ids (`amLayerKeyToId`)
   * are deterministic so the same gisavimvp key always maps to the same
   * AM layer id across re-renders. The internal `assetManagement` overlay
   * (added by the bridge itself) is excluded.
   */
  private amSyncLayersFromRegistry(): void {
    const profiles = GisavimvpComponent.AM_LAYER_PROFILES;
    const now = new Date().toISOString();
    const list: AssetLayer[] = [];

    for (const key of this.layerOrder) {
      if (!key || key === 'assetManagement') continue;
      const profile =
        profiles[key] ||
        {
          name: key.charAt(0).toUpperCase() + key.slice(1),
          classification: 'overground' as AssetClassification,
          category: 'custom',
          geometry: 'POINT' as const,
        };
      list.push({
        id: this.amLayerKeyToId(key),
        organization_id: null,
        branch_id: null,
        code: key.toUpperCase(),
        name: profile.name,
        description: this.layerMetadata?.[key]?.description || '',
        classification: profile.classification,
        asset_category: profile.category,
        geometry_type: profile.geometry,
        srid: 4326,
        is_active: true,
        created_at: now,
        updated_at: now,
      });
    }

    this.amLayers = list;
    this.amBridge.useLocalLayers(list);
  }

  /** Stable, deterministic numeric id derived from a gisavimvp layer key.
   *  Uses a 32-bit hash over the string, then masks to a positive integer
   *  in the synthetic range 50000-99999. */
  private amLayerKeyToId(key: string): number {
    let h = 0;
    for (let i = 0; i < key.length; i += 1) {
      h = (h * 31 + key.charCodeAt(i)) | 0;
    }
    return 50000 + Math.abs(h) % 50000;
  }

  /** Look up the gisavimvp layer key that corresponds to an AM layer id. */
  private amIdToLayerKey(id: number | null | undefined): string | null {
    if (id == null) return null;
    for (const key of this.layerOrder) {
      if (this.amLayerKeyToId(key) === id) return key;
    }
    return null;
  }
}